import React, { useState, useEffect, useRef } from 'react';
import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
  ActivityIndicator,
  Modal,
  Image,
  Animated,
} from 'react-native';
import { Feather, MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { db, auth, functions, storage, STRIPE_PUBLISHABLE_KEY } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Firebase callable functions for payments and emails
const createPaymentIntent = httpsCallable(functions, 'createPaymentIntent');
const sendInvoiceEmail = httpsCallable(functions, 'sendInvoiceEmail');
const sendBookingConfirmation = httpsCallable(functions, 'sendBookingConfirmation');
const sendFollowUpEmail = httpsCallable(functions, 'sendFollowUpEmail');
const sendReminderEmail = httpsCallable(functions, 'sendReminderEmail');
const sendLeadFollowUp = httpsCallable(functions, 'sendLeadFollowUp');
const createInvoice = httpsCallable(functions, 'createInvoice');
const sendInvoiceLink = httpsCallable(functions, 'sendInvoiceLink');

// Icon component for consistent sizing
const Icon = ({ name, size = 18, color = '#94a3b8', type = 'feather' }) => {
  if (type === 'material') {
    return <MaterialCommunityIcons name={name} size={size} color={color} />;
  }
  if (type === 'ionicons') {
    return <Ionicons name={name} size={size} color={color} />;
  }
  return <Feather name={name} size={size} color={color} />;
};
import { doc, getDoc, setDoc, collection, getDocs, addDoc, updateDoc, deleteDoc, query, orderBy, where, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';

const { width, height } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

// Inject CSS for admin theme
if (isWeb && typeof document !== 'undefined') {
  const adminStyle = document.createElement('style');
  adminStyle.id = 'admin-theme-styles';
  if (!document.getElementById('admin-theme-styles')) {
    adminStyle.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
      
      .admin-container * {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important;
      }
      
      @keyframes adminShimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
      
      .admin-btn-primary {
        position: relative;
        overflow: hidden;
        transition: all 0.2s ease;
        box-shadow: 0 4px 15px rgba(212, 168, 75, 0.25);
      }
      
      .admin-btn-primary:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 20px rgba(212, 168, 75, 0.35);
      }
      
      .admin-btn-primary::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
        animation: adminShimmer 2.5s ease-in-out infinite;
        pointer-events: none;
      }
      
      .admin-card {
        transition: all 0.2s ease;
      }
      
      .admin-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 30px rgba(0,0,0,0.3);
      }
      
      .admin-input:focus {
        outline: none;
        border-color: #d4a84b !important;
        box-shadow: 0 0 0 3px rgba(212, 168, 75, 0.15);
      }
      
      /* Force white text color on all inputs and textareas */
      input, textarea {
        color: #FFFFFF !important;
        -webkit-text-fill-color: #FFFFFF !important;
      }
      
      input::placeholder, textarea::placeholder {
        color: #6B7280 !important;
        -webkit-text-fill-color: #6B7280 !important;
      }
      
      .admin-sidebar-item {
        transition: all 0.15s ease;
      }
      
      .admin-sidebar-item:hover {
        background-color: rgba(212, 168, 75, 0.08);
      }
    `;
    document.head.appendChild(adminStyle);
  }
}

// Brand Colors - Matching main website theme
const colors = {
  darkNavy: '#0a0e14',
  navy: '#12171f',
  navyLight: '#1a2332',
  navyMid: '#252f3f',
  // Primary accent - Gold (matches main site)
  teal: '#d4a84b',           // Primary accent (gold)
  tealDark: '#c9975a',
  tealBright: '#f0c865',
  tealGlow: 'rgba(212, 168, 75, 0.15)',
  // Secondary accent - Teal/Cyan
  gold: '#d4a84b',           // Actual gold color
  goldLight: '#f0c865',
  cyan: '#40b4b4',           // Teal/Cyan
  cyanLight: '#4ecdc4',
  green: '#3fb950',          // Success green
  greenLight: '#56d364',
  white: '#ffffff',
  cream: '#f8fafc',
  gray: '#6b7a90',
  grayLight: '#94a3b8',
  grayDark: '#3d4a5c',
  textMuted: '#6b7a90',      // Muted text color
  red: '#f85149',
  orange: '#e67e22',
  blue: '#58a6ff',
  purple: '#a371f7',
  glassBorder: 'rgba(255, 255, 255, 0.08)',
  accent: '#d4a84b',
};

// Mapbox public token for route mapping and service area visualization
const MAPBOX_PUBLIC_TOKEN = 'pk.eyJ1IjoibWFrZW5vaXNlbWVkaWEiLCJhIjoiY2xvZGtmdW53MDdxYjJqcGw3ZTNlNGlxMyJ9.dGhAeNbJqE5Sb1xy6eRrgw';

// Event/Job Sources
const EVENT_SOURCES = [
  { id: 'website', label: 'Website Estimator', iconName: 'globe', color: colors.teal },
  { id: 'phone', label: 'Phone Call', iconName: 'phone', color: colors.blue },
  { id: 'email', label: 'Email', iconName: 'mail', color: colors.purple },
  { id: 'referral', label: 'Word of Mouth', iconName: 'message-circle', color: colors.gold },
  { id: 'manual', label: 'Manual Entry', iconName: 'edit-3', color: colors.gray },
];

// Complexity Types
const COMPLEXITY_TYPES = [
  { id: 'simple', label: 'Simple', color: colors.teal, desc: 'Quick fix, single task' },
  { id: 'moderate', label: 'Moderate', color: colors.gold, desc: 'Multiple tasks, 2-4 hours' },
  { id: 'complex', label: 'Complex', color: colors.orange, desc: 'Major work, half day+' },
];

// Client Types
const CLIENT_TYPES = [
  { id: 'potential', label: 'Potential', color: colors.orange },
  { id: 'client', label: 'Client', color: colors.teal },
  { id: 'returning', label: 'Returning', color: colors.blue },
  { id: 'vip', label: 'VIP', color: colors.gold },
  { id: 'archived', label: 'Archived', color: colors.gray },
];

// Client categories for main filter tabs
const CLIENT_CATEGORIES = [
  { id: 'all', label: 'All Clients', types: ['client', 'returning', 'vip', 'potential', 'new'], icon: 'users' },
  { id: 'potential', label: 'Potential', types: ['potential', 'new'], icon: 'user-plus' },
  { id: 'client', label: 'Active', types: ['client'], icon: 'user-check' },
  { id: 'returning', label: 'Returning', types: ['returning'], icon: 'repeat' },
  { id: 'vip', label: 'VIP', types: ['vip'], icon: 'star' },
  { id: 'archived', label: 'Archived', types: ['archived'], icon: 'archive' },
];

// No mock data - using real Firestore data only

// Sidebar Component with hierarchical navigation
const Sidebar = ({ activeTab, setActiveTab, activeSubTab, setActiveSubTab, onLogout, isCollapsed, onToggle, isMobile }) => {
  const [expandedSections, setExpandedSections] = useState(['dashboard']);
  const slideAnim = useRef(new Animated.Value(isCollapsed ? -280 : 0)).current;
  const overlayAnim = useRef(new Animated.Value(isCollapsed ? 0 : 1)).current;
  
  // Animate sidebar when isCollapsed changes
  useEffect(() => {
    Animated.parallel([
      Animated.spring(slideAnim, {
        toValue: isCollapsed ? -280 : 0,
        friction: 20,
        tension: 100,
        useNativeDriver: false,
      }),
      Animated.timing(overlayAnim, {
        toValue: isCollapsed ? 0 : 1,
        duration: 200,
        useNativeDriver: false,
      })
    ]).start();
  }, [isCollapsed]);

  const menuStructure = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      iconName: 'bar-chart-2',
      subItems: [
        { id: 'overview', label: 'Overview', iconName: 'trending-up' },
        { id: 'leads', label: 'Leads & Estimates', iconName: 'inbox', hasBadge: true },
        { id: 'scheduler', label: 'Scheduler', iconName: 'calendar' },
        { id: 'appointments', label: 'Appointments', iconName: 'clipboard' },
        { id: 'route-mapper', label: 'Route Mapper', iconName: 'map' },
        { id: 'clients', label: 'Clients', iconName: 'users' },
        { id: 'finances', label: 'Finances', iconName: 'dollar-sign' },
      ]
    },
    { id: 'field-manuals', label: 'Field Manuals', iconName: 'book-open' },
    { 
      id: 'site-management', 
      label: 'Site Management', 
      iconName: 'tool',
      subItems: [
        { id: 'hours', label: 'Business Hours', iconName: 'clock' },
        { id: 'services', label: 'Services Display', iconName: 'grid' },
        { id: 'portfolio', label: 'Portfolio Gallery', iconName: 'image' },
        { id: 'testimonials', label: 'Testimonials', iconName: 'star' },
        { id: 'estimates', label: 'Estimates & Quotes', iconName: 'dollar-sign' },
        { id: 'payment', label: 'Payment Settings', iconName: 'credit-card' },
        { id: 'maintenance', label: 'Maintenance Mode', iconName: 'alert-triangle' },
      ]
    },
    { id: 'reminders', label: 'Reminders', iconName: 'bell' },
    { id: 'settings', label: 'Settings', iconName: 'settings' },
  ];

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleItemPress = (item, subItem = null) => {
    if (item.subItems) {
      toggleSection(item.id);
      if (!expandedSections.includes(item.id)) {
        setActiveTab(item.id);
        setActiveSubTab(item.subItems[0].id);
      }
    } else {
      setActiveTab(item.id);
      setActiveSubTab(null);
    }
  };

  const handleSubItemPress = (parentId, subItemId) => {
    setActiveTab(parentId);
    setActiveSubTab(subItemId);
    // Close sidebar on mobile after selection
    if (isMobile && onToggle) {
      onToggle();
    }
  };

  const handleItemPressWithClose = (item) => {
    handleItemPress(item);
    // Close sidebar on mobile if no subitems
    if (isMobile && !item.subItems && onToggle) {
      onToggle();
    }
  };

  // Always render for animation, but control visibility with animation
  // Both mobile and desktop use slide animation (no fade)
  const sidebarStyle = {
    transform: [{ translateX: slideAnim }]
  };

  return (
    <>
      {/* Mobile overlay with animated opacity */}
      {isMobile && (
        <Animated.View 
          style={[
            styles.sidebarOverlay,
            { 
              opacity: overlayAnim,
              pointerEvents: isCollapsed ? 'none' : 'auto'
            }
          ]}
        >
        <TouchableOpacity 
            style={{ flex: 1 }} 
          onPress={onToggle}
          activeOpacity={1}
        />
        </Animated.View>
      )}
      <Animated.View style={[
        styles.sidebar,
        isMobile ? styles.sidebarMobile : styles.sidebarDesktop,
        sidebarStyle,
        { overflow: 'hidden' }
      ]}>
      <View style={styles.sidebarHeader}>
          <View style={styles.sidebarLogoRow}>
        <Text style={styles.sidebarLogo}>HANDY</Text>
        <Text style={styles.sidebarLogoAccent}>HAWK</Text>
          </View>
        <View style={styles.sidebarAdminBadge}>
          <View style={styles.sidebarAdminDot} />
          <Text style={styles.sidebarAdminText}>Admin Panel</Text>
        </View>
        {/* Close button - Mobile shows X, Desktop shows collapse arrow */}
        <TouchableOpacity 
          style={[styles.sidebarCloseBtn, !isMobile && styles.sidebarCollapseBtnDesktop]} 
          onPress={onToggle}
        >
          <Icon 
            name={isMobile ? "x" : "chevrons-left"} 
            size={isMobile ? 24 : 20} 
            color={isMobile ? colors.white : colors.gray} 
          />
            </TouchableOpacity>
      </View>
      
      <ScrollView style={styles.sidebarMenu} showsVerticalScrollIndicator={false}>
        {menuStructure.map((item) => (
          <View key={item.id}>
          <TouchableOpacity
            style={[
              styles.sidebarItem,
                activeTab === item.id && !item.subItems && styles.sidebarItemActive,
                item.subItems && expandedSections.includes(item.id) && styles.sidebarItemExpanded
            ]}
              onPress={() => handleItemPress(item)}
          >
            <View style={styles.sidebarItemIcon}>
              <Icon 
                name={item.iconName} 
                size={18} 
                color={(activeTab === item.id || expandedSections.includes(item.id)) ? colors.gold : colors.text} 
              />
            </View>
            <Text style={[
              styles.sidebarItemText,
                (activeTab === item.id || expandedSections.includes(item.id)) && styles.sidebarItemTextActive
            ]}>
              {item.label}
              </Text>
              {item.subItems && (
                <View style={styles.sidebarExpandIcon}>
                  <Icon 
                    name={expandedSections.includes(item.id) ? 'chevron-down' : 'chevron-right'} 
                    size={14} 
                    color={colors.textMuted} 
                  />
                </View>
              )}
            </TouchableOpacity>
            
            {/* Sub-items */}
            {item.subItems && expandedSections.includes(item.id) && (
              <View style={styles.sidebarSubMenu}>
                {item.subItems.map((subItem) => (
                  <TouchableOpacity
                    key={subItem.id}
                    style={[
                      styles.sidebarSubItem,
                      activeTab === item.id && activeSubTab === subItem.id && styles.sidebarSubItemActive
                    ]}
                    onPress={() => handleSubItemPress(item.id, subItem.id)}
                  >
                    <View style={styles.sidebarSubItemIcon}>
                      <Icon 
                        name={subItem.iconName} 
                        size={16} 
                        color={(activeTab === item.id && activeSubTab === subItem.id) ? colors.gold : colors.textMuted} 
                      />
                    </View>
                    <Text style={[
                      styles.sidebarSubItemText,
                      activeTab === item.id && activeSubTab === subItem.id && styles.sidebarSubItemTextActive
                    ]}>
                      {subItem.label}
                    </Text>
                    {subItem.hasBadge && subItem.id === 'leads' && (
                      <View style={styles.sidebarBadge}>
                        <View style={styles.sidebarBadgeDot} />
                      </View>
                    )}
          </TouchableOpacity>
        ))}
      </View>
            )}
          </View>
        ))}
      </ScrollView>

      <View style={styles.sidebarFooter}>
        <TouchableOpacity style={styles.logoutBtn} onPress={onLogout}>
            <View style={styles.logoutIcon}>
              <Icon name="log-out" size={18} color={colors.error} />
            </View>
          <Text style={styles.logoutText}>Logout</Text>
        </TouchableOpacity>
      </View>
      </Animated.View>
    </>
  );
};

// Overview Tab (Dashboard > Overview)
const OverviewTab = ({ onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pendingJobs: 0,
    todayJobs: 0,
    weekRevenue: 0,
    monthRevenue: 0,
    todayNextJob: null,
    activeClients: 0,
    potentialClients: 0,
    completedThisWeek: 0,
    newEstimates: 0,
    scheduledJobs: 0,
    pendingReviews: 0,
  });
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  // Navigation helper
  const navigateTo = (tab, subTab = null, options = null) => {
    if (onNavigate) {
      onNavigate(tab, subTab, options);
    }
  };

  // Load real data from Firestore
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      // Get all events/jobs
      const eventsRef = collection(db, 'events');
      const eventsQuery = query(eventsRef, orderBy('date', 'asc'));
      const eventsSnapshot = await getDocs(eventsQuery);
      const allEvents = [];
      eventsSnapshot.forEach(doc => {
        allEvents.push({ id: doc.id, ...doc.data() });
      });
      
      // Filter for upcoming jobs (today onwards)
      const upcoming = allEvents.filter(e => e.date >= todayStr).slice(0, 5);
      setUpcomingJobs(upcoming);
      
      // Count pending jobs (need approval)
      const pendingJobs = allEvents.filter(e => e.status === 'pending' || !e.status).length;
      
      // Today's jobs
      const todayJobs = allEvents.filter(e => e.date === todayStr);
      const todayJobsCount = todayJobs.length;
      
      // Next job today
      const confirmedToday = todayJobs.filter(e => e.status === 'confirmed');
      const nextJob = confirmedToday.length > 0 ? confirmedToday[0] : null;
      
      // Completed this week
      const completedThisWeek = allEvents.filter(e => 
        e.status === 'completed' && e.date >= weekAgo && e.date <= todayStr
      ).length;
      
      // Scheduled jobs this week (upcoming confirmed/approved jobs within the next 7 days)
      const scheduledJobs = allEvents.filter(e => 
        (e.status === 'confirmed' || e.status === 'approved') && 
        e.date >= todayStr && e.date <= weekFromNow
      ).length;
      
      // Get clients counts
      const clientsSnapshot = await getDocs(collection(db, 'clients'));
      let activeClients = 0;
      let potentialClients = 0;
      clientsSnapshot.forEach(doc => {
        const client = doc.data();
        if (client.type === 'potential' || client.type === 'new') {
          potentialClients++;
        } else if (client.type !== 'archived') {
          activeClients++;
        }
      });
      
      // Get new estimates count
      let newEstimates = 0;
      try {
        const estimatesRef = collection(db, 'estimates');
        const estimatesSnapshot = await getDocs(estimatesRef);
        newEstimates = estimatesSnapshot.size;
      } catch (e) {
        // estimates collection may not exist yet
      }
      
      // Get pending reviews count
      let pendingReviews = 0;
      try {
      const testimonialsRef = collection(db, 'testimonials');
        const testimonialsSnapshot = await getDocs(testimonialsRef);
        testimonialsSnapshot.forEach(doc => {
          const testimonial = doc.data();
          if (testimonial.status === 'pending' || !testimonial.active) {
            pendingReviews++;
          }
        });
      } catch (e) {
        // testimonials collection may not exist yet
      }
      
      // Get revenue from payments
      let weekRevenue = 0;
      let monthRevenue = 0;
      let todayRevenue = 0;
      try {
        const paymentsRef = collection(db, 'payments');
        const paymentsSnapshot = await getDocs(paymentsRef);
        paymentsSnapshot.forEach(doc => {
          const payment = doc.data();
          const paymentDate = payment.createdAt?.toDate?.()?.toISOString().split('T')[0] || payment.date;
          const amount = parseFloat(payment.amount) || 0;
          
          if (paymentDate === todayStr) {
            todayRevenue += amount;
          }
          if (paymentDate >= weekAgo) {
            weekRevenue += amount;
          }
          if (paymentDate >= monthAgo) {
            monthRevenue += amount;
          }
        });
      } catch (e) {
        // payments collection may not exist yet
      }
      
      setStats({
        pendingJobs,
        todayJobs: todayJobsCount,
        weekRevenue,
        monthRevenue,
        todayRevenue,
        todayNextJob: nextJob,
        activeClients,
        potentialClients,
        completedThisWeek,
        newEstimates,
        scheduledJobs,
        pendingReviews,
      });
      
      // Build recent activity from payments and events
      const activity = [];
      
      // Add recent events as activity
      allEvents.slice(0, 5).forEach(event => {
        activity.push({
          type: 'job',
          text: `${event.service}`,
          subtext: `for ${event.clientName}`,
          time: event.date,
          color: colors.blue
        });
      });
      
      // Try to get recent payments
      try {
        const paymentsRef = collection(db, 'payments');
        const paymentsQuery = query(paymentsRef, orderBy('createdAt', 'desc'));
        const paymentsSnapshot = await getDocs(paymentsQuery);
        paymentsSnapshot.docs.slice(0, 3).forEach(doc => {
          const payment = doc.data();
          activity.push({
            type: 'payment',
            text: `Payment received`,
            subtext: `$${payment.amount} from ${payment.clientName}`,
            time: payment.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently',
            color: colors.teal
          });
        });
      } catch (e) {
        // payments collection may not exist yet
      }
      
      setRecentActivity(activity.slice(0, 5));
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
  <View style={styles.tabContent}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.teal} />
          <Text style={styles.loadingText}>Loading dashboard...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.tabContent}>
      <Text style={styles.tabTitle}>Dashboard Overview</Text>
    <Text style={styles.tabSubtitle}>Welcome back! Here's what's happening today.</Text>
    
    <View style={styles.statsGrid}>
      <TouchableOpacity 
        style={[styles.statCard, styles.statCardClickable, { borderLeftColor: colors.gold }]}
        onPress={() => navigateTo('dashboard', 'appointments')}
        activeOpacity={0.7}
      >
        <Text style={styles.statNumber}>{stats.pendingJobs}</Text>
        <Text style={styles.statLabel}>Pending Jobs</Text>
        <Text style={styles.statChange}>Need approval</Text>
        <View style={styles.statCardArrow}><Text style={styles.statCardArrowText}>→</Text></View>
      </TouchableOpacity>
      <TouchableOpacity 
        style={[styles.statCard, styles.statCardClickable, { borderLeftColor: colors.blue }]}
        onPress={() => navigateTo('dashboard', 'scheduler')}
        activeOpacity={0.7}
      >
        <Text style={styles.statNumber}>{stats.scheduledJobs}</Text>
        <Text style={styles.statLabel}>Upcoming Jobs</Text>
        <Text style={styles.statChange}>This week</Text>
        <View style={styles.statCardArrow}><Text style={styles.statCardArrowText}>→</Text></View>
      </TouchableOpacity>
      <TouchableOpacity 
        style={[styles.statCard, styles.statCardClickable, { borderLeftColor: colors.teal }]}
        onPress={() => navigateTo('dashboard', 'finances')}
        activeOpacity={0.7}
      >
        <Text style={styles.statNumber}>${stats.todayRevenue || 0}</Text>
        <Text style={styles.statLabel}>Today's Revenue</Text>
        <Text style={styles.statChange}>Payments received</Text>
        <View style={styles.statCardArrow}><Text style={styles.statCardArrowText}>→</Text></View>
      </TouchableOpacity>
      <TouchableOpacity 
        style={[styles.statCard, styles.statCardClickable, { borderLeftColor: colors.purple }]}
        onPress={() => navigateTo('dashboard', 'clients', { category: 'potential' })}
        activeOpacity={0.7}
      >
        <Text style={styles.statNumber}>{stats.potentialClients}</Text>
        <Text style={styles.statLabel}>Potential Clients</Text>
        <Text style={styles.statChange}>From estimates</Text>
        <View style={styles.statCardArrow}><Text style={styles.statCardArrowText}>→</Text></View>
      </TouchableOpacity>
      </View>

      <View style={styles.dashboardGrid}>
        {/* Upcoming Jobs */}
        <TouchableOpacity 
          style={styles.dashboardCard}
          onPress={() => navigateTo('dashboard', 'scheduler')}
          activeOpacity={0.8}
        >
          <View style={styles.dashboardCardHeader}>
            <View style={styles.dashboardCardTitleWrap}>
              <Icon name="calendar" size={18} color={colors.gold} />
              <Text style={styles.dashboardCardTitle}>Upcoming Jobs</Text>
      </View>
            <Text style={styles.dashboardCardLink}>View All →</Text>
    </View>
          {upcomingJobs.length === 0 ? (
            <Text style={styles.emptyStateText}>No upcoming jobs scheduled</Text>
          ) : (
            upcomingJobs.slice(0, 3).map((event, idx) => {
              const eventDate = event.date ? new Date(event.date + 'T12:00:00') : null;
              const formattedDate = eventDate ? eventDate.toLocaleDateString('en-US', { 
                weekday: 'short', 
                month: 'short', 
                day: 'numeric' 
              }) : event.date;
              
              return (
              <View key={idx} style={styles.miniEventCard}>
                <View style={[styles.miniEventDot, { backgroundColor: COMPLEXITY_TYPES.find(c => c.id === event.complexity)?.color || colors.gray }]} />
                <View style={styles.miniEventInfo}>
                  <Text style={styles.miniEventTitle}>{event.service}</Text>
                  <Text style={styles.miniEventClient}>{event.clientName}</Text>
                </View>
                <View style={styles.miniEventTime}>
                    <Text style={styles.miniEventDate}>{formattedDate}</Text>
                  <Text style={styles.miniEventHour}>{event.time}</Text>
                </View>
              </View>
              );
            })
          )}
        </TouchableOpacity>

        {/* Quick Actions */}
        <View style={styles.dashboardCard}>
          <View style={styles.dashboardCardHeader}>
            <View style={styles.dashboardCardTitleWrap}>
              <Icon name="zap" size={18} color={colors.gold} />
              <Text style={styles.dashboardCardTitle}>Quick Actions</Text>
        </View>
              </View>
          <View style={styles.quickActions}>
            <TouchableOpacity 
              style={styles.quickActionBtn}
              onPress={() => navigateTo('dashboard', 'scheduler')}
            >
              <View style={styles.quickActionIcon}>
                <Icon name="plus" size={20} color={colors.gold} />
            </View>
              <Text style={styles.quickActionText}>New Job</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.quickActionBtn}
              onPress={() => navigateTo('dashboard', 'clients')}
            >
              <View style={styles.quickActionIcon}>
                <Icon name="user-plus" size={20} color={colors.gold} />
              </View>
              <Text style={styles.quickActionText}>Add Client</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.quickActionBtn}
              onPress={() => navigateTo('site-management', 'estimates')}
            >
              <View style={styles.quickActionIcon}>
                <Icon name="file-text" size={20} color={colors.gold} />
              </View>
              <Text style={styles.quickActionText}>New Estimate</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.quickActionBtn}
              onPress={() => navigateTo('site-management', 'payment')}
            >
              <View style={styles.quickActionIcon}>
                <Icon name="credit-card" size={20} color={colors.gold} />
              </View>
              <Text style={styles.quickActionText}>Payments</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* Recent Activity Feed */}
      <View style={styles.activitySection}>
        <View style={styles.activityHeader}>
          <Text style={styles.sectionTitle}>Recent Activity</Text>
          <TouchableOpacity onPress={() => navigateTo('dashboard', 'scheduler')}>
            <Text style={styles.activityViewAll}>View All →</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.activityFeed}>
          {recentActivity.length === 0 ? (
            <Text style={styles.emptyStateText}>No recent activity</Text>
          ) : (
            recentActivity.map((activity, idx) => (
              <TouchableOpacity 
                key={idx} 
                style={styles.activityItem}
                onPress={() => {
                  if (activity.type === 'job') navigateTo('dashboard', 'scheduler');
                  else if (activity.type === 'payment') navigateTo('site-management', 'payment');
                  else if (activity.type === 'review') navigateTo('site-management', 'testimonials');
                }}
                activeOpacity={0.7}
              >
                <View style={[styles.activityDot, { backgroundColor: activity.color }]} />
                <View style={styles.activityContent}>
                  <Text style={styles.activityText}>
                    <Text style={styles.activityBold}>{activity.text}</Text> {activity.subtext}
                  </Text>
                  <Text style={styles.activityTime}>{activity.time}</Text>
                </View>
                <Icon name="chevron-right" size={16} color={colors.gray} />
              </TouchableOpacity>
            ))
          )}
        </View>
      </View>
    </View>
  );
};

// Scheduler Tab with Calendar View (Dashboard > Scheduler)
const SchedulerTab = ({ onNavigateToRoute, onNavigateToAppointments }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showEventMenu, setShowEventMenu] = useState(null); // event id for showing action menu
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusAction, setStatusAction] = useState(null);
  const [sendEmailOnChange, setSendEmailOnChange] = useState(true);
  const [processingStatus, setProcessingStatus] = useState(false);
  const [newEvent, setNewEvent] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    service: '',
    date: '',
    time: '',
    complexity: 'simple',
    source: 'manual',
    clientType: 'new',
    notes: '',
    address: '',
    estimateTotal: ''
  });

  // Load events from Firestore on mount
  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const eventsRef = collection(db, 'events');
      const q = query(eventsRef, orderBy('date', 'asc'));
      const snapshot = await getDocs(q);
      
      const loadedEvents = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        // Filter out archived events
        if (data.status !== 'archived') {
          loadedEvents.push({ id: doc.id, ...data });
        }
      });
      
      setEvents(loadedEvents);
    } catch (error) {
      console.error('Error loading events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Archive an event
  const archiveEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to archive this event? It will be hidden from all views.')) {
      return;
    }
    
    try {
      const eventRef = doc(db, 'events', eventId);
      await updateDoc(eventRef, {
        status: 'archived',
        archivedAt: new Date().toISOString()
      });
      
      // Remove from local state
      setEvents(prev => prev.filter(e => e.id !== eventId));
      setShowEventMenu(null);
      alert('Event archived successfully');
    } catch (error) {
      console.error('Error archiving event:', error);
      alert('Failed to archive event');
    }
  };

  // Open status change modal
  const openStatusModal = (type, event) => {
    setStatusAction({ type, appointment: event });
    setSendEmailOnChange(true);
    setShowStatusModal(true);
    setShowEventMenu(null);
  };

  // Process status change
  const processStatusChange = async () => {
    if (!statusAction) return;
    
    setProcessingStatus(true);
    const { type, appointment } = statusAction;
    
    try {
      const eventRef = doc(db, 'events', appointment.id);
      
      if (type === 'delete') {
        // Permanently delete
        await deleteDoc(eventRef);
        setEvents(prev => prev.filter(e => e.id !== appointment.id));
        alert('Event deleted permanently');
      } else {
        // Update status
        const updateData = {
          status: type,
          updatedAt: serverTimestamp(),
        };
        
        if (type === 'confirmed') {
          updateData.confirmedAt = new Date().toISOString();
        } else if (type === 'completed') {
          updateData.completedAt = new Date().toISOString();
        } else if (type === 'cancelled') {
          updateData.cancelledAt = new Date().toISOString();
        } else if (type === 'pending') {
          updateData.revertedAt = new Date().toISOString();
        }
        
        await updateDoc(eventRef, updateData);
        
        // Update local state
        setEvents(prev => prev.map(e => 
          e.id === appointment.id ? { ...e, status: type, ...updateData } : e
        ));
        
        // Send email notification if enabled
        if (sendEmailOnChange && appointment.email) {
          try {
            const sendStatusUpdateEmail = httpsCallable(functions, 'sendStatusUpdateEmail');
            await sendStatusUpdateEmail({
              clientEmail: appointment.email || appointment.clientEmail,
              clientName: appointment.clientName,
              appointmentDate: appointment.date,
              appointmentTime: appointment.time,
              service: appointment.service,
              newStatus: type,
            });
          } catch (emailError) {
            console.warn('Could not send status update email:', emailError);
          }
        }
        
        alert(`Status changed to ${type}`);
      }
      
      setShowStatusModal(false);
      setStatusAction(null);
    } catch (error) {
      console.error('Error changing status:', error);
      alert('Failed to change status');
    } finally {
      setProcessingStatus(false);
    }
  };

  // Delete event permanently
  const deleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY DELETE this event? This cannot be undone.')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'events', eventId));
      setEvents(prev => prev.filter(e => e.id !== eventId));
      setShowEventMenu(null);
      alert('Event deleted permanently');
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event');
    }
  };

  // Format date for display
  const formatEventDate = (dateStr) => {
    if (!dateStr) return 'No date';
    try {
      const date = new Date(dateStr + 'T12:00:00');
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  // Get the next upcoming job (uses local date)
  const getNextJob = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;
    const currentTime = now.getHours() * 100 + now.getMinutes();
    
    // Filter and sort upcoming jobs
    const upcomingJobs = events
      .filter(e => {
        if (e.status === 'cancelled' || e.status === 'rejected' || e.status === 'completed') return false;
        if (e.date < todayStr) return false;
        if (e.date === todayStr && e.time) {
          const timeStr = e.time.replace(/[^0-9]/g, '');
          const aptHour = parseInt(timeStr.slice(0, 2)) || 0;
          const aptMin = parseInt(timeStr.slice(2, 4)) || 0;
          const aptTime = aptHour * 100 + aptMin;
          if (aptTime < currentTime) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const dateComp = (a.date || '').localeCompare(b.date || '');
        if (dateComp !== 0) return dateComp;
        const timeA = a.time?.replace(/[^0-9:]/g, '') || '00:00';
        const timeB = b.time?.replace(/[^0-9:]/g, '') || '00:00';
        return timeA.localeCompare(timeB);
      });
    
    return upcomingJobs[0] || null;
  };

  const navigateToNextJob = () => {
    const nextJob = getNextJob();
    if (nextJob && nextJob.address) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(nextJob.address)}&travelmode=driving`,
        '_blank'
      );
    } else if (nextJob) {
      alert(`Next job: ${nextJob.clientName || 'Client'} at ${nextJob.time || 'TBD'}\nNo address on file for navigation.`);
    } else {
      alert('No upcoming jobs scheduled.');
    }
  };

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < startingDay; i++) {
      days.push({ day: null, events: [] });
    }
    
    // Actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayEvents = events.filter(e => e.date === dateStr);
      days.push({ day, date: dateStr, events: dayEvents });
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const navigateMonth = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedDate(newDate);
  };

  // Get local date string (avoids timezone issues with toISOString)
  const getLocalToday = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const localToday = getLocalToday();

  // Today's events - only events scheduled for today
  const todayEvents = events.filter(e => e.date === localToday);

  // Upcoming events - events scheduled for AFTER today (not including today)
  const upcomingEvents = events
    .filter(e => e.date > localToday && e.status !== 'completed' && e.status !== 'cancelled')
    .sort((a, b) => {
      const dateComp = (a.date || '').localeCompare(b.date || '');
      if (dateComp !== 0) return dateComp;
      // Sort by time within same day
      const timeA = a.time?.replace(/[^0-9:]/g, '') || '00:00';
      const timeB = b.time?.replace(/[^0-9:]/g, '') || '00:00';
      return timeA.localeCompare(timeB);
    })
    .slice(0, 10);

  const addNewEvent = async () => {
    if (!newEvent.clientName || !newEvent.service || !newEvent.date || !newEvent.time) {
      alert('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      const eventData = {
        ...newEvent,
        status: 'confirmed',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'events'), eventData);
      
      // Add to local state with the new ID
      setEvents([...events, { id: docRef.id, ...eventData }]);
      
      // Send booking confirmation email if client email is available
      if (newEvent.clientEmail) {
        try {
          await sendBookingConfirmation({
            clientEmail: newEvent.clientEmail,
            clientName: newEvent.clientName,
            appointmentDate: new Date(newEvent.date).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            }),
            appointmentTime: newEvent.time,
            service: newEvent.service,
            address: newEvent.address || '',
            estimateTotal: newEvent.estimateTotal || null
          });
          console.log('Booking confirmation email sent');
        } catch (emailError) {
          console.error('Error sending booking confirmation:', emailError);
          // Don't fail the event creation if email fails
        }
      }
      
      setShowAddModal(false);
      setNewEvent({
        clientName: '',
        clientEmail: '',
        service: '',
        date: '',
        time: '',
        complexity: 'simple',
        source: 'manual',
        clientType: 'new',
        notes: '',
        address: '',
        estimateTotal: ''
      });
    } catch (error) {
      console.error('Error adding event:', error);
      alert('Failed to save event. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
  <View style={styles.tabContent}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.teal} />
          <Text style={styles.loadingText}>Loading schedule...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.schedulerContainer}>
      {/* Main Calendar Area */}
      <View style={styles.calendarMain}>
    <View style={styles.tabHeader}>
      <View>
            <Text style={styles.tabTitle}>Scheduler</Text>
            <Text style={styles.tabSubtitle}>Manage your appointments and jobs</Text>
      </View>
          <View style={styles.tabHeaderButtons}>
            {getNextJob() && (
              <TouchableOpacity 
                style={[styles.nextJobBtn]}
                onPress={navigateToNextJob}
                title="Next Job"
              >
                <Icon name="navigation" size={16} color={colors.darkNavy} style={isWeb && width > 900 ? { marginRight: 8 } : {}} />
                {isWeb && width > 900 && <Text style={styles.nextJobBtnText}>Next Job</Text>}
              </TouchableOpacity>
            )}
            <TouchableOpacity style={styles.secondaryBtn} onPress={onNavigateToAppointments} title="Appointments">
              <Icon name="clipboard" size={16} color={colors.teal} />
              {isWeb && width > 900 && <Text style={styles.secondaryBtnText}>Appointments</Text>}
            </TouchableOpacity>
            <TouchableOpacity style={styles.secondaryBtn} onPress={onNavigateToRoute} title="See Route">
              <Icon name="map" size={16} color={colors.teal} />
              {isWeb && width > 900 && <Text style={styles.secondaryBtnText}>Route</Text>}
            </TouchableOpacity>
            <TouchableOpacity style={styles.primaryBtn} onPress={() => setShowAddModal(true)} title="Add Event">
              <Icon name="plus" size={16} color={colors.darkNavy} />
              {isWeb && width > 900 && <Text style={styles.primaryBtnText}>Add Event</Text>}
      </TouchableOpacity>
          </View>
    </View>

        {/* Calendar Navigation */}
        <View style={styles.calendarNav}>
          <TouchableOpacity style={styles.calendarNavBtn} onPress={() => navigateMonth(-1)}>
            <Icon name="chevron-left" size={20} color={colors.text} />
          </TouchableOpacity>
          <Text style={styles.calendarMonthTitle}>
            {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()}
          </Text>
          <TouchableOpacity style={styles.calendarNavBtn} onPress={() => navigateMonth(1)}>
            <Icon name="chevron-right" size={20} color={colors.text} />
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.todayBtn} 
            onPress={() => setSelectedDate(new Date())}
          >
            <Text style={styles.todayBtnText}>Today</Text>
          </TouchableOpacity>
        </View>

        {/* Calendar Grid */}
        <View style={styles.calendarGrid}>
          {/* Day Headers */}
          <View style={styles.calendarHeader}>
            {dayNames.map(day => (
              <View key={day} style={styles.calendarHeaderCell}>
                <Text style={styles.calendarHeaderText}>{day}</Text>
          </View>
        ))}
      </View>

          {/* Calendar Days */}
          <View style={styles.calendarBody}>
            {calendarDays.map((day, idx) => {
              // Use local date for comparisons (avoids timezone issues)
              const now = new Date();
              const todayLocal = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
              const selectedLocal = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
              const isToday = day.date === todayLocal;
              const isSelected = day.date === selectedLocal;
              return (
                <TouchableOpacity 
                  key={idx} 
                  style={[
                    styles.calendarCell,
                    isToday && styles.calendarCellToday,
                    isSelected && styles.calendarCellSelected,
                    day.events.length > 0 && styles.calendarCellHasEvents
                  ]}
                  disabled={!day.day}
                  onPress={() => {
                    if (day.day && day.date) {
                      // Only select the day - don't auto-navigate to appointments
                      setSelectedDate(new Date(day.date + 'T12:00:00'));
                    }
                  }}
                >
                  {day.day && (
                    <>
                      <Text style={[
                        styles.calendarDayNum, 
                        isToday && styles.calendarDayNumToday,
                        isSelected && styles.calendarDayNumSelected
                      ]}>
                        {day.day}
                      </Text>
                      {day.events.length > 0 && (
                        <View style={styles.calendarEventsList}>
                          {day.events.slice(0, 2).map((evt, i) => (
                            <TouchableOpacity 
                              key={i} 
                              style={[
                                styles.calendarEventItem, 
                                { borderLeftColor: COMPLEXITY_TYPES.find(c => c.id === evt.complexity)?.color || colors.gray }
                              ]}
                              onPress={(e) => {
                                e.stopPropagation();
                                if (onNavigateToAppointments) {
                                  onNavigateToAppointments(evt.id);
                                }
                              }}
                            >
                              <Text style={styles.calendarEventTime}>{evt.time?.split(' ')[0] || ''}</Text>
                              <Text style={styles.calendarEventTitle} numberOfLines={1}>{evt.service || evt.clientName}</Text>
                            </TouchableOpacity>
                          ))}
                          {day.events.length > 2 && (
                            <Text style={styles.calendarMoreEvents}>+{day.events.length - 2} more</Text>
                          )}
                        </View>
                      )}
                    </>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* Legend */}
        <View style={styles.calendarLegend}>
          {COMPLEXITY_TYPES.map(type => (
            <View key={type.id} style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: type.color }]} />
              <Text style={styles.legendText}>{type.label}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Events Sidebar */}
      <View style={styles.eventsSidebar}>
        {/* Selected Day Section - Shows when a different day is selected */}
        {(() => {
          // Use local dates for comparison (avoids timezone issues)
          const selectedLocal = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
          const isSelectedDifferentFromToday = selectedLocal !== localToday;
          
          if (!isSelectedDifferentFromToday) return null;
          
          return (
          <View style={[styles.sidebarSection, styles.sidebarSectionSelected]}>
            <View style={styles.sidebarSectionHeader}>
              <Icon name="target" size={16} color={colors.teal} />
              <Text style={styles.sidebarSectionTitle}>
                {selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </Text>
            </View>
            {(() => {
              const selectedDateStr = selectedLocal;
              const selectedDayEvents = events.filter(e => e.date === selectedDateStr);
              
              if (selectedDayEvents.length === 0) {
                return <Text style={styles.noEventsText}>No events on this day</Text>;
              }
              
              return selectedDayEvents.map(event => {
                const sourceInfo = EVENT_SOURCES.find(s => s.id === event.source);
                const isMenuOpen = showEventMenu === event.id;
                return (
                  <View key={event.id} style={styles.sidebarEventCardWrapper}>
                    <TouchableOpacity 
                      style={[styles.sidebarEventCard, { flex: 1 }]}
                      onPress={() => {
                        if (onNavigateToAppointments) {
                          onNavigateToAppointments(event.id);
                        }
                      }}
                      activeOpacity={0.7}
                    >
                      <View style={[styles.sidebarEventBar, { backgroundColor: COMPLEXITY_TYPES.find(c => c.id === event.complexity)?.color }]} />
                      <View style={styles.sidebarEventContent}>
                        <Text style={styles.sidebarEventTime}>{event.time}</Text>
                        <Text style={styles.sidebarEventTitle}>{event.service}</Text>
                        <Text style={styles.sidebarEventClient}>{event.clientName}</Text>
                        {event.address && (
                          <View style={styles.sidebarEventAddressWrap}>
                            <Icon name="map-pin" size={12} color={colors.textMuted} />
                            <Text style={styles.sidebarEventAddress} numberOfLines={1}>{event.address}</Text>
                          </View>
                        )}
                        <View style={styles.sidebarEventMeta}>
                          <View style={styles.sidebarEventSource}>
                            {sourceInfo && <Icon name={sourceInfo.iconName} size={12} color={colors.textMuted} />}
                            <Text style={styles.sidebarEventSourceText}>{sourceInfo?.label}</Text>
                          </View>
                          <View style={[
                            styles.sidebarEventStatusBadge, 
                            { backgroundColor: event.status === 'confirmed' ? colors.green + '20' : event.status === 'pending' ? colors.gold + '20' : colors.gray + '20' }
                          ]}>
                            <Text style={[
                              styles.sidebarEventStatusText,
                              { color: event.status === 'confirmed' ? colors.green : event.status === 'pending' ? colors.gold : colors.gray }
                            ]}>
                              {event.status || 'pending'}
                            </Text>
                          </View>
                        </View>
                      </View>
                    </TouchableOpacity>
                    <View style={styles.sidebarEventActions}>
                      <TouchableOpacity 
                        style={styles.sidebarEventMenuBtn}
                        onPress={() => setShowEventMenu(isMenuOpen ? null : event.id)}
                      >
                        <Icon name="more-vertical" size={16} color={colors.gray} />
                      </TouchableOpacity>
                      {isMenuOpen && (
                        <View style={styles.sidebarEventMenu}>
                          {event.status !== 'confirmed' && (
                            <TouchableOpacity style={styles.sidebarEventMenuItem} onPress={() => openStatusModal('confirmed', event)}>
                              <Icon name="check" size={14} color={colors.teal} />
                              <Text style={[styles.sidebarEventMenuText, { color: colors.teal }]}>Confirm</Text>
                            </TouchableOpacity>
                          )}
                          {event.status !== 'completed' && (
                            <TouchableOpacity style={styles.sidebarEventMenuItem} onPress={() => openStatusModal('completed', event)}>
                              <Icon name="flag" size={14} color={colors.green} />
                              <Text style={[styles.sidebarEventMenuText, { color: colors.green }]}>Complete</Text>
                            </TouchableOpacity>
                          )}
                          {event.status !== 'pending' && event.status && (
                            <TouchableOpacity style={styles.sidebarEventMenuItem} onPress={() => openStatusModal('pending', event)}>
                              <Icon name="clock" size={14} color={colors.gold} />
                              <Text style={[styles.sidebarEventMenuText, { color: colors.gold }]}>Pending</Text>
                            </TouchableOpacity>
                          )}
                          <View style={styles.sidebarEventMenuDivider} />
                          <TouchableOpacity style={styles.sidebarEventMenuItem} onPress={() => { archiveEvent(event.id); }}>
                            <Icon name="archive" size={14} color={colors.gray} />
                            <Text style={[styles.sidebarEventMenuText, { color: colors.gray }]}>Archive</Text>
                          </TouchableOpacity>
                          <TouchableOpacity style={styles.sidebarEventMenuItem} onPress={() => openStatusModal('delete', event)}>
                            <Icon name="trash-2" size={14} color={colors.red} />
                            <Text style={[styles.sidebarEventMenuText, { color: colors.red }]}>Delete</Text>
                          </TouchableOpacity>
                        </View>
                      )}
                    </View>
                  </View>
                );
              });
            })()}
          </View>
          );
        })()}

        {/* Today's Schedule */}
        <View style={styles.sidebarSection}>
          <View style={styles.sidebarSectionHeader}>
            <Icon name="calendar" size={16} color={colors.gold} />
            <Text style={styles.sidebarSectionTitle}>Today's Schedule</Text>
          </View>
          {todayEvents.length === 0 ? (
            <Text style={styles.noEventsText}>No events scheduled for today</Text>
          ) : (
            todayEvents.map(event => {
              const sourceInfo = EVENT_SOURCES.find(s => s.id === event.source);
              const isMenuOpen = showEventMenu === event.id;
              return (
              <View key={event.id} style={styles.sidebarEventCardWrapper}>
              <TouchableOpacity 
                  style={[styles.sidebarEventCard, { flex: 1 }]}
                onPress={() => {
                  if (onNavigateToAppointments) {
                    onNavigateToAppointments(event.id);
                  }
                }}
                activeOpacity={0.7}
              >
                <View style={[styles.sidebarEventBar, { backgroundColor: COMPLEXITY_TYPES.find(c => c.id === event.complexity)?.color }]} />
                <View style={styles.sidebarEventContent}>
                  <Text style={styles.sidebarEventTime}>{event.time}</Text>
                  <Text style={styles.sidebarEventTitle}>{event.service}</Text>
                  <Text style={styles.sidebarEventClient}>{event.clientName}</Text>
                  <View style={styles.sidebarEventMeta}>
                    <View style={styles.sidebarEventSource}>
                      {sourceInfo && <Icon name={sourceInfo.iconName} size={12} color={colors.textMuted} />}
                      <Text style={styles.sidebarEventSourceText}>{sourceInfo?.label}</Text>
                    </View>
                    <View style={[styles.clientTypeBadge, { backgroundColor: CLIENT_TYPES.find(t => t.id === event.clientType)?.color + '30' }]}>
                      <Text style={[styles.clientTypeBadgeText, { color: CLIENT_TYPES.find(t => t.id === event.clientType)?.color }]}>
                        {CLIENT_TYPES.find(t => t.id === event.clientType)?.label}
                      </Text>
                    </View>
                  </View>
                  {event.notes && (
                    <View style={styles.sidebarEventNotesWrap}>
                      <Icon name="file-text" size={12} color={colors.textMuted} />
                      <Text style={styles.sidebarEventNotes}>{event.notes}</Text>
                    </View>
                  )}
    </View>
              </TouchableOpacity>
                <View style={styles.sidebarEventActions}>
                  <TouchableOpacity 
                    style={styles.sidebarEventMenuBtn}
                    onPress={() => setShowEventMenu(isMenuOpen ? null : event.id)}
                  >
                    <Icon name="more-vertical" size={16} color={colors.gray} />
                  </TouchableOpacity>
                  {isMenuOpen && (
                    <View style={styles.sidebarEventMenu}>
                      {event.status !== 'confirmed' && (
                        <TouchableOpacity style={styles.sidebarEventMenuItem} onPress={() => openStatusModal('confirmed', event)}>
                          <Icon name="check" size={14} color={colors.teal} />
                          <Text style={[styles.sidebarEventMenuText, { color: colors.teal }]}>Confirm</Text>
                        </TouchableOpacity>
                      )}
                      {event.status !== 'completed' && (
                        <TouchableOpacity style={styles.sidebarEventMenuItem} onPress={() => openStatusModal('completed', event)}>
                          <Icon name="flag" size={14} color={colors.green} />
                          <Text style={[styles.sidebarEventMenuText, { color: colors.green }]}>Complete</Text>
                        </TouchableOpacity>
                      )}
                      {event.status !== 'pending' && event.status && (
                        <TouchableOpacity style={styles.sidebarEventMenuItem} onPress={() => openStatusModal('pending', event)}>
                          <Icon name="clock" size={14} color={colors.gold} />
                          <Text style={[styles.sidebarEventMenuText, { color: colors.gold }]}>Pending</Text>
                        </TouchableOpacity>
                      )}
                      <View style={styles.sidebarEventMenuDivider} />
                      <TouchableOpacity style={styles.sidebarEventMenuItem} onPress={() => archiveEvent(event.id)}>
                        <Icon name="archive" size={14} color={colors.gray} />
                        <Text style={[styles.sidebarEventMenuText, { color: colors.gray }]}>Archive</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.sidebarEventMenuItem} onPress={() => openStatusModal('delete', event)}>
                        <Icon name="trash-2" size={14} color={colors.red} />
                        <Text style={[styles.sidebarEventMenuText, { color: colors.red }]}>Delete</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              </View>
);
            })
          )}
        </View>

        <View style={styles.sidebarSection}>
          <View style={styles.sidebarSectionHeader}>
            <Icon name="clock" size={16} color={colors.gold} />
            <Text style={styles.sidebarSectionTitle}>Upcoming</Text>
          </View>
          {upcomingEvents.slice(0, 5).map(event => (
            <View key={event.id} style={styles.sidebarEventMiniWrapper}>
            <TouchableOpacity 
              style={styles.sidebarEventMini}
              onPress={() => {
                if (onNavigateToAppointments) {
                  onNavigateToAppointments(event.id);
                }
              }}
              activeOpacity={0.7}
            >
              <View style={[styles.sidebarEventMiniBar, { backgroundColor: COMPLEXITY_TYPES.find(c => c.id === event.complexity)?.color }]} />
              <View style={styles.sidebarEventMiniContent}>
                <Text style={styles.sidebarEventMiniDate}>{event.date} • {event.time}</Text>
                <Text style={styles.sidebarEventMiniTitle}>{event.service}</Text>
                <Text style={styles.sidebarEventMiniClient}>{event.clientName}</Text>
              </View>
            </TouchableOpacity>
              <TouchableOpacity 
                style={styles.sidebarEventMiniArchiveBtn}
                onPress={(e) => {
                  e.stopPropagation();
                  archiveEvent(event.id);
                }}
              >
                <Icon name="archive" size={12} color={colors.red} />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      </View>

      {/* Add Event Modal */}
      <Modal visible={showAddModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add New Event</Text>
              <TouchableOpacity onPress={() => setShowAddModal(false)}>
                <Text style={styles.modalClose}>✕</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalBody}>
              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Client Name *</Text>
                <TextInput
                  style={styles.modalInput}
                  value={newEvent.clientName}
                  onChangeText={(val) => setNewEvent({...newEvent, clientName: val})}
                  placeholder="Enter client name"
                  placeholderTextColor={colors.gray}
                />
              </View>

              <View style={styles.modalRow}>
                <View style={[styles.modalField, { flex: 1 }]}>
                  <Text style={styles.modalLabel}>Email (for confirmation)</Text>
                  <TextInput
                    style={styles.modalInput}
                    value={newEvent.clientEmail}
                    onChangeText={(val) => setNewEvent({...newEvent, clientEmail: val})}
                    placeholder="client@email.com"
                    placeholderTextColor={colors.gray}
                    keyboardType="email-address"
                    autoCapitalize="none"
                  />
                </View>
                <View style={[styles.modalField, { flex: 1, marginLeft: 12 }]}>
                  <Text style={styles.modalLabel}>Phone</Text>
                  <TextInput
                    style={styles.modalInput}
                    value={newEvent.clientPhone}
                    onChangeText={(val) => setNewEvent({...newEvent, clientPhone: val})}
                    placeholder="(702) 555-1234"
                    placeholderTextColor={colors.gray}
                    keyboardType="phone-pad"
                  />
                </View>
              </View>

              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Address</Text>
                <TextInput
                  style={styles.modalInput}
                  value={newEvent.address}
                  onChangeText={(val) => setNewEvent({...newEvent, address: val})}
                  placeholder="123 Main St, Las Vegas, NV"
                  placeholderTextColor={colors.gray}
                />
              </View>

              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Service *</Text>
                <TextInput
                  style={styles.modalInput}
                  value={newEvent.service}
                  onChangeText={(val) => setNewEvent({...newEvent, service: val})}
                  placeholder="What service is needed?"
                  placeholderTextColor={colors.gray}
                />
              </View>

              <View style={styles.modalRow}>
                <View style={[styles.modalField, { flex: 1 }]}>
                  <Text style={styles.modalLabel}>Date *</Text>
                  <TextInput
                    style={styles.modalInput}
                    value={newEvent.date}
                    onChangeText={(val) => setNewEvent({...newEvent, date: val})}
                    placeholder="YYYY-MM-DD"
                    placeholderTextColor={colors.gray}
                  />
                </View>
                <View style={[styles.modalField, { flex: 1, marginLeft: 12 }]}>
                  <Text style={styles.modalLabel}>Time *</Text>
                  <TextInput
                    style={styles.modalInput}
                    value={newEvent.time}
                    onChangeText={(val) => setNewEvent({...newEvent, time: val})}
                    placeholder="9:00 AM"
                    placeholderTextColor={colors.gray}
                  />
                </View>
              </View>

              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Complexity</Text>
                <View style={styles.modalOptions}>
                  {COMPLEXITY_TYPES.map(type => (
                    <TouchableOpacity
                      key={type.id}
                      style={[
                        styles.modalOption,
                        newEvent.complexity === type.id && { backgroundColor: type.color + '30', borderColor: type.color }
                      ]}
                      onPress={() => setNewEvent({...newEvent, complexity: type.id})}
                    >
                      <Text style={[styles.modalOptionText, newEvent.complexity === type.id && { color: type.color }]}>
                        {type.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Source</Text>
                <View style={styles.modalOptions}>
                  {EVENT_SOURCES.map(source => (
                    <TouchableOpacity
                      key={source.id}
                      style={[
                        styles.modalOption,
                        newEvent.source === source.id && { backgroundColor: source.color + '30', borderColor: source.color }
                      ]}
                      onPress={() => setNewEvent({...newEvent, source: source.id})}
                    >
                      <Icon name={source.iconName} size={14} color={newEvent.source === source.id ? source.color : colors.textMuted} style={{marginRight: 6}} />
                      <Text style={[styles.modalOptionText, newEvent.source === source.id && { color: source.color }]}>
                        {source.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Client Type</Text>
                <View style={styles.modalOptions}>
                  {CLIENT_TYPES.map(type => (
                    <TouchableOpacity
                      key={type.id}
                      style={[
                        styles.modalOption,
                        newEvent.clientType === type.id && { backgroundColor: type.color + '30', borderColor: type.color }
                      ]}
                      onPress={() => setNewEvent({...newEvent, clientType: type.id})}
                    >
                      <Text style={[styles.modalOptionText, newEvent.clientType === type.id && { color: type.color }]}>
                        {type.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Notes</Text>
                <TextInput
                  style={[styles.modalInput, styles.modalTextarea]}
                  value={newEvent.notes}
                  onChangeText={(val) => setNewEvent({...newEvent, notes: val})}
                  placeholder="Additional notes about the job..."
                  placeholderTextColor={colors.gray}
                  multiline
                  numberOfLines={3}
                />
              </View>
            </ScrollView>

            <View style={styles.modalFooter}>
              <TouchableOpacity style={styles.modalCancelBtn} onPress={() => setShowAddModal(false)} disabled={saving}>
                <Text style={styles.modalCancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.modalSubmitBtn, saving && styles.primaryBtnDisabled]} 
                onPress={addNewEvent}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color={colors.darkNavy} />
                ) : (
                  <Text style={styles.modalSubmitBtnText}>Add Event</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Status Change Modal */}
      <Modal visible={showStatusModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.statusChangeModal}>
            <View style={styles.statusChangeHeader}>
              <View style={[styles.statusChangeIcon, {
                backgroundColor: statusAction?.type === 'delete' ? colors.red + '20' :
                                 statusAction?.type === 'pending' ? colors.gold + '20' :
                                 statusAction?.type === 'confirmed' ? colors.teal + '20' :
                                 statusAction?.type === 'completed' ? colors.green + '20' : colors.gray + '20'
              }]}>
                <Icon 
                  name={statusAction?.type === 'delete' ? 'trash-2' :
                        statusAction?.type === 'pending' ? 'clock' :
                        statusAction?.type === 'confirmed' ? 'check' :
                        statusAction?.type === 'completed' ? 'flag' : 'x'}
                  size={24}
                  color={statusAction?.type === 'delete' ? colors.red :
                         statusAction?.type === 'pending' ? colors.gold :
                         statusAction?.type === 'confirmed' ? colors.teal :
                         statusAction?.type === 'completed' ? colors.green : colors.gray}
                />
              </View>
              <Text style={styles.statusChangeTitle}>
                {statusAction?.type === 'delete' ? 'Delete Event' :
                 `Change to ${statusAction?.type?.charAt(0).toUpperCase() + statusAction?.type?.slice(1)}`}
              </Text>
            </View>
            
            {/* Event Summary */}
            <View style={styles.statusChangeSummary}>
              <Text style={styles.statusChangeClient}>{statusAction?.appointment?.clientName}</Text>
              <Text style={styles.statusChangeService}>{statusAction?.appointment?.service}</Text>
              <Text style={styles.statusChangeDate}>
                {formatEventDate(statusAction?.appointment?.date)} at {statusAction?.appointment?.time || 'TBD'}
              </Text>
            </View>
            
            {/* Warning for delete */}
            {statusAction?.type === 'delete' && (
              <View style={styles.statusChangeWarning}>
                <Icon name="alert-triangle" size={16} color={colors.red} />
                <Text style={styles.statusChangeWarningText}>
                  This will permanently delete this event. This cannot be undone.
                </Text>
              </View>
            )}
            
            {/* Email notification option */}
            {statusAction?.type !== 'delete' && (statusAction?.appointment?.email || statusAction?.appointment?.clientEmail) && (
              <TouchableOpacity 
                style={styles.statusChangeEmailOption}
                onPress={() => setSendEmailOnChange(!sendEmailOnChange)}
              >
                <Icon 
                  name={sendEmailOnChange ? 'check-square' : 'square'} 
                  size={20} 
                  color={sendEmailOnChange ? colors.teal : colors.gray} 
                />
                <View style={styles.statusChangeEmailOptionText}>
                  <Text style={styles.statusChangeEmailLabel}>Send email notification to client</Text>
                  <Text style={styles.statusChangeEmailDesc}>
                    Notify {statusAction?.appointment?.clientName} about this status change
                  </Text>
                </View>
              </TouchableOpacity>
            )}
            
            {/* Actions */}
            <View style={styles.statusChangeActions}>
              <TouchableOpacity 
                style={styles.statusChangeCancel}
                onPress={() => {
                  setShowStatusModal(false);
                  setStatusAction(null);
                }}
              >
                <Text style={styles.statusChangeCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.statusChangeConfirm, {
                  backgroundColor: statusAction?.type === 'delete' ? colors.red :
                                   statusAction?.type === 'pending' ? colors.gold :
                                   statusAction?.type === 'confirmed' ? colors.teal :
                                   statusAction?.type === 'completed' ? colors.green : colors.gray
                }]}
                onPress={processStatusChange}
                disabled={processingStatus}
              >
                {processingStatus ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <>
                    <Icon 
                      name={statusAction?.type === 'delete' ? 'trash-2' : 'check'} 
                      size={14} 
                      color={colors.white} 
                    />
                    <Text style={styles.statusChangeConfirmText}>
                      {statusAction?.type === 'delete' ? 'Delete Permanently' : 'Confirm Change'}
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// Helper to get local date string in YYYY-MM-DD format (avoids timezone issues with toISOString)
const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Route Mapper Tab (Dashboard > Route Mapper) - Enhanced with full Mapbox integration
const RouteMapperTab = ({ onNavigateToClient, onNavigateToAppointments }) => {
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [appointments, setAppointments] = useState([]);
  const [allEvents, setAllEvents] = useState([]); // All events for Next Job feature
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingAddress, setStartingAddress] = useState('1505 E John Bevy Ct, North Las Vegas, NV 89086');
  const [startingCoords, setStartingCoords] = useState({ lat: 36.2619, lng: -115.1177 }); // Geocoded starting location
  const [showSettings, setShowSettings] = useState(false);
  const [customStops, setCustomStops] = useState([]); // { name, address, lat, lng, afterIndex }
  const [showAddStopModal, setShowAddStopModal] = useState(false);
  const [newStopForm, setNewStopForm] = useState({ name: '', address: '', afterIndex: 0, duration: 15 });
  const [routeData, setRouteData] = useState(null); // { totalDistance, totalDuration, legs: [] }
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [geocodedLocations, setGeocodedLocations] = useState({}); // { address: { lat, lng } }
  const [geocodingErrors, setGeocodingErrors] = useState([]); // Track addresses that failed to geocode
  const [sendingNotification, setSendingNotification] = useState(null); // index of appointment being notified
  const [currentLocation, setCurrentLocation] = useState(null);
  const [nearbyStores, setNearbyStores] = useState([]); // Home Depot, Lowe's nearby
  const [showStoreOptions, setShowStoreOptions] = useState(null); // index to show store options for
  const [mapLoaded, setMapLoaded] = useState(false);
  const [stopDurations, setStopDurations] = useState({}); // { appointmentId: durationInMinutes }
  const [loadingStores, setLoadingStores] = useState(false); // Loading state for store search
  const [mapZoom, setMapZoom] = useState(11); // Default zoom level for route map
  
  // Refs for interactive map
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  
  // Default starting point coordinates (1505 E John Bevy Ct, North Las Vegas NV 89086)
  const DEFAULT_START = { lat: 36.2619, lng: -115.1177 };
  
  // Common store searches
  const STORE_TYPES = [
    { name: 'Home Depot', query: 'home depot', icon: '🏠', color: '#f96302' },
    { name: "Lowe's", query: 'lowes', icon: '🔧', color: '#004990' },
    { name: 'Hardware Store', query: 'hardware store', icon: '🔩', color: '#666' },
    { name: 'Plumbing Supply', query: 'plumbing supply', icon: '🚿', color: '#0088cc' },
    { name: 'Electrical Supply', query: 'electrical supply', icon: '⚡', color: '#ffcc00' },
  ];
  
  useEffect(() => {
    loadDayAppointments();
  }, [selectedDate]);

  // Load route settings (stop durations, custom stops) from Firestore
  useEffect(() => {
    const loadRouteSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'routeMapper'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          if (data.stopDurations) {
            setStopDurations(data.stopDurations);
          }
          if (data.customStops) {
            setCustomStops(data.customStops);
          }
          if (data.startingAddress) {
            setStartingAddress(data.startingAddress);
          }
        }
      } catch (error) {
        console.error('Error loading route settings:', error);
      }
    };
    loadRouteSettings();
  }, []);

  // Save route settings when they change
  useEffect(() => {
    const saveRouteSettings = async () => {
      try {
        await setDoc(doc(db, 'settings', 'routeMapper'), {
          stopDurations,
          customStops,
          startingAddress,
          updatedAt: new Date().toISOString()
        }, { merge: true });
      } catch (error) {
        console.error('Error saving route settings:', error);
      }
    };
    
    // Debounce the save to avoid too many writes
    const timeoutId = setTimeout(saveRouteSettings, 1000);
    return () => clearTimeout(timeoutId);
  }, [stopDurations, customStops, startingAddress]);

  // Try to get current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => console.log('Location access denied:', error),
        { enableHighAccuracy: true }
      );
    }
  }, []);

  // Calculate route when appointments, custom stops, or starting location change
  useEffect(() => {
    if (appointments.length > 0 || customStops.length > 0) {
      calculateFullRoute();
    }
  }, [appointments, customStops, geocodedLocations, startingCoords]);

  // Geocode starting address when it changes
  useEffect(() => {
    const geocodeStartingAddress = async () => {
      if (!startingAddress) return;
      
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(startingAddress)}.json?access_token=${MAPBOX_PUBLIC_TOKEN}&country=US&proximity=-115.1398,36.1699`
        );
        const data = await response.json();
        
        if (data.features && data.features.length > 0) {
          const [lng, lat] = data.features[0].center;
          setStartingCoords({ lat, lng });
          console.log('Starting location geocoded:', lat, lng);
        } else {
          console.warn('Could not geocode starting address, using default');
          setStartingCoords(DEFAULT_START);
        }
      } catch (error) {
        console.error('Error geocoding starting address:', error);
        setStartingCoords(DEFAULT_START);
      }
    };
    
    // Debounce the geocoding to avoid too many API calls
    const timeoutId = setTimeout(geocodeStartingAddress, 500);
    return () => clearTimeout(timeoutId);
  }, [startingAddress]);

  // Generate static map URL with route and markers
  const generateStaticMapUrl = () => {
    const waypoints = getAllWaypoints();
    
    // Default map when no waypoints
    if (waypoints.length === 0) {
      return `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/-115.14,36.17,9/900x350@2x?access_token=${MAPBOX_PUBLIC_TOKEN}`;
    }
    
    // Build markers string (simpler format)
    const markerParts = waypoints.map((wp, idx) => {
      const color = wp.type === 'start' ? '22c55e' : wp.type === 'custom' ? 'f96302' : 'd4a84b';
      const label = wp.type === 'start' ? 'h' : wp.type === 'custom' ? 's' : idx.toString();
      return `pin-s-${label}+${color}(${wp.lng.toFixed(4)},${wp.lat.toFixed(4)})`;
    });
    
    const markers = markerParts.join(',');
    
    // Calculate center and zoom based on waypoints
    const lngs = waypoints.map(w => w.lng);
    const lats = waypoints.map(w => w.lat);
    const centerLng = ((Math.min(...lngs) + Math.max(...lngs)) / 2).toFixed(4);
    const centerLat = ((Math.min(...lats) + Math.max(...lats)) / 2).toFixed(4);
    
    // Calculate zoom based on spread
    const lngSpread = Math.max(...lngs) - Math.min(...lngs);
    const latSpread = Math.max(...lats) - Math.min(...lats);
    const maxSpread = Math.max(lngSpread, latSpread);
    let zoom = 11;
    if (maxSpread > 0.5) zoom = 9;
    else if (maxSpread > 0.2) zoom = 10;
    else if (maxSpread > 0.1) zoom = 11;
    else zoom = 12;
    
    return `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/${markers}/${centerLng},${centerLat},${zoom}/900x350@2x?access_token=${MAPBOX_PUBLIC_TOKEN}`;
  };

  // Set map as loaded immediately since we're using static maps
  useEffect(() => {
    setMapLoaded(true);
  }, []);

  // Update map markers and route when data changes
  useEffect(() => {
    if (!isWeb || !mapRef.current || !mapLoaded) return;
    
    const map = mapRef.current;
    const waypoints = getAllWaypoints();
    
    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    
    // Add markers for each waypoint
    waypoints.forEach((wp, idx) => {
      const el = document.createElement('div');
      el.style.cssText = `
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 14px;
        color: white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        cursor: pointer;
      `;
      
      if (wp.type === 'start') {
        el.style.backgroundColor = '#22c55e';
        el.innerHTML = '🏠';
      } else if (wp.type === 'custom') {
        el.style.backgroundColor = '#f96302';
        el.innerHTML = '🛒';
      } else {
        el.style.backgroundColor = '#d4a84b';
        el.innerHTML = idx.toString();
      }
      
      const marker = new window.mapboxgl.Marker(el)
        .setLngLat([wp.lng, wp.lat])
        .setPopup(new window.mapboxgl.Popup({ offset: 25 })
          .setHTML(`<strong>${wp.name}</strong><br/>${wp.address || ''}`))
        .addTo(map);
      
      markersRef.current.push(marker);
    });
    
    // Update route line if we have route data
    if (routeData?.geometry && map.getSource('route')) {
      map.getSource('route').setData({
        type: 'Feature',
        geometry: routeData.geometry
      });
    } else if (map.getSource('route')) {
      map.getSource('route').setData({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [] }
      });
    }
    
    // Fit map to show all markers
    if (waypoints.length > 0) {
      const bounds = new window.mapboxgl.LngLatBounds();
      waypoints.forEach(wp => bounds.extend([wp.lng, wp.lat]));
      map.fitBounds(bounds, { padding: 60, maxZoom: 13 });
    } else {
      // Reset to default view when no waypoints
      map.flyTo({ center: [-115.14, 36.17], zoom: 9 });
    }
  }, [mapLoaded, appointments, customStops, routeData, startingCoords, geocodedLocations]);

  const loadDayAppointments = async () => {
    setLoading(true);
    try {
      // Load events for selected date
      const eventsRef = collection(db, 'events');
      const eventsSnapshot = await getDocs(eventsRef);
      
      const dayEvents = [];
      const allUpcoming = [];
      const today = new Date().toISOString().split('T')[0];
      
      eventsSnapshot.forEach(doc => {
        const event = { id: doc.id, ...doc.data() };
        // Include pending, confirmed, and approved - exclude cancelled/rejected
        if (event.date === selectedDate && event.status !== 'cancelled' && event.status !== 'rejected') {
          dayEvents.push(event);
        }
        // Collect all upcoming events for Next Job button (only confirmed/approved)
        if (event.date >= today && (event.status === 'confirmed' || event.status === 'approved')) {
          allUpcoming.push(event);
        }
      });
      
      // Sort by time
      dayEvents.sort((a, b) => {
        const timeA = a.time?.replace(/[^0-9:]/g, '') || '00:00';
        const timeB = b.time?.replace(/[^0-9:]/g, '') || '00:00';
        return timeA.localeCompare(timeB);
      });
      
      // Sort all upcoming by date and time
      allUpcoming.sort((a, b) => {
        const dateComp = (a.date || '').localeCompare(b.date || '');
        if (dateComp !== 0) return dateComp;
        const timeA = a.time?.replace(/[^0-9:]/g, '') || '00:00';
        const timeB = b.time?.replace(/[^0-9:]/g, '') || '00:00';
        return timeA.localeCompare(timeB);
      });
      
      setAppointments(dayEvents);
      setAllEvents(allUpcoming);
      
      // Load clients for address info
      const clientsRef = collection(db, 'clients');
      const clientsSnapshot = await getDocs(clientsRef);
      const clientsList = [];
      clientsSnapshot.forEach(doc => {
        clientsList.push({ id: doc.id, ...doc.data() });
      });
      setClients(clientsList);
      
      // Geocode all addresses
      for (const event of dayEvents) {
        const addr = event.address || event.zipcode;
        if (addr && !geocodedLocations[addr]) {
          geocodeAddress(addr);
        }
      }
      
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
    setLoading(false);
  };

  // Geocode an address using Mapbox Geocoding API
  const geocodeAddress = async (address) => {
    if (!address) {
      console.warn('geocodeAddress called with empty address');
      return null;
    }
    if (geocodedLocations[address]) {
      return geocodedLocations[address];
    }
    
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${MAPBOX_PUBLIC_TOKEN}&country=US&proximity=-115.1398,36.1699`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        setGeocodedLocations(prev => ({
          ...prev,
          [address]: { lat, lng, displayName: data.features[0].place_name }
        }));
        // Remove from errors if it was there
        setGeocodingErrors(prev => prev.filter(e => e !== address));
        console.log('Geocoded address:', address, '→', lat, lng);
        return { lat, lng };
      } else {
        console.warn('No geocoding results for:', address);
        setGeocodingErrors(prev => prev.includes(address) ? prev : [...prev, address]);
      }
    } catch (error) {
      console.error('Geocoding error for', address, ':', error);
      setGeocodingErrors(prev => prev.includes(address) ? prev : [...prev, address]);
    }
    return null;
  };

  // Calculate full route with ETAs using Mapbox Directions API
  const calculateFullRoute = async () => {
    const waypoints = getAllWaypoints();
    console.log('Calculating route with', waypoints.length, 'waypoints:', waypoints.map(w => w.name));
    
    if (waypoints.length < 2) {
      console.log('Not enough waypoints for route calculation (need at least 2)');
      setRouteData(null);
      return;
    }
    
    // Validate all waypoints have valid coordinates
    const invalidWaypoints = waypoints.filter(w => !w.lat || !w.lng || isNaN(w.lat) || isNaN(w.lng));
    if (invalidWaypoints.length > 0) {
      console.error('Invalid waypoints found:', invalidWaypoints);
      setRouteData(null);
      return;
    }
    
    setLoadingRoute(true);
    try {
      const coords = waypoints.map(w => `${w.lng},${w.lat}`).join(';');
      console.log('Requesting route for coords:', coords);
      
      const response = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${coords}?access_token=${MAPBOX_PUBLIC_TOKEN}&geometries=geojson&overview=full&steps=true`
      );
      const data = await response.json();
      
      if (data.code && data.code !== 'Ok') {
        console.error('Mapbox Directions API error:', data.code, data.message);
        setRouteData(null);
      } else if (data.routes && data.routes.length > 0) {
        const route = data.routes[0];
        console.log('Route calculated:', formatDistance(route.distance), formatDuration(route.duration));
        setRouteData({
          totalDistance: route.distance, // meters
          totalDuration: route.duration, // seconds
          geometry: route.geometry,
          legs: route.legs.map((leg, idx) => ({
            distance: leg.distance,
            duration: leg.duration,
            summary: leg.summary,
            from: waypoints[idx],
            to: waypoints[idx + 1]
          }))
        });
      } else {
        console.warn('No routes returned from Mapbox API');
        setRouteData(null);
      }
    } catch (error) {
      console.error('Route calculation error:', error);
      setRouteData(null);
    }
    setLoadingRoute(false);
  };
  
  // Helper functions for formatting (moved up for use in calculateFullRoute logging)
  const formatDuration = (seconds) => {
    if (!seconds) return '--';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.round((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins} min`;
  };

  const formatDistance = (meters) => {
    if (!meters) return '--';
    const miles = (meters / 1609.34).toFixed(1);
    return `${miles} mi`;
  };

  // Update stop duration for an appointment
  const updateStopDuration = (appointmentId, minutes) => {
    const mins = parseInt(minutes) || 0;
    setStopDurations(prev => ({
      ...prev,
      [appointmentId]: mins > 0 ? mins : 0
    }));
  };

  // Calculate total stop time
  const getTotalStopTime = () => {
    let total = 0;
    Object.values(stopDurations).forEach(mins => {
      total += parseInt(mins) || 0;
    });
    // Add custom stop durations (default 15 min each)
    customStops.forEach(stop => {
      total += stop.duration || 15;
    });
    return total * 60; // Return in seconds to match route duration format
  };

  // Calculate total journey time (drive time + stop time)
  const getTotalJourneyTime = () => {
    const driveTime = routeData?.totalDuration || 0;
    const stopTime = getTotalStopTime();
    return driveTime + stopTime;
  };

  // Get all waypoints in order (start -> appointments/stops -> end)
  const getAllWaypoints = () => {
    const waypoints = [{ ...startingCoords, type: 'start', name: 'Starting Location', address: startingAddress }];
    
    // Combine appointments and custom stops, sorted by index
    const combinedStops = [];
    
    appointments.forEach((apt, idx) => {
      const addr = getClientAddress(apt);
      const coords = apt.lat && apt.lng 
        ? { lat: apt.lat, lng: apt.lng }
        : geocodedLocations[addr];
      
      if (coords) {
        combinedStops.push({
          ...coords,
          type: 'appointment',
          index: idx,
          name: apt.clientName || 'Client',
          address: addr,
          appointment: apt
        });
      }
      
      // Add any custom stops after this appointment
      customStops
        .filter(stop => stop.afterIndex === idx)
        .forEach(stop => {
          combinedStops.push({
            lat: stop.lat,
            lng: stop.lng,
            type: 'custom',
            name: stop.name,
            address: stop.address,
            customStop: stop
          });
        });
    });
    
    return [...waypoints, ...combinedStops];
  };

  // Search for nearby stores (Home Depot, Lowe's, etc.)
  const searchNearbyStores = async (queries, nearLat, nearLng) => {
    setLoadingStores(true);
    try {
      // Split queries and search each separately for better results
      const searchQueries = Array.isArray(queries) ? queries : ['home depot', 'lowes'];
      const allResults = [];
      
      for (const query of searchQueries) {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_PUBLIC_TOKEN}&proximity=${nearLng},${nearLat}&types=poi&limit=2`
        );
        const data = await response.json();
        
        if (data.features) {
          data.features.forEach(f => {
            allResults.push({
              name: f.text,
              fullName: f.place_name,
              lat: f.center[1],
              lng: f.center[0],
              distance: f.properties?.distance || 0
            });
          });
        }
      }
      
      // Sort by distance and return top results
      allResults.sort((a, b) => a.distance - b.distance);
      setLoadingStores(false);
      return allResults.slice(0, 5);
    } catch (error) {
      console.error('Store search error:', error);
      setLoadingStores(false);
    }
    return [];
  };

  // Add a custom stop (like Home Depot)
  const addCustomStop = async () => {
    if (!newStopForm.address) {
      alert('Please enter an address');
      return;
    }
    
    const coords = await geocodeAddress(newStopForm.address);
    if (coords) {
      setCustomStops(prev => [...prev, {
        id: Date.now(),
        name: newStopForm.name || 'Custom Stop',
        address: newStopForm.address,
        lat: coords.lat,
        lng: coords.lng,
        afterIndex: newStopForm.afterIndex,
        duration: newStopForm.duration || 15
      }]);
      setShowAddStopModal(false);
      setNewStopForm({ name: '', address: '', afterIndex: 0, duration: 15 });
    } else {
      alert('Could not find that address. Please try again.');
    }
  };

  // Add a store as a stop
  const addStoreAsStop = (store, afterIndex) => {
    setCustomStops(prev => [...prev, {
      id: Date.now(),
      name: store.name,
      address: store.fullName,
      lat: store.lat,
      lng: store.lng,
      afterIndex
    }]);
    setShowStoreOptions(null);
    setNearbyStores([]);
  };

  // Remove a custom stop
  const removeCustomStop = (stopId) => {
    setCustomStops(prev => prev.filter(s => s.id !== stopId));
  };

  // Send "On My Way" notification to client
  const sendOnMyWayNotification = async (appointment, legIndex) => {
    setSendingNotification(legIndex);
    
    try {
      const leg = routeData?.legs?.[legIndex];
      const etaMinutes = leg ? Math.round(leg.duration / 60) : null;
      
      // Call Firebase function to send notification
      const sendNotification = httpsCallable(functions, 'sendOnMyWayNotification');
      await sendNotification({
        clientEmail: appointment.clientEmail || appointment.email,
        clientPhone: appointment.clientPhone || appointment.phone,
        clientName: appointment.clientName,
        technicianName: 'HandyHawk',
        etaMinutes,
        appointmentTime: appointment.time,
        service: appointment.service
      });
      
      alert(`Notification sent! ETA: ~${etaMinutes} minutes`);
    } catch (error) {
      console.error('Error sending notification:', error);
      // Fallback - show message to copy
      const leg = routeData?.legs?.[legIndex];
      const etaMinutes = leg ? Math.round(leg.duration / 60) : 'soon';
      const message = `Hi ${appointment.clientName}, I'm on my way! I should arrive in about ${etaMinutes} minutes.`;
      
      if (appointment.clientPhone || appointment.phone) {
        const phone = appointment.clientPhone || appointment.phone;
        if (confirm(`Send SMS notification?\n\n"${message}"\n\nThis will open your messaging app.`)) {
          window.open(`sms:${phone}?body=${encodeURIComponent(message)}`, '_self');
        }
      } else {
        alert(`Could not send automatic notification. Message: "${message}"`);
      }
    }
    
    setSendingNotification(null);
  };

  // Get next upcoming job (today or future) - uses ALL confirmed/approved events
  const getNextJob = () => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const currentTime = now.getHours() * 100 + now.getMinutes();
    
    // Filter allEvents to find the next one that hasn't passed
    const upcomingJobs = allEvents.filter(apt => {
      if (apt.date > todayStr) return true; // Future days always count
      if (apt.date < todayStr) return false; // Past days don't count
      
      // For today, check if the time has passed
      if (!apt.time) return true;
      const timeStr = apt.time.replace(/[^0-9]/g, '');
      const aptHour = parseInt(timeStr.slice(0, 2)) || 0;
      const aptMin = parseInt(timeStr.slice(2, 4)) || 0;
      const aptTime = aptHour * 100 + aptMin;
      return aptTime >= currentTime;
    });
    
    if (upcomingJobs.length > 0) {
      return { job: upcomingJobs[0], isToday: upcomingJobs[0].date === todayStr };
    }
    
    return null;
  };
  
  // Approve a pending appointment
  const approveAppointment = async (apt) => {
    try {
      await updateDoc(doc(db, 'events', apt.id), {
        status: 'approved',
        approvedAt: serverTimestamp()
      });
      // Refresh the data
      loadDayAppointments();
    } catch (error) {
      console.error('Error approving appointment:', error);
      alert('Failed to approve appointment');
    }
  };
  
  // Reject a pending appointment
  const rejectAppointment = async (apt) => {
    if (!confirm(`Are you sure you want to reject this appointment for ${apt.clientName || 'this client'}?`)) return;
    try {
      await updateDoc(doc(db, 'events', apt.id), {
        status: 'rejected',
        rejectedAt: serverTimestamp()
      });
      // Refresh the data
      loadDayAppointments();
    } catch (error) {
      console.error('Error rejecting appointment:', error);
      alert('Failed to reject appointment');
    }
  };

  const goToPreviousDay = () => {
    const current = new Date(selectedDate + 'T12:00:00'); // Add time to avoid timezone issues
    current.setDate(current.getDate() - 1);
    setSelectedDate(getLocalDateString(current));
    setCustomStops([]); // Clear custom stops when changing days
  };

  const goToNextDay = () => {
    const current = new Date(selectedDate + 'T12:00:00'); // Add time to avoid timezone issues
    current.setDate(current.getDate() + 1);
    setSelectedDate(getLocalDateString(current));
    setCustomStops([]);
  };

  const goToToday = () => {
    setSelectedDate(getLocalDateString());
    setCustomStops([]);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isToday = selectedDate === getLocalDateString();

  // Get client address from client list or event data
  const getClientAddress = (appointment) => {
    if (appointment.address) return appointment.address;
    
    if (appointment.clientId) {
      const client = clients.find(c => c.id === appointment.clientId);
      if (client?.address) return client.address;
      if (client?.zipcode) return `${client.zipcode}, Las Vegas, NV`;
    }
    
    if (appointment.zipcode) return `${appointment.zipcode}, Las Vegas, NV`;
    
    return null;
  };

  // Generate Google Maps directions URL for full route
  const generateRouteUrl = () => {
    const waypoints = getAllWaypoints();
    if (waypoints.length < 2) return null;
    
    const origin = encodeURIComponent(startingAddress);
    const allWaypoints = waypoints.slice(1).map(w => encodeURIComponent(w.address || `${w.lat},${w.lng}`));
    const destination = allWaypoints.pop();
    
    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;
    if (allWaypoints.length > 0) {
      url += `&waypoints=${allWaypoints.join('|')}`;
    }
    url += '&travelmode=driving';
    
    return url;
  };

  const openRoute = () => {
    const url = generateRouteUrl();
    if (url) {
      window.open(url, '_blank');
    } else {
      alert('No addresses available for routing. Please ensure appointments have addresses.');
    }
  };

  // Generate single leg navigation URL
  const openSingleLeg = (fromWaypoint, toWaypoint) => {
    const origin = fromWaypoint.address || `${fromWaypoint.lat},${fromWaypoint.lng}`;
    const destination = toWaypoint.address || `${toWaypoint.lat},${toWaypoint.lng}`;
    
    const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&travelmode=driving`;
    window.open(url, '_blank');
  };

  // Generate enhanced Mapbox static map URL with actual route line
  const generateRouteMapUrl = () => {
    const waypoints = getAllWaypoints();
    if (waypoints.length === 0) {
      // Zoom level 9 shows Las Vegas metro area when no route
      return `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/-115.14,36.17,9/900x450@2x?access_token=${MAPBOX_PUBLIC_TOKEN}`;
    }
    
    // Calculate center point from all waypoints
    const lngs = waypoints.map(w => w.lng);
    const lats = waypoints.map(w => w.lat);
    const centerLng = ((Math.min(...lngs) + Math.max(...lngs)) / 2).toFixed(5);
    const centerLat = ((Math.min(...lats) + Math.max(...lats)) / 2).toFixed(5);
    
    // Build markers
    const markers = waypoints.map((wp, idx) => {
      if (wp.type === 'start') {
        return `pin-l-home+22c55e(${wp.lng},${wp.lat})`;
      } else if (wp.type === 'custom') {
        return `pin-s-shop+f96302(${wp.lng},${wp.lat})`;
      } else {
        return `pin-l-${idx}+d4a84b(${wp.lng},${wp.lat})`;
      }
    }).join(',');
    
    // If we have route geometry from Directions API, use path overlay
    if (routeData?.geometry?.coordinates && routeData.geometry.coordinates.length > 0) {
      // Simplify the route for static map (take every Nth point to stay under URL limits)
      const coords = routeData.geometry.coordinates;
      const simplifyFactor = Math.max(1, Math.floor(coords.length / 25));
      const simplifiedCoords = coords.filter((_, i) => i % simplifyFactor === 0 || i === coords.length - 1);
      
      // Mapbox Static API path format: path-strokeWidth+color-opacity(lng,lat,lng,lat,...)
      const pathPoints = simplifiedCoords.map(c => `${c[0].toFixed(5)},${c[1].toFixed(5)}`).join(',');
      const pathOverlay = `path-5+00b894-0.9(${pathPoints})`;
      
      // Build URL with path, markers, and explicit center/zoom
      const testUrl = `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/${pathOverlay},${markers}/${centerLng},${centerLat},${mapZoom}/900x450@2x?access_token=${MAPBOX_PUBLIC_TOKEN}`;
      
      if (testUrl.length < 7500) {
        return testUrl;
      }
      
      // If too long, simplify more
      const fewerCoords = simplifiedCoords.filter((_, i) => i % 2 === 0 || i === simplifiedCoords.length - 1);
      const shorterPath = fewerCoords.map(c => `${c[0].toFixed(4)},${c[1].toFixed(4)}`).join(',');
      return `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/path-4+00b894-0.9(${shorterPath}),${markers}/${centerLng},${centerLat},${mapZoom}/900x450@2x?access_token=${MAPBOX_PUBLIC_TOKEN}`;
    }
    
    // Fallback: draw simple line connecting waypoints directly
    if (waypoints.length > 1) {
      const pathPoints = waypoints.map(w => `${w.lng.toFixed(5)},${w.lat.toFixed(5)}`).join(',');
      const pathOverlay = `path-4+00b894-0.8(${pathPoints})`;
      return `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/${pathOverlay},${markers}/${centerLng},${centerLat},${mapZoom}/900x450@2x?access_token=${MAPBOX_PUBLIC_TOKEN}`;
    }
    
    // Single marker only
    return `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/${markers}/${centerLng},${centerLat},${mapZoom}/900x450@2x?access_token=${MAPBOX_PUBLIC_TOKEN}`;
  };

  if (loading) {
    return (
      <View style={styles.tabContent}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.teal} />
          <Text style={styles.loadingText}>Loading route data...</Text>
        </View>
      </View>
    );
  }

  const waypoints = getAllWaypoints();
  const nextJob = getNextJob();

  return (
    <View style={styles.tabContent}>
      <View style={styles.tabHeader}>
        <View>
          <Text style={styles.tabTitle}>Route Mapper</Text>
          <Text style={styles.tabSubtitle}>Plan your daily route with real-time ETAs</Text>
        </View>
        <View style={styles.tabHeaderButtons}>
          {nextJob && (
            <TouchableOpacity 
              style={styles.nextJobBtn}
              onPress={() => {
                const nextApt = nextJob.job;
                const addr = getClientAddress(nextApt);
                if (addr) {
                  const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}&travelmode=driving`;
                  window.open(url, '_blank');
                } else {
                  alert(`Next job: ${nextApt.clientName || 'Client'} at ${nextApt.time || 'TBD'}\nNo address on file for navigation.`);
                }
              }}
              title="Next Job"
            >
              <Icon name="navigation" size={16} color={colors.darkNavy} style={isWeb && width > 900 ? { marginRight: 8 } : {}} />
              {isWeb && width > 900 && <Text style={styles.nextJobBtnText}>Next Job</Text>}
            </TouchableOpacity>
          )}
          <TouchableOpacity 
            style={styles.settingsBtn}
            onPress={() => setShowSettings(!showSettings)}
          >
            <Icon name="settings" size={18} color={colors.white} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Settings Panel */}
      {showSettings && (
        <View style={styles.routeSettingsPanel}>
          <Text style={styles.routeSettingsTitle}>Starting Location</Text>
          <TextInput
            style={styles.routeSettingsInput}
            value={startingAddress}
            onChangeText={setStartingAddress}
            placeholder="Enter your starting address"
            placeholderTextColor={colors.gray}
          />
          <Text style={styles.routeSettingsHint}>This is where your route will begin each day</Text>
          {currentLocation && (
            <View style={styles.routeSettingsLocation}>
              <Icon name="crosshair" size={14} color={colors.teal} />
              <Text style={styles.routeSettingsLocationText}>
                Current GPS: {currentLocation.lat.toFixed(4)}, {currentLocation.lng.toFixed(4)}
              </Text>
            </View>
          )}
        </View>
      )}

      {/* Date Navigation */}
      <View style={styles.routeDateNav}>
        <TouchableOpacity style={styles.routeDateArrow} onPress={goToPreviousDay}>
          <Icon name="chevron-left" size={24} color={colors.white} />
        </TouchableOpacity>
        
        <View style={styles.routeDateCenter}>
          <Text style={styles.routeDateText}>{formatDate(selectedDate)}</Text>
          {!isToday && (
            <TouchableOpacity style={styles.routeTodayBtn} onPress={goToToday}>
              <Text style={styles.routeTodayBtnText}>Go to Today</Text>
            </TouchableOpacity>
          )}
          {isToday && (
            <View style={styles.routeTodayBadge}>
              <Text style={styles.routeTodayBadgeText}>TODAY</Text>
            </View>
          )}
        </View>
        
        <TouchableOpacity style={styles.routeDateArrow} onPress={goToNextDay}>
          <Icon name="chevron-right" size={24} color={colors.white} />
        </TouchableOpacity>
      </View>

      {/* Route Summary with Stats */}
      <View style={styles.routeSummaryCard}>
        <View style={styles.routeSummaryHeader}>
          <View style={styles.routeSummaryIcon}>
            <Icon name="map" size={24} color={colors.teal} />
          </View>
          <View style={styles.routeSummaryInfo}>
            <Text style={styles.routeSummaryTitle}>
              {appointments.length} Stop{appointments.length !== 1 ? 's' : ''} 
              {customStops.length > 0 ? ` + ${customStops.length} extra` : ''}
            </Text>
            <Text style={styles.routeSummarySubtitle}>
              {routeData ? `${formatDistance(routeData.totalDistance)} • ${formatDuration(routeData.totalDuration)}` : 'Calculating...'}
            </Text>
          </View>
        </View>
        
        {/* Enhanced Route Stats */}
        {routeData && (
          <View style={styles.routeStatsRow}>
            <View style={styles.routeStatItem}>
              <Icon name="navigation" size={18} color={colors.teal} />
              <Text style={styles.routeStatValue}>{formatDuration(routeData.totalDuration)}</Text>
              <Text style={styles.routeStatLabel}>Drive Time</Text>
            </View>
            <View style={styles.routeStatItem}>
              <Icon name="clock" size={18} color={colors.gold} />
              <Text style={styles.routeStatValue}>{formatDuration(getTotalJourneyTime())}</Text>
              <Text style={styles.routeStatLabel}>Total Time</Text>
            </View>
            <View style={styles.routeStatItem}>
              <Icon name="map-pin" size={18} color={colors.cyan} />
              <Text style={styles.routeStatValue}>{formatDistance(routeData.totalDistance)}</Text>
              <Text style={styles.routeStatLabel}>Distance</Text>
            </View>
            <View style={styles.routeStatItem}>
              <Icon name="flag" size={18} color={colors.green} />
              <Text style={styles.routeStatValue}>{waypoints.length}</Text>
              <Text style={styles.routeStatLabel}>Stops</Text>
            </View>
          </View>
        )}
        
        {/* Interactive Map Preview */}
        <TouchableOpacity 
          style={styles.routeMapContainer}
          onPress={openRoute}
          activeOpacity={0.9}
        >
          {loadingRoute && (
            <View style={styles.routeMapLoading}>
              <ActivityIndicator size="small" color={colors.teal} />
              <Text style={styles.routeMapLoadingText}>Calculating route...</Text>
            </View>
          )}
          {isWeb ? (
            <View style={{ position: 'relative', width: '100%', height: 350 }}>
              <Image
                source={{ uri: generateRouteMapUrl() }}
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: 12,
                  backgroundColor: colors.navyMid,
                }}
                resizeMode="cover"
              />
              {/* Zoom Controls */}
              <View style={styles.mapZoomControls}>
                <TouchableOpacity 
                  style={styles.mapZoomBtn}
                  onPress={() => setMapZoom(prev => Math.min(prev + 1, 16))}
                >
                  <Icon name="plus" size={18} color={colors.white} />
                </TouchableOpacity>
                <Text style={styles.mapZoomLevel}>{mapZoom}</Text>
                <TouchableOpacity 
                  style={styles.mapZoomBtn}
                  onPress={() => setMapZoom(prev => Math.max(prev - 1, 8))}
                >
                  <Icon name="minus" size={18} color={colors.white} />
                </TouchableOpacity>
                <TouchableOpacity 
                  style={[styles.mapZoomBtn, { marginTop: 8, backgroundColor: colors.teal + '40' }]}
                  onPress={() => {
                    // Auto-fit zoom based on route spread
                    const wps = getAllWaypoints();
                    if (wps.length > 1) {
                      const lngs = wps.map(w => w.lng);
                      const lats = wps.map(w => w.lat);
                      const spread = Math.max(
                        Math.max(...lngs) - Math.min(...lngs),
                        Math.max(...lats) - Math.min(...lats)
                      );
                      if (spread > 0.3) setMapZoom(9);
                      else if (spread > 0.15) setMapZoom(10);
                      else if (spread > 0.08) setMapZoom(11);
                      else setMapZoom(12);
                    } else {
                      setMapZoom(12);
                    }
                  }}
                >
                  <Icon name="maximize-2" size={16} color={colors.teal} />
                </TouchableOpacity>
              </View>
              <View style={{ 
                position: 'absolute', 
                bottom: 10, 
                left: 10, 
                zIndex: 20 
              }}>
                <TouchableOpacity 
                  style={styles.mapClickBadge}
                  onPress={openRoute}
                >
                  <Icon name="external-link" size={14} color={colors.white} />
                  <Text style={styles.mapClickText}>Open in Google Maps</Text>
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            <View style={styles.mapPlaceholder}>
              <Icon name="map" size={32} color={colors.textMuted} />
              <Text style={styles.mapPlaceholderText}>Map available on web</Text>
            </View>
          )}
        </TouchableOpacity>
        
        {/* Action Buttons */}
        <View style={styles.routeActionsRow}>
          <TouchableOpacity style={styles.openRouteBtn} onPress={openRoute}>
            <Icon name="navigation" size={16} color={colors.darkNavy} />
            <Text style={styles.openRouteBtnText}>Full Route</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.openRouteBtn, { backgroundColor: colors.navyMid }]}
            onPress={() => setShowAddStopModal(true)}
          >
            <Icon name="plus" size={16} color={colors.white} />
            <Text style={[styles.openRouteBtnText, { color: colors.white }]}>Add Stop</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.openRouteBtn, { backgroundColor: colors.navyMid }]}
            onPress={calculateFullRoute}
          >
            <Icon name="refresh-cw" size={16} color={colors.white} />
            <Text style={[styles.openRouteBtnText, { color: colors.white }]}>Refresh</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Route Stops List */}
      <View style={styles.routeStopsList}>
        <View style={styles.routeStopsHeader}>
          <View>
            <Text style={styles.routeStopsTitle}>Route Order</Text>
            <View style={styles.routeStopsStatusCounts}>
              <View style={styles.routeStopsCountBadge}>
                <View style={[styles.routeStopsCountDot, { backgroundColor: colors.teal }]} />
                <Text style={[styles.routeStopsCountText, { color: colors.teal }]}>
                  {appointments.filter(a => a.status === 'confirmed' || a.status === 'approved').length} Approved
                </Text>
              </View>
              <View style={styles.routeStopsCountBadge}>
                <View style={[styles.routeStopsCountDot, { backgroundColor: colors.gold }]} />
                <Text style={[styles.routeStopsCountText, { color: colors.gold }]}>
                  {appointments.filter(a => a.status === 'pending' || !a.status).length} Pending
                </Text>
              </View>
            </View>
          </View>
          <Text style={styles.routeStopsSubtitle}>Tap stops for options</Text>
        </View>
        
        {/* Geocoding Errors Warning */}
        {geocodingErrors.length > 0 && (
          <View style={styles.routeWarningBanner}>
            <Icon name="alert-triangle" size={16} color={colors.gold} />
            <Text style={styles.routeWarningText}>
              {geocodingErrors.length} address{geocodingErrors.length > 1 ? 'es' : ''} could not be geocoded and won't appear in the route.
            </Text>
          </View>
        )}
        
        {/* Starting Point */}
        <View style={styles.routeStop}>
          <View style={[styles.routeStopMarker, { backgroundColor: colors.green }]}>
            <Icon name="home" size={16} color={colors.white} />
          </View>
          <View style={styles.routeStopLine} />
          <View style={styles.routeStopContent}>
            <Text style={styles.routeStopLabel}>START</Text>
            <Text style={styles.routeStopTitle}>Starting Location</Text>
            <Text style={styles.routeStopAddress}>{startingAddress}</Text>
          </View>
        </View>

        {appointments.length === 0 ? (
          <View style={styles.routeEmptyState}>
            <Icon name="calendar" size={48} color={colors.navyMid} />
            <Text style={styles.routeEmptyTitle}>No Appointments</Text>
            <Text style={styles.routeEmptyText}>
              No appointments scheduled for this day.{'\n'}
              Use the date navigation to check other days.
            </Text>
          </View>
        ) : (
          waypoints.slice(1).map((wp, index) => {
            const leg = routeData?.legs?.[index];
            const isAppointment = wp.type === 'appointment';
            const isCustom = wp.type === 'custom';
            const apt = wp.appointment;
            const isLast = index === waypoints.length - 2;
            const prevWp = waypoints[index];
            
            return (
              <View key={wp.appointment?.id || wp.customStop?.id || index}>
                {/* Leg info between stops */}
                {leg && (
                  <View style={styles.routeLegInfo}>
                    <View style={styles.routeLegLine} />
                    <View style={styles.routeLegBadge}>
                      <Icon name="truck" size={12} color={colors.teal} />
                      <Text style={styles.routeLegText}>
                        {formatDistance(leg.distance)} • {formatDuration(leg.duration)}
                      </Text>
                    </View>
                  </View>
                )}
                
                <View style={[
                  styles.routeStop,
                  isAppointment && (apt?.status === 'pending' || !apt?.status) && styles.routeStopPending
                ]}>
                  <View style={[styles.routeStopMarker, { 
                    backgroundColor: isCustom ? colors.orange : 
                      (apt?.status === 'confirmed' || apt?.status === 'approved') ? colors.teal : 
                      (apt?.status === 'pending' || !apt?.status) ? colors.gold : colors.gray
                  }]}>
                    {isCustom ? (
                      <Icon name="shopping-bag" size={14} color={colors.white} />
                    ) : (
                      <Text style={styles.routeStopNumber}>{index + 1}</Text>
                    )}
                  </View>
                  {!isLast && <View style={styles.routeStopLine} />}
                  
                  <View style={styles.routeStopContent}>
                    {isAppointment && apt && (
                      <>
                        {/* Status Badge */}
                        <View style={[
                          styles.routeStopStatusBadge,
                          { backgroundColor: (apt.status === 'confirmed' || apt.status === 'approved') ? colors.teal + '20' : colors.gold + '20' }
                        ]}>
                          <View style={[
                            styles.routeStopStatusDot,
                            { backgroundColor: (apt.status === 'confirmed' || apt.status === 'approved') ? colors.teal : colors.gold }
                          ]} />
                          <Text style={[
                            styles.routeStopStatusText,
                            { color: (apt.status === 'confirmed' || apt.status === 'approved') ? colors.teal : colors.gold }
                          ]}>
                            {(apt.status === 'confirmed' || apt.status === 'approved') ? 'APPROVED' : 'PENDING'}
                          </Text>
                        </View>
                        
                        <View style={styles.routeStopHeader}>
                          <View style={styles.routeStopHeaderInfo}>
                            <Text style={styles.routeStopTime}>{apt.time || 'TBD'}</Text>
                            <TouchableOpacity onPress={() => onNavigateToClient && onNavigateToClient(apt.clientId)}>
                              <Text style={styles.routeStopTitle}>{apt.clientName || 'Client'}</Text>
                            </TouchableOpacity>
                          </View>
                          {leg && (
                            <View style={styles.routeStopETA}>
                              <Text style={styles.routeStopETAText}>ETA: {formatDuration(leg.duration)}</Text>
                            </View>
                          )}
                        </View>
                        <Text style={styles.routeStopService}>{apt.service || 'Service'}</Text>
                        <View style={styles.routeStopAddressRow}>
                          <Icon name="map-pin" size={12} color={colors.gray} />
                          <Text style={styles.routeStopAddress}>{wp.address || 'No address'}</Text>
                        </View>
                        
                        {/* Stop Duration Input */}
                        <View style={styles.stopDurationRow}>
                          <Icon name="clock" size={12} color={colors.gold} />
                          <Text style={styles.stopDurationLabel}>Job time:</Text>
                          <TextInput
                            style={styles.stopDurationInput}
                            value={String(stopDurations[apt.id] || apt.estimatedDuration || '')}
                            onChangeText={(val) => updateStopDuration(apt.id, val)}
                            placeholder="30"
                            placeholderTextColor={colors.gray}
                            keyboardType="numeric"
                          />
                          <Text style={styles.stopDurationUnit}>min</Text>
                        </View>
                        
                        {/* Approve/Reject buttons for pending */}
                        {(apt.status === 'pending' || !apt.status) && (
                          <View style={styles.routePendingActions}>
                            <TouchableOpacity 
                              style={styles.routeApproveBtn}
                              onPress={() => approveAppointment(apt)}
                            >
                              <Icon name="check" size={14} color={colors.darkNavy} />
                              <Text style={styles.routeApproveBtnText}>Approve</Text>
                            </TouchableOpacity>
                            <TouchableOpacity 
                              style={styles.routeRejectBtn}
                              onPress={() => rejectAppointment(apt)}
                            >
                              <Icon name="x" size={14} color={colors.white} />
                              <Text style={styles.routeRejectBtnText}>Reject</Text>
                            </TouchableOpacity>
                          </View>
                        )}

                        {/* Action buttons */}
                        <View style={styles.routeStopActionsRow}>
                          <TouchableOpacity 
                            style={styles.routeStopActionBtn}
                            onPress={() => openSingleLeg(prevWp, wp)}
                          >
                            <Icon name="navigation" size={12} color={colors.teal} />
                            <Text style={styles.routeStopActionBtnText}>Navigate</Text>
                          </TouchableOpacity>
                          
                          <TouchableOpacity 
                            style={[styles.routeStopActionBtn, sendingNotification === index && styles.routeStopActionBtnDisabled]}
                            onPress={() => sendOnMyWayNotification(apt, index)}
                            disabled={sendingNotification === index}
                          >
                            <Icon name="send" size={12} color={colors.green} />
                            <Text style={[styles.routeStopActionBtnText, { color: colors.green }]}>
                              {sendingNotification === index ? 'Sending...' : 'On My Way'}
                            </Text>
                          </TouchableOpacity>
                          
                          {(apt.phone || apt.clientPhone) && (
                            <TouchableOpacity 
                              style={styles.routeStopActionBtn}
                              onPress={() => window.open(`tel:${apt.phone || apt.clientPhone}`, '_self')}
                            >
                              <Icon name="phone" size={12} color={colors.white} />
                              <Text style={styles.routeStopActionBtnText}>Call</Text>
                            </TouchableOpacity>
                          )}
                          
                          {(apt.email || apt.clientEmail) && (
                            <TouchableOpacity 
                              style={styles.routeStopActionBtn}
                              onPress={() => window.open(`mailto:${apt.email || apt.clientEmail}`, '_self')}
                            >
                              <Icon name="mail" size={12} color={colors.teal} />
                              <Text style={styles.routeStopActionBtnText}>Email</Text>
                            </TouchableOpacity>
                          )}
                          
                          {(apt.phone || apt.clientPhone) && (
                            <TouchableOpacity 
                              style={styles.routeStopActionBtn}
                              onPress={() => {
                                const phone = apt.phone || apt.clientPhone;
                                const leg = routeData?.legs?.[index];
                                const etaMinutes = leg ? Math.round(leg.duration / 60) : 'soon';
                                const message = `Hi ${apt.clientName}, I'm on my way! I should arrive in about ${etaMinutes} minutes.`;
                                window.open(`sms:${phone}?body=${encodeURIComponent(message)}`, '_self');
                              }}
                            >
                              <Icon name="message-square" size={12} color={colors.green} />
                              <Text style={[styles.routeStopActionBtnText, { color: colors.green }]}>SMS</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                        
                        {/* Add Store button - only when approved */}
                        {(apt.status === 'confirmed' || apt.status === 'approved') && (
                          <TouchableOpacity 
                            style={styles.routeAddStoreBtn}
                            onPress={async () => {
                              setShowStoreOptions(index);
                              const stores = await searchNearbyStores(['home depot', 'lowes'], wp.lat, wp.lng);
                              setNearbyStores(stores);
                            }}
                          >
                            <Icon name="shopping-bag" size={12} color={colors.orange} />
                            <Text style={styles.routeAddStoreBtnText}>{loadingStores && showStoreOptions === index ? 'Loading...' : '+ Add Supply Stop'}</Text>
                          </TouchableOpacity>
                        )}
                        
                        {/* Store options dropdown */}
                        {showStoreOptions === index && (
                          <View style={styles.storeOptionsDropdown}>
                            <Text style={styles.storeOptionsTitle}>Add Nearby Store Stop:</Text>
                            {loadingStores ? (
                              <View style={{ padding: 12, alignItems: 'center' }}>
                                <ActivityIndicator size="small" color={colors.teal} />
                                <Text style={{ color: colors.textMuted, marginTop: 8 }}>Searching nearby stores...</Text>
                              </View>
                            ) : nearbyStores.length > 0 ? (
                              nearbyStores.map((store, storeIdx) => (
                                <TouchableOpacity 
                                  key={storeIdx}
                                  style={styles.storeOption}
                                  onPress={() => addStoreAsStop(store, wp.index)}
                                >
                                  <Icon name="shopping-bag" size={14} color={colors.orange} />
                                  <View style={styles.storeOptionInfo}>
                                    <Text style={styles.storeOptionName}>{store.name}</Text>
                                    <Text style={styles.storeOptionAddress} numberOfLines={1}>{store.fullName}</Text>
                                  </View>
                                  <Icon name="plus" size={16} color={colors.teal} />
                                </TouchableOpacity>
                              ))
                            ) : (
                              <Text style={{ color: colors.textMuted, padding: 12 }}>No stores found nearby</Text>
                            )}
                            <TouchableOpacity 
                              style={styles.storeOptionsClose}
                              onPress={() => { setShowStoreOptions(null); setNearbyStores([]); }}
                            >
                              <Text style={styles.storeOptionsCloseText}>Close</Text>
                            </TouchableOpacity>
                          </View>
                        )}
                      </>
                    )}
                    
                    {isCustom && (
                      <>
                        <View style={styles.routeStopHeader}>
                          <View style={styles.routeStopHeaderInfo}>
                            <Text style={styles.routeStopLabel}>SUPPLY STOP</Text>
                            <Text style={styles.routeStopTitle}>{wp.name}</Text>
                          </View>
                          <TouchableOpacity 
                            style={styles.routeStopRemoveBtn}
                            onPress={() => removeCustomStop(wp.customStop.id)}
                          >
                            <Icon name="x" size={16} color={colors.red} />
                          </TouchableOpacity>
                        </View>
                        <View style={styles.routeStopAddressRow}>
                          <Icon name="map-pin" size={12} color={colors.gray} />
                          <Text style={styles.routeStopAddress} numberOfLines={1}>{wp.address}</Text>
                        </View>
                        <View style={styles.routeStopActionsRow}>
                          <TouchableOpacity 
                            style={styles.routeStopActionBtn}
                            onPress={() => openSingleLeg(prevWp, wp)}
                          >
                            <Icon name="navigation" size={12} color={colors.teal} />
                            <Text style={styles.routeStopActionBtnText}>Navigate</Text>
                          </TouchableOpacity>
                        </View>
                      </>
                    )}
                  </View>
                </View>
              </View>
            );
          })
        )}
      </View>

      {/* Add Stop Modal */}
      <Modal visible={showAddStopModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.addStopModal}>
            <View style={styles.addStopModalHeader}>
              <Text style={styles.addStopModalTitle}>Add Custom Stop</Text>
              <TouchableOpacity onPress={() => setShowAddStopModal(false)}>
                <Icon name="x" size={24} color={colors.white} />
              </TouchableOpacity>
            </View>
            
            <View style={styles.addStopModalBody}>
              <Text style={styles.addStopLabel}>Stop Name</Text>
              <TextInput
                style={styles.addStopInput}
                value={newStopForm.name}
                onChangeText={(val) => setNewStopForm(prev => ({ ...prev, name: val }))}
                placeholder="e.g., Home Depot, Supply Store"
                placeholderTextColor={colors.gray}
              />
              
              <Text style={styles.addStopLabel}>Address</Text>
              <TextInput
                style={styles.addStopInput}
                value={newStopForm.address}
                onChangeText={(val) => setNewStopForm(prev => ({ ...prev, address: val }))}
                placeholder="Enter full address"
                placeholderTextColor={colors.gray}
              />
              
              <Text style={styles.addStopLabel}>Estimated Time at Stop</Text>
              <View style={styles.addStopDurationRow}>
                <TextInput
                  style={[styles.addStopInput, { width: 80, textAlign: 'center' }]}
                  value={String(newStopForm.duration || '')}
                  onChangeText={(val) => setNewStopForm(prev => ({ ...prev, duration: parseInt(val) || 0 }))}
                  placeholder="15"
                  placeholderTextColor={colors.gray}
                  keyboardType="numeric"
                />
                <Text style={styles.addStopDurationUnit}>minutes</Text>
              </View>
              
              <Text style={styles.addStopLabel}>Insert After Stop</Text>
              <View style={styles.addStopSelectRow}>
                {appointments.map((apt, idx) => (
                  <TouchableOpacity 
                    key={idx}
                    style={[styles.addStopSelectBtn, newStopForm.afterIndex === idx && styles.addStopSelectBtnActive]}
                    onPress={() => setNewStopForm(prev => ({ ...prev, afterIndex: idx }))}
                  >
                    <Text style={[styles.addStopSelectBtnText, newStopForm.afterIndex === idx && styles.addStopSelectBtnTextActive]}>
                      {idx + 1}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
              
              {/* Quick Store Buttons */}
              <Text style={styles.addStopLabel}>Or Search Nearby:</Text>
              <View style={styles.quickStoreButtons}>
                {STORE_TYPES.map((store, idx) => (
                  <TouchableOpacity 
                    key={idx}
                    style={[styles.quickStoreBtn, { borderColor: store.color }]}
                    onPress={async () => {
                      const results = await searchNearbyStores(store.query, DEFAULT_START.lat, DEFAULT_START.lng);
                      if (results.length > 0) {
                        setNewStopForm(prev => ({
                          ...prev,
                          name: results[0].name,
                          address: results[0].fullName
                        }));
                      }
                    }}
                  >
                    <Text style={styles.quickStoreBtnIcon}>{store.icon}</Text>
                    <Text style={styles.quickStoreBtnText}>{store.name}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            
            <View style={styles.addStopModalActions}>
              <TouchableOpacity 
                style={styles.addStopCancelBtn}
                onPress={() => setShowAddStopModal(false)}
              >
                <Text style={styles.addStopCancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.addStopSaveBtn}
                onPress={addCustomStop}
              >
                <Icon name="plus" size={16} color={colors.darkNavy} />
                <Text style={styles.addStopSaveBtnText}>Add Stop</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// Appointments Tab (Dashboard > Appointments) - Full job management
const AppointmentsTab = ({ onNavigateToClient }) => {
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [businessHours, setBusinessHours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeColumn, setActiveColumn] = useState('pending');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [rejectionMode, setRejectionMode] = useState('outright'); // 'outright' or 'revision'
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [suggestedManuals, setSuggestedManuals] = useState([]);
  const [selectedManuals, setSelectedManuals] = useState([]);
  const [fieldManuals, setFieldManuals] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedAppointment, setEditedAppointment] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusAction, setStatusAction] = useState(null); // { type: 'pending'|'confirmed'|'completed'|'cancelled'|'delete', appointment }
  const [sendEmailOnChange, setSendEmailOnChange] = useState(true);
  const [processingStatus, setProcessingStatus] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all events
      const eventsRef = collection(db, 'events');
      const eventsSnapshot = await getDocs(eventsRef);
      const eventsList = [];
      eventsSnapshot.forEach(doc => {
        const data = doc.data();
        // Filter out archived events
        if (data.status !== 'archived') {
          eventsList.push({ id: doc.id, ...data });
        }
      });
      
      // Sort by date and time
      eventsList.sort((a, b) => {
        const dateComp = (a.date || '').localeCompare(b.date || '');
        if (dateComp !== 0) return dateComp;
        const timeA = a.time?.replace(/[^0-9:]/g, '') || '00:00';
        const timeB = b.time?.replace(/[^0-9:]/g, '') || '00:00';
        return timeA.localeCompare(timeB);
      });
      
      setAppointments(eventsList);
      
      // Load clients
      const clientsRef = collection(db, 'clients');
      const clientsSnapshot = await getDocs(clientsRef);
      const clientsList = [];
      clientsSnapshot.forEach(doc => {
        clientsList.push({ id: doc.id, ...doc.data() });
      });
      setClients(clientsList);
      
      // Load business hours
      const hoursDoc = await getDoc(doc(db, 'settings', 'businessHours'));
      if (hoursDoc.exists()) {
        setBusinessHours(hoursDoc.data());
      }
      
      // Load field manuals for suggestions
      const manualsRef = collection(db, 'fieldManuals');
      const manualsSnapshot = await getDocs(manualsRef);
      const manualsList = [];
      manualsSnapshot.forEach(doc => {
        manualsList.push({ id: doc.id, ...doc.data() });
      });
      setFieldManuals(manualsList);
      
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
    setLoading(false);
  };
  
  // AI-based manual suggestions
  const suggestManualsForJob = (appointment) => {
    const service = (appointment.service || '').toLowerCase();
    const description = (appointment.description || '').toLowerCase();
    const searchText = `${service} ${description}`;
    
    // Find matching manuals based on keywords
    const matched = fieldManuals.filter(manual => {
      const manualText = `${manual.title || ''} ${manual.category || ''} ${manual.tags?.join(' ') || ''}`.toLowerCase();
      const keywords = searchText.split(' ').filter(w => w.length > 3);
      return keywords.some(keyword => manualText.includes(keyword));
    });
    
    setSuggestedManuals(matched.slice(0, 5));
    return matched.slice(0, 5);
  };

  // Filter appointments by status
  const pendingAppointments = appointments.filter(a => a.status === 'pending' || !a.status);
  const upcomingAppointments = appointments.filter(a => 
    (a.status === 'confirmed' || a.status === 'approved') && 
    new Date(a.date) >= new Date(new Date().toISOString().split('T')[0])
  );
  const inProgressAppointments = appointments.filter(a => a.status === 'in-progress');
  const completedAppointments = appointments.filter(a => a.status === 'completed');
  const cancelledAppointments = appointments.filter(a => a.status === 'cancelled' || a.status === 'rejected');

  const getActiveAppointments = () => {
    switch (activeColumn) {
      case 'pending': return pendingAppointments;
      case 'upcoming': return upcomingAppointments;
      case 'in-progress': return inProgressAppointments;
      case 'completed': return completedAppointments;
      case 'cancelled': return cancelledAppointments;
      default: return pendingAppointments;
    }
  };

  // Archive an appointment
  const archiveAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to archive this appointment? It will be hidden from all views.')) {
      return;
    }
    
    try {
      const eventRef = doc(db, 'events', appointmentId);
      await updateDoc(eventRef, {
        status: 'archived',
        archivedAt: new Date().toISOString()
      });
      
      // Remove from local state
      setAppointments(prev => prev.filter(a => a.id !== appointmentId));
      setShowDetailModal(false);
      setSelectedAppointment(null);
      alert('Appointment archived successfully');
    } catch (error) {
      console.error('Error archiving appointment:', error);
      alert('Failed to archive appointment');
    }
  };

  // Open status change modal
  const openStatusModal = (type, appointment) => {
    setStatusAction({ type, appointment });
    setSendEmailOnChange(true);
    setShowStatusModal(true);
  };

  // Process status change
  const processStatusChange = async () => {
    if (!statusAction) return;
    
    setProcessingStatus(true);
    const { type, appointment } = statusAction;
    
    try {
      const eventRef = doc(db, 'events', appointment.id);
      
      if (type === 'delete') {
        // Permanently delete
        await deleteDoc(eventRef);
        setAppointments(prev => prev.filter(a => a.id !== appointment.id));
        alert('Appointment deleted permanently');
      } else {
        // Update status
        const updateData = {
          status: type,
          updatedAt: serverTimestamp(),
        };
        
        // Add timestamp for the specific status
        if (type === 'confirmed') {
          updateData.confirmedAt = new Date().toISOString();
        } else if (type === 'completed') {
          updateData.completedAt = new Date().toISOString();
        } else if (type === 'cancelled') {
          updateData.cancelledAt = new Date().toISOString();
        } else if (type === 'pending') {
          updateData.revertedAt = new Date().toISOString();
        }
        
        await updateDoc(eventRef, updateData);
        
        // Update local state
        setAppointments(prev => prev.map(a => 
          a.id === appointment.id ? { ...a, status: type, ...updateData } : a
        ));
        
        // Send email notification if enabled
        if (sendEmailOnChange && appointment.email) {
          try {
            const sendStatusUpdateEmail = httpsCallable(functions, 'sendStatusUpdateEmail');
            await sendStatusUpdateEmail({
              clientEmail: appointment.email || appointment.clientEmail,
              clientName: appointment.clientName,
              appointmentDate: appointment.date,
              appointmentTime: appointment.time,
              service: appointment.service,
              newStatus: type,
            });
          } catch (emailError) {
            console.warn('Could not send status update email:', emailError);
          }
        }
        
        alert(`Appointment status changed to ${type}`);
      }
      
      setShowStatusModal(false);
      setShowDetailModal(false);
      setSelectedAppointment(null);
      setStatusAction(null);
    } catch (error) {
      console.error('Error changing status:', error);
      alert('Failed to change status');
    } finally {
      setProcessingStatus(false);
    }
  };

  // Delete appointment permanently
  const deleteAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY DELETE this appointment? This cannot be undone.')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'events', appointmentId));
      setAppointments(prev => prev.filter(a => a.id !== appointmentId));
      setShowDetailModal(false);
      setSelectedAppointment(null);
      alert('Appointment deleted permanently');
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert('Failed to delete appointment');
    }
  };

  // Check for scheduling conflicts
  const findConflicts = (appointment) => {
    const conflicts = [];
    const aptDate = appointment.date;
    const aptTime = appointment.time?.replace(/[^0-9:]/g, '') || '00:00';
    
    appointments.forEach(other => {
      if (other.id === appointment.id) return;
      if (other.date !== aptDate) return;
      if (other.status === 'cancelled' || other.status === 'rejected') return;
      
      const otherTime = other.time?.replace(/[^0-9:]/g, '') || '00:00';
      // Simple overlap check (within 2 hours)
      const aptHour = parseInt(aptTime.split(':')[0]);
      const otherHour = parseInt(otherTime.split(':')[0]);
      if (Math.abs(aptHour - otherHour) < 2) {
        conflicts.push(other);
      }
    });
    
    return conflicts;
  };

  // Suggest alternative times based on business hours
  const suggestAlternativeTimes = (appointment) => {
    if (!businessHours) return [];
    
    const suggestions = [];
    const aptDate = new Date(appointment.date + 'T12:00:00');
    const dayOfWeek = aptDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const dayHours = businessHours[dayOfWeek];
    
    if (!dayHours || dayHours.closed) {
      // Find next open day
      for (let i = 1; i <= 7; i++) {
        const nextDate = new Date(aptDate);
        nextDate.setDate(nextDate.getDate() + i);
        const nextDay = nextDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        const nextHours = businessHours[nextDay];
        if (nextHours && !nextHours.closed) {
          suggestions.push({
            date: nextDate.toISOString().split('T')[0],
            time: nextHours.open,
            reason: `${nextDate.toLocaleDateString('en-US', { weekday: 'long' })} is open`
          });
          break;
        }
      }
    } else {
      // Suggest different times on same day
      const busyTimes = appointments
        .filter(a => a.date === appointment.date && a.id !== appointment.id && a.status !== 'cancelled')
        .map(a => parseInt((a.time || '00:00').split(':')[0]));
      
      const openHour = parseInt(dayHours.open?.split(':')[0] || '8');
      const closeHour = parseInt(dayHours.close?.split(':')[0] || '17');
      
      for (let hour = openHour; hour < closeHour; hour++) {
        if (!busyTimes.some(t => Math.abs(t - hour) < 2)) {
          suggestions.push({
            date: appointment.date,
            time: `${hour}:00 ${hour < 12 ? 'AM' : 'PM'}`,
            reason: 'Available slot'
          });
          if (suggestions.length >= 3) break;
        }
      }
    }
    
    return suggestions;
  };

  const handleApprove = async (appointment) => {
    setSendingNotification(true);
    try {
      // Update appointment with confirmed status and attached manuals
      await updateDoc(doc(db, 'events', appointment.id), {
        status: 'confirmed',
        approvedAt: new Date().toISOString(),
        attachedManuals: selectedManuals.map(m => m.id),
      });
      
      // Move client from potential to "client" type if they were potential
      if (appointment.clientId) {
        try {
          const clientRef = doc(db, 'clients', appointment.clientId);
          const clientSnap = await getDoc(clientRef);
          if (clientSnap.exists()) {
            const clientData = clientSnap.data();
            // Only upgrade if they're a potential client
            if (clientData.type === 'potential' || clientData.type === 'new') {
              await updateDoc(clientRef, {
                type: 'client',
                convertedAt: new Date().toISOString(),
                updatedAt: serverTimestamp(),
              });
            }
          }
        } catch (clientError) {
          console.warn('Could not update client type:', clientError);
        }
      }
      
      // Send approval notification email
      const clientEmail = appointment.email || appointment.clientEmail;
      if (clientEmail) {
        try {
          const sendApprovalEmail = httpsCallable(functions, 'sendApprovalEmail');
          await sendApprovalEmail({
            clientEmail,
            clientName: appointment.clientName,
            appointmentDate: appointment.date,
            appointmentTime: appointment.time,
            service: appointment.service,
          });
        } catch (emailError) {
          console.warn('Could not send approval email:', emailError);
        }
      }
      
      loadData();
      setShowConfirmModal(false);
      setSelectedManuals([]);
      setSuggestedManuals([]);
    } catch (error) {
      console.error('Error approving appointment:', error);
      alert('Failed to approve appointment. Please try again.');
    }
    setSendingNotification(false);
  };

  const handleReject = async (appointment, mode = 'outright', message = '') => {
    setSendingNotification(true);
    try {
      const isRevision = mode === 'revision';
      
      await updateDoc(doc(db, 'events', appointment.id), {
        status: isRevision ? 'pending' : 'rejected',
        rejectedAt: isRevision ? null : new Date().toISOString(),
        revisionRequested: isRevision,
        revisionMessage: isRevision ? message : null,
        isRevised: isRevision,
      });
      
      // Send rejection/revision email
      const clientEmail = appointment.email || appointment.clientEmail;
      if (clientEmail) {
        try {
          const sendRejectionEmail = httpsCallable(functions, 'sendRejectionEmail');
          await sendRejectionEmail({
            clientEmail,
            clientName: appointment.clientName,
            appointmentDate: appointment.date,
            service: appointment.service,
            isRevision,
            rejectionMessage: message || 'We were unable to accommodate this request at this time.',
          });
        } catch (emailError) {
          console.warn('Could not send rejection email:', emailError);
        }
      }
      
      loadData();
      setShowConfirmModal(false);
      setRejectionMessage('');
      setRejectionMode('outright');
    } catch (error) {
      console.error('Error rejecting appointment:', error);
      alert('Failed to reject appointment. Please try again.');
    }
    setSendingNotification(false);
  };

  const handleComplete = async (appointment) => {
    try {
      await updateDoc(doc(db, 'events', appointment.id), {
        status: 'completed',
        completedAt: new Date().toISOString()
      });
      loadData();
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Error completing appointment:', error);
    }
  };

  const handleStartJob = async (appointment) => {
    try {
      await updateDoc(doc(db, 'events', appointment.id), {
        status: 'in-progress',
        startedAt: new Date().toISOString()
      });
      loadData();
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Error starting job:', error);
    }
  };

  // Start editing an appointment
  const startEditing = (appointment) => {
    setEditedAppointment({
      date: appointment.date || '',
      time: appointment.time || '',
      clientName: appointment.clientName || '',
      phone: appointment.phone || appointment.clientPhone || '',
      email: appointment.email || appointment.clientEmail || '',
      service: appointment.service || '',
      address: appointment.address || '',
      description: appointment.description || '',
      notes: appointment.notes || '',
      estimate: typeof appointment.estimate === 'object' 
        ? appointment.estimate.lowEstimate?.toString() || ''
        : appointment.estimate?.toString() || '',
      estimateHigh: typeof appointment.estimate === 'object'
        ? appointment.estimate.highEstimate?.toString() || ''
        : '',
    });
    setIsEditing(true);
  };

  // Save edited appointment
  const saveAppointmentEdit = async () => {
    if (!selectedAppointment || !editedAppointment) return;
    
    setSavingEdit(true);
    try {
      const updateData = {
        date: editedAppointment.date,
        time: editedAppointment.time,
        clientName: editedAppointment.clientName,
        phone: editedAppointment.phone,
        clientPhone: editedAppointment.phone,
        email: editedAppointment.email,
        clientEmail: editedAppointment.email,
        service: editedAppointment.service,
        address: editedAppointment.address,
        description: editedAppointment.description,
        notes: editedAppointment.notes,
        updatedAt: serverTimestamp(),
      };
      
      // Handle estimate (can be single value or range)
      if (editedAppointment.estimate) {
        if (editedAppointment.estimateHigh) {
          updateData.estimate = {
            lowEstimate: parseFloat(editedAppointment.estimate) || 0,
            highEstimate: parseFloat(editedAppointment.estimateHigh) || 0,
          };
        } else {
          updateData.estimate = parseFloat(editedAppointment.estimate) || 0;
        }
      }
      
      await updateDoc(doc(db, 'events', selectedAppointment.id), updateData);
      
      // Update the selected appointment with new data
      const updatedAppointment = { ...selectedAppointment, ...updateData };
      setSelectedAppointment(updatedAppointment);
      
      setIsEditing(false);
      setEditedAppointment(null);
      loadData();
    } catch (error) {
      console.error('Error saving appointment:', error);
      alert('Failed to save changes. Please try again.');
    }
    setSavingEdit(false);
  };

  // Cancel editing
  const cancelEditing = () => {
    setIsEditing(false);
    setEditedAppointment(null);
  };

  // Revert confirmed appointment to pending
  const revertToPending = async (appointment) => {
    if (!confirm(`Are you sure you want to revert "${appointment.clientName || 'this appointment'}" back to pending status?`)) return;
    
    try {
      await updateDoc(doc(db, 'events', appointment.id), {
        status: 'pending',
        approvedAt: null,
        revertedAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      });
      
      // Update the selected appointment
      setSelectedAppointment({ ...appointment, status: 'pending', approvedAt: null });
      loadData();
    } catch (error) {
      console.error('Error reverting appointment:', error);
      alert('Failed to revert appointment. Please try again.');
    }
  };

  const openConfirmModal = (type, appointment) => {
    setConfirmAction({ type, appointment });
    setShowConfirmModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No date';
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric'
    });
  };

  const getClient = (clientId) => clients.find(c => c.id === clientId);

  // Get next upcoming job
  const getNextJob = () => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    
    // Filter to confirmed/approved future appointments
    const upcoming = appointments.filter(apt => {
      if (apt.status !== 'confirmed' && apt.status !== 'approved') return false;
      if (apt.date < todayStr) return false;
      if (apt.date === todayStr) {
        const aptTime = apt.time?.replace(/[^0-9:]/g, '') || '00:00';
        const [aptHour, aptMin] = aptTime.split(':').map(Number);
        if (aptHour < currentHour || (aptHour === currentHour && aptMin <= currentMinutes)) {
          return false;
        }
      }
      return true;
    });
    
    // Sort by date and time
    upcoming.sort((a, b) => {
      const dateComp = (a.date || '').localeCompare(b.date || '');
      if (dateComp !== 0) return dateComp;
      const timeA = a.time?.replace(/[^0-9:]/g, '') || '00:00';
      const timeB = b.time?.replace(/[^0-9:]/g, '') || '00:00';
      return timeA.localeCompare(timeB);
    });
    
    return upcoming[0] || null;
  };

  const navigateToNextJob = () => {
    const nextJob = getNextJob();
    if (nextJob) {
      const client = getClient(nextJob.clientId);
      const address = client?.address || nextJob.address;
      if (address) {
        window.open(
          `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`,
          '_blank'
        );
      } else {
        alert(`Next job: ${nextJob.clientName || client?.name || 'Client'} at ${nextJob.time || 'TBD'}\nNo address on file for navigation.`);
      }
    } else {
      alert('No upcoming jobs scheduled.');
    }
  };

  const nextJob = getNextJob();

  if (loading) {
    return (
      <View style={styles.tabContent}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.teal} />
          <Text style={styles.loadingText}>Loading appointments...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.tabContent}>
      <View style={styles.tabHeader}>
        <View>
          <Text style={styles.tabTitle}>Appointments</Text>
          <Text style={styles.tabSubtitle}>Manage jobs, approve requests, and track completion</Text>
        </View>
        <View style={styles.tabHeaderButtons}>
          {nextJob && (
            <TouchableOpacity 
              style={styles.nextJobBtn}
              onPress={navigateToNextJob}
              title="Next Job"
            >
              <Icon name="navigation" size={16} color={colors.darkNavy} style={isWeb && width > 900 ? { marginRight: 8 } : {}} />
              {isWeb && width > 900 && <Text style={styles.nextJobBtnText}>Next Job</Text>}
            </TouchableOpacity>
          )}
          <TouchableOpacity style={styles.primaryBtn} onPress={loadData}>
            <Icon name="refresh-cw" size={16} color={colors.darkNavy} style={isWeb && width > 900 ? { marginRight: 8 } : {}} />
            {isWeb && width > 900 && <Text style={styles.primaryBtnText}>Refresh</Text>}
          </TouchableOpacity>
        </View>
      </View>

      {/* Compact Status Tabs */}
      <View style={styles.aptStatusTabs}>
        {[
          { key: 'pending', label: 'Pending', count: pendingAppointments.length, color: colors.gold },
          { key: 'upcoming', label: 'Upcoming', count: upcomingAppointments.length, color: colors.teal },
          { key: 'in-progress', label: 'In Progress', count: inProgressAppointments.length, color: colors.blue },
          { key: 'completed', label: 'Completed', count: completedAppointments.length, color: colors.green },
          { key: 'cancelled', label: 'Cancelled', count: cancelledAppointments.length, color: colors.red },
        ].map(tab => (
          <TouchableOpacity 
            key={tab.key}
            style={[styles.aptStatusTab, activeColumn === tab.key && styles.aptStatusTabActive]}
            onPress={() => setActiveColumn(tab.key)}
          >
            <Text style={[styles.aptStatusTabCount, { color: tab.color }]}>{tab.count}</Text>
            <Text style={[styles.aptStatusTabLabel, activeColumn === tab.key && styles.aptStatusTabLabelActive]}>
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Appointments Grid - Compact Cards */}
      {getActiveAppointments().length === 0 ? (
        <View style={styles.aptEmptyState}>
          <View style={styles.aptEmptyIcon}>
            <Icon name="inbox" size={32} color={colors.gray} />
          </View>
          <Text style={styles.aptEmptyTitle}>No {activeColumn === 'in-progress' ? 'in progress' : activeColumn} appointments</Text>
          <Text style={styles.aptEmptyText}>
            {activeColumn === 'pending' ? 'New requests will appear here' :
             activeColumn === 'upcoming' ? 'Approved jobs will show here' :
             activeColumn === 'in-progress' ? 'Jobs you\'re working on show here' :
             activeColumn === 'completed' ? 'Completed jobs appear here' :
             'Rejected jobs appear here'}
          </Text>
        </View>
      ) : (
        <View style={styles.aptGrid}>
          {getActiveAppointments().map(apt => {
            const conflicts = activeColumn === 'pending' ? findConflicts(apt) : [];
            const isRevised = apt.isRevised;
            
            return (
              <TouchableOpacity 
                key={apt.id} 
                style={styles.aptCard}
                onPress={() => { setSelectedAppointment(apt); setShowDetailModal(true); }}
                activeOpacity={0.7}
              >
                {/* Top Row: Date & Status */}
                <View style={styles.aptCardTop}>
                  <View style={styles.aptCardDate}>
                    <Text style={styles.aptCardDateDay}>
                      {apt.date ? new Date(apt.date + 'T12:00:00').getDate() : '?'}
                    </Text>
                    <Text style={styles.aptCardDateMonth}>
                      {apt.date ? new Date(apt.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short' }) : ''}
                    </Text>
                  </View>
                  <View style={styles.aptCardTopRight}>
                    {isRevised && (
                      <View style={styles.aptRevisedBadge}>
                        <Text style={styles.aptRevisedBadgeText}>Revised</Text>
                      </View>
                    )}
                    {conflicts.length > 0 && (
                      <View style={styles.aptConflictBadge}>
                        <Icon name="alert-triangle" size={10} color={colors.gold} />
                      </View>
                    )}
                    <View style={[styles.aptStatusBadge, {
                      backgroundColor: apt.status === 'pending' || !apt.status ? colors.gold + '20' :
                                       apt.status === 'confirmed' ? colors.teal + '20' :
                                       apt.status === 'completed' ? colors.green + '20' : colors.red + '20'
                    }]}>
                      <View style={[styles.aptStatusDot, {
                        backgroundColor: apt.status === 'pending' || !apt.status ? colors.gold :
                                         apt.status === 'confirmed' ? colors.teal :
                                         apt.status === 'completed' ? colors.green : colors.red
                      }]} />
                    </View>
                  </View>
                </View>
                
                {/* Time */}
                {apt.time && (
                  <Text style={styles.aptCardTime}>{apt.time}</Text>
                )}
                
                {/* Client Name */}
                <Text style={styles.aptCardClient} numberOfLines={1}>
                  {apt.clientName || 'Unknown Client'}
                </Text>
                
                {/* Service Type */}
                <Text style={styles.aptCardService} numberOfLines={1}>
                  {apt.service || 'Service'}
                </Text>
                
                {/* Description Preview */}
                {apt.description && (
                  <Text style={styles.aptCardDesc} numberOfLines={2}>
                    {apt.description}
                  </Text>
                )}
                
                {/* Estimate */}
                {apt.estimate && (
                  <View style={styles.aptCardEstimate}>
                    <Text style={styles.aptCardEstimateText}>
                      {typeof apt.estimate === 'object' 
                        ? `$${apt.estimate.lowEstimate || 0} - $${apt.estimate.highEstimate || 0}`
                        : `$${apt.estimate}`}
                    </Text>
                  </View>
                )}
                
                {/* Quick Contact Icons */}
                <View style={styles.aptCardQuickActions}>
                  {(apt.phone || apt.clientPhone) && (
                    <TouchableOpacity 
                      style={styles.aptCardQuickBtn}
                      onPress={(e) => { e.stopPropagation(); window.open(`tel:${apt.phone || apt.clientPhone}`, '_self'); }}
                    >
                      <Icon name="phone" size={14} color={colors.teal} />
                    </TouchableOpacity>
                  )}
                  {(apt.email || apt.clientEmail) && (
                    <TouchableOpacity 
                      style={styles.aptCardQuickBtn}
                      onPress={(e) => { e.stopPropagation(); window.open(`mailto:${apt.email || apt.clientEmail}`, '_self'); }}
                    >
                      <Icon name="mail" size={14} color={colors.teal} />
                    </TouchableOpacity>
                  )}
                </View>
                
                {/* Action Buttons */}
                <View style={styles.aptCardActions}>
                  {activeColumn === 'pending' && (
                    <>
                      <TouchableOpacity 
                        style={styles.aptApproveBtn}
                        onPress={(e) => { e.stopPropagation(); openConfirmModal('approve', apt); }}
                      >
                        <Icon name="check" size={14} color={colors.white} />
                      </TouchableOpacity>
                      <TouchableOpacity 
                        style={styles.aptRejectBtn}
                        onPress={(e) => { e.stopPropagation(); openConfirmModal('reject', apt); }}
                      >
                        <Icon name="x" size={14} color={colors.white} />
                      </TouchableOpacity>
                    </>
                  )}
                  {activeColumn === 'upcoming' && (
                    <>
                      <TouchableOpacity 
                        style={styles.aptStartBtn}
                        onPress={(e) => { e.stopPropagation(); openConfirmModal('start', apt); }}
                      >
                        <Icon name="play" size={14} color={colors.white} />
                        <Text style={styles.aptStartBtnText}>Start</Text>
                      </TouchableOpacity>
                      <TouchableOpacity 
                        style={styles.aptCompleteBtn}
                        onPress={(e) => { e.stopPropagation(); openConfirmModal('complete', apt); }}
                      >
                        <Icon name="check-circle" size={14} color={colors.white} />
                        <Text style={styles.aptCompleteBtnText}>Done</Text>
                      </TouchableOpacity>
                    </>
                  )}
                  {activeColumn === 'in-progress' && (
                    <TouchableOpacity 
                      style={styles.aptCompleteBtn}
                      onPress={(e) => { e.stopPropagation(); openConfirmModal('complete', apt); }}
                    >
                      <Icon name="check-circle" size={14} color={colors.white} />
                      <Text style={styles.aptCompleteBtnText}>Complete</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      {/* Confirmation Modal - Enhanced with Rejection Modes and Manual Attachment */}
      <Modal visible={showConfirmModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.aptConfirmModal}>
            <View style={styles.aptConfirmHeader}>
              <View style={[styles.aptConfirmIcon, {
                backgroundColor: confirmAction?.type === 'approve' ? colors.green + '20' :
                                 confirmAction?.type === 'reject' ? colors.red + '20' :
                                 confirmAction?.type === 'start' ? colors.blue + '20' : colors.teal + '20'
              }]}>
                <Icon 
                  name={confirmAction?.type === 'approve' ? 'check-circle' :
                        confirmAction?.type === 'reject' ? 'x-circle' :
                        confirmAction?.type === 'start' ? 'play-circle' : 'flag'}
                  size={24}
                  color={confirmAction?.type === 'approve' ? colors.green :
                         confirmAction?.type === 'reject' ? colors.red :
                         confirmAction?.type === 'start' ? colors.blue : colors.teal}
                />
              </View>
              <Text style={styles.aptConfirmTitle}>
                {confirmAction?.type === 'approve' ? 'Approve Job' :
                 confirmAction?.type === 'reject' ? 'Handle Rejection' :
                 confirmAction?.type === 'start' ? 'Start Job' : 'Complete Job'}
              </Text>
            </View>
            
            {/* Job Summary */}
            <View style={styles.aptConfirmSummary}>
              <Text style={styles.aptConfirmClient}>{confirmAction?.appointment?.clientName}</Text>
              <Text style={styles.aptConfirmService}>{confirmAction?.appointment?.service}</Text>
              <Text style={styles.aptConfirmDate}>
                {formatDate(confirmAction?.appointment?.date)} at {confirmAction?.appointment?.time || 'TBD'}
              </Text>
            </View>
            
            {/* Approval: Manual Selection */}
            {confirmAction?.type === 'approve' && (
              <View style={styles.aptManualSection}>
                <Text style={styles.aptManualTitle}>
                  <Icon name="book" size={14} color={colors.gold} /> Attach Field Manuals
                </Text>
                
                {/* Selected Manuals */}
                {selectedManuals.length > 0 && (
                  <View style={styles.aptSelectedManuals}>
                    {selectedManuals.map(manual => (
                      <View key={manual.id} style={styles.aptSelectedManualTag}>
                        <Icon name="file-text" size={12} color={colors.teal} />
                        <Text style={styles.aptSelectedManualText} numberOfLines={1}>{manual.title}</Text>
                        <TouchableOpacity onPress={() => setSelectedManuals(selectedManuals.filter(m => m.id !== manual.id))}>
                          <Icon name="x" size={14} color={colors.gray} />
                        </TouchableOpacity>
                      </View>
                    ))}
                  </View>
                )}
                
                {/* Action buttons */}
                <View style={styles.aptManualActions}>
                  <TouchableOpacity 
                    style={styles.aptSuggestBtn}
                    onPress={() => suggestManualsForJob(confirmAction.appointment)}
                  >
                    <Icon name="zap" size={14} color={colors.gold} />
                    <Text style={styles.aptSuggestBtnText}>AI Suggest</Text>
                  </TouchableOpacity>
                </View>
                
                {/* AI Suggested Manuals */}
                {suggestedManuals.length > 0 && (
                  <View style={styles.aptSuggestedSection}>
                    <Text style={styles.aptSuggestedLabel}>Suggested Based on Job</Text>
                    <View style={styles.aptSuggestedList}>
                      {suggestedManuals.map(manual => (
                        <TouchableOpacity 
                          key={manual.id}
                          style={[styles.aptSuggestedItem, selectedManuals.some(m => m.id === manual.id) && styles.aptSuggestedItemSelected]}
                          onPress={() => {
                            if (selectedManuals.some(m => m.id === manual.id)) {
                              setSelectedManuals(selectedManuals.filter(m => m.id !== manual.id));
                            } else {
                              setSelectedManuals([...selectedManuals, manual]);
                            }
                          }}
                        >
                          <Icon 
                            name={selectedManuals.some(m => m.id === manual.id) ? 'check-square' : 'square'} 
                            size={14} 
                            color={colors.teal} 
                          />
                          <Text style={styles.aptSuggestedItemText}>{manual.title}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                )}
                
                {/* All Field Manuals Browser */}
                <View style={styles.aptAllManualsSection}>
                  <Text style={styles.aptSuggestedLabel}>All Field Manuals</Text>
                  <ScrollView style={styles.aptAllManualsList} nestedScrollEnabled showsVerticalScrollIndicator={false}>
                    {fieldManuals.length === 0 ? (
                      <Text style={styles.aptNoManualsText}>No field manuals created yet</Text>
                    ) : (
                      fieldManuals.map(manual => (
                        <TouchableOpacity 
                          key={manual.id}
                          style={[styles.aptManualBrowseItem, selectedManuals.some(m => m.id === manual.id) && styles.aptManualBrowseItemSelected]}
                          onPress={() => {
                            if (selectedManuals.some(m => m.id === manual.id)) {
                              setSelectedManuals(selectedManuals.filter(m => m.id !== manual.id));
                            } else {
                              setSelectedManuals([...selectedManuals, manual]);
                            }
                          }}
                        >
                          <View style={styles.aptManualBrowseCheck}>
                            <Icon 
                              name={selectedManuals.some(m => m.id === manual.id) ? 'check-circle' : 'circle'} 
                              size={18} 
                              color={selectedManuals.some(m => m.id === manual.id) ? colors.teal : colors.gray} 
                            />
                          </View>
                          <View style={styles.aptManualBrowseInfo}>
                            <Text style={styles.aptManualBrowseTitle}>{manual.title}</Text>
                            {manual.steps?.length > 0 && (
                              <Text style={styles.aptManualBrowseMeta}>{manual.steps.length} steps</Text>
                            )}
                          </View>
                        </TouchableOpacity>
                      ))
                    )}
                  </ScrollView>
                </View>
                
                <Text style={styles.aptManualNote}>
                  {selectedManuals.length > 0 
                    ? `${selectedManuals.length} manual${selectedManuals.length > 1 ? 's' : ''} will be attached to this job`
                    : 'No manuals attached - you can add them later'}
                </Text>
              </View>
            )}
            
            {/* Rejection: Mode Selection */}
            {confirmAction?.type === 'reject' && (
              <View style={styles.aptRejectSection}>
                <Text style={styles.aptRejectModeTitle}>Rejection Type</Text>
                <View style={styles.aptRejectModes}>
                  <TouchableOpacity 
                    style={[styles.aptRejectMode, rejectionMode === 'outright' && styles.aptRejectModeActive]}
                    onPress={() => setRejectionMode('outright')}
                  >
                    <Icon name="x-circle" size={16} color={rejectionMode === 'outright' ? colors.red : colors.gray} />
                    <Text style={[styles.aptRejectModeText, rejectionMode === 'outright' && { color: colors.red }]}>
                      Reject Outright
                    </Text>
                    <Text style={styles.aptRejectModeDesc}>Decline permanently</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={[styles.aptRejectMode, rejectionMode === 'revision' && styles.aptRejectModeActive]}
                    onPress={() => setRejectionMode('revision')}
                  >
                    <Icon name="edit-2" size={16} color={rejectionMode === 'revision' ? colors.gold : colors.gray} />
                    <Text style={[styles.aptRejectModeText, rejectionMode === 'revision' && { color: colors.gold }]}>
                      Request Revision
                    </Text>
                    <Text style={styles.aptRejectModeDesc}>Send back for changes</Text>
                  </TouchableOpacity>
                </View>
                
                <Text style={styles.aptRejectMsgLabel}>Message to Client</Text>
                <TextInput
                  style={styles.aptRejectMsgInput}
                  placeholder={rejectionMode === 'outright' 
                    ? "Sorry, we cannot accommodate this request at this time..." 
                    : "Please update your request with the following changes..."}
                  placeholderTextColor={colors.gray}
                  multiline
                  numberOfLines={3}
                  value={rejectionMessage}
                  onChangeText={setRejectionMessage}
                />
              </View>
            )}
            
            {/* Actions */}
            <View style={styles.aptConfirmActions}>
              <TouchableOpacity 
                style={styles.aptConfirmCancel}
                onPress={() => {
                  setShowConfirmModal(false);
                  setRejectionMessage('');
                  setRejectionMode('outright');
                  setSuggestedManuals([]);
                  setSelectedManuals([]);
                }}
              >
                <Text style={styles.aptConfirmCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.aptConfirmSubmit, {
                  backgroundColor: confirmAction?.type === 'approve' ? colors.green :
                                   confirmAction?.type === 'reject' && rejectionMode === 'outright' ? colors.red :
                                   confirmAction?.type === 'reject' && rejectionMode === 'revision' ? colors.gold : colors.teal
                }]}
                onPress={() => {
                  if (confirmAction?.type === 'approve') handleApprove(confirmAction.appointment);
                  else if (confirmAction?.type === 'reject') handleReject(confirmAction.appointment, rejectionMode, rejectionMessage);
                  else if (confirmAction?.type === 'start') handleStartJob(confirmAction.appointment);
                  else if (confirmAction?.type === 'complete') handleComplete(confirmAction.appointment);
                }}
                disabled={sendingNotification}
              >
                {sendingNotification ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <>
                    <Icon 
                      name={confirmAction?.type === 'approve' ? 'check' :
                            confirmAction?.type === 'reject' && rejectionMode === 'outright' ? 'x' :
                            confirmAction?.type === 'reject' && rejectionMode === 'revision' ? 'send' : 'flag'}
                      size={14} 
                      color={colors.white} 
                    />
                    <Text style={styles.aptConfirmSubmitText}>
                      {confirmAction?.type === 'approve' ? 'Approve & Notify' :
                       confirmAction?.type === 'reject' && rejectionMode === 'outright' ? 'Reject' :
                       confirmAction?.type === 'reject' && rejectionMode === 'revision' ? 'Send Revision Request' : 'Complete'}
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Detail Modal - Full-Featured */}
      <Modal visible={showDetailModal} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.aptDetailModal}>
            {/* Header */}
            <View style={styles.aptDetailHeader}>
              <View style={styles.aptDetailHeaderLeft}>
                <Text style={styles.aptDetailTitle}>{isEditing ? 'Edit Appointment' : 'Job Details'}</Text>
                {selectedAppointment?.isRevised && !isEditing && (
                  <View style={styles.aptDetailRevisedTag}>
                    <Icon name="edit-2" size={10} color={colors.gold} />
                    <Text style={styles.aptDetailRevisedText}>Revised</Text>
                  </View>
                )}
              </View>
              <View style={styles.aptDetailHeaderRight}>
                {isEditing ? (
                  <>
                    <TouchableOpacity style={styles.aptDetailCancelBtn} onPress={cancelEditing}>
                      <Text style={styles.aptDetailCancelBtnText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity 
                      style={[styles.aptDetailSaveBtn, savingEdit && { opacity: 0.6 }]} 
                      onPress={saveAppointmentEdit}
                      disabled={savingEdit}
                    >
                      {savingEdit ? (
                        <ActivityIndicator size="small" color={colors.darkNavy} />
                      ) : (
                        <Text style={styles.aptDetailSaveBtnText}>Save</Text>
                      )}
                    </TouchableOpacity>
                  </>
                ) : (
                  <TouchableOpacity style={styles.aptDetailClose} onPress={() => { setShowDetailModal(false); setIsEditing(false); }}>
                <Icon name="x" size={20} color={colors.white} />
              </TouchableOpacity>
                )}
              </View>
            </View>
            
            {selectedAppointment && (
              <ScrollView style={styles.aptDetailContent} showsVerticalScrollIndicator={false}>
                {isEditing ? (
                  /* ==================== EDIT MODE ==================== */
                  <View style={styles.aptEditForm}>
                    {/* Date & Time Row */}
                    <View style={styles.aptEditRow}>
                      <View style={[styles.aptEditField, { flex: 1 }]}>
                        <Text style={styles.aptEditLabel}>Date *</Text>
                        <TextInput
                          style={styles.aptEditInput}
                          value={editedAppointment?.date || ''}
                          onChangeText={(val) => setEditedAppointment({ ...editedAppointment, date: val })}
                          placeholder="YYYY-MM-DD"
                          placeholderTextColor={colors.gray}
                        />
                      </View>
                      <View style={[styles.aptEditField, { flex: 1 }]}>
                        <Text style={styles.aptEditLabel}>Time *</Text>
                        <TextInput
                          style={styles.aptEditInput}
                          value={editedAppointment?.time || ''}
                          onChangeText={(val) => setEditedAppointment({ ...editedAppointment, time: val })}
                          placeholder="9:00 AM"
                          placeholderTextColor={colors.gray}
                        />
                      </View>
                    </View>
                    
                    {/* Client Name */}
                    <View style={styles.aptEditField}>
                      <Text style={styles.aptEditLabel}>Client Name *</Text>
                      <TextInput
                        style={styles.aptEditInput}
                        value={editedAppointment?.clientName || ''}
                        onChangeText={(val) => setEditedAppointment({ ...editedAppointment, clientName: val })}
                        placeholder="Client name"
                        placeholderTextColor={colors.gray}
                      />
                    </View>
                    
                    {/* Phone & Email Row */}
                    <View style={styles.aptEditRow}>
                      <View style={[styles.aptEditField, { flex: 1 }]}>
                        <Text style={styles.aptEditLabel}>Phone</Text>
                        <TextInput
                          style={styles.aptEditInput}
                          value={editedAppointment?.phone || ''}
                          onChangeText={(val) => setEditedAppointment({ ...editedAppointment, phone: val })}
                          placeholder="(702) 555-1234"
                          placeholderTextColor={colors.gray}
                          keyboardType="phone-pad"
                        />
                      </View>
                      <View style={[styles.aptEditField, { flex: 1 }]}>
                        <Text style={styles.aptEditLabel}>Email</Text>
                        <TextInput
                          style={styles.aptEditInput}
                          value={editedAppointment?.email || ''}
                          onChangeText={(val) => setEditedAppointment({ ...editedAppointment, email: val })}
                          placeholder="email@example.com"
                          placeholderTextColor={colors.gray}
                          keyboardType="email-address"
                        />
                      </View>
                    </View>
                    
                    {/* Service */}
                    <View style={styles.aptEditField}>
                      <Text style={styles.aptEditLabel}>Service *</Text>
                      <TextInput
                        style={styles.aptEditInput}
                        value={editedAppointment?.service || ''}
                        onChangeText={(val) => setEditedAppointment({ ...editedAppointment, service: val })}
                        placeholder="What service is needed?"
                        placeholderTextColor={colors.gray}
                      />
                    </View>
                    
                    {/* Address */}
                    <View style={styles.aptEditField}>
                      <Text style={styles.aptEditLabel}>Address</Text>
                      <TextInput
                        style={styles.aptEditInput}
                        value={editedAppointment?.address || ''}
                        onChangeText={(val) => setEditedAppointment({ ...editedAppointment, address: val })}
                        placeholder="123 Main St, Las Vegas, NV"
                        placeholderTextColor={colors.gray}
                      />
                    </View>
                    
                    {/* Description */}
                    <View style={styles.aptEditField}>
                      <Text style={styles.aptEditLabel}>Description</Text>
                      <TextInput
                        style={[styles.aptEditInput, styles.aptEditTextarea]}
                        value={editedAppointment?.description || ''}
                        onChangeText={(val) => setEditedAppointment({ ...editedAppointment, description: val })}
                        placeholder="Detailed description of the job..."
                        placeholderTextColor={colors.gray}
                        multiline
                        numberOfLines={3}
                      />
                    </View>
                    
                    {/* Estimate Row */}
                    <View style={styles.aptEditRow}>
                      <View style={[styles.aptEditField, { flex: 1 }]}>
                        <Text style={styles.aptEditLabel}>Estimate ($)</Text>
                        <TextInput
                          style={styles.aptEditInput}
                          value={editedAppointment?.estimate || ''}
                          onChangeText={(val) => setEditedAppointment({ ...editedAppointment, estimate: val })}
                          placeholder="Min or single price"
                          placeholderTextColor={colors.gray}
                          keyboardType="decimal-pad"
                        />
                      </View>
                      <View style={[styles.aptEditField, { flex: 1 }]}>
                        <Text style={styles.aptEditLabel}>High Estimate ($)</Text>
                        <TextInput
                          style={styles.aptEditInput}
                          value={editedAppointment?.estimateHigh || ''}
                          onChangeText={(val) => setEditedAppointment({ ...editedAppointment, estimateHigh: val })}
                          placeholder="Max (optional)"
                          placeholderTextColor={colors.gray}
                          keyboardType="decimal-pad"
                        />
                      </View>
                    </View>
                    
                    {/* Notes */}
                    <View style={styles.aptEditField}>
                      <Text style={styles.aptEditLabel}>Internal Notes</Text>
                      <TextInput
                        style={[styles.aptEditInput, styles.aptEditTextarea]}
                        value={editedAppointment?.notes || ''}
                        onChangeText={(val) => setEditedAppointment({ ...editedAppointment, notes: val })}
                        placeholder="Internal notes (not visible to client)..."
                        placeholderTextColor={colors.gray}
                        multiline
                        numberOfLines={2}
                      />
                    </View>
                  </View>
                ) : (
                  /* ==================== VIEW MODE ==================== */
                  <>
                {/* Status Banner */}
                <View style={[styles.aptDetailStatusBanner, {
                  backgroundColor: selectedAppointment.status === 'pending' || !selectedAppointment.status ? colors.gold + '15' :
                                   selectedAppointment.status === 'confirmed' ? colors.teal + '15' :
                                   selectedAppointment.status === 'completed' ? colors.green + '15' : colors.red + '15',
                  borderLeftColor: selectedAppointment.status === 'pending' || !selectedAppointment.status ? colors.gold :
                                   selectedAppointment.status === 'confirmed' ? colors.teal :
                                   selectedAppointment.status === 'completed' ? colors.green : colors.red
                }]}>
                  <View style={[styles.aptDetailStatusDot, {
                    backgroundColor: selectedAppointment.status === 'pending' || !selectedAppointment.status ? colors.gold :
                                     selectedAppointment.status === 'confirmed' ? colors.teal :
                                     selectedAppointment.status === 'completed' ? colors.green : colors.red
                  }]} />
                  <Text style={styles.aptDetailStatusText}>
                    {(selectedAppointment.status || 'Pending').charAt(0).toUpperCase() + (selectedAppointment.status || 'pending').slice(1)}
                  </Text>
                </View>
                
                {/* Date & Time Card */}
                <View style={styles.aptDetailCard}>
                  <View style={styles.aptDetailCardIcon}>
                    <Icon name="calendar" size={18} color={colors.teal} />
                  </View>
                  <View style={styles.aptDetailCardContent}>
                    <Text style={styles.aptDetailCardLabel}>Appointment</Text>
                    <Text style={styles.aptDetailCardValue}>
                      {formatDate(selectedAppointment.date)}
                    </Text>
                    <Text style={styles.aptDetailCardSub}>{selectedAppointment.time || 'Time TBD'}</Text>
                  </View>
                </View>
                
                {/* Client Card */}
                <View style={styles.aptDetailCard}>
                  <View style={styles.aptDetailCardIcon}>
                    <Icon name="user" size={18} color={colors.teal} />
                  </View>
                  <View style={styles.aptDetailCardContent}>
                    <Text style={styles.aptDetailCardLabel}>Client</Text>
                    <Text style={styles.aptDetailCardValue}>{selectedAppointment.clientName || 'Unknown'}</Text>
                    <View style={styles.aptDetailContactRow}>
                      {(selectedAppointment.phone || selectedAppointment.clientPhone) && (
                        <TouchableOpacity 
                          style={styles.aptDetailContactBtn}
                          onPress={() => window.open(`tel:${selectedAppointment.phone || selectedAppointment.clientPhone}`, '_self')}
                        >
                          <Icon name="phone" size={12} color={colors.white} />
                          <Text style={styles.aptDetailContactBtnText}>Call</Text>
                        </TouchableOpacity>
                      )}
                      {(selectedAppointment.email || selectedAppointment.clientEmail) && (
                        <TouchableOpacity 
                          style={styles.aptDetailContactBtn}
                          onPress={() => window.open(`mailto:${selectedAppointment.email || selectedAppointment.clientEmail}`, '_self')}
                        >
                          <Icon name="mail" size={12} color={colors.white} />
                          <Text style={styles.aptDetailContactBtnText}>Email</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  </View>
                </View>
                
                {/* Service Card */}
                <View style={styles.aptDetailCard}>
                  <View style={styles.aptDetailCardIcon}>
                    <Icon name="tool" size={18} color={colors.teal} />
                  </View>
                  <View style={styles.aptDetailCardContent}>
                    <Text style={styles.aptDetailCardLabel}>Service</Text>
                    <Text style={styles.aptDetailCardValue}>{selectedAppointment.service || 'General Service'}</Text>
                  </View>
                </View>
                
                {/* Description */}
                {selectedAppointment.description && (
                  <View style={styles.aptDetailSection}>
                    <Text style={styles.aptDetailSectionTitle}>
                      <Icon name="file-text" size={14} color={colors.gray} /> Description
                    </Text>
                    <Text style={styles.aptDetailDescription}>{selectedAppointment.description}</Text>
                  </View>
                )}
                
                {/* Estimate */}
                {selectedAppointment.estimate && (
                  <View style={styles.aptDetailEstimateCard}>
                    <Icon name="dollar-sign" size={16} color={colors.green} />
                    <Text style={styles.aptDetailEstimateLabel}>Estimate</Text>
                    <Text style={styles.aptDetailEstimateValue}>
                      {typeof selectedAppointment.estimate === 'object' 
                        ? `$${selectedAppointment.estimate.lowEstimate || 0} - $${selectedAppointment.estimate.highEstimate || 0}`
                        : `$${selectedAppointment.estimate}`}
                    </Text>
                  </View>
                )}
                
                {/* Location */}
                {(selectedAppointment.address || selectedAppointment.zipcode) && (
                  <View style={styles.aptDetailCard}>
                    <View style={styles.aptDetailCardIcon}>
                      <Icon name="map-pin" size={18} color={colors.teal} />
                    </View>
                    <View style={styles.aptDetailCardContent}>
                      <Text style={styles.aptDetailCardLabel}>Location</Text>
                      <Text style={styles.aptDetailCardValue}>
                        {selectedAppointment.address || `${selectedAppointment.zipcode}, Las Vegas, NV`}
                      </Text>
                      <TouchableOpacity 
                        style={styles.aptDetailMapBtn}
                        onPress={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(selectedAppointment.address || selectedAppointment.zipcode + ', Las Vegas, NV')}`, '_blank')}
                      >
                        <Icon name="navigation" size={12} color={colors.teal} />
                        <Text style={styles.aptDetailMapBtnText}>Open in Maps</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
                
                {/* Attached Manuals */}
                {selectedAppointment.attachedManuals?.length > 0 && (
                  <View style={styles.aptDetailSection}>
                    <Text style={styles.aptDetailSectionTitle}>
                      <Icon name="book" size={14} color={colors.gold} /> Attached Field Manuals
                    </Text>
                    <View style={styles.aptDetailManualsList}>
                      {selectedAppointment.attachedManuals.map((manualId, idx) => {
                        const manual = fieldManuals.find(m => m.id === manualId);
                        return (
                          <View key={idx} style={styles.aptDetailManualItem}>
                            <Icon name="file-text" size={14} color={colors.teal} />
                            <Text style={styles.aptDetailManualText}>{manual?.title || manualId}</Text>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                )}
                
                {/* Revision Message */}
                {selectedAppointment.revisionMessage && (
                  <View style={styles.aptDetailRevisionBox}>
                    <Text style={styles.aptDetailRevisionTitle}>
                      <Icon name="message-circle" size={14} color={colors.gold} /> Revision Notes
                    </Text>
                    <Text style={styles.aptDetailRevisionText}>{selectedAppointment.revisionMessage}</Text>
                  </View>
                )}
                
                {/* Timestamps */}
                <View style={styles.aptDetailMeta}>
                  {selectedAppointment.createdAt && (
                    <Text style={styles.aptDetailMetaText}>
                      Created: {new Date(selectedAppointment.createdAt).toLocaleString()}
                    </Text>
                  )}
                  {selectedAppointment.approvedAt && (
                    <Text style={styles.aptDetailMetaText}>
                      Approved: {new Date(selectedAppointment.approvedAt).toLocaleString()}
                    </Text>
                  )}
                  {selectedAppointment.completedAt && (
                    <Text style={styles.aptDetailMetaText}>
                      Completed: {new Date(selectedAppointment.completedAt).toLocaleString()}
                    </Text>
                  )}
                </View>
                
                {/* Actions based on status */}
                <View style={styles.aptDetailActions}>
                      {/* Edit Button - always visible for pending and confirmed */}
                      {(selectedAppointment.status === 'pending' || !selectedAppointment.status || selectedAppointment.status === 'confirmed') && (
                        <TouchableOpacity 
                          style={[styles.aptDetailActionBtn, styles.aptDetailEditBtn]}
                          onPress={() => startEditing(selectedAppointment)}
                        >
                          <Icon name="edit-2" size={16} color={colors.white} />
                          <Text style={styles.aptDetailActionText}>Edit Appointment</Text>
                        </TouchableOpacity>
                      )}
                      
                  {(selectedAppointment.status === 'pending' || !selectedAppointment.status) && (
                    <>
                      <TouchableOpacity 
                        style={[styles.aptDetailActionBtn, styles.aptDetailApproveBtn]}
                        onPress={() => { setShowDetailModal(false); openConfirmModal('approve', selectedAppointment); }}
                      >
                        <Icon name="check" size={16} color={colors.white} />
                        <Text style={styles.aptDetailActionText}>Approve Job</Text>
                      </TouchableOpacity>
                      <TouchableOpacity 
                        style={[styles.aptDetailActionBtn, styles.aptDetailRejectBtn]}
                        onPress={() => { setShowDetailModal(false); openConfirmModal('reject', selectedAppointment); }}
                      >
                        <Icon name="x" size={16} color={colors.white} />
                        <Text style={styles.aptDetailActionText}>Reject / Request Revision</Text>
                      </TouchableOpacity>
                    </>
                  )}
                  {selectedAppointment.status === 'confirmed' && (
                        <>
                    <TouchableOpacity 
                      style={[styles.aptDetailActionBtn, styles.aptDetailCompleteBtn]}
                      onPress={() => { setShowDetailModal(false); openConfirmModal('complete', selectedAppointment); }}
                    >
                      <Icon name="check-circle" size={16} color={colors.white} />
                      <Text style={styles.aptDetailActionText}>Mark as Complete</Text>
                    </TouchableOpacity>
                          <TouchableOpacity 
                            style={[styles.aptDetailActionBtn, styles.aptDetailRevertBtn]}
                            onPress={() => revertToPending(selectedAppointment)}
                          >
                            <Icon name="rotate-ccw" size={16} color={colors.white} />
                            <Text style={styles.aptDetailActionText}>Revert to Pending</Text>
                          </TouchableOpacity>
                        </>
                  )}
                  {onNavigateToClient && selectedAppointment.clientId && (
                    <TouchableOpacity 
                      style={styles.aptDetailClientBtn}
                      onPress={() => { setShowDetailModal(false); onNavigateToClient(selectedAppointment.clientId); }}
                    >
                      <Icon name="external-link" size={14} color={colors.teal} />
                      <Text style={styles.aptDetailClientBtnText}>View Client Profile</Text>
                    </TouchableOpacity>
                  )}
                  
                  {/* Status Change Section */}
                  <View style={styles.aptDetailStatusSection}>
                    <Text style={styles.aptDetailStatusSectionTitle}>Change Status</Text>
                    <View style={styles.aptDetailStatusBtns}>
                      {selectedAppointment.status !== 'pending' && !selectedAppointment.status && (
                        <TouchableOpacity 
                          style={[styles.aptDetailStatusBtn, { borderColor: colors.gold }]}
                          onPress={() => openStatusModal('pending', selectedAppointment)}
                        >
                          <Icon name="clock" size={14} color={colors.gold} />
                          <Text style={[styles.aptDetailStatusBtnText, { color: colors.gold }]}>Pending</Text>
                        </TouchableOpacity>
                      )}
                      {selectedAppointment.status !== 'confirmed' && (
                        <TouchableOpacity 
                          style={[styles.aptDetailStatusBtn, { borderColor: colors.teal }]}
                          onPress={() => openStatusModal('confirmed', selectedAppointment)}
                        >
                          <Icon name="check" size={14} color={colors.teal} />
                          <Text style={[styles.aptDetailStatusBtnText, { color: colors.teal }]}>Confirmed</Text>
                        </TouchableOpacity>
                      )}
                      {selectedAppointment.status !== 'completed' && (
                        <TouchableOpacity 
                          style={[styles.aptDetailStatusBtn, { borderColor: colors.green }]}
                          onPress={() => openStatusModal('completed', selectedAppointment)}
                        >
                          <Icon name="flag" size={14} color={colors.green} />
                          <Text style={[styles.aptDetailStatusBtnText, { color: colors.green }]}>Completed</Text>
                        </TouchableOpacity>
                      )}
                      {selectedAppointment.status !== 'cancelled' && (
                        <TouchableOpacity 
                          style={[styles.aptDetailStatusBtn, { borderColor: colors.gray }]}
                          onPress={() => openStatusModal('cancelled', selectedAppointment)}
                        >
                          <Icon name="x" size={14} color={colors.gray} />
                          <Text style={[styles.aptDetailStatusBtnText, { color: colors.gray }]}>Cancelled</Text>
                    </TouchableOpacity>
                  )}
                    </View>
                  </View>
                  
                  {/* Archive & Delete Buttons */}
                  <View style={styles.aptDetailDangerSection}>
                    <TouchableOpacity 
                      style={styles.aptDetailArchiveBtn}
                      onPress={() => archiveAppointment(selectedAppointment.id)}
                    >
                      <Icon name="archive" size={14} color={colors.gold} />
                      <Text style={[styles.aptDetailArchiveBtnText, { color: colors.gold }]}>Archive</Text>
                    </TouchableOpacity>
                    <TouchableOpacity 
                      style={styles.aptDetailDeleteBtn}
                      onPress={() => openStatusModal('delete', selectedAppointment)}
                    >
                      <Icon name="trash-2" size={14} color={colors.red} />
                      <Text style={styles.aptDetailDeleteBtnText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                </View>
                  </>
                )}
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* Status Change Modal */}
      <Modal visible={showStatusModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.statusChangeModal}>
            <View style={styles.statusChangeHeader}>
              <View style={[styles.statusChangeIcon, {
                backgroundColor: statusAction?.type === 'delete' ? colors.red + '20' :
                                 statusAction?.type === 'pending' ? colors.gold + '20' :
                                 statusAction?.type === 'confirmed' ? colors.teal + '20' :
                                 statusAction?.type === 'completed' ? colors.green + '20' : colors.gray + '20'
              }]}>
                <Icon 
                  name={statusAction?.type === 'delete' ? 'trash-2' :
                        statusAction?.type === 'pending' ? 'clock' :
                        statusAction?.type === 'confirmed' ? 'check' :
                        statusAction?.type === 'completed' ? 'flag' : 'x'}
                  size={24}
                  color={statusAction?.type === 'delete' ? colors.red :
                         statusAction?.type === 'pending' ? colors.gold :
                         statusAction?.type === 'confirmed' ? colors.teal :
                         statusAction?.type === 'completed' ? colors.green : colors.gray}
                />
              </View>
              <Text style={styles.statusChangeTitle}>
                {statusAction?.type === 'delete' ? 'Delete Appointment' :
                 `Change to ${statusAction?.type?.charAt(0).toUpperCase() + statusAction?.type?.slice(1)}`}
              </Text>
            </View>
            
            {/* Appointment Summary */}
            <View style={styles.statusChangeSummary}>
              <Text style={styles.statusChangeClient}>{statusAction?.appointment?.clientName}</Text>
              <Text style={styles.statusChangeService}>{statusAction?.appointment?.service}</Text>
              <Text style={styles.statusChangeDate}>
                {formatDate(statusAction?.appointment?.date)} at {statusAction?.appointment?.time || 'TBD'}
              </Text>
            </View>
            
            {/* Warning for delete */}
            {statusAction?.type === 'delete' && (
              <View style={styles.statusChangeWarning}>
                <Icon name="alert-triangle" size={16} color={colors.red} />
                <Text style={styles.statusChangeWarningText}>
                  This will permanently delete this appointment. This cannot be undone.
                </Text>
              </View>
            )}
            
            {/* Email notification option (not for delete) */}
            {statusAction?.type !== 'delete' && statusAction?.appointment?.email && (
              <TouchableOpacity 
                style={styles.statusChangeEmailOption}
                onPress={() => setSendEmailOnChange(!sendEmailOnChange)}
              >
                <Icon 
                  name={sendEmailOnChange ? 'check-square' : 'square'} 
                  size={20} 
                  color={sendEmailOnChange ? colors.teal : colors.gray} 
                />
                <View style={styles.statusChangeEmailOptionText}>
                  <Text style={styles.statusChangeEmailLabel}>Send email notification to client</Text>
                  <Text style={styles.statusChangeEmailDesc}>
                    Notify {statusAction?.appointment?.clientName} about this status change
                  </Text>
                </View>
              </TouchableOpacity>
            )}
            
            {/* Actions */}
            <View style={styles.statusChangeActions}>
              <TouchableOpacity 
                style={styles.statusChangeCancel}
                onPress={() => {
                  setShowStatusModal(false);
                  setStatusAction(null);
                }}
              >
                <Text style={styles.statusChangeCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.statusChangeConfirm, {
                  backgroundColor: statusAction?.type === 'delete' ? colors.red :
                                   statusAction?.type === 'pending' ? colors.gold :
                                   statusAction?.type === 'confirmed' ? colors.teal :
                                   statusAction?.type === 'completed' ? colors.green : colors.gray
                }]}
                onPress={processStatusChange}
                disabled={processingStatus}
              >
                {processingStatus ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <>
                    <Icon 
                      name={statusAction?.type === 'delete' ? 'trash-2' : 'check'} 
                      size={14} 
                      color={colors.white} 
                    />
                    <Text style={styles.statusChangeConfirmText}>
                      {statusAction?.type === 'delete' ? 'Delete Permanently' : 'Confirm Change'}
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// Clients Tab (Dashboard > Clients)
const ClientsTab = ({ onViewClientJobs, initialCategory, onCategoryViewed }) => {
  const [clients, setClients] = useState([]);
  const [clientJobs, setClientJobs] = useState({}); // Map of clientId -> jobs array
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(initialCategory || 'all'); // Category tab
  
  // Handle initialCategory changes from navigation
  useEffect(() => {
    if (initialCategory) {
      setActiveCategory(initialCategory);
      if (onCategoryViewed) {
        // Clear the nav option after it's been applied
        setTimeout(() => onCategoryViewed(), 100);
      }
    }
  }, [initialCategory]);
  const [sortBy, setSortBy] = useState('upcoming'); // 'upcoming', 'name', 'recent', 'spent'
  const [selectedClient, setSelectedClient] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentClient, setPaymentClient] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [newClient, setNewClient] = useState({
    name: '', email: '', phone: '', address: '', type: 'potential', notes: ''
  });

  const openPaymentFlow = (client) => {
    setPaymentClient(client);
    setShowPaymentModal(true);
  };

  const handlePaymentComplete = () => {
    // Refresh clients to show updated stats
    loadClients();
  };

  // Load clients from Firestore on mount
  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      // Load clients
      const clientsRef = collection(db, 'clients');
      const q = query(clientsRef, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      
      const loadedClients = [];
      snapshot.forEach(doc => {
        loadedClients.push({ id: doc.id, ...doc.data() });
      });
      
      setClients(loadedClients);
      
      // Load upcoming jobs to determine which clients have upcoming appointments
      const today = new Date().toISOString().split('T')[0];
      const eventsRef = collection(db, 'events');
      const eventsQuery = query(eventsRef, where('date', '>=', today), orderBy('date', 'asc'));
      const eventsSnapshot = await getDocs(eventsQuery);
      
      const jobsMap = {};
      eventsSnapshot.forEach(doc => {
        const event = { id: doc.id, ...doc.data() };
        const clientId = event.clientId;
        if (clientId) {
          if (!jobsMap[clientId]) {
            jobsMap[clientId] = [];
          }
          jobsMap[clientId].push(event);
        }
      });
      
      setClientJobs(jobsMap);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  // Get the allowed types for the active category
  const activeTypesForCategory = CLIENT_CATEGORIES.find(c => c.id === activeCategory)?.types || [];
  
  // Filter and sort clients
  const filteredClients = clients
    .filter(client => {
    const matchesSearch = client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         client.phone?.includes(searchQuery);
      // Check if client type matches the active category
      const matchesCategory = activeTypesForCategory.includes(client.type);
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      // ALWAYS prioritize clients with in-progress jobs first
      const aInProgress = clientJobs[a.id]?.some(j => j.status === 'in-progress');
      const bInProgress = clientJobs[b.id]?.some(j => j.status === 'in-progress');
      if (aInProgress && !bInProgress) return -1;
      if (!aInProgress && bInProgress) return 1;
      
      // Sort by upcoming jobs next
      if (sortBy === 'upcoming') {
        const aHasJobs = (clientJobs[a.id]?.length || 0) > 0;
        const bHasJobs = (clientJobs[b.id]?.length || 0) > 0;
        if (aHasJobs && !bHasJobs) return -1;
        if (!aHasJobs && bHasJobs) return 1;
        // If both have jobs, sort by earliest job date
        if (aHasJobs && bHasJobs) {
          const aDate = clientJobs[a.id][0]?.date || '';
          const bDate = clientJobs[b.id][0]?.date || '';
          return aDate.localeCompare(bDate);
        }
        // Otherwise sort by name
        return (a.name || '').localeCompare(b.name || '');
      }
      if (sortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '');
      }
      if (sortBy === 'recent') {
        return (b.lastJob || '').localeCompare(a.lastJob || '');
      }
      if (sortBy === 'spent') {
        return (b.totalSpent || 0) - (a.totalSpent || 0);
      }
      return 0;
    });
  
  // Count clients in each category for badges
  const categoryCounts = CLIENT_CATEGORIES.reduce((acc, cat) => {
    acc[cat.id] = clients.filter(c => cat.types.includes(c.type)).length;
    return acc;
  }, {});
  
  // Edit client functions
  const openEditModal = (client) => {
    setEditingClient({ ...client });
    setShowEditModal(true);
  };
  
  const saveEditedClient = async () => {
    if (!editingClient) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'clients', editingClient.id), {
        name: editingClient.name,
        email: editingClient.email,
        phone: editingClient.phone,
        address: editingClient.address,
        type: editingClient.type,
        notes: editingClient.notes,
        updatedAt: serverTimestamp(),
      });
      setShowEditModal(false);
      setEditingClient(null);
      loadClients();
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Failed to save client. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  const archiveClient = async (clientId) => {
    if (!confirm('Are you sure you want to archive this client?')) return;
    try {
      await updateDoc(doc(db, 'clients', clientId), {
        type: 'archived',
        archivedAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      });
      loadClients();
    } catch (error) {
      console.error('Error archiving client:', error);
      alert('Failed to archive client. Please try again.');
    }
  };
  
  const restoreClient = async (clientId) => {
    try {
      await updateDoc(doc(db, 'clients', clientId), {
        type: 'client',
        archivedAt: null,
        restoredAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      });
      loadClients();
    } catch (error) {
      console.error('Error restoring client:', error);
      alert('Failed to restore client. Please try again.');
    }
  };

  const addNewClient = async () => {
    if (!newClient.name || !newClient.phone) {
      alert('Please enter at least a name and phone number');
      return;
    }
    
    setSaving(true);
    try {
      const clientData = {
        ...newClient,
        totalJobs: 0,
        totalSpent: 0,
        lastJob: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'clients'), clientData);
      
      // Add to local state with the new ID
      setClients([...clients, { id: docRef.id, ...clientData }]);
      setShowAddModal(false);
      setNewClient({ name: '', email: '', phone: '', address: '', type: 'potential', notes: '' });
    } catch (error) {
      console.error('Error adding client:', error);
      alert('Failed to save client. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const deleteClient = async (clientId) => {
    if (!confirm('Are you sure you want to delete this client?')) return;
    
    try {
      await deleteDoc(doc(db, 'clients', clientId));
      setClients(clients.filter(c => c.id !== clientId));
      if (selectedClient?.id === clientId) {
        setSelectedClient(null);
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Failed to delete client. Please try again.');
    }
  };

  if (loading) {
    return (
      <View style={styles.tabContent}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.teal} />
          <Text style={styles.loadingText}>Loading clients...</Text>
        </View>
      </View>
    );
  }

  return (
  <View style={styles.tabContent}>
    <View style={styles.tabHeader}>
      <View>
          <Text style={styles.tabTitle}>Clients</Text>
          <Text style={styles.tabSubtitle}>Manage your customer database ({clients.length} total)</Text>
      </View>
        <TouchableOpacity style={styles.primaryBtn} onPress={() => setShowAddModal(true)}>
          <Text style={styles.primaryBtnText}>+ Add Client</Text>
      </TouchableOpacity>
    </View>

      {/* Category Tabs - My Clients, Potential, Archived */}
      <View style={styles.clientCategoryTabs}>
        {CLIENT_CATEGORIES.map(cat => (
          <TouchableOpacity
            key={cat.id}
            style={[styles.clientCategoryTab, activeCategory === cat.id && styles.clientCategoryTabActive]}
            onPress={() => setActiveCategory(cat.id)}
          >
            <Icon name={cat.icon} size={16} color={activeCategory === cat.id ? colors.teal : colors.gray} />
            <Text style={[styles.clientCategoryTabText, activeCategory === cat.id && styles.clientCategoryTabTextActive]}>
              {cat.label}
            </Text>
            <View style={[styles.clientCategoryBadge, activeCategory === cat.id && styles.clientCategoryBadgeActive]}>
              <Text style={styles.clientCategoryBadgeText}>{categoryCounts[cat.id] || 0}</Text>
            </View>
          </TouchableOpacity>
        ))}
      </View>

      {/* Search */}
      <View style={styles.clientsToolbar}>
        <View style={styles.searchBox}>
          <Text style={styles.searchIcon}>🔍</Text>
          <TextInput
            style={styles.searchInput}
            placeholder="Search clients..."
            placeholderTextColor={colors.gray}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>
    </View>

      {/* Clients Grid */}
      <View style={styles.clientsGrid}>
        {filteredClients.map(client => (
          <TouchableOpacity 
            key={client.id} 
            style={styles.clientCard}
            onPress={() => setSelectedClient(client)}
          >
            <View style={styles.clientCardHeader}>
              <View style={styles.clientAvatar}>
                <Text style={styles.clientAvatarText}>{client.name.charAt(0)}</Text>
      </View>
              <View style={styles.clientCardInfo}>
                <Text style={styles.clientName}>{client.name}</Text>
                <View style={[styles.clientTypeBadge, { backgroundColor: CLIENT_TYPES.find(t => t.id === client.type)?.color + '30' }]}>
                  <Text style={[styles.clientTypeBadgeText, { color: CLIENT_TYPES.find(t => t.id === client.type)?.color }]}>
                    {CLIENT_TYPES.find(t => t.id === client.type)?.label}
                  </Text>
          </View>
            </View>
          </View>
            
            <View style={styles.clientCardBody}>
              <View style={styles.clientContactRow}>
                <View style={styles.clientContactIcon}>
                  <Icon name="mail" size={14} color={colors.textMuted} />
                </View>
                <Text style={styles.clientContactText}>{client.email || 'No email'}</Text>
              </View>
              <View style={styles.clientContactRow}>
                <View style={styles.clientContactIcon}>
                  <Icon name="phone" size={14} color={colors.textMuted} />
                </View>
                <Text style={styles.clientContactText}>{client.phone}</Text>
              </View>
              {client.address && (
                <View style={styles.clientContactRow}>
                  <View style={styles.clientContactIcon}>
                    <Icon name="map-pin" size={14} color={colors.textMuted} />
                  </View>
                  <Text style={styles.clientContactText} numberOfLines={1}>{client.address}</Text>
                </View>
              )}
              
              {/* Upcoming jobs indicator */}
              {clientJobs[client.id]?.length > 0 && (
                <View style={styles.clientUpcomingBadge}>
                  <Icon name="calendar" size={12} color={colors.green} />
                  <Text style={styles.clientUpcomingText}>
                    {clientJobs[client.id].length} upcoming job{clientJobs[client.id].length > 1 ? 's' : ''}
                  </Text>
                </View>
              )}
            </View>

            <View style={styles.clientCardFooter}>
              <View style={styles.clientStat}>
                <Text style={styles.clientStatValue}>{client.totalJobs || 0}</Text>
                <Text style={styles.clientStatLabel}>Jobs</Text>
              </View>
              <View style={styles.clientStat}>
                <Text style={styles.clientStatValue}>${client.totalSpent || 0}</Text>
                <Text style={styles.clientStatLabel}>Spent</Text>
              </View>
              <View style={styles.clientStat}>
                <Text style={styles.clientStatValue}>{client.lastJob || '—'}</Text>
                <Text style={styles.clientStatLabel}>Last Job</Text>
              </View>
            </View>
            
            {/* Action buttons */}
            <View style={styles.clientCardActions}>
              <TouchableOpacity 
                style={styles.clientEditBtn}
                onPress={(e) => { e.stopPropagation(); openEditModal(client); }}
              >
                <Icon name="edit-2" size={14} color={colors.teal} />
              </TouchableOpacity>
              {client.type === 'archived' ? (
                <TouchableOpacity 
                  style={styles.clientRestoreBtn}
                  onPress={(e) => { e.stopPropagation(); restoreClient(client.id); }}
                >
                  <Icon name="refresh-cw" size={14} color={colors.green} />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity 
                  style={styles.clientArchiveBtn}
                  onPress={(e) => { e.stopPropagation(); archiveClient(client.id); }}
                >
                  <Icon name="archive" size={14} color={colors.gray} />
                </TouchableOpacity>
              )}
            </View>
            </TouchableOpacity>
        ))}
      </View>

      {/* Add Client Modal */}
      <Modal visible={showAddModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add New Client</Text>
              <TouchableOpacity onPress={() => setShowAddModal(false)}>
                <Text style={styles.modalClose}>✕</Text>
            </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalBody}>
              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Name *</Text>
                <TextInput
                  style={styles.modalInput}
                  value={newClient.name}
                  onChangeText={(val) => setNewClient({...newClient, name: val})}
                  placeholder="Client name"
                  placeholderTextColor={colors.gray}
                />
              </View>

              <View style={styles.modalRow}>
                <View style={[styles.modalField, { flex: 1 }]}>
                  <Text style={styles.modalLabel}>Email</Text>
                  <TextInput
                    style={styles.modalInput}
                    value={newClient.email}
                    onChangeText={(val) => setNewClient({...newClient, email: val})}
                    placeholder="email@example.com"
                    placeholderTextColor={colors.gray}
                    keyboardType="email-address"
                  />
                </View>
                <View style={[styles.modalField, { flex: 1, marginLeft: 12 }]}>
                  <Text style={styles.modalLabel}>Phone *</Text>
                  <TextInput
                    style={styles.modalInput}
                    value={newClient.phone}
                    onChangeText={(val) => setNewClient({...newClient, phone: val})}
                    placeholder="(702) 555-0123"
                    placeholderTextColor={colors.gray}
                    keyboardType="phone-pad"
                  />
                </View>
              </View>

              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Address</Text>
                <TextInput
                  style={styles.modalInput}
                  value={newClient.address}
                  onChangeText={(val) => setNewClient({...newClient, address: val})}
                  placeholder="Street address, city, state"
                  placeholderTextColor={colors.gray}
                />
              </View>

              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Client Type</Text>
                <View style={styles.modalOptions}>
                  {CLIENT_TYPES.map(type => (
                    <TouchableOpacity
                      key={type.id}
                      style={[
                        styles.modalOption,
                        newClient.type === type.id && { backgroundColor: type.color + '30', borderColor: type.color }
                      ]}
                      onPress={() => setNewClient({...newClient, type: type.id})}
                    >
                      <Text style={[styles.modalOptionText, newClient.type === type.id && { color: type.color }]}>
                        {type.label}
                      </Text>
            </TouchableOpacity>
                  ))}
          </View>
              </View>

              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Notes</Text>
                <TextInput
                  style={[styles.modalInput, styles.modalTextarea]}
                  value={newClient.notes}
                  onChangeText={(val) => setNewClient({...newClient, notes: val})}
                  placeholder="Notes about this client..."
                  placeholderTextColor={colors.gray}
                  multiline
                  numberOfLines={3}
                />
              </View>
            </ScrollView>

            <View style={styles.modalFooter}>
              <TouchableOpacity style={styles.modalCancelBtn} onPress={() => setShowAddModal(false)}>
                <Text style={styles.modalCancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.modalSubmitBtn, saving && styles.primaryBtnDisabled]} 
                onPress={addNewClient}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color={colors.darkNavy} />
                ) : (
                  <Text style={styles.modalSubmitBtnText}>Add Client</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Edit Client Modal */}
      <Modal visible={showEditModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Edit Client</Text>
              <TouchableOpacity onPress={() => { setShowEditModal(false); setEditingClient(null); }}>
                <Text style={styles.modalClose}>✕</Text>
              </TouchableOpacity>
            </View>

            {editingClient && (
              <ScrollView style={styles.modalBody}>
                <View style={styles.modalField}>
                  <Text style={styles.modalLabel}>Name *</Text>
                  <TextInput
                    style={styles.modalInput}
                    value={editingClient.name}
                    onChangeText={(val) => setEditingClient({...editingClient, name: val})}
                    placeholder="Client name"
                    placeholderTextColor={colors.gray}
                  />
                </View>

                <View style={styles.modalRow}>
                  <View style={[styles.modalField, { flex: 1 }]}>
                    <Text style={styles.modalLabel}>Email</Text>
                    <TextInput
                      style={styles.modalInput}
                      value={editingClient.email}
                      onChangeText={(val) => setEditingClient({...editingClient, email: val})}
                      placeholder="email@example.com"
                      placeholderTextColor={colors.gray}
                      keyboardType="email-address"
                    />
                  </View>
                  <View style={[styles.modalField, { flex: 1, marginLeft: 12 }]}>
                    <Text style={styles.modalLabel}>Phone *</Text>
                    <TextInput
                      style={styles.modalInput}
                      value={editingClient.phone}
                      onChangeText={(val) => setEditingClient({...editingClient, phone: val})}
                      placeholder="(702) 555-0123"
                      placeholderTextColor={colors.gray}
                      keyboardType="phone-pad"
                    />
                  </View>
                </View>

                <View style={styles.modalField}>
                  <Text style={styles.modalLabel}>Address</Text>
                  <TextInput
                    style={styles.modalInput}
                    value={editingClient.address}
                    onChangeText={(val) => setEditingClient({...editingClient, address: val})}
                    placeholder="Street address, city, state"
                    placeholderTextColor={colors.gray}
                  />
                </View>

                <View style={styles.modalField}>
                  <Text style={styles.modalLabel}>Client Type</Text>
                  <View style={styles.modalOptions}>
                    {CLIENT_TYPES.filter(t => t.id !== 'archived').map(type => (
                      <TouchableOpacity
                        key={type.id}
                        style={[
                          styles.modalOption,
                          editingClient.type === type.id && { backgroundColor: type.color + '30', borderColor: type.color }
                        ]}
                        onPress={() => setEditingClient({...editingClient, type: type.id})}
                      >
                        <Text style={[styles.modalOptionText, editingClient.type === type.id && { color: type.color }]}>
                          {type.label}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                <View style={styles.modalField}>
                  <Text style={styles.modalLabel}>Notes</Text>
                  <TextInput
                    style={[styles.modalInput, styles.modalTextarea]}
                    value={editingClient.notes}
                    onChangeText={(val) => setEditingClient({...editingClient, notes: val})}
                    placeholder="Notes about this client..."
                    placeholderTextColor={colors.gray}
                    multiline
                    numberOfLines={3}
                  />
                </View>
              </ScrollView>
            )}

            <View style={styles.modalFooter}>
              <TouchableOpacity style={styles.modalCancelBtn} onPress={() => { setShowEditModal(false); setEditingClient(null); }}>
                <Text style={styles.modalCancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.modalSubmitBtn, saving && styles.primaryBtnDisabled]} 
                onPress={saveEditedClient}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color={colors.darkNavy} />
                ) : (
                  <Text style={styles.modalSubmitBtnText}>Save Changes</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Client Detail Modal */}
      {selectedClient && (
        <ClientDetailModal 
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
          onCheckout={() => {
            openPaymentFlow(selectedClient);
          }}
          onRefresh={loadClients}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && paymentClient && (
        <PaymentCollectionModal
          visible={showPaymentModal}
          client={paymentClient}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentClient(null);
          }}
          onComplete={handlePaymentComplete}
        />
      )}
    </View>
  );
};

// ==================== CLIENT DETAIL MODAL ====================
// Enhanced client view with jobs, history, and checkout
const ClientDetailModal = ({ client, onClose, onCheckout, onRefresh }) => {
  const [clientJobs, setClientJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [activeJobTab, setActiveJobTab] = useState('upcoming');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [recentPayments, setRecentPayments] = useState([]);
  const [showPaymentAlert, setShowPaymentAlert] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    lineItems: [{ description: 'Service', quantity: 1, price: 0 }],
    laborHours: 0,
    laborRate: 75,
    materialsCost: 0,
    notes: '',
  });
  
  // Editable client state
  const [isEditing, setIsEditing] = useState(false);
  const [editedClient, setEditedClient] = useState({
    name: client.name || '',
    email: client.email || '',
    phone: client.phone || '',
    address: client.address || '',
    notes: client.notes || '',
    totalJobs: client.totalJobs || 0,
    totalSpent: client.totalSpent || 0,
    lastJob: client.lastJob || '',
    type: client.type || 'new',
  });
  const [savingClient, setSavingClient] = useState(false);
  
  const saveClientChanges = async () => {
    setSavingClient(true);
    try {
      await updateDoc(doc(db, 'clients', client.id), {
        ...editedClient,
        totalJobs: parseInt(editedClient.totalJobs) || 0,
        totalSpent: parseFloat(editedClient.totalSpent) || 0,
        updatedAt: serverTimestamp(),
      });
      setIsEditing(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Failed to save client changes. Please try again.');
    } finally {
      setSavingClient(false);
    }
  };

  // Load jobs and set up real-time payment listener
  useEffect(() => {
    loadClientJobs();
    
    // Real-time listener for payments (without orderBy to avoid index requirement)
    const paymentsRef = collection(db, 'payments');
    const q = query(paymentsRef, where('clientId', '==', client.id));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const payments = [];
      snapshot.forEach(doc => {
        payments.push({ id: doc.id, ...doc.data() });
      });
      
      // Check for new payment
      if (recentPayments.length > 0 && payments.length > recentPayments.length) {
        const newPayment = payments[0];
        setShowPaymentAlert({
          amount: newPayment.total,
          method: newPayment.paymentMethod,
        });
        
        // Auto-hide alert after 5 seconds
        setTimeout(() => setShowPaymentAlert(null), 5000);
        
        // Refresh jobs to get updated status
        loadClientJobs();
        if (onRefresh) onRefresh();
      }
      
      setRecentPayments(payments);
    }, (error) => {
      console.error('Error listening to payments:', error);
    });
    
    return () => unsubscribe();
  }, [client.id]);

  const loadClientJobs = async () => {
    try {
      const eventsRef = collection(db, 'events');
      const q = query(eventsRef, where('clientId', '==', client.id));
      const snapshot = await getDocs(q);
      
      const jobs = [];
      snapshot.forEach(doc => {
        jobs.push({ id: doc.id, ...doc.data() });
      });
      
      // Also try by client name if no clientId matches
      if (jobs.length === 0) {
        const nameQuery = query(eventsRef, where('clientName', '==', client.name));
        const nameSnapshot = await getDocs(nameQuery);
        nameSnapshot.forEach(doc => {
          jobs.push({ id: doc.id, ...doc.data() });
        });
      }
      
      // Sort by date
      jobs.sort((a, b) => new Date(b.date) - new Date(a.date));
      setClientJobs(jobs);
    } catch (error) {
      console.error('Error loading client jobs:', error);
    } finally {
      setLoadingJobs(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const pendingJobs = clientJobs.filter(j => j.status === 'pending' || !j.status);
  const upcomingJobs = clientJobs.filter(j => (j.status === 'confirmed' || j.status === 'approved') && j.date >= today);
  const inProgressJobs = clientJobs.filter(j => j.status === 'in-progress');
  const completedJobs = clientJobs.filter(j => j.status === 'completed');
  const cancelledJobs = clientJobs.filter(j => j.status === 'cancelled' || j.status === 'rejected');
  const pastJobs = clientJobs.filter(j => j.date < today || j.status === 'completed');

  const sendPaymentEmail = async (job) => {
    if (!client.email) {
      alert('Client does not have an email address on file.');
      return;
    }
    
    setSendingEmail(true);
    try {
      // Generate checkout URL
      const checkoutUrl = `${window.location.origin}/checkout/${client.id}/${job.id}`;
      
      // Try to send via Firebase function
      try {
        const sendEmail = httpsCallable(functions, 'sendPaymentEmail');
        await sendEmail({ 
          clientEmail: client.email, 
          clientName: client.name, 
          jobId: job.id,
          checkoutUrl,
          jobDescription: job.service,
          amount: job.estimatedAmount
        });
        alert(`Payment link sent to ${client.email}!`);
      } catch (funcError) {
        // Fallback: show link to copy
        console.warn('Email function not available, showing link:', funcError);
        const confirmed = confirm(
          `Email service unavailable. Would you like to copy the payment link?\n\n${checkoutUrl}`
        );
        if (confirmed && navigator.clipboard) {
          await navigator.clipboard.writeText(checkoutUrl);
          alert('Payment link copied to clipboard!');
        }
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const openCheckoutInNewTab = (job = null) => {
    const jobParam = job ? `/${job.id}` : '';
    const checkoutUrl = `/checkout/${client.id}${jobParam}`;
    window.open(checkoutUrl, '_blank');
  };

  // Create and send invoice link
  const createAndSendInvoice = async () => {
    if (!client.email) {
      alert('Client does not have an email address on file.');
      return;
    }
    
    // Calculate total
    const itemsTotal = invoiceData.lineItems.reduce((sum, item) => sum + ((item.quantity || 1) * (item.price || 0)), 0);
    const laborTotal = (invoiceData.laborHours || 0) * (invoiceData.laborRate || 75);
    const total = itemsTotal + laborTotal + (invoiceData.materialsCost || 0);
    
    if (total <= 0) {
      alert('Please add items or charges to the invoice.');
      return;
    }
    
    setCreatingInvoice(true);
    try {
      // Create invoice
      const result = await createInvoice({
        clientId: client.id,
        clientName: client.name,
        clientEmail: client.email,
        clientPhone: client.phone,
        clientAddress: client.address,
        lineItems: invoiceData.lineItems.filter(item => item.description && item.price > 0),
        laborHours: invoiceData.laborHours,
        laborRate: invoiceData.laborRate,
        materialsCost: invoiceData.materialsCost,
        notes: invoiceData.notes,
        service: invoiceData.lineItems[0]?.description || 'Handyman Service',
      });
      
      if (result.data.success) {
        // Send invoice link via email
        await sendInvoiceLink({
          invoiceId: result.data.invoiceId,
          customMessage: invoiceData.notes,
        });
        
        alert(`Invoice sent to ${client.email}!\n\nPayment link: ${result.data.paymentLink}`);
        setShowInvoiceModal(false);
        setInvoiceData({
          lineItems: [{ description: 'Service', quantity: 1, price: 0 }],
          laborHours: 0,
          laborRate: 75,
          materialsCost: 0,
          notes: '',
        });
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      alert('Failed to create invoice. Please try again.');
    } finally {
      setCreatingInvoice(false);
    }
  };

  // Add line item to invoice
  const addInvoiceLineItem = () => {
    setInvoiceData({
      ...invoiceData,
      lineItems: [...invoiceData.lineItems, { description: '', quantity: 1, price: 0 }]
    });
  };

  // Update line item
  const updateInvoiceLineItem = (index, field, value) => {
    const updatedItems = [...invoiceData.lineItems];
    if (field === 'quantity' || field === 'price') {
      updatedItems[index][field] = parseFloat(value) || 0;
    } else {
      updatedItems[index][field] = value;
    }
    setInvoiceData({ ...invoiceData, lineItems: updatedItems });
  };

  // Remove line item
  const removeInvoiceLineItem = (index) => {
    if (invoiceData.lineItems.length > 1) {
      const updatedItems = invoiceData.lineItems.filter((_, i) => i !== index);
      setInvoiceData({ ...invoiceData, lineItems: updatedItems });
    }
  };

  // Calculate invoice total
  const invoiceTotal = invoiceData.lineItems.reduce((sum, item) => sum + ((item.quantity || 1) * (item.price || 0)), 0) 
    + (invoiceData.laborHours || 0) * (invoiceData.laborRate || 75) 
    + (invoiceData.materialsCost || 0);

  return (
    <Modal visible={true} transparent animationType="slide">
      <View style={styles.clientDetailOverlay}>
        <View style={styles.clientDetailModal}>
          {/* Payment Alert */}
          {showPaymentAlert && (
            <View style={styles.paymentAlertBanner}>
              <View style={styles.paymentAlertIcon}>
                <Icon name="check-circle" size={24} color={colors.green} />
              </View>
              <View style={styles.paymentAlertContent}>
                <Text style={styles.paymentAlertTitle}>Payment Received!</Text>
                <Text style={styles.paymentAlertText}>
                  ${showPaymentAlert.amount?.toFixed(2)} via {showPaymentAlert.method}
                </Text>
              </View>
              <TouchableOpacity onPress={() => setShowPaymentAlert(null)}>
                <Icon name="x" size={20} color={colors.white} />
              </TouchableOpacity>
            </View>
          )}

          {/* Header */}
          <View style={styles.clientDetailHeader}>
            <View style={styles.clientDetailHeaderLeft}>
              <View style={styles.clientDetailAvatar}>
                <Text style={styles.clientDetailAvatarText}>{(isEditing ? editedClient.name : client.name)?.charAt(0)}</Text>
              </View>
              <View>
                {isEditing ? (
                  <TextInput
                    style={[styles.clientDetailName, styles.editableNameInput]}
                    value={editedClient.name}
                    onChangeText={(val) => setEditedClient({ ...editedClient, name: val })}
                    placeholder="Client Name"
                    placeholderTextColor={colors.gray}
                  />
                ) : (
                <Text style={styles.clientDetailName}>{client.name}</Text>
                )}
                {isEditing ? (
                  <View style={styles.clientTypeSelector}>
                    {CLIENT_TYPES.map(type => (
                      <TouchableOpacity
                        key={type.id}
                        style={[
                          styles.clientTypeSelectorBtn,
                          editedClient.type === type.id && { backgroundColor: type.color + '30', borderColor: type.color }
                        ]}
                        onPress={() => setEditedClient({ ...editedClient, type: type.id })}
                      >
                        <Text style={[styles.clientTypeSelectorText, editedClient.type === type.id && { color: type.color }]}>
                          {type.label}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                ) : (
                <View style={[styles.clientTypeBadge, { backgroundColor: CLIENT_TYPES.find(t => t.id === client.type)?.color + '30' }]}>
                  <Text style={[styles.clientTypeBadgeText, { color: CLIENT_TYPES.find(t => t.id === client.type)?.color }]}>
                    {CLIENT_TYPES.find(t => t.id === client.type)?.label || 'Client'}
                  </Text>
                </View>
                )}
              </View>
            </View>
            {/* Edit/Cancel/Save buttons - top left */}
            <View style={styles.clientDetailHeaderRight}>
              {isEditing ? (
                <>
                  <TouchableOpacity 
                    style={[styles.clientDetailHeaderBtn, styles.clientDetailHeaderBtnCancel]} 
                    onPress={() => {
                      setIsEditing(false);
                      setEditedClient({
                        name: client.name || '',
                        email: client.email || '',
                        phone: client.phone || '',
                        address: client.address || '',
                        notes: client.notes || '',
                        totalJobs: client.totalJobs || 0,
                        totalSpent: client.totalSpent || 0,
                        lastJob: client.lastJob || '',
                        type: client.type || 'new',
                      });
                    }}
                  >
                    <Icon name="x" size={16} color={colors.gray} />
                    <Text style={styles.clientDetailHeaderBtnCancelText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={[styles.clientDetailHeaderBtn, styles.clientDetailHeaderBtnSave]} 
                    onPress={saveClientChanges}
                    disabled={savingClient}
                  >
                    {savingClient ? (
                      <ActivityIndicator size="small" color={colors.white} />
                    ) : (
                      <>
                        <Icon name="check" size={16} color={colors.white} />
                        <Text style={styles.clientDetailHeaderBtnSaveText}>Save</Text>
                      </>
                    )}
                  </TouchableOpacity>
                </>
              ) : (
                <TouchableOpacity 
                  style={[styles.clientDetailHeaderBtn, styles.clientDetailHeaderBtnEdit]} 
                  onPress={() => setIsEditing(true)}
                >
                  <Icon name="edit-2" size={16} color={colors.teal} />
                </TouchableOpacity>
              )}
            </View>
            {/* Close button - top right (separate from edit buttons) */}
            <TouchableOpacity style={styles.clientDetailCloseBtn} onPress={onClose}>
              <Icon name="x" size={24} color={colors.white} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.clientDetailBody} showsVerticalScrollIndicator={false}>
            {/* Contact Info */}
            <View style={styles.clientDetailSection}>
              <Text style={styles.clientDetailSectionTitle}>Contact Info</Text>
              {isEditing ? (
                <View style={styles.clientDetailEditGrid}>
                  <View style={styles.clientDetailEditField}>
                    <Text style={styles.clientDetailEditLabel}>Email</Text>
                    <View style={styles.clientDetailEditInputRow}>
                      <Icon name="mail" size={16} color={colors.teal} />
                      <TextInput
                        style={styles.clientDetailEditInput}
                        value={editedClient.email}
                        onChangeText={(val) => setEditedClient({ ...editedClient, email: val })}
                        placeholder="email@example.com"
                        placeholderTextColor={colors.gray}
                        keyboardType="email-address"
                      />
                    </View>
                  </View>
                  <View style={styles.clientDetailEditField}>
                    <Text style={styles.clientDetailEditLabel}>Phone</Text>
                    <View style={styles.clientDetailEditInputRow}>
                      <Icon name="phone" size={16} color={colors.teal} />
                      <TextInput
                        style={styles.clientDetailEditInput}
                        value={editedClient.phone}
                        onChangeText={(val) => setEditedClient({ ...editedClient, phone: val })}
                        placeholder="(702) 555-0123"
                        placeholderTextColor={colors.gray}
                        keyboardType="phone-pad"
                      />
                    </View>
                  </View>
                  <View style={styles.clientDetailEditField}>
                    <Text style={styles.clientDetailEditLabel}>Address</Text>
                    <View style={styles.clientDetailEditInputRow}>
                      <Icon name="map-pin" size={16} color={colors.teal} />
                      <TextInput
                        style={styles.clientDetailEditInput}
                        value={editedClient.address}
                        onChangeText={(val) => setEditedClient({ ...editedClient, address: val })}
                        placeholder="123 Main St, Las Vegas, NV"
                        placeholderTextColor={colors.gray}
                      />
                    </View>
                  </View>
                </View>
              ) : (
              <View style={styles.clientDetailInfoGrid}>
                <View style={styles.clientDetailInfoItem}>
                  <Icon name="mail" size={18} color={colors.teal} />
                  <Text style={styles.clientDetailInfoText}>{client.email || 'No email'}</Text>
                </View>
                <View style={styles.clientDetailInfoItem}>
                  <Icon name="phone" size={18} color={colors.teal} />
                  <Text style={styles.clientDetailInfoText}>{client.phone || 'No phone'}</Text>
                </View>
                <View style={[styles.clientDetailInfoItem, { borderBottomWidth: 0 }]}>
                  <Icon name="map-pin" size={18} color={colors.teal} />
                  <Text style={styles.clientDetailInfoText}>{client.address || 'No address'}</Text>
                </View>
                  </View>
                )}
            </View>

            {/* Stats - Editable */}
            <View style={styles.clientDetailStatsRow}>
              <View style={styles.clientDetailStat}>
                {isEditing ? (
                  <TextInput
                    style={styles.clientDetailStatValueInput}
                    value={editedClient.totalJobs.toString()}
                    onChangeText={(val) => setEditedClient({ ...editedClient, totalJobs: val.replace(/[^0-9]/g, '') })}
                    keyboardType="number-pad"
                    placeholder="0"
                    placeholderTextColor={colors.gray}
                  />
                ) : (
                <Text style={[styles.clientDetailStatValue, { color: colors.teal }]}>{client.totalJobs || 0}</Text>
                )}
                <Text style={styles.clientDetailStatLabel}>Total Jobs</Text>
              </View>
              <View style={styles.clientDetailStat}>
                {isEditing ? (
                  <View style={styles.clientDetailStatValueInputRow}>
                    <Text style={styles.clientDetailStatDollar}>$</Text>
                    <TextInput
                      style={styles.clientDetailStatMoneyInput}
                      value={editedClient.totalSpent.toString()}
                      onChangeText={(val) => setEditedClient({ ...editedClient, totalSpent: val.replace(/[^0-9.]/g, '') })}
                      keyboardType={isWeb ? 'default' : 'decimal-pad'}
                      placeholder="0"
                      placeholderTextColor={colors.gray}
                    />
                  </View>
                ) : (
                <Text style={[styles.clientDetailStatValue, { color: colors.green }]}>${client.totalSpent || 0}</Text>
                )}
                <Text style={styles.clientDetailStatLabel}>Total Spent</Text>
              </View>
              <View style={[styles.clientDetailStat, { borderRightWidth: 0 }]}>
                {isEditing ? (
                  <TextInput
                    style={styles.clientDetailStatValueInput}
                    value={editedClient.lastJob}
                    onChangeText={(val) => setEditedClient({ ...editedClient, lastJob: val })}
                    placeholder="YYYY-MM-DD"
                    placeholderTextColor={colors.gray}
                  />
                ) : (
                <Text style={[styles.clientDetailStatValue, { color: colors.gold }]}>{client.lastJob || '—'}</Text>
                )}
                <Text style={styles.clientDetailStatLabel}>Last Job</Text>
              </View>
            </View>

            {/* Notes Section */}
            <View style={styles.clientDetailSection}>
              <Text style={styles.clientDetailSectionTitle}>Notes</Text>
              {isEditing ? (
                <TextInput
                  style={styles.clientDetailNotesInput}
                  value={editedClient.notes}
                  onChangeText={(val) => setEditedClient({ ...editedClient, notes: val })}
                  placeholder="Add notes about this client (preferences, job history, special requests, etc.)"
                  placeholderTextColor={colors.gray}
                  multiline
                  numberOfLines={4}
                />
              ) : (
                <View style={styles.clientDetailNotesDisplay}>
                  <Text style={styles.clientDetailNotesText}>
                    {client.notes || 'No notes yet. Click "Edit" to add notes about this client.'}
                  </Text>
                </View>
              )}
            </View>

            {/* Jobs Section */}
            <View style={styles.clientDetailSection}>
              <Text style={styles.clientDetailSectionTitle}>Jobs & History</Text>
              
              {/* Job Tabs */}
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View style={styles.clientDetailJobTabs}>
                  {pendingJobs.length > 0 && (
                    <TouchableOpacity 
                      style={[styles.clientDetailJobTab, activeJobTab === 'pending' && styles.clientDetailJobTabActive, { backgroundColor: activeJobTab === 'pending' ? colors.gold + '20' : 'transparent' }]}
                      onPress={() => setActiveJobTab('pending')}
                    >
                      <View style={[styles.clientDetailJobTabBadge, { backgroundColor: colors.gold }]}>
                        <Text style={styles.clientDetailJobTabBadgeText}>{pendingJobs.length}</Text>
                      </View>
                      <Text style={[styles.clientDetailJobTabText, activeJobTab === 'pending' && { color: colors.gold }]}>
                        Pending
                      </Text>
                    </TouchableOpacity>
                  )}
                <TouchableOpacity 
                  style={[styles.clientDetailJobTab, activeJobTab === 'upcoming' && styles.clientDetailJobTabActive]}
                  onPress={() => setActiveJobTab('upcoming')}
                >
                  <Text style={[styles.clientDetailJobTabText, activeJobTab === 'upcoming' && styles.clientDetailJobTabTextActive]}>
                    Upcoming ({upcomingJobs.length})
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity 
                  style={[styles.clientDetailJobTab, activeJobTab === 'inprogress' && styles.clientDetailJobTabActive]}
                  onPress={() => setActiveJobTab('inprogress')}
                >
                  <Text style={[styles.clientDetailJobTabText, activeJobTab === 'inprogress' && styles.clientDetailJobTabTextActive]}>
                    In Progress ({inProgressJobs.length})
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity 
                    style={[styles.clientDetailJobTab, activeJobTab === 'completed' && styles.clientDetailJobTabActive]}
                    onPress={() => setActiveJobTab('completed')}
                >
                    <Text style={[styles.clientDetailJobTabText, activeJobTab === 'completed' && styles.clientDetailJobTabTextActive]}>
                      Completed ({completedJobs.length})
                  </Text>
                </TouchableOpacity>
                  {cancelledJobs.length > 0 && (
                    <TouchableOpacity 
                      style={[styles.clientDetailJobTab, activeJobTab === 'cancelled' && styles.clientDetailJobTabActive, { backgroundColor: activeJobTab === 'cancelled' ? colors.red + '20' : 'transparent' }]}
                      onPress={() => setActiveJobTab('cancelled')}
                    >
                      <Text style={[styles.clientDetailJobTabText, activeJobTab === 'cancelled' && { color: colors.red }]}>
                        Cancelled ({cancelledJobs.length})
                      </Text>
                    </TouchableOpacity>
                  )}
              </View>
              </ScrollView>

              {/* Job List */}
              {loadingJobs ? (
                <View style={styles.clientDetailJobsLoading}>
                  <ActivityIndicator size="small" color={colors.teal} />
                  <Text style={styles.clientDetailJobsLoadingText}>Loading jobs...</Text>
                </View>
              ) : (
                <View style={styles.clientDetailJobsList}>
                  {(activeJobTab === 'pending' ? pendingJobs :
                    activeJobTab === 'upcoming' ? upcomingJobs : 
                    activeJobTab === 'inprogress' ? inProgressJobs :
                    activeJobTab === 'completed' ? completedJobs :
                    activeJobTab === 'cancelled' ? cancelledJobs : pastJobs
                  ).length === 0 ? (
                    <Text style={styles.clientDetailNoJobs}>
                      No {activeJobTab} jobs
                    </Text>
                  ) : (
                    (activeJobTab === 'pending' ? pendingJobs :
                      activeJobTab === 'upcoming' ? upcomingJobs : 
                      activeJobTab === 'inprogress' ? inProgressJobs :
                      activeJobTab === 'completed' ? completedJobs :
                      activeJobTab === 'cancelled' ? cancelledJobs : pastJobs
                    ).map(job => (
                      <View key={job.id} style={styles.clientDetailJobCard}>
                        <View style={styles.clientDetailJobHeader}>
                          <View style={[styles.clientDetailJobDot, { backgroundColor: COMPLEXITY_TYPES.find(c => c.id === job.complexity)?.color || colors.gray }]} />
                          <View style={styles.clientDetailJobInfo}>
                            <Text style={styles.clientDetailJobTitle}>{job.service}</Text>
                            <Text style={styles.clientDetailJobDate}>{job.date} • {job.time}</Text>
                          </View>
                          <View style={[styles.clientDetailJobStatus, { 
                            backgroundColor: job.status === 'completed' ? colors.green + '20' : 
                                           job.status === 'pending' || !job.status ? colors.gold + '20' :
                                           job.status === 'cancelled' || job.status === 'rejected' ? colors.red + '20' :
                                           colors.teal + '20' 
                          }]}>
                            <Text style={[styles.clientDetailJobStatusText, { 
                              color: job.status === 'completed' ? colors.green : 
                                     job.status === 'pending' || !job.status ? colors.gold :
                                     job.status === 'cancelled' || job.status === 'rejected' ? colors.red :
                                     colors.teal 
                            }]}>
                              {job.status || 'Pending'}
                            </Text>
                          </View>
                        </View>
                        
                        {/* Project Description from Quote */}
                        {job.description && (
                          <View style={styles.clientDetailJobDescription}>
                            <Text style={styles.clientDetailJobDescLabel}>Project Details:</Text>
                            <Text style={styles.clientDetailJobDescText}>{job.description}</Text>
                          </View>
                        )}
                        
                        {/* Estimate from Quote */}
                        {job.estimate && (
                          <View style={styles.clientDetailJobEstimate}>
                            <Text style={styles.clientDetailJobEstLabel}>Quote: ${job.estimate.lowEstimate} - ${job.estimate.highEstimate}</Text>
                            {job.estimate.breakdown && job.estimate.breakdown.length > 0 && (
                              <Text style={styles.clientDetailJobEstBreakdown}>
                                {job.estimate.breakdown.map(b => b.name).join(' • ')}
                              </Text>
                            )}
                          </View>
                        )}
                        
                        {job.notes && (
                          <Text style={styles.clientDetailJobNotes}>{job.notes}</Text>
                        )}
                        
                        {/* Job Actions */}
                        {(activeJobTab === 'upcoming' || activeJobTab === 'inprogress') && (
                          <View style={styles.clientDetailJobActions}>
                            <TouchableOpacity 
                              style={styles.clientDetailJobActionBtn}
                              onPress={() => sendPaymentEmail(job)}
                              disabled={sendingEmail}
                            >
                              <Icon name="mail" size={14} color={colors.teal} />
                              <Text style={styles.clientDetailJobActionText}>Send Payment Link</Text>
                            </TouchableOpacity>
                            <TouchableOpacity 
                              style={[styles.clientDetailJobActionBtn, styles.clientDetailJobActionBtnPrimary]}
                              onPress={() => openCheckoutInNewTab(job)}
                            >
                              <Icon name="credit-card" size={14} color={colors.darkNavy} />
                              <Text style={styles.clientDetailJobActionTextPrimary}>Checkout</Text>
                            </TouchableOpacity>
                          </View>
                        )}
                      </View>
                    ))
                  )}
                </View>
              )}
            </View>

            {/* Initial Quote Info (from website) */}
            {client.lastDescription && (
              <View style={styles.clientDetailSection}>
                <Text style={styles.clientDetailSectionTitle}>Latest Project Request</Text>
                <View style={styles.clientDetailQuoteBox}>
                  <Text style={styles.clientDetailQuoteText}>{client.lastDescription}</Text>
                  {client.lastEstimate && (
                    <View style={styles.clientDetailQuoteEstimate}>
                      <Text style={styles.clientDetailQuoteEstLabel}>Quoted:</Text>
                      <Text style={styles.clientDetailQuoteEstValue}>
                        ${client.lastEstimate.lowEstimate} - ${client.lastEstimate.highEstimate}
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            )}

            {/* Notes */}
            {client.notes && (
              <View style={styles.clientDetailSection}>
                <Text style={styles.clientDetailSectionTitle}>Notes</Text>
                <Text style={styles.clientDetailNotes}>{client.notes}</Text>
              </View>
            )}
          </ScrollView>

          {/* Footer Actions */}
          <View style={styles.clientDetailFooterContainer}>
            {/* Top row: Call, Email, Send Invoice */}
            <View style={styles.clientDetailFooterRow}>
            <TouchableOpacity 
              style={styles.clientDetailFooterBtnSecondary}
              onPress={() => {
                if (client.phone) {
                  window.open(`tel:${client.phone}`, '_self');
                }
              }}
            >
              <Icon name="phone" size={18} color={colors.teal} />
              <Text style={styles.clientDetailFooterBtnSecondaryText}>Call</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.clientDetailFooterBtnSecondary}
              onPress={() => {
                if (client.email) {
                  window.open(`mailto:${client.email}`, '_self');
                }
              }}
            >
              <Icon name="mail" size={18} color={colors.teal} />
              <Text style={styles.clientDetailFooterBtnSecondaryText}>Email</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.clientDetailFooterBtnSecondary, { backgroundColor: colors.gold + '20', borderColor: colors.gold + '40' }]}
              onPress={() => setShowInvoiceModal(true)}
            >
              <Icon name="file-text" size={18} color={colors.gold} />
                <Text style={[styles.clientDetailFooterBtnSecondaryText, { color: colors.gold }]}>Invoice</Text>
            </TouchableOpacity>
            </View>
            {/* Bottom row: Large Checkout button */}
            <TouchableOpacity 
              style={styles.clientDetailFooterBtnCheckout}
              onPress={onCheckout}
            >
              <Icon name="credit-card" size={20} color={colors.darkNavy} />
              <Text style={styles.clientDetailFooterBtnCheckoutText}>Checkout</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* Send Invoice Modal */}
      <Modal visible={showInvoiceModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { maxWidth: 500 }]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Create & Send Invoice</Text>
              <TouchableOpacity style={styles.modalCloseBtn} onPress={() => setShowInvoiceModal(false)}>
                <Icon name="x" size={20} color={colors.white} />
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
              {/* Client Info */}
              <View style={styles.invoiceClientInfo}>
                <Text style={styles.invoiceClientName}>{client.name}</Text>
                <Text style={styles.invoiceClientEmail}>{client.email || 'No email on file'}</Text>
              </View>
              
              {/* Line Items */}
              <View style={styles.invoiceSection}>
                <View style={styles.invoiceSectionHeader}>
                  <Text style={styles.invoiceSectionTitle}>Line Items</Text>
                  <TouchableOpacity style={styles.invoiceAddItemBtn} onPress={addInvoiceLineItem}>
                    <Icon name="plus" size={14} color={colors.teal} />
                    <Text style={styles.invoiceAddItemBtnText}>Add Item</Text>
                  </TouchableOpacity>
                </View>
                
                {invoiceData.lineItems.map((item, index) => (
                  <View key={index} style={styles.invoiceLineItem}>
                    <TextInput
                      style={[styles.invoiceLineItemInput, { flex: 2 }]}
                      value={item.description}
                      onChangeText={(val) => updateInvoiceLineItem(index, 'description', val)}
                      placeholder="Description"
                      placeholderTextColor={colors.gray}
                    />
                    <TextInput
                      style={[styles.invoiceLineItemInput, { width: 60 }]}
                      value={item.quantity ? item.quantity.toString() : ''}
                      onChangeText={(val) => updateInvoiceLineItem(index, 'quantity', val)}
                      placeholder="Qty"
                      placeholderTextColor={colors.gray}
                      keyboardType="decimal-pad"
                    />
                    <TextInput
                      style={[styles.invoiceLineItemInput, { width: 80 }]}
                      value={item.price ? item.price.toString() : ''}
                      onChangeText={(val) => updateInvoiceLineItem(index, 'price', val)}
                      placeholder="$0.00"
                      placeholderTextColor={colors.gray}
                      keyboardType="decimal-pad"
                    />
                    {invoiceData.lineItems.length > 1 && (
                      <TouchableOpacity 
                        style={styles.invoiceRemoveItemBtn} 
                        onPress={() => removeInvoiceLineItem(index)}
                      >
                        <Icon name="x" size={16} color={colors.red} />
                      </TouchableOpacity>
                    )}
                  </View>
                ))}
              </View>
              
              {/* Labor */}
              <View style={styles.invoiceSection}>
                <Text style={styles.invoiceSectionTitle}>Labor</Text>
                <View style={styles.invoiceLaborRow}>
                  <View style={styles.invoiceLaborField}>
                    <Text style={styles.invoiceLaborLabel}>Hours</Text>
                    <TextInput
                      style={styles.invoiceLaborInput}
                      value={invoiceData.laborHours ? invoiceData.laborHours.toString() : ''}
                      onChangeText={(val) => setInvoiceData({ ...invoiceData, laborHours: parseFloat(val) || 0 })}
                      placeholder="0"
                      placeholderTextColor={colors.gray}
                      keyboardType="decimal-pad"
                    />
                  </View>
                  <Text style={styles.invoiceLaborX}>×</Text>
                  <View style={styles.invoiceLaborField}>
                    <Text style={styles.invoiceLaborLabel}>Rate ($/hr)</Text>
                    <TextInput
                      style={styles.invoiceLaborInput}
                      value={invoiceData.laborRate ? invoiceData.laborRate.toString() : ''}
                      onChangeText={(val) => setInvoiceData({ ...invoiceData, laborRate: parseFloat(val) || 0 })}
                      placeholder="75"
                      placeholderTextColor={colors.gray}
                      keyboardType="decimal-pad"
                    />
                  </View>
                  <Text style={styles.invoiceLaborTotal}>=  ${((invoiceData.laborHours || 0) * (invoiceData.laborRate || 0)).toFixed(2)}</Text>
                </View>
              </View>
              
              {/* Materials */}
              <View style={styles.invoiceSection}>
                <Text style={styles.invoiceSectionTitle}>Materials</Text>
                <View style={styles.invoiceMaterialsRow}>
                  <Text style={styles.invoiceMaterialsLabel}>$</Text>
                  <TextInput
                    style={styles.invoiceMaterialsInput}
                    value={invoiceData.materialsCost ? invoiceData.materialsCost.toString() : ''}
                    onChangeText={(val) => setInvoiceData({ ...invoiceData, materialsCost: parseFloat(val) || 0 })}
                    placeholder="0.00"
                    placeholderTextColor={colors.gray}
                    keyboardType="decimal-pad"
                  />
                </View>
              </View>
              
              {/* Notes */}
              <View style={styles.invoiceSection}>
                <Text style={styles.invoiceSectionTitle}>Note to Client (optional)</Text>
                <TextInput
                  style={styles.invoiceNotesInput}
                  value={invoiceData.notes}
                  onChangeText={(val) => setInvoiceData({ ...invoiceData, notes: val })}
                  placeholder="Add a message to include with the invoice..."
                  placeholderTextColor={colors.gray}
                  multiline
                  numberOfLines={3}
                />
              </View>
              
              {/* Total */}
              <View style={styles.invoiceTotalSection}>
                <Text style={styles.invoiceTotalLabel}>Invoice Total</Text>
                <Text style={styles.invoiceTotalAmount}>${invoiceTotal.toFixed(2)}</Text>
              </View>
            </ScrollView>
            
            <View style={styles.modalFooter}>
              <TouchableOpacity style={styles.modalCancelBtn} onPress={() => setShowInvoiceModal(false)}>
                <Text style={styles.modalCancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.modalSaveBtn, (!client.email || invoiceTotal <= 0) && { opacity: 0.5 }]} 
                onPress={createAndSendInvoice}
                disabled={creatingInvoice || !client.email || invoiceTotal <= 0}
              >
                {creatingInvoice ? (
                  <ActivityIndicator size="small" color={colors.darkNavy} />
                ) : (
                  <>
                    <Icon name="send" size={16} color={colors.darkNavy} style={{marginRight: 6}} />
                    <Text style={styles.modalSaveBtnText}>Send Invoice</Text>
                  </>
                )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
      </Modal>
    </Modal>
  );
};

// ==================== LEADS & ESTIMATES TAB ====================
// View all incoming estimate requests and follow up on leads
const LeadsTab = ({ onConvertToAppointment }) => {
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // all, new, contacted, converted, archived
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, highest, lowest
  
  // Email compose modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Load estimates from Firestore
  useEffect(() => {
    const estimatesRef = collection(db, 'estimates');
    const q = query(estimatesRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const estimatesList = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        estimatesList.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
        });
      });
      setEstimates(estimatesList);
      setLoading(false);
    }, (error) => {
      console.error('Error loading estimates:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Update estimate status
  const updateEstimateStatus = async (estimateId, status) => {
    try {
      await updateDoc(doc(db, 'estimates', estimateId), {
        status,
        updatedAt: serverTimestamp(),
        ...(status === 'contacted' ? { contactedAt: serverTimestamp() } : {}),
      });
    } catch (error) {
      console.error('Error updating estimate:', error);
      alert('Failed to update status');
    }
  };

  // Archive estimate
  const archiveEstimate = async (estimateId) => {
    if (!confirm('Archive this estimate? It will be hidden from the main view.')) return;
    await updateEstimateStatus(estimateId, 'archived');
    setSelectedEstimate(null);
  };

  // Open email compose modal
  const openEmailCompose = (estimate) => {
    setEmailRecipient(estimate);
    setEmailSubject(`Following up on your Handy Hawk estimate`);
    setEmailBody(`Hi ${estimate.customerInfo?.name?.split(' ')[0] || 'there'},

Thank you for your interest in Handy Hawk! I wanted to follow up on your recent estimate request.

Your estimate was for: ${estimate.description?.substring(0, 100) || 'handyman services'}...

Estimated cost: $${estimate.lowEstimate || estimate.total} - $${estimate.highEstimate || estimate.total}

I'd love to help you get this project done. Would you like to schedule an appointment?

Feel free to call me directly or reply to this email.

Best regards,
Jesse

---
Handy Hawk - Your Trusted Las Vegas Handyman
📞 (702) 936-9959
✉️ jesse@handyhawk.com`);
    setShowEmailModal(true);
  };

  // Send the email
  const handleSendEmail = async () => {
    if (!emailRecipient?.customerInfo?.email || !emailSubject.trim() || !emailBody.trim()) {
      alert('Please fill in all fields');
      return;
    }

    setSendingEmail(true);
    try {
      await sendLeadFollowUp({
        clientEmail: emailRecipient.customerInfo.email,
        clientName: emailRecipient.customerInfo?.name || '',
        subject: emailSubject,
        body: emailBody,
        estimateId: emailRecipient.id
      });

      alert('Email sent successfully!');
      setShowEmailModal(false);
      setEmailSubject('');
      setEmailBody('');
      setEmailRecipient(null);
      
      // Update local state to show contacted
      setSelectedEstimate(prev => prev ? { ...prev, status: 'contacted' } : null);
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  // Filter and sort estimates
  const filteredEstimates = estimates
    .filter(e => {
      const matchesStatus = filterStatus === 'all' || e.status === filterStatus || 
        (filterStatus === 'new' && (!e.status || e.status === 'pending'));
      const matchesSearch = !searchQuery || 
        e.customerInfo?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.customerInfo?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.description?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'oldest': return new Date(a.createdAt) - new Date(b.createdAt);
        case 'highest': return (b.total || 0) - (a.total || 0);
        case 'lowest': return (a.total || 0) - (b.total || 0);
        default: return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

  // Count by status
  const statusCounts = {
    all: estimates.length,
    new: estimates.filter(e => !e.status || e.status === 'pending').length,
    contacted: estimates.filter(e => e.status === 'contacted').length,
    converted: estimates.filter(e => e.status === 'converted').length,
    archived: estimates.filter(e => e.status === 'archived').length,
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'contacted': return colors.blue;
      case 'converted': return colors.green;
      case 'archived': return colors.gray;
      default: return colors.gold; // new/pending
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'contacted': return 'Contacted';
      case 'converted': return 'Converted';
      case 'archived': return 'Archived';
      default: return 'New Lead';
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.gold} />
        <Text style={styles.loadingText}>Loading estimates...</Text>
      </View>
    );
  }

  return (
    <View style={styles.tabContainer}>
      {/* Header */}
      <View style={styles.tabHeader}>
        <View>
          <Text style={styles.tabTitle}>Leads & Estimates</Text>
          <Text style={styles.tabSubtitle}>
            {statusCounts.new} new lead{statusCounts.new !== 1 ? 's' : ''} awaiting follow-up
          </Text>
        </View>
      </View>

      {/* Stats Cards */}
      <View style={styles.leadsStatsRow}>
        <View style={[styles.leadsStatCard, { borderLeftColor: colors.gold }]}>
          <Text style={styles.leadsStatNumber}>{statusCounts.new}</Text>
          <Text style={styles.leadsStatLabel}>New Leads</Text>
        </View>
        <View style={[styles.leadsStatCard, { borderLeftColor: colors.blue }]}>
          <Text style={styles.leadsStatNumber}>{statusCounts.contacted}</Text>
          <Text style={styles.leadsStatLabel}>Contacted</Text>
        </View>
        <View style={[styles.leadsStatCard, { borderLeftColor: colors.green }]}>
          <Text style={styles.leadsStatNumber}>{statusCounts.converted}</Text>
          <Text style={styles.leadsStatLabel}>Converted</Text>
        </View>
        <View style={[styles.leadsStatCard, { borderLeftColor: colors.gray }]}>
          <Text style={styles.leadsStatNumber}>{estimates.length}</Text>
          <Text style={styles.leadsStatLabel}>Total</Text>
        </View>
      </View>

      {/* Filters */}
      <View style={styles.leadsFilters}>
        <View style={styles.leadsFilterTabs}>
          {[
            { id: 'all', label: 'All' },
            { id: 'new', label: 'New', badge: statusCounts.new },
            { id: 'contacted', label: 'Contacted' },
            { id: 'converted', label: 'Converted' },
            { id: 'archived', label: 'Archived' },
          ].map(tab => (
            <TouchableOpacity
              key={tab.id}
              style={[styles.leadsFilterTab, filterStatus === tab.id && styles.leadsFilterTabActive]}
              onPress={() => setFilterStatus(tab.id)}
            >
              <Text style={[styles.leadsFilterTabText, filterStatus === tab.id && styles.leadsFilterTabTextActive]}>
                {tab.label}
              </Text>
              {tab.badge > 0 && tab.id === 'new' && (
                <View style={styles.leadsFilterBadge}>
                  <Text style={styles.leadsFilterBadgeText}>{tab.badge}</Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </View>
        <View style={styles.leadsSearchSort}>
          <View style={styles.leadsSearchBox}>
            <Icon name="search" size={16} color={colors.gray} />
            <TextInput
              style={styles.leadsSearchInput}
              placeholder="Search leads..."
              placeholderTextColor={colors.gray}
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
          </View>
        </View>
      </View>

      {/* Estimates List */}
      <ScrollView style={styles.leadsList} showsVerticalScrollIndicator={false}>
        {filteredEstimates.length === 0 ? (
          <View style={styles.leadsEmptyState}>
            <Icon name="inbox" size={48} color={colors.gray} />
            <Text style={styles.leadsEmptyTitle}>No estimates found</Text>
            <Text style={styles.leadsEmptyText}>
              {filterStatus === 'new' 
                ? 'All leads have been followed up on!' 
                : 'Estimates from the website will appear here.'}
            </Text>
          </View>
        ) : (
          filteredEstimates.map(estimate => (
            <TouchableOpacity
              key={estimate.id}
              style={[
                styles.leadsCard,
                (!estimate.status || estimate.status === 'pending') && styles.leadsCardNew,
                selectedEstimate?.id === estimate.id && styles.leadsCardSelected
              ]}
              onPress={() => setSelectedEstimate(estimate)}
              activeOpacity={0.7}
            >
              <View style={styles.leadsCardHeader}>
                <View style={styles.leadsCardLeft}>
                  <View style={[styles.leadsStatusDot, { backgroundColor: getStatusColor(estimate.status) }]} />
                  <View>
                    <Text style={styles.leadsCardName}>
                      {estimate.customerInfo?.name || 'Unknown'}
                    </Text>
                    <Text style={styles.leadsCardEmail}>
                      {estimate.customerInfo?.email || 'No email'}
                    </Text>
                  </View>
                </View>
                <View style={styles.leadsCardRight}>
                  <Text style={styles.leadsCardAmount}>${estimate.total || 0}</Text>
                  <Text style={styles.leadsCardTime}>{formatDate(estimate.createdAt)}</Text>
                </View>
              </View>
              <Text style={styles.leadsCardDescription} numberOfLines={2}>
                {estimate.description || 'No description'}
              </Text>
              <View style={styles.leadsCardFooter}>
                <View style={[styles.leadsCardStatus, { backgroundColor: getStatusColor(estimate.status) + '20' }]}>
                  <Text style={[styles.leadsCardStatusText, { color: getStatusColor(estimate.status) }]}>
                    {getStatusLabel(estimate.status)}
                  </Text>
                </View>
                {estimate.customerInfo?.zipcode && (
                  <Text style={styles.leadsCardZip}>📍 {estimate.customerInfo.zipcode}</Text>
                )}
                {estimate.customerInfo?.phone && (
                  <Text style={styles.leadsCardPhone}>📞 {estimate.customerInfo.phone}</Text>
                )}
              </View>
            </TouchableOpacity>
          ))
        )}
      </ScrollView>

      {/* Detail Modal */}
      <Modal
        visible={!!selectedEstimate}
        transparent
        animationType="fade"
        onRequestClose={() => setSelectedEstimate(null)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { maxWidth: 600 }]}>
            {selectedEstimate && (
              <>
                <View style={styles.modalHeader}>
                  <View>
                    <Text style={styles.modalTitle}>Estimate Details</Text>
                    <Text style={styles.modalSubtitle}>
                      Submitted {formatDate(selectedEstimate.createdAt)}
                    </Text>
                  </View>
                  <TouchableOpacity onPress={() => setSelectedEstimate(null)}>
                    <Icon name="x" size={24} color={colors.gray} />
                  </TouchableOpacity>
                </View>

                <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
                  {/* Customer Info */}
                  <View style={styles.leadsDetailSection}>
                    <Text style={styles.leadsDetailSectionTitle}>Customer Information</Text>
                    <View style={styles.leadsDetailRow}>
                      <Icon name="user" size={16} color={colors.teal} />
                      <Text style={styles.leadsDetailText}>
                        {selectedEstimate.customerInfo?.name || 'Not provided'}
                      </Text>
                    </View>
                    <TouchableOpacity 
                      style={styles.leadsDetailRow}
                      onPress={() => window.open(`mailto:${selectedEstimate.customerInfo?.email}`)}
                    >
                      <Icon name="mail" size={16} color={colors.teal} />
                      <Text style={[styles.leadsDetailText, { color: colors.teal }]}>
                        {selectedEstimate.customerInfo?.email || 'Not provided'}
                      </Text>
                    </TouchableOpacity>
                    {selectedEstimate.customerInfo?.phone && (
                      <TouchableOpacity 
                        style={styles.leadsDetailRow}
                        onPress={() => window.open(`tel:${selectedEstimate.customerInfo.phone}`)}
                      >
                        <Icon name="phone" size={16} color={colors.teal} />
                        <Text style={[styles.leadsDetailText, { color: colors.teal }]}>
                          {selectedEstimate.customerInfo.phone}
                        </Text>
                      </TouchableOpacity>
                    )}
                    <View style={styles.leadsDetailRow}>
                      <Icon name="map-pin" size={16} color={colors.teal} />
                      <Text style={styles.leadsDetailText}>
                        {selectedEstimate.customerInfo?.zipcode || selectedEstimate.zipcode || 'Not provided'}
                      </Text>
                    </View>
                  </View>

                  {/* Job Description */}
                  <View style={styles.leadsDetailSection}>
                    <Text style={styles.leadsDetailSectionTitle}>Job Description</Text>
                    <Text style={styles.leadsDetailDescription}>
                      {selectedEstimate.description || 'No description provided'}
                    </Text>
                  </View>

                  {/* Estimate Breakdown */}
                  <View style={styles.leadsDetailSection}>
                    <Text style={styles.leadsDetailSectionTitle}>Estimate Breakdown</Text>
                    {selectedEstimate.breakdown?.map((item, idx) => (
                      <View key={idx} style={styles.leadsDetailBreakdownRow}>
                        <Text style={styles.leadsDetailBreakdownItem}>{item.name}</Text>
                        <Text style={styles.leadsDetailBreakdownPrice}>${item.total || item.price || 0}</Text>
                      </View>
                    )) || <Text style={styles.leadsDetailText}>No breakdown available</Text>}
                    
                    {selectedEstimate.travelFee > 0 && (
                      <View style={styles.leadsDetailBreakdownRow}>
                        <Text style={styles.leadsDetailBreakdownItem}>Travel Fee</Text>
                        <Text style={styles.leadsDetailBreakdownPrice}>${selectedEstimate.travelFee}</Text>
                      </View>
                    )}
                    
                    <View style={[styles.leadsDetailBreakdownRow, styles.leadsDetailBreakdownTotal]}>
                      <Text style={styles.leadsDetailBreakdownTotalLabel}>Estimated Total</Text>
                      <Text style={styles.leadsDetailBreakdownTotalPrice}>
                        ${selectedEstimate.lowEstimate || selectedEstimate.total} - ${selectedEstimate.highEstimate || selectedEstimate.total}
                      </Text>
                    </View>
                  </View>

                  {/* Status & Actions */}
                  <View style={styles.leadsDetailSection}>
                    <Text style={styles.leadsDetailSectionTitle}>Status</Text>
                    <View style={styles.leadsStatusButtons}>
                      {['pending', 'contacted', 'converted'].map(status => (
                        <TouchableOpacity
                          key={status}
                          style={[
                            styles.leadsStatusBtn,
                            (selectedEstimate.status === status || (!selectedEstimate.status && status === 'pending')) && 
                              { backgroundColor: getStatusColor(status), borderColor: getStatusColor(status) }
                          ]}
                          onPress={() => updateEstimateStatus(selectedEstimate.id, status)}
                        >
                          <Text style={[
                            styles.leadsStatusBtnText,
                            (selectedEstimate.status === status || (!selectedEstimate.status && status === 'pending')) && 
                              { color: colors.white }
                          ]}>
                            {status === 'pending' ? 'New' : status.charAt(0).toUpperCase() + status.slice(1)}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                </ScrollView>

                {/* Footer Actions */}
                <View style={styles.modalFooter}>
                  <TouchableOpacity 
                    style={styles.leadsArchiveBtn}
                    onPress={() => archiveEstimate(selectedEstimate.id)}
                  >
                    <Icon name="archive" size={16} color={colors.gray} />
                    <Text style={styles.leadsArchiveBtnText}>Archive</Text>
                  </TouchableOpacity>
                  <View style={styles.leadsActionBtns}>
                    {selectedEstimate.customerInfo?.email && (
                      <TouchableOpacity 
                        style={styles.leadsEmailBtn}
                        onPress={() => openEmailCompose(selectedEstimate)}
                      >
                        <Icon name="mail" size={16} color={colors.white} />
                        <Text style={styles.leadsEmailBtnText}>Send Email</Text>
                      </TouchableOpacity>
                    )}
                    {selectedEstimate.customerInfo?.phone && (
                      <TouchableOpacity 
                        style={styles.leadsCallBtn}
                        onPress={() => window.open(`tel:${selectedEstimate.customerInfo.phone}`)}
                      >
                        <Icon name="phone" size={16} color={colors.white} />
                        <Text style={styles.leadsCallBtnText}>Call</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                </View>
              </>
            )}
          </View>
        </View>
      </Modal>

      {/* Email Compose Modal */}
      <Modal
        visible={showEmailModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowEmailModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { maxWidth: 650, maxHeight: '90%' }]}>
            <View style={styles.modalHeader}>
              <View>
                <Text style={styles.modalTitle}>Compose Email</Text>
                <Text style={styles.modalSubtitle}>
                  To: {emailRecipient?.customerInfo?.email || 'Unknown'}
                </Text>
              </View>
              <TouchableOpacity onPress={() => setShowEmailModal(false)}>
                <Icon name="x" size={24} color={colors.gray} />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
              {/* Recipient Info */}
              <View style={styles.leadsEmailRecipient}>
                <View style={styles.leadsEmailRecipientIcon}>
                  <Icon name="user" size={20} color={colors.teal} />
                </View>
                <View>
                  <Text style={styles.leadsEmailRecipientName}>
                    {emailRecipient?.customerInfo?.name || 'Unknown'}
                  </Text>
                  <Text style={styles.leadsEmailRecipientEmail}>
                    {emailRecipient?.customerInfo?.email}
                  </Text>
                </View>
              </View>

              {/* Subject */}
              <View style={styles.leadsEmailField}>
                <Text style={styles.leadsEmailFieldLabel}>Subject</Text>
                <TextInput
                  style={styles.leadsEmailInput}
                  value={emailSubject}
                  onChangeText={setEmailSubject}
                  placeholder="Email subject..."
                  placeholderTextColor={colors.gray}
                />
              </View>

              {/* Body */}
              <View style={styles.leadsEmailField}>
                <Text style={styles.leadsEmailFieldLabel}>Message</Text>
                <TextInput
                  style={[styles.leadsEmailInput, styles.leadsEmailTextarea]}
                  value={emailBody}
                  onChangeText={setEmailBody}
                  placeholder="Write your message..."
                  placeholderTextColor={colors.gray}
                  multiline
                  numberOfLines={12}
                  textAlignVertical="top"
                />
              </View>

              {/* Quick Templates */}
              <View style={styles.leadsEmailTemplates}>
                <Text style={styles.leadsEmailTemplatesLabel}>Quick Templates:</Text>
                <View style={styles.leadsEmailTemplatesBtns}>
                  <TouchableOpacity 
                    style={styles.leadsEmailTemplateBtn}
                    onPress={() => {
                      setEmailSubject('Quick question about your estimate');
                      setEmailBody(`Hi ${emailRecipient?.customerInfo?.name?.split(' ')[0] || 'there'},

I noticed you requested an estimate recently and wanted to check in. Do you have any questions about the quote?

I'm available to discuss the project details or schedule a time that works for you.

Best,
Jesse

---
Handy Hawk - Your Trusted Las Vegas Handyman
📞 (702) 936-9959
✉️ jesse@handyhawk.com`);
                    }}
                  >
                    <Text style={styles.leadsEmailTemplateBtnText}>Quick Check-in</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={styles.leadsEmailTemplateBtn}
                    onPress={() => {
                      setEmailSubject('Ready to schedule your project?');
                      setEmailBody(`Hi ${emailRecipient?.customerInfo?.name?.split(' ')[0] || 'there'},

I wanted to follow up on your estimate request. I have some availability coming up and would love to help you get this project done.

Would any of the following times work for you?
- [Day/Time option 1]
- [Day/Time option 2]

Let me know what works best!

Best,
Jesse

---
Handy Hawk - Your Trusted Las Vegas Handyman
📞 (702) 936-9959
✉️ jesse@handyhawk.com`);
                    }}
                  >
                    <Text style={styles.leadsEmailTemplateBtnText}>Schedule Prompt</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={styles.leadsEmailTemplateBtn}
                    onPress={() => {
                      setEmailSubject('Special offer on your estimate');
                      setEmailBody(`Hi ${emailRecipient?.customerInfo?.name?.split(' ')[0] || 'there'},

I wanted to reach out with a special offer! If you book your project this week, I can offer you 10% off the estimated price.

Your original estimate: $${emailRecipient?.lowEstimate || emailRecipient?.total} - $${emailRecipient?.highEstimate || emailRecipient?.total}

Don't miss out - give me a call or reply to this email to schedule.

Best,
Jesse

---
Handy Hawk - Your Trusted Las Vegas Handyman
📞 (702) 936-9959
✉️ jesse@handyhawk.com`);
                    }}
                  >
                    <Text style={styles.leadsEmailTemplateBtnText}>Special Offer</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </ScrollView>

            {/* Footer */}
            <View style={styles.modalFooter}>
              <TouchableOpacity 
                style={styles.modalCancelBtn} 
                onPress={() => setShowEmailModal(false)}
              >
                <Text style={styles.modalCancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.leadsEmailSendBtn, sendingEmail && { opacity: 0.6 }]}
                onPress={handleSendEmail}
                disabled={sendingEmail}
              >
                {sendingEmail ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <>
                    <Icon name="send" size={16} color={colors.white} />
                    <Text style={styles.leadsEmailSendBtnText}>Send Email</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// ==================== FINANCES TAB ====================
// Track income, expenses, and financial data with export
const FinancesTab = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [filterType, setFilterType] = useState('all'); // all, income, expense
  const [dateRange, setDateRange] = useState('month'); // week, month, year, all
  const [newTransaction, setNewTransaction] = useState({
    type: 'income',
    category: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    notes: '',
  });
  
  // Use real-time listeners for both finances and payments
  useEffect(() => {
    let financesUnsubscribe;
    let paymentsUnsubscribe;
    let financesDocs = [];
    let paymentsDocs = [];
    
    const updateTransactions = () => {
      const allTransactions = [...financesDocs, ...paymentsDocs];
      allTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));
      setTransactions(allTransactions);
      setLoading(false);
    };
    
    // Listen to finances collection
    const financesRef = collection(db, 'finances');
    const financesQuery = query(financesRef, orderBy('date', 'desc'));
    financesUnsubscribe = onSnapshot(financesQuery, (snapshot) => {
      financesDocs = [];
      snapshot.forEach(doc => {
        financesDocs.push({ id: doc.id, ...doc.data() });
      });
      updateTransactions();
    }, (error) => {
      console.error('Error listening to finances:', error);
      setLoading(false);
    });
    
    // Listen to payments collection for completed payments
      const paymentsRef = collection(db, 'payments');
    paymentsUnsubscribe = onSnapshot(paymentsRef, (snapshot) => {
      paymentsDocs = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        // Include completed payments OR payments with amount/total
        if (data.status === 'completed' && (data.total || data.amount)) {
          paymentsDocs.push({
            id: `payment-${doc.id}`,
            type: 'income',
            category: 'Job Payment',
            description: `Payment from ${data.clientName || 'Client'}`,
            amount: data.total || data.amount,
            date: data.createdAt?.toDate?.()?.toISOString?.()?.split('T')[0] || new Date().toISOString().split('T')[0],
            paymentMethod: data.paymentMethod || '',
            isFromPayments: true,
            originalId: doc.id,
          });
        }
      });
      updateTransactions();
    }, (error) => {
      console.error('Error listening to payments:', error);
    });
    
    // Cleanup listeners on unmount
    return () => {
      if (financesUnsubscribe) financesUnsubscribe();
      if (paymentsUnsubscribe) paymentsUnsubscribe();
    };
  }, []);
  
  const addTransaction = async () => {
    if (!newTransaction.amount || !newTransaction.description) {
      alert('Please enter an amount and description');
      return;
    }
    
    setSaving(true);
    try {
      const transactionData = {
        ...newTransaction,
        amount: parseFloat(newTransaction.amount) || 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      
      const docRef = await addDoc(collection(db, 'finances'), transactionData);
      setTransactions([{ id: docRef.id, ...transactionData }, ...transactions]);
      setShowAddModal(false);
      setNewTransaction({
        type: 'income',
        category: '',
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        paymentMethod: '',
        notes: '',
      });
    } catch (error) {
      console.error('Error adding transaction:', error);
      alert('Failed to add transaction. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  const deleteTransaction = async (transactionId) => {
    if (!confirm('Are you sure you want to delete this transaction?')) return;
    
    // Don't allow deleting auto-imported payments
    const transaction = transactions.find(t => t.id === transactionId);
    if (transaction?.isFromPayments) {
      alert('This transaction was auto-imported from payments and cannot be deleted here.');
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'finances', transactionId));
      setTransactions(transactions.filter(t => t.id !== transactionId));
    } catch (error) {
      console.error('Error deleting transaction:', error);
    }
  };
  
  const updateTransaction = async (transactionId, updates) => {
    const transaction = transactions.find(t => t.id === transactionId);
    
    setSaving(true);
    try {
      if (transaction?.isFromPayments) {
        // Update the original payment in the payments collection
        const originalPaymentId = transaction.originalId;
        const paymentUpdates = {
          total: parseFloat(updates.amount) || transaction.amount,
          amount: parseFloat(updates.amount) || transaction.amount,
          clientName: updates.clientName || transaction.description?.replace('Payment from ', '') || '',
          paymentMethod: updates.paymentMethod || transaction.paymentMethod,
          notes: updates.notes || '',
          updatedAt: serverTimestamp(),
        };
        
        await updateDoc(doc(db, 'payments', originalPaymentId), paymentUpdates);
        
        // Also update client's totalSpent if amount changed
        if (updates.amount && parseFloat(updates.amount) !== transaction.amount) {
          const paymentDoc = await getDoc(doc(db, 'payments', originalPaymentId));
          if (paymentDoc.exists()) {
            const paymentData = paymentDoc.data();
            if (paymentData.clientId) {
              const clientDoc = await getDoc(doc(db, 'clients', paymentData.clientId));
              if (clientDoc.exists()) {
                const clientData = clientDoc.data();
                const amountDiff = parseFloat(updates.amount) - transaction.amount;
                await updateDoc(doc(db, 'clients', paymentData.clientId), {
                  totalSpent: (clientData.totalSpent || 0) + amountDiff,
                  updatedAt: serverTimestamp(),
                });
              }
            }
          }
        }
      } else {
        // Update regular finance transaction
      await updateDoc(doc(db, 'finances', transactionId), {
        ...updates,
          amount: parseFloat(updates.amount) || 0,
        updatedAt: serverTimestamp(),
      });
      }
      
      setShowEditModal(false);
      setEditingTransaction(null);
      // Real-time listeners will update the UI automatically
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert('Failed to update transaction. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Open edit modal with transaction data
  const openEditModal = (transaction) => {
    setEditingTransaction({
      ...transaction,
      amount: transaction.amount?.toString() || '',
      clientName: transaction.isFromPayments 
        ? transaction.description?.replace('Payment from ', '') 
        : '',
    });
    setShowEditModal(true);
  };
  
  // Filter transactions
  const getDateFilter = () => {
    const now = new Date();
    switch (dateRange) {
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return weekAgo.toISOString().split('T')[0];
      case 'month':
        const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        return monthAgo.toISOString().split('T')[0];
      case 'year':
        const yearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        return yearAgo.toISOString().split('T')[0];
      default:
        return null;
    }
  };
  
  const filteredTransactions = transactions.filter(t => {
    const typeMatch = filterType === 'all' || t.type === filterType;
    const dateFilter = getDateFilter();
    const dateMatch = !dateFilter || t.date >= dateFilter;
    return typeMatch && dateMatch;
  });
  
  // Calculate totals
  const totals = filteredTransactions.reduce((acc, t) => {
    if (t.type === 'income') {
      acc.income += t.amount || 0;
    } else {
      acc.expenses += t.amount || 0;
    }
    return acc;
  }, { income: 0, expenses: 0 });
  
  totals.profit = totals.income - totals.expenses;
  
  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Type', 'Category', 'Description', 'Amount', 'Payment Method', 'Notes'];
    const rows = filteredTransactions.map(t => [
      t.date,
      t.type,
      t.category || '',
      t.description || '',
      t.amount?.toFixed(2) || '0.00',
      t.paymentMethod || '',
      (t.notes || '').replace(/,/g, ';'),
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `handyhawk-finances-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };
  
  const INCOME_CATEGORIES = ['Job Payment', 'Deposit', 'Materials Markup', 'Service Fee', 'Tip', 'Other Income'];
  const EXPENSE_CATEGORIES = ['Materials', 'Tools', 'Gas/Fuel', 'Vehicle', 'Insurance', 'Marketing', 'Software', 'Office', 'Subcontractor', 'Other Expense'];
  
  if (loading) {
    return (
      <View style={styles.tabContent}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.teal} />
          <Text style={styles.loadingText}>Loading finances...</Text>
        </View>
      </View>
    );
  }
  
  return (
    <View style={styles.tabContent}>
      <View style={styles.tabHeader}>
        <View>
          <Text style={styles.tabTitle}>Finances</Text>
          <Text style={styles.tabSubtitle}>Track income, expenses, and profits</Text>
        </View>
        <View style={styles.financesHeaderBtns}>
          <TouchableOpacity style={styles.exportBtn} onPress={exportToCSV}>
            <Icon name="download" size={16} color={colors.teal} />
            <Text style={styles.exportBtnText}>Export CSV</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.primaryBtn} onPress={() => setShowAddModal(true)}>
            <Text style={styles.primaryBtnText}>+ Add Transaction</Text>
          </TouchableOpacity>
        </View>
      </View>
      
      {/* Summary Cards */}
      <View style={styles.financesSummaryRow}>
        <View style={[styles.financesSummaryCard, styles.financesSummaryIncome]}>
          <View style={[styles.financesSummaryIconWrap, { backgroundColor: colors.gold + '20' }]}>
            <Icon name="trending-up" size={24} color={colors.gold} />
          </View>
          <Text style={styles.financesSummaryLabel}>Total Income</Text>
          <Text style={[styles.financesSummaryValue, { color: colors.gold }]}>${totals.income.toFixed(2)}</Text>
        </View>
        <View style={[styles.financesSummaryCard, styles.financesSummaryExpense]}>
          <View style={[styles.financesSummaryIconWrap, { backgroundColor: colors.red + '20' }]}>
            <Icon name="trending-down" size={24} color={colors.red} />
          </View>
          <Text style={styles.financesSummaryLabel}>Total Expenses</Text>
          <Text style={[styles.financesSummaryValue, { color: colors.red }]}>${totals.expenses.toFixed(2)}</Text>
        </View>
        <View style={[styles.financesSummaryCard, styles.financesSummaryProfit]}>
          <View style={[styles.financesSummaryIconWrap, { backgroundColor: (totals.profit >= 0 ? colors.green : colors.red) + '20' }]}>
            <Icon name="dollar-sign" size={24} color={totals.profit >= 0 ? colors.green : colors.red} />
          </View>
          <Text style={styles.financesSummaryLabel}>Net Profit</Text>
          <Text style={[styles.financesSummaryValue, { color: totals.profit >= 0 ? colors.green : colors.red }]}>
            {totals.profit >= 0 ? '' : '-'}${Math.abs(totals.profit).toFixed(2)}
          </Text>
        </View>
      </View>
      
      {/* Filters */}
      <View style={styles.financesFilters}>
        <View style={styles.filterGroup}>
          <Text style={styles.filterGroupLabel}>Type:</Text>
          <View style={styles.filterBar}>
            {['all', 'income', 'expense'].map(type => (
              <TouchableOpacity
                key={type}
                style={[styles.filterBtn, filterType === type && styles.filterBtnActive]}
                onPress={() => setFilterType(type)}
              >
                <Text style={[styles.filterBtnText, filterType === type && styles.filterBtnTextActive]}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
        <View style={styles.filterGroup}>
          <Text style={styles.filterGroupLabel}>Period:</Text>
          <View style={styles.filterBar}>
            {['week', 'month', 'year', 'all'].map(range => (
              <TouchableOpacity
                key={range}
                style={[styles.filterBtn, dateRange === range && styles.filterBtnActive]}
                onPress={() => setDateRange(range)}
              >
                <Text style={[styles.filterBtnText, dateRange === range && styles.filterBtnTextActive]}>
                  {range.charAt(0).toUpperCase() + range.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
      
      {/* Transactions List */}
      <View style={styles.financesTableContainer}>
        <View style={styles.financesTableHeader}>
          <Text style={[styles.financesTableHeaderCell, { flex: 0.8 }]}>Date</Text>
          <Text style={[styles.financesTableHeaderCell, { flex: 0.6 }]}>Type</Text>
          <Text style={[styles.financesTableHeaderCell, { flex: 1 }]}>Category</Text>
          <Text style={[styles.financesTableHeaderCell, { flex: 1.5 }]}>Description</Text>
          <Text style={[styles.financesTableHeaderCell, { flex: 0.8, textAlign: 'right' }]}>Amount</Text>
          <Text style={[styles.financesTableHeaderCell, { flex: 0.5, textAlign: 'center' }]}>Actions</Text>
        </View>
        
        {filteredTransactions.length === 0 ? (
          <View style={styles.financesEmpty}>
            <Icon name="inbox" size={40} color={colors.gray} />
            <Text style={styles.financesEmptyText}>No transactions found</Text>
            <TouchableOpacity style={styles.financesEmptyBtn} onPress={() => setShowAddModal(true)}>
              <Text style={styles.financesEmptyBtnText}>Add your first transaction</Text>
            </TouchableOpacity>
          </View>
        ) : (
          filteredTransactions.map((transaction, idx) => (
            <View 
              key={transaction.id} 
              style={[
                styles.financesTableRow,
                idx % 2 === 0 && styles.financesTableRowAlt,
                transaction.isFromPayments && styles.financesTableRowImported,
              ]}
            >
              <Text style={[styles.financesTableCell, { flex: 0.8 }]}>{transaction.date}</Text>
              <View style={[styles.financesTableCell, { flex: 0.6 }]}>
                <View style={[
                  styles.financesTypeBadge,
                  transaction.type === 'income' ? styles.financesTypeBadgeIncome : styles.financesTypeBadgeExpense
                ]}>
                  <Icon 
                    name={transaction.type === 'income' ? 'arrow-down-left' : 'arrow-up-right'} 
                    size={12} 
                    color={transaction.type === 'income' ? colors.green : colors.red} 
                  />
                </View>
              </View>
              <Text style={[styles.financesTableCell, { flex: 1 }]}>{transaction.category || '—'}</Text>
              <View style={[styles.financesTableCell, { flex: 1.5 }]}>
                <Text style={styles.financesTableCellText} numberOfLines={1}>{transaction.description}</Text>
                {transaction.isFromPayments && (
                  <Text style={styles.financesImportedBadge}>Auto-imported</Text>
                )}
              </View>
              <Text style={[
                styles.financesTableCell, 
                { flex: 0.8, textAlign: 'right', fontWeight: '600' },
                transaction.type === 'income' ? { color: colors.green } : { color: colors.red }
              ]}>
                {transaction.type === 'income' ? '+' : '-'}${transaction.amount?.toFixed(2) || '0.00'}
              </Text>
              <View style={[styles.financesTableCell, { flex: 0.5, flexDirection: 'row', justifyContent: 'center', gap: 8 }]}>
                <TouchableOpacity onPress={() => openEditModal(transaction)}>
                  <Icon name="edit-2" size={16} color={colors.teal} />
                </TouchableOpacity>
                {!transaction.isFromPayments && (
                  <TouchableOpacity onPress={() => deleteTransaction(transaction.id)}>
                    <Icon name="trash-2" size={16} color={colors.red} />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          ))
        )}
      </View>
      
      {/* Add Transaction Modal */}
      <Modal visible={showAddModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.financesModal}>
            <View style={styles.financesModalHeader}>
              <View style={styles.financesModalHeaderIcon}>
                <Icon name={newTransaction.type === 'income' ? 'trending-up' : 'trending-down'} size={24} color={newTransaction.type === 'income' ? colors.gold : colors.red} />
              </View>
              <View>
                <Text style={styles.financesModalTitle}>Add Transaction</Text>
                <Text style={styles.financesModalSubtitle}>Record {newTransaction.type === 'income' ? 'income' : 'an expense'}</Text>
              </View>
              <TouchableOpacity style={styles.financesModalCloseBtn} onPress={() => setShowAddModal(false)}>
                <Icon name="x" size={24} color={colors.gray} />
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.financesModalBody} showsVerticalScrollIndicator={false}>
              {/* Transaction Type */}
              <View style={styles.financesModalSection}>
                <Text style={styles.financesModalSectionTitle}>Transaction Type</Text>
                <View style={styles.financesTypeToggle}>
                  <TouchableOpacity
                    style={[
                      styles.financesTypeToggleBtn,
                      newTransaction.type === 'income' && styles.financesTypeToggleBtnIncomeActive
                    ]}
                    onPress={() => setNewTransaction({ ...newTransaction, type: 'income', category: '' })}
                  >
                    <View style={[styles.financesTypeIcon, newTransaction.type === 'income' && { backgroundColor: colors.gold + '20' }]}>
                      <Icon name="trending-up" size={20} color={newTransaction.type === 'income' ? colors.gold : colors.gray} />
                    </View>
                    <Text style={[
                      styles.financesTypeToggleText,
                      newTransaction.type === 'income' && { color: colors.gold, fontWeight: '600' }
                    ]}>Income</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.financesTypeToggleBtn,
                      newTransaction.type === 'expense' && styles.financesTypeToggleBtnExpenseActive
                    ]}
                    onPress={() => setNewTransaction({ ...newTransaction, type: 'expense', category: '' })}
                  >
                    <View style={[styles.financesTypeIcon, newTransaction.type === 'expense' && { backgroundColor: colors.red + '20' }]}>
                      <Icon name="trending-down" size={20} color={newTransaction.type === 'expense' ? colors.red : colors.gray} />
                    </View>
                    <Text style={[
                      styles.financesTypeToggleText,
                      newTransaction.type === 'expense' && { color: colors.red, fontWeight: '600' }
                    ]}>Expense</Text>
                  </TouchableOpacity>
                </View>
              </View>
              
              {/* Amount - Prominent */}
              <View style={styles.financesModalSection}>
                <Text style={styles.financesModalSectionTitle}>Amount</Text>
                <View style={[
                  styles.financesAmountBox,
                  { borderColor: newTransaction.type === 'income' ? colors.gold + '40' : colors.red + '40' }
                ]}>
                  <Text style={[styles.financesAmountDollar, { color: newTransaction.type === 'income' ? colors.gold : colors.red }]}>$</Text>
                  <TextInput
                    style={[styles.financesAmountField, { color: newTransaction.type === 'income' ? colors.gold : colors.red }]}
                    value={newTransaction.amount}
                    onChangeText={(val) => setNewTransaction({ ...newTransaction, amount: val.replace(/[^0-9.]/g, '') })}
                    placeholder="0.00"
                    placeholderTextColor={colors.gray}
                    keyboardType={isWeb ? 'default' : 'decimal-pad'}
                  />
                </View>
              </View>
              
              {/* Category */}
              <View style={styles.financesModalSection}>
                <Text style={styles.financesModalSectionTitle}>Category</Text>
                <View style={styles.financesCategoryGrid}>
                  {(newTransaction.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map(cat => (
                    <TouchableOpacity
                      key={cat}
                      style={[
                        styles.financesCategoryBtn,
                        newTransaction.category === cat && (newTransaction.type === 'income' ? styles.financesCategoryBtnIncomeActive : styles.financesCategoryBtnExpenseActive)
                      ]}
                      onPress={() => setNewTransaction({ ...newTransaction, category: cat })}
                    >
                      <Text style={[
                        styles.financesCategoryBtnText,
                        newTransaction.category === cat && { color: newTransaction.type === 'income' ? colors.gold : colors.red, fontWeight: '600' }
                      ]}>{cat}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              
              {/* Details Section */}
              <View style={styles.financesModalSection}>
                <Text style={styles.financesModalSectionTitle}>Details</Text>
                
                {/* Date */}
                <View style={styles.financesInputGroup}>
                  <View style={styles.financesInputIcon}>
                    <Icon name="calendar" size={16} color={colors.teal} />
                  </View>
                  <TextInput
                    style={styles.financesInputField}
                    value={newTransaction.date}
                    onChangeText={(val) => setNewTransaction({ ...newTransaction, date: val })}
                    placeholder="Date (YYYY-MM-DD)"
                    placeholderTextColor={colors.gray}
                  />
                </View>
                
                {/* Description */}
                <View style={styles.financesInputGroup}>
                  <View style={styles.financesInputIcon}>
                    <Icon name="file-text" size={16} color={colors.teal} />
                  </View>
                  <TextInput
                    style={styles.financesInputField}
                    value={newTransaction.description}
                    onChangeText={(val) => setNewTransaction({ ...newTransaction, description: val })}
                    placeholder="Description (e.g., Payment for kitchen repair)"
                    placeholderTextColor={colors.gray}
                  />
                </View>
                
                {/* Payment Method */}
                <View style={styles.financesInputGroup}>
                  <View style={styles.financesInputIcon}>
                    <Icon name="credit-card" size={16} color={colors.teal} />
                  </View>
                  <TextInput
                    style={styles.financesInputField}
                    value={newTransaction.paymentMethod}
                    onChangeText={(val) => setNewTransaction({ ...newTransaction, paymentMethod: val })}
                    placeholder="Payment method (optional)"
                    placeholderTextColor={colors.gray}
                  />
                </View>
                
                {/* Notes */}
                <View style={[styles.financesInputGroup, { alignItems: 'flex-start' }]}>
                  <View style={[styles.financesInputIcon, { marginTop: 12 }]}>
                    <Icon name="edit-3" size={16} color={colors.teal} />
                  </View>
                  <TextInput
                    style={[styles.financesInputField, styles.financesInputFieldMulti]}
                    value={newTransaction.notes}
                    onChangeText={(val) => setNewTransaction({ ...newTransaction, notes: val })}
                    placeholder="Notes (optional)"
                    placeholderTextColor={colors.gray}
                    multiline
                    numberOfLines={3}
                  />
                </View>
              </View>
            </ScrollView>
            
            <View style={styles.financesModalFooter}>
              <TouchableOpacity style={styles.financesModalCancelBtn} onPress={() => setShowAddModal(false)}>
                <Text style={styles.financesModalCancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[
                  styles.financesModalSaveBtn, 
                  saving && { opacity: 0.7 },
                  { backgroundColor: newTransaction.type === 'income' ? colors.gold : colors.teal }
                ]} 
                onPress={addTransaction}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color={colors.darkNavy} />
                ) : (
                  <>
                    <Icon name={newTransaction.type === 'income' ? 'plus-circle' : 'minus-circle'} size={18} color={colors.darkNavy} />
                    <Text style={styles.financesModalSaveBtnText}>
                      Add {newTransaction.type === 'income' ? 'Income' : 'Expense'}
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Edit Transaction Modal */}
      <Modal visible={showEditModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.financesModal}>
            <View style={styles.financesModalHeader}>
              <View style={styles.financesModalHeaderIcon}>
                <Icon name="edit-2" size={24} color={colors.teal} />
              </View>
              <View>
                <Text style={styles.financesModalTitle}>Edit Transaction</Text>
                <Text style={styles.financesModalSubtitle}>
                  {editingTransaction?.isFromPayments ? 'Edit payment record' : 'Update transaction details'}
                </Text>
              </View>
              <TouchableOpacity style={styles.financesModalCloseBtn} onPress={() => { setShowEditModal(false); setEditingTransaction(null); }}>
                <Icon name="x" size={24} color={colors.gray} />
              </TouchableOpacity>
            </View>
            
            {editingTransaction && (
              <ScrollView style={styles.financesModalBody} showsVerticalScrollIndicator={false}>
                {/* Amount */}
                <View style={styles.financesModalSection}>
                  <Text style={styles.financesModalSectionTitle}>Amount</Text>
                  <View style={[
                    styles.financesAmountBox,
                    { borderColor: editingTransaction.type === 'income' ? colors.gold + '40' : colors.red + '40' }
                  ]}>
                    <Text style={[styles.financesAmountDollar, { color: editingTransaction.type === 'income' ? colors.gold : colors.red }]}>$</Text>
                    <TextInput
                      style={[styles.financesAmountField, { color: editingTransaction.type === 'income' ? colors.gold : colors.red }]}
                      value={editingTransaction.amount}
                      onChangeText={(val) => setEditingTransaction({ ...editingTransaction, amount: val.replace(/[^0-9.]/g, '') })}
                      placeholder="0.00"
                      placeholderTextColor={colors.gray}
                      keyboardType={isWeb ? 'default' : 'decimal-pad'}
                    />
                  </View>
                </View>

                {/* Client Name (for payments) */}
                {editingTransaction.isFromPayments && (
                  <View style={styles.financesModalSection}>
                    <Text style={styles.financesModalSectionTitle}>Client Name</Text>
                    <View style={styles.financesInputGroup}>
                      <View style={styles.financesInputIcon}>
                        <Icon name="user" size={16} color={colors.teal} />
                      </View>
                      <TextInput
                        style={styles.financesInputField}
                        value={editingTransaction.clientName}
                        onChangeText={(val) => setEditingTransaction({ ...editingTransaction, clientName: val })}
                        placeholder="Client name"
                        placeholderTextColor={colors.gray}
                      />
                    </View>
                  </View>
                )}

                {/* Category (for non-payments) */}
                {!editingTransaction.isFromPayments && (
                  <View style={styles.financesModalSection}>
                    <Text style={styles.financesModalSectionTitle}>Category</Text>
                    <View style={styles.financesCategoryGrid}>
                      {(editingTransaction.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map(cat => (
                        <TouchableOpacity
                          key={cat}
                          style={[
                            styles.financesCategoryBtn,
                            editingTransaction.category === cat && (editingTransaction.type === 'income' ? styles.financesCategoryBtnIncomeActive : styles.financesCategoryBtnExpenseActive)
                          ]}
                          onPress={() => setEditingTransaction({ ...editingTransaction, category: cat })}
                        >
                          <Text style={[
                            styles.financesCategoryBtnText,
                            editingTransaction.category === cat && { color: editingTransaction.type === 'income' ? colors.gold : colors.red, fontWeight: '600' }
                          ]}>{cat}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                )}

                {/* Details */}
                <View style={styles.financesModalSection}>
                  <Text style={styles.financesModalSectionTitle}>Details</Text>
                  
                  {/* Date (for non-payments) */}
                  {!editingTransaction.isFromPayments && (
                    <View style={styles.financesInputGroup}>
                      <View style={styles.financesInputIcon}>
                        <Icon name="calendar" size={16} color={colors.teal} />
                      </View>
                      <TextInput
                        style={styles.financesInputField}
                        value={editingTransaction.date}
                        onChangeText={(val) => setEditingTransaction({ ...editingTransaction, date: val })}
                        placeholder="Date (YYYY-MM-DD)"
                        placeholderTextColor={colors.gray}
                      />
                    </View>
                  )}

                  {/* Description (for non-payments) */}
                  {!editingTransaction.isFromPayments && (
                    <View style={styles.financesInputGroup}>
                      <View style={styles.financesInputIcon}>
                        <Icon name="file-text" size={16} color={colors.teal} />
                      </View>
                      <TextInput
                        style={styles.financesInputField}
                        value={editingTransaction.description}
                        onChangeText={(val) => setEditingTransaction({ ...editingTransaction, description: val })}
                        placeholder="Description"
                        placeholderTextColor={colors.gray}
                      />
                    </View>
                  )}

                  {/* Payment Method */}
                  <View style={styles.financesInputGroup}>
                    <View style={styles.financesInputIcon}>
                      <Icon name="credit-card" size={16} color={colors.teal} />
                    </View>
                    <TextInput
                      style={styles.financesInputField}
                      value={editingTransaction.paymentMethod}
                      onChangeText={(val) => setEditingTransaction({ ...editingTransaction, paymentMethod: val })}
                      placeholder="Payment method"
                      placeholderTextColor={colors.gray}
                    />
                  </View>

                  {/* Notes */}
                  <View style={[styles.financesInputGroup, { alignItems: 'flex-start' }]}>
                    <View style={[styles.financesInputIcon, { marginTop: 12 }]}>
                      <Icon name="edit-3" size={16} color={colors.teal} />
                    </View>
                    <TextInput
                      style={[styles.financesInputField, styles.financesInputFieldMulti]}
                      value={editingTransaction.notes || ''}
                      onChangeText={(val) => setEditingTransaction({ ...editingTransaction, notes: val })}
                      placeholder="Notes (optional)"
                      placeholderTextColor={colors.gray}
                      multiline
                      numberOfLines={3}
                    />
                  </View>
                </View>

                {/* Info badge for payments */}
                {editingTransaction.isFromPayments && (
                  <View style={styles.financesEditInfoBox}>
                    <Icon name="info" size={16} color={colors.blue} />
                    <Text style={styles.financesEditInfoText}>
                      This payment was recorded from a checkout. Changes will update the original payment record and client's total spent.
                    </Text>
                  </View>
                )}
              </ScrollView>
            )}

            <View style={styles.financesModalFooter}>
              <TouchableOpacity style={styles.financesModalCancelBtn} onPress={() => { setShowEditModal(false); setEditingTransaction(null); }}>
                <Text style={styles.financesModalCancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.financesModalSaveBtn, saving && { opacity: 0.7 }]} 
                onPress={() => updateTransaction(editingTransaction.id, editingTransaction)}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color={colors.darkNavy} />
                ) : (
                  <>
                    <Icon name="check" size={18} color={colors.darkNavy} />
                    <Text style={styles.financesModalSaveBtnText}>Save Changes</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// ==================== PAYMENT COLLECTION FLOW ====================
// 3-Step Payment Collection with Testimonial Integration
const PaymentCollectionModal = ({ visible, client, onClose, onComplete }) => {
  const [step, setStep] = useState(1); // 1: Price, 2: Review & Choose, 3: Payment/Thank You
  const [loading, setLoading] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState({
    testimonialDiscount: 10, // percentage
    customMessage: 'Thank you for choosing HandyHawk! We appreciate your business.',
    testimonialPrompt: 'We\'d love to hear about your experience! Leave a review and save {discount}% on your bill.',
    venmoHandle: '@HandyHawk',
    cashappHandle: '$HandyHawk',
    zelleEmail: 'payments@handyhawk.net',
  });
  
  // Invoice data
  const [invoice, setInvoice] = useState({
    items: [
      { description: 'Service Call', quantity: 1, price: 50 },
    ],
    laborHours: 0,
    laborRate: 75,
    materialsCost: 0,
    notes: '',
  });
  
  // Testimonial data
  const [testimonialData, setTestimonialData] = useState({
    rating: 5,
    text: '',
  });
  
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [withTestimonial, setWithTestimonial] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);

  // Load payment settings from Firestore
  useEffect(() => {
    if (visible) {
      loadPaymentSettings();
    }
  }, [visible]);

  const loadPaymentSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'payment'));
      if (settingsDoc.exists()) {
        setPaymentSettings({ ...paymentSettings, ...settingsDoc.data() });
      }
    } catch (error) {
      console.log('Using default payment settings');
    }
  };

  // Calculate totals
  const subtotal = invoice.items.reduce((sum, item) => sum + (item.quantity * item.price), 0) +
                   (invoice.laborHours * invoice.laborRate) +
                   invoice.materialsCost;
  
  const discountAmount = withTestimonial ? (subtotal * paymentSettings.testimonialDiscount / 100) : 0;
  const total = subtotal - discountAmount;

  const addInvoiceItem = () => {
    setInvoice({
      ...invoice,
      items: [...invoice.items, { description: '', quantity: 1, price: 0 }]
    });
  };

  const updateInvoiceItem = (index, field, value) => {
    const newItems = [...invoice.items];
    newItems[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    setInvoice({ ...invoice, items: newItems });
  };

  const removeInvoiceItem = (index) => {
    const newItems = invoice.items.filter((_, i) => i !== index);
    setInvoice({ ...invoice, items: newItems });
  };

  const handleChooseWithTestimonial = () => {
    setWithTestimonial(true);
    setStep(3);
  };

  const handleChooseWithoutTestimonial = () => {
    setWithTestimonial(false);
    setStep(3);
  };

  const submitTestimonialAndPay = async () => {
    if (withTestimonial && (!testimonialData.text || testimonialData.text.length < 10)) {
      alert('Please write at least a few words about your experience!');
      return;
    }
    
    setLoading(true);
    try {
      // Generate invoice ID
      const invoiceId = `HH-${Date.now().toString(36).toUpperCase()}`;
      
      // Save testimonial if provided
      if (withTestimonial) {
        await addDoc(collection(db, 'testimonials'), {
          name: client.name,
          location: client.address?.split(',').slice(-2).join(',').trim() || 'Las Vegas, NV',
          text: testimonialData.text,
          rating: testimonialData.rating,
          active: false, // Admin needs to approve
          source: 'payment_flow',
          clientId: client.id,
          createdAt: serverTimestamp(),
        });
      }
      
      // Update client stats
      await updateDoc(doc(db, 'clients', client.id), {
        totalJobs: (client.totalJobs || 0) + 1,
        totalSpent: (client.totalSpent || 0) + total,
        lastJob: new Date().toISOString().split('T')[0],
        updatedAt: serverTimestamp(),
      });
      
      // Build line items for receipt
      const receiptLineItems = [
        ...invoice.items.map(item => ({
          name: item.description || 'Service Item',
          quantity: item.quantity,
          price: (item.quantity * item.price).toFixed(2)
        }))
      ];
      
      // Add labor if applicable
      if (invoice.laborHours > 0) {
        receiptLineItems.push({
          name: `Labor (${invoice.laborHours} hrs @ $${invoice.laborRate}/hr)`,
          quantity: 1,
          price: (invoice.laborHours * invoice.laborRate).toFixed(2)
        });
      }
      
      // Add materials if applicable
      if (invoice.materialsCost > 0) {
        receiptLineItems.push({
          name: 'Materials',
          quantity: 1,
          price: invoice.materialsCost.toFixed(2)
        });
      }
      
      // Add discount if testimonial was provided
      if (withTestimonial && discountAmount > 0) {
        receiptLineItems.push({
          name: `Review Discount (${paymentSettings.testimonialDiscount}%)`,
          quantity: 1,
          price: `-${discountAmount.toFixed(2)}`
        });
      }
      
      // Handle card payments via Stripe
      let stripePaymentLink = null;
      if (selectedPaymentMethod === 'card' && client.email) {
        try {
          const paymentIntentResult = await createPaymentIntent({
            amount: total,
            clientEmail: client.email,
            clientName: client.name,
            invoiceId,
            description: invoice.items.map(i => i.description).join(', ')
          });
          
          if (paymentIntentResult.data?.success) {
            // For now, we'll note that a payment intent was created
            // In a full implementation, you'd have a payment page
            stripePaymentLink = `https://handyhawk.org/pay/${invoiceId}`;
            console.log('Stripe payment intent created:', paymentIntentResult.data.paymentIntentId);
          }
        } catch (stripeError) {
          console.error('Error creating Stripe payment:', stripeError);
          // Continue without Stripe if it fails
        }
      }
      
      // Save payment record
      const paymentStatus = selectedPaymentMethod === 'card' ? 'pending' : 'completed';
      const paymentRef = await addDoc(collection(db, 'payments'), {
        invoiceId,
        clientId: client.id,
        clientName: client.name,
        clientEmail: client.email,
        amount: total,
        subtotal,
        discount: discountAmount,
        withTestimonial,
        paymentMethod: selectedPaymentMethod,
        items: invoice.items,
        laborHours: invoice.laborHours,
        laborRate: invoice.laborRate,
        materialsCost: invoice.materialsCost,
        notes: invoice.notes,
        status: paymentStatus,
        stripePaymentLink,
        createdAt: serverTimestamp(),
      });
      
      // Send itemized receipt/invoice email if client has email
      if (client.email) {
        try {
          const emailDueDate = selectedPaymentMethod === 'card' 
            ? 'Due Upon Receipt - Pay via link below'
            : 'Paid - Thank you!';
          
          await sendInvoiceEmail({
            clientEmail: client.email,
            clientName: client.name,
            invoiceId,
            lineItems: receiptLineItems,
            total: total.toFixed(2),
            dueDate: emailDueDate,
            paymentLink: stripePaymentLink
          });
          console.log('Invoice/receipt email sent successfully');
        } catch (emailError) {
          console.error('Error sending invoice email:', emailError);
          // Don't fail the whole payment if email fails
        }
        
        // Send follow-up request for review (if they didn't leave one during payment)
        if (!withTestimonial) {
          // Schedule follow-up email for later (only for completed payments)
          if (paymentStatus === 'completed') {
            try {
              // In production, use Firebase scheduled functions instead
              console.log('Follow-up email will be sent via scheduled function');
            } catch (followUpError) {
              console.error('Error scheduling follow-up:', followUpError);
            }
          }
        }
      }
      
      setPaymentComplete(true);
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setStep(1);
    setWithTestimonial(false);
    setPaymentComplete(false);
    setSelectedPaymentMethod(null);
    setTestimonialData({ rating: 5, text: '' });
    setInvoice({
      items: [{ description: 'Service Call', quantity: 1, price: 50 }],
      laborHours: 0,
      laborRate: 75,
      materialsCost: 0,
      notes: '',
    });
    onClose();
  };

  if (!visible) return null;

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.paymentModalOverlay}>
        <View style={styles.paymentModalContent}>
          {/* Header */}
          <View style={styles.paymentModalHeader}>
            <View style={styles.paymentModalHeaderLeft}>
              {step > 1 && !paymentComplete && (
                <TouchableOpacity onPress={() => setStep(step - 1)} style={styles.paymentBackBtn}>
                  <Text style={styles.paymentBackBtnText}>← Back</Text>
                </TouchableOpacity>
              )}
            </View>
            <View style={styles.paymentModalHeaderCenter}>
              <Text style={styles.paymentModalTitle}>
                {paymentComplete ? '✅ Complete' : `Step ${step} of 3`}
              </Text>
            </View>
            <TouchableOpacity onPress={resetAndClose} style={styles.paymentCloseBtn}>
              <Text style={styles.paymentCloseBtnText}>✕</Text>
            </TouchableOpacity>
          </View>

          {/* Progress Bar */}
          {!paymentComplete && (
            <View style={styles.paymentProgress}>
              {[1, 2, 3].map(s => (
                <View 
                  key={s} 
                  style={[
                    styles.paymentProgressStep,
                    s <= step && styles.paymentProgressStepActive,
                    s < step && styles.paymentProgressStepComplete
                  ]}
                >
                  <Text style={styles.paymentProgressStepText}>
                    {s < step ? '✓' : s}
                  </Text>
        </View>
      ))}
              <View style={styles.paymentProgressLine}>
                <View style={[styles.paymentProgressLineFill, { width: `${((step - 1) / 2) * 100}%` }]} />
    </View>
  </View>
          )}

          <ScrollView style={styles.paymentModalBody} showsVerticalScrollIndicator={false}>
            {/* ==================== STEP 1: CALCULATE PRICE ==================== */}
            {step === 1 && (
              <View style={styles.paymentStep}>
                <Text style={styles.paymentStepTitle}>💰 Calculate Total</Text>
                <Text style={styles.paymentStepSubtitle}>Add line items for {client?.name}</Text>

                {/* Client Info Card */}
                <View style={styles.paymentClientCard}>
                  <View style={styles.paymentClientAvatar}>
                    <Text style={styles.paymentClientAvatarText}>{client?.name?.charAt(0)}</Text>
                  </View>
                  <View style={styles.paymentClientInfo}>
                    <Text style={styles.paymentClientName}>{client?.name}</Text>
                    <Text style={styles.paymentClientContact}>{client?.phone}</Text>
                  </View>
                </View>

                {/* Line Items */}
                <View style={styles.paymentSection}>
                  <Text style={styles.paymentSectionLabel}>Line Items</Text>
                  {invoice.items.map((item, index) => (
                    <View key={index} style={styles.paymentLineItem}>
                      <TextInput
                        style={[styles.paymentInput, { flex: 2 }]}
                        value={item.description}
                        onChangeText={(val) => updateInvoiceItem(index, 'description', val)}
                        placeholder="Description"
                        placeholderTextColor={colors.gray}
                      />
                      <TextInput
                        style={[styles.paymentInput, styles.paymentInputSmall]}
                        value={String(item.quantity)}
                        onChangeText={(val) => updateInvoiceItem(index, 'quantity', val)}
                        keyboardType="numeric"
                        placeholder="Qty"
                        placeholderTextColor={colors.gray}
                      />
                      <View style={styles.paymentInputWithPrefix}>
                        <Text style={styles.paymentInputPrefix}>$</Text>
                        <TextInput
                          style={[styles.paymentInput, styles.paymentInputPrice]}
                          value={String(item.price)}
                          onChangeText={(val) => updateInvoiceItem(index, 'price', val)}
                          keyboardType="numeric"
                          placeholder="0"
                          placeholderTextColor={colors.gray}
                        />
                      </View>
                      {invoice.items.length > 1 && (
                        <TouchableOpacity 
                          style={styles.paymentRemoveItem} 
                          onPress={() => removeInvoiceItem(index)}
                        >
                          <Text style={styles.paymentRemoveItemText}>✕</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  ))}
                  <TouchableOpacity style={styles.paymentAddItem} onPress={addInvoiceItem}>
                    <Text style={styles.paymentAddItemText}>+ Add Item</Text>
                  </TouchableOpacity>
                </View>

                {/* Labor */}
                <View style={styles.paymentSection}>
                  <Text style={styles.paymentSectionLabel}>Labor</Text>
                  <View style={styles.paymentRow}>
                    <View style={styles.paymentFieldHalf}>
                      <Text style={styles.paymentFieldLabel}>Hours</Text>
                      <TextInput
                        style={styles.paymentInput}
                        value={String(invoice.laborHours)}
                        onChangeText={(val) => setInvoice({ ...invoice, laborHours: parseFloat(val) || 0 })}
                        keyboardType="numeric"
                        placeholder="0"
                        placeholderTextColor={colors.gray}
                      />
                    </View>
                    <View style={styles.paymentFieldHalf}>
                      <Text style={styles.paymentFieldLabel}>Rate/hr</Text>
                      <View style={styles.paymentInputWithPrefix}>
                        <Text style={styles.paymentInputPrefix}>$</Text>
                        <TextInput
                          style={[styles.paymentInput, { flex: 1 }]}
                          value={String(invoice.laborRate)}
                          onChangeText={(val) => setInvoice({ ...invoice, laborRate: parseFloat(val) || 0 })}
                          keyboardType="numeric"
                          placeholder="75"
                          placeholderTextColor={colors.gray}
                        />
                      </View>
                    </View>
                  </View>
                </View>

                {/* Materials */}
                <View style={styles.paymentSection}>
                  <Text style={styles.paymentSectionLabel}>Materials Cost</Text>
                  <View style={styles.paymentInputWithPrefix}>
                    <Text style={styles.paymentInputPrefix}>$</Text>
                    <TextInput
                      style={[styles.paymentInput, { flex: 1 }]}
                      value={String(invoice.materialsCost)}
                      onChangeText={(val) => setInvoice({ ...invoice, materialsCost: parseFloat(val) || 0 })}
                      keyboardType="numeric"
                      placeholder="0"
                      placeholderTextColor={colors.gray}
                    />
                  </View>
                </View>

                {/* Total Preview */}
                <View style={styles.paymentTotalCard}>
                  <Text style={styles.paymentTotalLabel}>Subtotal</Text>
                  <Text style={styles.paymentTotalAmount}>${subtotal.toFixed(2)}</Text>
                </View>
              </View>
            )}

            {/* ==================== STEP 2: REVIEW & CHOOSE ==================== */}
            {step === 2 && (
              <View style={styles.paymentStep}>
                <Text style={styles.paymentStepTitle}>📋 Review Invoice</Text>
                <Text style={styles.paymentStepSubtitle}>Choose how to proceed</Text>

                {/* Invoice Summary */}
                <View style={styles.paymentInvoiceCard}>
                  <View style={styles.paymentInvoiceHeader}>
                    <Text style={styles.paymentInvoiceLogo}>HANDY<Text style={{ color: colors.teal }}>HAWK</Text></Text>
                    <Text style={styles.paymentInvoiceDate}>{new Date().toLocaleDateString()}</Text>
                  </View>

                  <View style={styles.paymentInvoiceClientSection}>
                    <Text style={styles.paymentInvoiceLabel}>Bill To:</Text>
                    <Text style={styles.paymentInvoiceClientName}>{client?.name}</Text>
                    <Text style={styles.paymentInvoiceClientDetail}>{client?.phone}</Text>
                    {client?.email && <Text style={styles.paymentInvoiceClientDetail}>{client?.email}</Text>}
                    {client?.address && <Text style={styles.paymentInvoiceClientDetail}>{client?.address}</Text>}
                  </View>

                  <View style={styles.paymentInvoiceDivider} />

                  {/* Line Items */}
                  {invoice.items.map((item, index) => (
                    <View key={index} style={styles.paymentInvoiceRow}>
                      <Text style={styles.paymentInvoiceItemName}>
                        {item.description || 'Item'} × {item.quantity}
                      </Text>
                      <Text style={styles.paymentInvoiceItemPrice}>
                        ${(item.quantity * item.price).toFixed(2)}
                      </Text>
                    </View>
                  ))}

                  {invoice.laborHours > 0 && (
                    <View style={styles.paymentInvoiceRow}>
                      <Text style={styles.paymentInvoiceItemName}>
                        Labor ({invoice.laborHours}hrs × ${invoice.laborRate}/hr)
                      </Text>
                      <Text style={styles.paymentInvoiceItemPrice}>
                        ${(invoice.laborHours * invoice.laborRate).toFixed(2)}
                      </Text>
                    </View>
                  )}

                  {invoice.materialsCost > 0 && (
                    <View style={styles.paymentInvoiceRow}>
                      <Text style={styles.paymentInvoiceItemName}>Materials</Text>
                      <Text style={styles.paymentInvoiceItemPrice}>
                        ${invoice.materialsCost.toFixed(2)}
                      </Text>
                    </View>
                  )}

                  <View style={styles.paymentInvoiceDivider} />

                  <View style={styles.paymentInvoiceTotalRow}>
                    <Text style={styles.paymentInvoiceTotalLabel}>Total Due</Text>
                    <Text style={styles.paymentInvoiceTotalAmount}>${subtotal.toFixed(2)}</Text>
                  </View>
                </View>

                {/* Testimonial Offer */}
                <View style={styles.paymentTestimonialOffer}>
                  <View style={styles.paymentTestimonialOfferIcon}>
                    <Text style={styles.paymentTestimonialOfferIconText}>⭐</Text>
                  </View>
                  <View style={styles.paymentTestimonialOfferContent}>
                    <Text style={styles.paymentTestimonialOfferTitle}>
                      Save {paymentSettings.testimonialDiscount}% with a Review!
                    </Text>
                    <Text style={styles.paymentTestimonialOfferText}>
                      {paymentSettings.testimonialPrompt.replace('{discount}', paymentSettings.testimonialDiscount)}
                    </Text>
                    <Text style={styles.paymentTestimonialOfferSavings}>
                      You'd pay ${(subtotal - (subtotal * paymentSettings.testimonialDiscount / 100)).toFixed(2)} instead of ${subtotal.toFixed(2)}
                    </Text>
                  </View>
                </View>

                {/* Action Buttons */}
                <View style={styles.paymentChoiceButtons}>
                  <TouchableOpacity 
                    style={styles.paymentChoicePrimary}
                    onPress={handleChooseWithTestimonial}
                  >
                    <Text style={styles.paymentChoicePrimaryIcon}>⭐</Text>
                    <Text style={styles.paymentChoicePrimaryText}>Leave Review & Pay</Text>
                    <Text style={styles.paymentChoicePrimarySave}>Save ${(subtotal * paymentSettings.testimonialDiscount / 100).toFixed(2)}</Text>
                  </TouchableOpacity>

                  <TouchableOpacity 
                    style={styles.paymentChoiceSecondary}
                    onPress={handleChooseWithoutTestimonial}
                  >
                    <Text style={styles.paymentChoiceSecondaryText}>Pay ${subtotal.toFixed(2)} without review</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {/* ==================== STEP 3: TESTIMONIAL & PAYMENT ==================== */}
            {step === 3 && !paymentComplete && (
              <View style={styles.paymentStep}>
                <Text style={styles.paymentStepTitle}>
                  {withTestimonial ? '⭐ Leave Your Review' : '💳 Complete Payment'}
                </Text>
                <Text style={styles.paymentStepSubtitle}>
                  {withTestimonial 
                    ? `You're saving ${paymentSettings.testimonialDiscount}% with this review!`
                    : 'Choose your payment method'}
                </Text>

                {/* Testimonial Form (if chosen) */}
                {withTestimonial && (
                  <View style={styles.paymentTestimonialForm}>
                    <Text style={styles.paymentSectionLabel}>Your Rating</Text>
                    <View style={styles.paymentRatingStars}>
                      {[1, 2, 3, 4, 5].map(star => (
                        <TouchableOpacity 
                          key={star} 
                          onPress={() => setTestimonialData({ ...testimonialData, rating: star })}
                        >
                          <Text style={[
                            styles.paymentRatingStar,
                            star <= testimonialData.rating && styles.paymentRatingStarActive
                          ]}>
                            ★
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>

                    <Text style={styles.paymentSectionLabel}>Your Review</Text>
                    <TextInput
                      style={[styles.paymentInput, styles.paymentTextarea]}
                      value={testimonialData.text}
                      onChangeText={(val) => setTestimonialData({ ...testimonialData, text: val })}
                      placeholder="Tell us about your experience with HandyHawk..."
                      placeholderTextColor={colors.gray}
                      multiline
                      numberOfLines={4}
                    />
                  </View>
                )}

                {/* Final Amount */}
                <View style={styles.paymentFinalAmount}>
                  {withTestimonial && (
                    <View style={styles.paymentFinalDiscount}>
                      <Text style={styles.paymentFinalDiscountText}>
                        Review Discount ({paymentSettings.testimonialDiscount}%)
                      </Text>
                      <Text style={styles.paymentFinalDiscountAmount}>
                        -${discountAmount.toFixed(2)}
                      </Text>
                    </View>
                  )}
                  <View style={styles.paymentFinalTotal}>
                    <Text style={styles.paymentFinalTotalLabel}>Amount Due</Text>
                    <Text style={styles.paymentFinalTotalAmount}>${total.toFixed(2)}</Text>
                  </View>
                </View>

                {/* Payment Methods */}
                <View style={styles.paymentMethods}>
                  <Text style={styles.paymentSectionLabel}>Payment Method</Text>
                  
                  <View style={styles.paymentMethodGrid}>
                    {[
                      { id: 'venmo', name: 'Venmo', icon: '💜', handle: paymentSettings.venmoHandle },
                      { id: 'cashapp', name: 'Cash App', icon: '💚', handle: paymentSettings.cashappHandle },
                      { id: 'zelle', name: 'Zelle', icon: '💙', handle: paymentSettings.zelleEmail },
                      { id: 'cash', name: 'Cash', icon: '💵', handle: 'Hand to Hand' },
                      { id: 'check', name: 'Check', icon: '📝', handle: 'To HandyHawk' },
                      { id: 'card', name: 'Card', icon: 'credit-card', handle: 'Visa/MC/Amex' },
                    ].map(method => (
                      <TouchableOpacity
                        key={method.id}
                        style={[
                          styles.paymentMethodCard,
                          selectedPaymentMethod === method.id && styles.paymentMethodCardActive,
                        ]}
                        onPress={() => setSelectedPaymentMethod(method.id)}
                      >
                        <Text style={styles.paymentMethodIcon}>{method.icon}</Text>
                        <Text style={styles.paymentMethodName}>{method.name}</Text>
                        <Text style={styles.paymentMethodHandle}>{method.handle}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                {/* Payment Instructions */}
                {selectedPaymentMethod && (
                  <View style={styles.paymentInstructions}>
                    <Text style={styles.paymentInstructionsTitle}>
                      {selectedPaymentMethod === 'cash' ? '💵 Cash Payment' :
                       selectedPaymentMethod === 'check' ? '📝 Check Payment' :
                       selectedPaymentMethod === 'card' ? '💳 Card Payment' :
                       `Send $${total.toFixed(2)} via ${selectedPaymentMethod.charAt(0).toUpperCase() + selectedPaymentMethod.slice(1)}`}
                    </Text>
                    <Text style={styles.paymentInstructionsText}>
                      {selectedPaymentMethod === 'cash' ? 'Collect cash from customer and confirm payment below.' :
                       selectedPaymentMethod === 'check' ? 'Make check payable to "HandyHawk" and confirm receipt below.' :
                       selectedPaymentMethod === 'card' ? 'A secure payment link will be sent to the customer\'s email. You can also use the Stripe dashboard to process the card.' :
                       `Send to: ${paymentSettings[`${selectedPaymentMethod}Handle`] || paymentSettings[`${selectedPaymentMethod}Email`] || paymentSettings.venmoHandle}`}
                    </Text>
                  </View>
                )}

                {/* Complete Button */}
                <TouchableOpacity 
                  style={[
                    styles.paymentCompleteBtn,
                    (!selectedPaymentMethod || (withTestimonial && testimonialData.text.length < 10)) && styles.paymentCompleteBtnDisabled
                  ]}
                  onPress={submitTestimonialAndPay}
                  disabled={loading || !selectedPaymentMethod || (withTestimonial && testimonialData.text.length < 10)}
                >
                  {loading ? (
                    <ActivityIndicator color={colors.darkNavy} />
                  ) : (
                    <>
                      <Text style={styles.paymentCompleteBtnText}>
                        {withTestimonial ? 'Submit Review & Confirm Payment' : 'Confirm Payment'}
                      </Text>
                      <Text style={styles.paymentCompleteBtnAmount}>${total.toFixed(2)}</Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            )}

            {/* ==================== SUCCESS SCREEN ==================== */}
            {paymentComplete && (
              <View style={styles.paymentSuccess}>
                <View style={styles.paymentSuccessIcon}>
                  <Text style={styles.paymentSuccessIconText}>✅</Text>
                </View>
                <Text style={styles.paymentSuccessTitle}>Payment Complete!</Text>
                <Text style={styles.paymentSuccessAmount}>${total.toFixed(2)}</Text>
                
                {withTestimonial && (
                  <View style={styles.paymentSuccessBadge}>
                    <Text style={styles.paymentSuccessBadgeText}>
                      ⭐ Review Submitted - Saved ${discountAmount.toFixed(2)}!
                    </Text>
                  </View>
                )}

                <Text style={styles.paymentSuccessMessage}>
                  {paymentSettings.customMessage}
                </Text>

                <TouchableOpacity 
                  style={styles.paymentSuccessBtn}
                  onPress={() => {
                    onComplete && onComplete();
                    resetAndClose();
                  }}
                >
                  <Text style={styles.paymentSuccessBtnText}>Done</Text>
                </TouchableOpacity>
              </View>
            )}
          </ScrollView>

          {/* Next Button for Step 1 */}
          {step === 1 && (
            <View style={styles.paymentModalFooter}>
              <TouchableOpacity 
                style={styles.paymentNextBtn}
                onPress={() => setStep(2)}
              >
                <Text style={styles.paymentNextBtnText}>Review Invoice →</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

// Business Hours Tab (Site Management > Hours)
const HoursTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeView, setActiveView] = useState('weekly'); // 'weekly' or 'calendar'
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [hours, setHours] = useState({
    monday: { open: '08:00', close: '17:00', isOpen: true },
    tuesday: { open: '08:00', close: '17:00', isOpen: true },
    wednesday: { open: '08:00', close: '17:00', isOpen: true },
    thursday: { open: '08:00', close: '17:00', isOpen: true },
    friday: { open: '08:00', close: '17:00', isOpen: true },
    saturday: { open: '08:00', close: '17:00', isOpen: true },
    sunday: { open: '08:00', close: '17:00', isOpen: false },
  });
  const [dateOverrides, setDateOverrides] = useState({}); // { '2025-12-25': { closed: true, reason: 'Christmas' } }
  const [selectedDate, setSelectedDate] = useState(null);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideForm, setOverrideForm] = useState({ closed: true, reason: '', open: '08:00', close: '17:00' });

  const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const calendarDayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  useEffect(() => {
    loadHours();
  }, []);

  const loadHours = async () => {
    try {
      const hoursDoc = await getDoc(doc(db, 'settings', 'hours'));
      if (hoursDoc.exists()) {
        const data = hoursDoc.data();
        setHours({
          monday: data.monday || hours.monday,
          tuesday: data.tuesday || hours.tuesday,
          wednesday: data.wednesday || hours.wednesday,
          thursday: data.thursday || hours.thursday,
          friday: data.friday || hours.friday,
          saturday: data.saturday || hours.saturday,
          sunday: data.sunday || hours.sunday,
        });
        if (data.dateOverrides) {
          setDateOverrides(data.dateOverrides);
        }
      }
    } catch (error) {
      console.log('Error loading hours:', error);
    }
    setLoading(false);
  };

  const saveHours = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'hours'), { ...hours, dateOverrides });
      alert('Hours saved successfully!');
    } catch (error) {
      console.error('Error saving hours:', error);
      alert('Failed to save hours');
    }
    setSaving(false);
  };

  const toggleDay = (day) => {
    setHours(prev => ({
      ...prev,
      [day]: { ...prev[day], isOpen: !prev[day].isOpen }
    }));
  };

  const updateTime = (day, field, value) => {
    setHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes || '00'} ${ampm}`;
  };

  const getTodayHours = () => {
    const todayStr = new Date().toISOString().split('T')[0];
    // Check for override first
    if (dateOverrides[todayStr]) {
      if (dateOverrides[todayStr].closed) return `Closed (${dateOverrides[todayStr].reason || 'Day Off'})`;
      return `${formatTime(dateOverrides[todayStr].open)} - ${formatTime(dateOverrides[todayStr].close)}`;
    }
    const today = dayNames[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];
    const todayData = hours[today];
    if (!todayData?.isOpen) return 'Closed';
    return `${formatTime(todayData.open)} - ${formatTime(todayData.close)}`;
  };

  // Calendar generation
  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    // Padding for first week
    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, date: null });
    }
    // Actual days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const dateStr = date.toISOString().split('T')[0];
      const dayOfWeek = date.getDay();
      const dayName = dayNames[dayOfWeek === 0 ? 6 : dayOfWeek - 1];
      const defaultHours = hours[dayName];
      const override = dateOverrides[dateStr];
      
      days.push({
        day: i,
        date: dateStr,
        dayName,
        defaultHours,
        override,
        isOpen: override ? !override.closed : defaultHours?.isOpen,
        isPast: date < new Date(new Date().toISOString().split('T')[0])
      });
    }
    return days;
  };

  const openOverrideModal = (dateStr) => {
    setSelectedDate(dateStr);
    const existing = dateOverrides[dateStr];
    setOverrideForm(existing || { closed: true, reason: '', open: '08:00', close: '17:00' });
    setShowOverrideModal(true);
  };

  const saveOverride = () => {
    setDateOverrides(prev => ({
      ...prev,
      [selectedDate]: overrideForm
    }));
    setShowOverrideModal(false);
  };

  const removeOverride = () => {
    setDateOverrides(prev => {
      const newOverrides = { ...prev };
      delete newOverrides[selectedDate];
      return newOverrides;
    });
    setShowOverrideModal(false);
  };

  const navigateMonth = (direction) => {
    setCalendarMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.teal} />
        <Text style={styles.loadingText}>Loading hours...</Text>
      </View>
    );
  }

  const calendarDays = getCalendarDays();

  return (
  <View style={styles.tabContent}>
    <View style={styles.tabHeader}>
      <View>
          <Text style={styles.tabTitle}>Business Hours</Text>
          <Text style={styles.tabSubtitle}>Set your weekly schedule and manage specific date closures</Text>
      </View>
        <TouchableOpacity 
          style={[styles.primaryBtn, saving && styles.primaryBtnDisabled]} 
          onPress={saveHours}
          disabled={saving}
        >
          <Icon name={saving ? 'loader' : 'save'} size={16} color="#0a0f1a" style={{marginRight: 8}} />
          <Text style={styles.primaryBtnText}>{saving ? 'Saving...' : 'Save All'}</Text>
      </TouchableOpacity>
    </View>

      {/* Current Status Preview */}
      <View style={styles.hoursPreview}>
        <Text style={styles.hoursPreviewLabel}>Currently showing on site:</Text>
        <View style={styles.hoursPreviewBadge}>
          <View style={styles.hoursPreviewIcon}>
            <Icon name="clock" size={16} color={colors.gold} />
          </View>
          <Text style={styles.hoursPreviewText}>Today: {getTodayHours()}</Text>
        </View>
    </View>

      {/* View Toggle */}
      <View style={styles.hoursViewToggle}>
        <TouchableOpacity 
          style={[styles.hoursViewBtn, activeView === 'weekly' && styles.hoursViewBtnActive]}
          onPress={() => setActiveView('weekly')}
        >
          <Icon name="list" size={16} color={activeView === 'weekly' ? colors.darkNavy : colors.gray} />
          <Text style={[styles.hoursViewBtnText, activeView === 'weekly' && styles.hoursViewBtnTextActive]}>Weekly Schedule</Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.hoursViewBtn, activeView === 'calendar' && styles.hoursViewBtnActive]}
          onPress={() => setActiveView('calendar')}
        >
          <Icon name="calendar" size={16} color={activeView === 'calendar' ? colors.darkNavy : colors.gray} />
          <Text style={[styles.hoursViewBtnText, activeView === 'calendar' && styles.hoursViewBtnTextActive]}>Calendar View</Text>
        </TouchableOpacity>
      </View>

      {activeView === 'weekly' ? (
        <>
      {/* Hours Editor */}
      <View style={styles.hoursGrid}>
        {dayNames.map((day, idx) => (
          <View key={day} style={styles.hoursRow}>
            <View style={styles.hoursDaySection}>
              <TouchableOpacity 
                style={[styles.hoursToggle, hours[day].isOpen && styles.hoursToggleOn]}
                onPress={() => toggleDay(day)}
              >
                <View style={[styles.hoursToggleDot, hours[day].isOpen && styles.hoursToggleDotOn]} />
              </TouchableOpacity>
              <Text style={[styles.hoursDayName, !hours[day].isOpen && styles.hoursDayNameClosed]}>
                {dayLabels[idx]}
              </Text>
          </View>
            
            {hours[day].isOpen ? (
              <View style={styles.hoursTimeSection}>
                <View style={styles.hoursTimeInput}>
                  <Text style={styles.hoursTimeLabel}>Open</Text>
                  <TextInput
                    style={styles.hoursInput}
                    value={hours[day].open}
                    onChangeText={(val) => updateTime(day, 'open', val)}
                    placeholder="08:00"
                    placeholderTextColor={colors.gray}
                  />
          </View>
                <Text style={styles.hoursTimeSep}>to</Text>
                <View style={styles.hoursTimeInput}>
                  <Text style={styles.hoursTimeLabel}>Close</Text>
                  <TextInput
                    style={styles.hoursInput}
                    value={hours[day].close}
                    onChangeText={(val) => updateTime(day, 'close', val)}
                    placeholder="17:00"
                    placeholderTextColor={colors.gray}
                  />
          </View>
              </View>
            ) : (
              <Text style={styles.hoursClosedText}>Closed</Text>
            )}
        </View>
      ))}
        </View>
        </>
      ) : (
        <>
          {/* Calendar View */}
          <View style={styles.hoursCalendarContainer}>
            <View style={styles.hoursCalendarNav}>
              <TouchableOpacity style={styles.hoursCalendarNavBtn} onPress={() => navigateMonth(-1)}>
                <Icon name="chevron-left" size={20} color={colors.white} />
              </TouchableOpacity>
              <Text style={styles.hoursCalendarTitle}>
                {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
              </Text>
              <TouchableOpacity style={styles.hoursCalendarNavBtn} onPress={() => navigateMonth(1)}>
                <Icon name="chevron-right" size={20} color={colors.white} />
              </TouchableOpacity>
    </View>
            
            <View style={styles.hoursCalendarHeader}>
              {calendarDayNames.map(d => (
                <View key={d} style={styles.hoursCalendarHeaderCell}>
                  <Text style={styles.hoursCalendarHeaderText}>{d}</Text>
                </View>
              ))}
            </View>
            
            <View style={styles.hoursCalendarGrid}>
              {calendarDays.map((day, idx) => (
                <TouchableOpacity 
                  key={idx}
                  style={[
                    styles.hoursCalendarCell,
                    day.isPast && styles.hoursCalendarCellPast,
                    !day.isOpen && day.day && styles.hoursCalendarCellClosed,
                    day.override && styles.hoursCalendarCellOverride
                  ]}
                  onPress={() => day.day && !day.isPast && openOverrideModal(day.date)}
                  disabled={!day.day || day.isPast}
                >
                  {day.day && (
                    <>
                      <Text style={[
                        styles.hoursCalendarDayNum,
                        !day.isOpen && styles.hoursCalendarDayNumClosed,
                        day.date === new Date().toISOString().split('T')[0] && styles.hoursCalendarDayNumToday
                      ]}>
                        {day.day}
                      </Text>
                      {day.override && (
                        <View style={[styles.hoursCalendarBadge, { backgroundColor: day.override.closed ? colors.red : colors.teal }]}>
                          <Icon name={day.override.closed ? 'x' : 'check'} size={8} color={colors.white} />
                        </View>
                      )}
                      {!day.isOpen && !day.override && (
                        <Text style={styles.hoursCalendarCellText}>Off</Text>
                      )}
                    </>
                  )}
                </TouchableOpacity>
              ))}
            </View>
            
            {/* Legend */}
            <View style={styles.hoursCalendarLegend}>
              <View style={styles.hoursCalendarLegendItem}>
                <View style={[styles.hoursCalendarLegendDot, { backgroundColor: colors.teal }]} />
                <Text style={styles.hoursCalendarLegendText}>Open (default)</Text>
              </View>
              <View style={styles.hoursCalendarLegendItem}>
                <View style={[styles.hoursCalendarLegendDot, { backgroundColor: colors.red }]} />
                <Text style={styles.hoursCalendarLegendText}>Closed / Off</Text>
              </View>
              <View style={styles.hoursCalendarLegendItem}>
                <View style={[styles.hoursCalendarLegendDot, { backgroundColor: colors.gold }]} />
                <Text style={styles.hoursCalendarLegendText}>Custom hours</Text>
              </View>
            </View>
            
            <Text style={styles.hoursCalendarHint}>Click a date to set custom hours or mark as closed</Text>
          </View>

          {/* Active Overrides List */}
          {Object.keys(dateOverrides).length > 0 && (
            <View style={styles.hoursOverridesList}>
              <Text style={styles.hoursOverridesTitle}>Scheduled Closures & Custom Hours</Text>
              {Object.entries(dateOverrides)
                .sort(([a], [b]) => a.localeCompare(b))
                .filter(([date]) => date >= new Date().toISOString().split('T')[0])
                .map(([date, override]) => (
                  <View key={date} style={styles.hoursOverrideItem}>
                    <View style={styles.hoursOverrideInfo}>
                      <Text style={styles.hoursOverrideDate}>
                        {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </Text>
                      <Text style={styles.hoursOverrideStatus}>
                        {override.closed ? `Closed${override.reason ? ` - ${override.reason}` : ''}` : 
                         `Custom: ${formatTime(override.open)} - ${formatTime(override.close)}`}
                      </Text>
                    </View>
                    <TouchableOpacity 
                      style={styles.hoursOverrideRemove}
                      onPress={() => { setSelectedDate(date); removeOverride(); }}
                    >
                      <Icon name="trash-2" size={14} color={colors.red} />
                    </TouchableOpacity>
                  </View>
                ))}
            </View>
          )}
        </>
      )}

      <View style={styles.hoursNote}>
        <View style={styles.hoursNoteIcon}>
          <Icon name="info" size={16} color={colors.gold} />
        </View>
        <Text style={styles.hoursNoteText}>
          Use 24-hour format (e.g., 08:00 for 8 AM, 17:00 for 5 PM). 
          Hours automatically display in the navbar and block calendar bookings on closed days.
        </Text>
    </View>

      {/* Override Modal */}
      <Modal visible={showOverrideModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.hoursOverrideModal}>
            <View style={styles.hoursOverrideModalHeader}>
              <Text style={styles.hoursOverrideModalTitle}>
                {selectedDate && new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { 
                  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
                })}
              </Text>
              <TouchableOpacity onPress={() => setShowOverrideModal(false)}>
                <Icon name="x" size={24} color={colors.white} />
              </TouchableOpacity>
            </View>
            
            <View style={styles.hoursOverrideModalBody}>
              <View style={styles.hoursOverrideToggleRow}>
                <TouchableOpacity 
                  style={[styles.hoursOverrideToggleBtn, overrideForm.closed && styles.hoursOverrideToggleBtnActive]}
                  onPress={() => setOverrideForm(prev => ({ ...prev, closed: true }))}
                >
                  <Icon name="x-circle" size={18} color={overrideForm.closed ? colors.darkNavy : colors.gray} />
                  <Text style={[styles.hoursOverrideToggleBtnText, overrideForm.closed && styles.hoursOverrideToggleBtnTextActive]}>
                    Mark as Closed
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity 
                  style={[styles.hoursOverrideToggleBtn, !overrideForm.closed && styles.hoursOverrideToggleBtnActive]}
                  onPress={() => setOverrideForm(prev => ({ ...prev, closed: false }))}
                >
                  <Icon name="clock" size={18} color={!overrideForm.closed ? colors.darkNavy : colors.gray} />
                  <Text style={[styles.hoursOverrideToggleBtnText, !overrideForm.closed && styles.hoursOverrideToggleBtnTextActive]}>
                    Custom Hours
                  </Text>
                </TouchableOpacity>
              </View>
              
              {overrideForm.closed ? (
                <View style={styles.hoursOverrideField}>
                  <Text style={styles.hoursOverrideFieldLabel}>Reason (optional)</Text>
                  <TextInput
                    style={styles.hoursOverrideInput}
                    value={overrideForm.reason}
                    onChangeText={(val) => setOverrideForm(prev => ({ ...prev, reason: val }))}
                    placeholder="e.g., Holiday, Vacation, etc."
                    placeholderTextColor={colors.gray}
                  />
                </View>
              ) : (
                <View style={styles.hoursOverrideTimeRow}>
                  <View style={styles.hoursOverrideField}>
                    <Text style={styles.hoursOverrideFieldLabel}>Open</Text>
                    <TextInput
                      style={styles.hoursOverrideInput}
                      value={overrideForm.open}
                      onChangeText={(val) => setOverrideForm(prev => ({ ...prev, open: val }))}
                      placeholder="08:00"
                      placeholderTextColor={colors.gray}
                    />
                  </View>
                  <Text style={styles.hoursOverrideTimeSep}>to</Text>
                  <View style={styles.hoursOverrideField}>
                    <Text style={styles.hoursOverrideFieldLabel}>Close</Text>
                    <TextInput
                      style={styles.hoursOverrideInput}
                      value={overrideForm.close}
                      onChangeText={(val) => setOverrideForm(prev => ({ ...prev, close: val }))}
                      placeholder="17:00"
                      placeholderTextColor={colors.gray}
                    />
                  </View>
                </View>
              )}
            </View>
            
            <View style={styles.hoursOverrideModalActions}>
              {dateOverrides[selectedDate] && (
                <TouchableOpacity style={styles.hoursOverrideRemoveBtn} onPress={removeOverride}>
                  <Icon name="trash-2" size={16} color={colors.red} />
                  <Text style={styles.hoursOverrideRemoveBtnText}>Remove Override</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={styles.hoursOverrideSaveBtn} onPress={saveOverride}>
                <Icon name="check" size={16} color={colors.darkNavy} />
                <Text style={styles.hoursOverrideSaveBtnText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
  </View>
);
};

// Services Display Tab (Site Management > Services)
const ServicesTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [editingService, setEditingService] = useState(null);
  const [showAllServices, setShowAllServices] = useState(false);

  const defaultServices = [
    { id: '1', iconName: 'tool', title: 'General Repairs', desc: "Fixing what's broken around your home", color: '#d4a84b', active: true, order: 1, detailedDesc: 'Our general repair services cover everything from squeaky doors to broken fixtures. We handle minor repairs quickly and efficiently.', autoTypeText: 'I need help with some general repairs around my home. ' },
    { id: '2', iconName: 'hammer', iconType: 'material', title: 'Carpentry', desc: 'Custom woodwork and installations', color: '#ef4444', active: true, order: 2, detailedDesc: 'From custom shelving to trim work and deck repairs, our carpentry services bring craftsmanship to every project.', autoTypeText: 'I need carpentry work done. ' },
    { id: '3', iconName: 'layout', title: 'Doors & Windows', desc: 'Installation and repair services', color: '#3b82f6', active: true, order: 3, detailedDesc: 'We install and repair interior and exterior doors, windows, and screens.', autoTypeText: 'I need help with my doors or windows. ' },
    { id: '4', iconName: 'edit-2', title: 'Painting', desc: 'Interior and exterior painting', color: '#a855f7', active: true, order: 4, detailedDesc: 'Transform your space with professional painting services. Interior and exterior projects welcome.', autoTypeText: 'I need painting services for my home. ' },
    { id: '5', iconName: 'zap', title: 'Electrical', desc: 'Light fixtures and outlet repairs', color: '#f59e0b', active: true, order: 5, detailedDesc: 'Safe, reliable electrical work including light fixture installation and outlet repairs.', autoTypeText: 'I have an electrical problem that needs attention. ' },
    { id: '6', iconName: 'droplet', title: 'Plumbing', desc: 'Leak repairs and installations', color: '#06b6d4', active: true, order: 6, detailedDesc: 'From leaky faucets to clogged drains, we handle all your plumbing needs.', autoTypeText: 'I need help with plumbing issues. ' },
    { id: '7', iconName: 'layers', title: 'Renovations', desc: 'Transform your living spaces', color: '#f97316', active: true, order: 7, detailedDesc: 'Ready for a change? We handle bathroom updates, kitchen refreshes, and room transformations.', autoTypeText: 'I am looking to renovate part of my home. ' },
    { id: '8', iconName: 'home', title: 'Roofing', desc: 'Roof repairs and maintenance', color: '#64748b', active: true, order: 8, detailedDesc: 'Protect your home with professional roof repairs and preventive maintenance.', autoTypeText: 'I need roofing repairs or inspection. ' },
    { id: '9', iconName: 'square', title: 'Drywall', desc: 'Patching and installation', color: '#78716c', active: false, order: 9, detailedDesc: 'Expert drywall repair, patching, and installation for walls and ceilings.', autoTypeText: 'I need drywall repair or installation. ' },
    { id: '10', iconName: 'grid', title: 'Tile & Grout', desc: 'Tile repair and regrouting', color: '#0ea5e9', active: false, order: 10, detailedDesc: 'Tile repair, regrouting, and minor tile installation services.', autoTypeText: 'I need help with tile or grout work. ' },
    { id: '11', iconName: 'sidebar', title: 'Garage Doors', desc: 'Opener installation and repair', color: '#84cc16', active: false, order: 11, detailedDesc: 'Garage door opener installation, repair, and maintenance services.', autoTypeText: 'I need help with my garage door. ' },
    { id: '12', iconName: 'package', title: 'Assembly', desc: 'Furniture and equipment assembly', color: '#ec4899', active: false, order: 12, detailedDesc: 'Professional assembly of furniture, equipment, and home gym setups.', autoTypeText: 'I need help assembling furniture or equipment. ' },
    { id: '13', iconName: 'tv', title: 'TV Mounting', desc: 'Professional TV installation', color: '#8b5cf6', active: false, order: 13, detailedDesc: 'Professional TV mounting with cable management and bracket installation.', autoTypeText: 'I need help mounting my TV. ' },
    { id: '14', iconName: 'wind', title: 'Pressure Washing', desc: 'Exterior cleaning services', color: '#14b8a6', active: false, order: 14, detailedDesc: 'Professional pressure washing for driveways, patios, and home exteriors.', autoTypeText: 'I need pressure washing services. ' },
    { id: '15', iconName: 'lock', title: 'Locks & Security', desc: 'Lock installation and repair', color: '#f43f5e', active: false, order: 15, detailedDesc: 'Lock installation, rekeying, and basic security improvements for your home.', autoTypeText: 'I need help with locks or home security. ' },
    { id: '16', iconName: 'thermometer', title: 'Water Heaters', desc: 'Maintenance and replacement', color: '#fb923c', active: false, order: 16, detailedDesc: 'Water heater maintenance, repair, and replacement services.', autoTypeText: 'I need help with my water heater. ' },
  ];

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const servicesDoc = await getDoc(doc(db, 'settings', 'services'));
      if (servicesDoc.exists()) {
        setServices(servicesDoc.data().items || defaultServices);
      } else {
        setServices(defaultServices);
      }
    } catch (error) {
      console.log('Error loading services:', error);
      setServices(defaultServices);
    }
    setLoading(false);
  };

  const saveServices = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'services'), { items: services });
      alert('Services saved successfully!');
    } catch (error) {
      console.error('Error saving services:', error);
      alert('Failed to save services');
    }
    setSaving(false);
  };

  const toggleService = (id) => {
    setServices(prev => prev.map(s => 
      s.id === id ? { ...s, active: !s.active } : s
    ));
  };

  const updateService = (id, field, value) => {
    setServices(prev => prev.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  const activeServices = services.filter(s => s.active).sort((a, b) => a.order - b.order);
  const displayServices = showAllServices ? services : services.slice(0, 8);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.teal} />
        <Text style={styles.loadingText}>Loading services...</Text>
      </View>
    );
  }

  return (
    <View style={styles.tabContent}>
      <View style={styles.tabHeader}>
        <View>
          <Text style={styles.tabTitle}>Services Display</Text>
          <Text style={styles.tabSubtitle}>Manage which services appear on your website (top 8 shown)</Text>
        </View>
        <TouchableOpacity 
          style={[styles.primaryBtn, saving && styles.primaryBtnDisabled]} 
          onPress={saveServices}
          disabled={saving}
        >
          <Icon name={saving ? 'loader' : 'save'} size={16} color="#0a0f1a" style={{marginRight: 8}} />
          <Text style={styles.primaryBtnText}>{saving ? 'Saving...' : 'Save Services'}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.servicesStats}>
        <View style={styles.servicesStat}>
          <Text style={styles.servicesStatNum}>{activeServices.length}</Text>
          <Text style={styles.servicesStatLabel}>Active Services</Text>
        </View>
        <View style={styles.servicesStat}>
          <Text style={styles.servicesStatNum}>8</Text>
          <Text style={styles.servicesStatLabel}>Displayed on Site</Text>
        </View>
      </View>

      <View style={styles.servicesHeader}>
        <Text style={styles.sectionTitle}>{showAllServices ? 'All Services (16)' : 'Top 8 Services'}</Text>
        <TouchableOpacity onPress={() => setShowAllServices(!showAllServices)}>
          <Text style={styles.viewAllLink}>{showAllServices ? 'Show Less' : 'View All 16 →'}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.servicesGrid}>
        {displayServices.map((service, idx) => (
          <View key={service.id} style={[styles.serviceCard, !service.active && styles.serviceCardInactive]}>
            {/* Displayed Badge - Top Right */}
            {idx < 8 && service.active && (
              <View style={styles.serviceDisplayBadge}>
                <Text style={styles.serviceDisplayBadgeText}>DISPLAYED</Text>
              </View>
            )}
            
            {/* Toggle - Below Badge */}
            <View style={styles.serviceToggleWrap}>
              <TouchableOpacity 
                style={[styles.serviceToggle, service.active && styles.serviceToggleOn]}
                onPress={() => toggleService(service.id)}
              >
                <View style={[styles.serviceToggleDot, service.active && styles.serviceToggleDotOn]} />
              </TouchableOpacity>
            </View>
            
            {/* Icon */}
            <View style={[styles.serviceIconBox, { backgroundColor: service.color + '20' }]}>
              <Icon 
                name={service.iconName || 'tool'} 
                type={service.iconType || 'feather'}
                size={24} 
                color={service.color} 
              />
            </View>
            
            {editingService === service.id ? (
              <View style={styles.serviceEditForm}>
                {/* Title */}
                <Text style={styles.serviceEditLabel}>Service Title</Text>
                <TextInput
                  style={styles.serviceEditInput}
                  value={service.title}
                  onChangeText={(val) => updateService(service.id, 'title', val)}
                  placeholder="Service Title"
                  placeholderTextColor={colors.gray}
                />
                
                {/* Short Description */}
                <Text style={styles.serviceEditLabel}>Short Description (card front)</Text>
                <TextInput
                  style={[styles.serviceEditInput, styles.serviceEditInputSmall]}
                  value={service.desc}
                  onChangeText={(val) => updateService(service.id, 'desc', val)}
                  placeholder="Brief description shown on card front"
                  placeholderTextColor={colors.gray}
                  multiline
                />
                
                {/* Detailed Description */}
                <Text style={[styles.serviceEditLabel, { color: colors.teal }]}>
                  📋 Detailed Description (card back)
                </Text>
                  <TextInput
                  style={[styles.serviceEditInput, styles.serviceEditTextarea]}
                  value={service.detailedDesc || ''}
                  onChangeText={(val) => updateService(service.id, 'detailedDesc', val)}
                  placeholder="Full description shown when user flips the card..."
                  placeholderTextColor={colors.gray}
                  multiline
                  numberOfLines={3}
                />
                
                {/* Auto-Type Prompt */}
                <Text style={[styles.serviceEditLabel, { color: colors.gold }]}>
                  ✏️ Auto-Type Prompt (quote form)
                </Text>
                <TextInput
                  style={[styles.serviceEditInput, styles.serviceEditInputSmall]}
                  value={service.autoTypeText || ''}
                  onChangeText={(val) => updateService(service.id, 'autoTypeText', val)}
                  placeholder="Text that auto-types when 'Get Estimate' is clicked..."
                  placeholderTextColor={colors.gray}
                  multiline
                />
                <Text style={styles.serviceEditHint}>
                  This text will automatically appear in the quote form when a user clicks "Get Estimate" on this service.
                </Text>
                
                {/* Icon & Color Row */}
                <View style={styles.serviceEditRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.serviceEditLabel}>Icon Name</Text>
                    <TextInput
                      style={styles.serviceEditInput}
                    value={service.iconName || ''}
                    onChangeText={(val) => updateService(service.id, 'iconName', val)}
                      placeholder="feather icon name"
                    placeholderTextColor={colors.gray}
                  />
                  </View>
                </View>
                
                {/* Color Picker */}
                <Text style={styles.serviceEditLabel}>Accent Color</Text>
                <View style={styles.serviceColorPicker}>
                  {['#d4a84b', '#ef4444', '#3b82f6', '#a855f7', '#f59e0b', '#06b6d4', '#f97316', '#10b981', '#64748b', '#ec4899'].map(clr => (
                    <TouchableOpacity
                      key={clr}
                      style={[
                        styles.serviceColorOption,
                        { backgroundColor: clr },
                        service.color === clr && styles.serviceColorOptionActive
                      ]}
                      onPress={() => updateService(service.id, 'color', clr)}
                    >
                      {service.color === clr && <Icon name="check" size={12} color={colors.white} />}
                    </TouchableOpacity>
                  ))}
                </View>
                
                {/* Done Button */}
                  <TouchableOpacity 
                    style={styles.serviceEditDone}
                    onPress={() => setEditingService(null)}
                  >
                  <Icon name="check" size={16} color={colors.darkNavy} style={{ marginRight: 6 }} />
                  <Text style={styles.serviceEditDoneText}>Done Editing</Text>
                  </TouchableOpacity>
              </View>
            ) : (
              <>
                <Text style={styles.serviceTitle}>{service.title}</Text>
                <Text style={styles.serviceDesc}>{service.desc}</Text>
                <TouchableOpacity 
                  style={styles.serviceEditBtn}
                  onPress={() => setEditingService(service.id)}
                >
                  <Icon name="edit" size={14} color={colors.gold} style={{marginRight: 6}} />
                  <Text style={styles.serviceEditBtnText}>Edit</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        ))}
      </View>
    </View>
  );
};

// Portfolio Gallery Tab (Site Management > Portfolio)
// Default portfolio templates (matches the website placeholders)
const DEFAULT_PORTFOLIO_TEMPLATES = [
  { id: 1, type: 'image', title: 'Kitchen Renovation', category: 'Renovation', color: '#ef4444', description: '' },
  { id: 2, type: 'video', title: 'Deck Building Process', category: 'Carpentry', color: '#3b82f6', description: '' },
  { id: 3, type: 'image', title: 'Bathroom Remodel', category: 'Plumbing', color: '#06b6d4', description: '' },
  { id: 4, type: 'image', title: 'Custom Cabinets', category: 'Carpentry', color: '#a855f7', description: '' },
  { id: 5, type: 'video', title: 'Full Home Repair', category: 'General', color: '#f59e0b', description: '' },
  { id: 6, type: 'image', title: 'Exterior Painting', category: 'Painting', color: '#f97316', description: '' },
];

const PortfolioTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showTemplates, setShowTemplates] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);
  const [newItem, setNewItem] = useState({
    title: '',
    category: 'Renovation',
    type: 'image',
    imageUrl: '',
    thumbnailUrl: '',
    url: '',
    description: '',
    color: '#d4a84b',
    active: true,
    order: 0,
  });

  const categories = ['Renovation', 'Carpentry', 'Plumbing', 'Painting', 'Electrical', 'General'];
  const colorOptions = ['#d4a84b', '#ef4444', '#3b82f6', '#a855f7', '#f59e0b', '#06b6d4', '#f97316', '#10b981'];

  // Handle portfolio image upload
  const handleImageUpload = async (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 10MB for portfolio images)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be less than 10MB');
      return;
    }

    setUploadingImage(true);
    try {
      const fileName = `portfolio/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      
      // Update the appropriate field based on item type
      if (newItem.type === 'video') {
        setNewItem({ ...newItem, thumbnailUrl: downloadUrl });
      } else {
        setNewItem({ ...newItem, imageUrl: downloadUrl });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  useEffect(() => {
    loadPortfolio();
  }, []);

  const loadPortfolio = async () => {
    try {
      const portfolioRef = collection(db, 'portfolio');
      const portfolioQuery = query(portfolioRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(portfolioQuery);
      const items = [];
      snapshot.forEach(doc => {
        items.push({ id: doc.id, ...doc.data() });
      });
      setPortfolioItems(items);
    } catch (error) {
      console.error('Error loading portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!newItem.title) {
      alert('Please enter a title');
      return;
    }
    
    setSaving(true);
    try {
      const itemData = {
        ...newItem,
        order: editingItem ? newItem.order : portfolioItems.length,
        updatedAt: serverTimestamp(),
      };

      if (editingItem) {
        await updateDoc(doc(db, 'portfolio', editingItem.id), itemData);
      } else {
        itemData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'portfolio'), itemData);
      }

      loadPortfolio();
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving portfolio item:', error);
      alert('Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm('Delete this portfolio item?')) return;
    
    try {
      await deleteDoc(doc(db, 'portfolio', itemId));
      loadPortfolio();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const toggleActive = async (item) => {
    try {
      await updateDoc(doc(db, 'portfolio', item.id), {
        active: !item.active,
        updatedAt: serverTimestamp(),
      });
      loadPortfolio();
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const resetForm = () => {
    setNewItem({
      title: '',
      category: 'Renovation',
      type: 'image',
      imageUrl: '',
      thumbnailUrl: '',
      url: '',
      description: '',
      color: '#d4a84b',
      active: true,
      order: 0,
    });
    setEditingItem(null);
  };

  const openEditModal = (item) => {
    setNewItem({ ...item });
    setEditingItem(item);
    setShowAddModal(true);
  };

  // Use a template to pre-fill the form
  const useTemplate = (template) => {
    setNewItem({
      title: template.title,
      category: template.category,
      type: template.type,
      imageUrl: '',
      thumbnailUrl: '',
      url: '',
      description: template.description || '',
      color: template.color,
      active: true,
      order: portfolioItems.length,
    });
    setEditingItem(null);
    setShowAddModal(true);
  };

  // Check which templates are already added (by title match)
  const getTemplateStatus = (template) => {
    return portfolioItems.some(item => 
      item.title.toLowerCase() === template.title.toLowerCase()
    );
  };

  if (loading) {
    return (
      <View style={styles.tabContent}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.teal} />
          <Text style={styles.loadingText}>Loading portfolio...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.tabContent}>
      <View style={styles.tabHeader}>
        <View>
          <Text style={styles.tabTitle}>Portfolio Gallery</Text>
          <Text style={styles.tabSubtitle}>Manage photos and videos of your work</Text>
        </View>
        <TouchableOpacity 
          style={styles.primaryBtn} 
          onPress={() => { resetForm(); setShowAddModal(true); }}
        >
          <Icon name="plus" size={16} color={colors.darkNavy} />
          <Text style={styles.primaryBtnText}>Add Item</Text>
        </TouchableOpacity>
      </View>

      {/* Stats */}
      <View style={styles.portfolioStatsRow}>
        <View style={styles.portfolioStatCard}>
          <Icon name="image" size={20} color={colors.teal} />
          <Text style={styles.portfolioStatNumber}>{portfolioItems.filter(i => i.type === 'image').length}</Text>
          <Text style={styles.portfolioStatLabel}>Photos</Text>
        </View>
        <View style={styles.portfolioStatCard}>
          <Icon name="video" size={20} color={colors.gold} />
          <Text style={styles.portfolioStatNumber}>{portfolioItems.filter(i => i.type === 'video').length}</Text>
          <Text style={styles.portfolioStatLabel}>Videos</Text>
        </View>
        <View style={styles.portfolioStatCard}>
          <Icon name="eye" size={20} color={colors.green} />
          <Text style={styles.portfolioStatNumber}>{portfolioItems.filter(i => i.active).length}</Text>
          <Text style={styles.portfolioStatLabel}>Active</Text>
        </View>
      </View>

      {/* Default Templates Section */}
      <View style={styles.templatesSection}>
        <TouchableOpacity 
          style={styles.templatesSectionHeader}
          onPress={() => setShowTemplates(!showTemplates)}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <Icon name="layout" size={18} color={colors.teal} />
            <Text style={styles.templatesSectionTitle}>Default Templates</Text>
            <View style={styles.templatesBadge}>
              <Text style={styles.templatesBadgeText}>{DEFAULT_PORTFOLIO_TEMPLATES.length}</Text>
            </View>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={styles.templatesHint}>Click to fill in with your content</Text>
            <Icon name={showTemplates ? 'chevron-up' : 'chevron-down'} size={18} color={colors.gray} />
          </View>
        </TouchableOpacity>

        {showTemplates && (
          <View style={styles.templatesGrid}>
            {DEFAULT_PORTFOLIO_TEMPLATES.map((template, index) => {
              const isAdded = getTemplateStatus(template);
              return (
                <TouchableOpacity 
                  key={template.id}
                  style={[styles.templateCard, isAdded && styles.templateCardAdded]}
                  onPress={() => !isAdded && useTemplate(template)}
                  disabled={isAdded}
                >
                  <View style={[styles.templatePreview, { backgroundColor: template.color + '25' }]}>
                    <Icon 
                      name={template.type === 'video' ? 'video' : 'image'} 
                      size={24} 
                      color={template.color} 
                    />
                    {isAdded && (
                      <View style={styles.templateAddedOverlay}>
                        <Icon name="check-circle" size={28} color={colors.green} />
                      </View>
                    )}
                  </View>
                  <View style={styles.templateInfo}>
                    <Text style={styles.templateTitle} numberOfLines={1}>{template.title}</Text>
                    <View style={styles.templateMeta}>
                      <View style={[styles.templateCategoryBadge, { backgroundColor: template.color + '20' }]}>
                        <Text style={[styles.templateCategoryText, { color: template.color }]}>{template.category}</Text>
                      </View>
                      <View style={styles.templateTypeBadge}>
                        <Icon name={template.type === 'video' ? 'video' : 'image'} size={10} color={colors.gray} />
                        <Text style={styles.templateTypeText}>{template.type}</Text>
                      </View>
                    </View>
                  </View>
                  {!isAdded && (
                    <View style={styles.templateAction}>
                      <Icon name="plus" size={16} color={colors.teal} />
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        )}
      </View>

      {/* Portfolio Grid */}
      {portfolioItems.length === 0 ? (
        <View style={styles.emptyState}>
          <Icon name="image" size={48} color={colors.gray} />
          <Text style={styles.emptyStateTitle}>No portfolio items yet</Text>
          <Text style={styles.emptyStateText}>Add photos and videos of your completed projects</Text>
        </View>
      ) : (
        <View style={styles.portfolioAdminGrid}>
          {portfolioItems.map((item, index) => (
            <View key={item.id} style={[styles.portfolioAdminCard, !item.active && styles.portfolioAdminCardInactive]}>
              {/* Preview */}
              <View style={styles.portfolioAdminPreview}>
                {item.thumbnailUrl || item.imageUrl ? (
                  <Image source={{ uri: item.thumbnailUrl || item.imageUrl }} style={styles.portfolioAdminImage} resizeMode="cover" />
                ) : (
                  <View style={[styles.portfolioAdminPlaceholder, { backgroundColor: item.color + '30' }]}>
                    <Icon name={item.type === 'video' ? 'video' : 'image'} size={32} color={item.color} />
                  </View>
                )}
                {item.type === 'video' && (
                  <View style={styles.portfolioAdminVideoBadge}>
                    <Icon name="play" size={12} color={colors.white} />
                  </View>
                )}
                {!item.active && (
                  <View style={styles.portfolioAdminHiddenBadge}>
                    <Text style={styles.portfolioAdminHiddenText}>HIDDEN</Text>
                  </View>
                )}
              </View>

              {/* Info */}
              <View style={styles.portfolioAdminInfo}>
                <Text style={styles.portfolioAdminTitle} numberOfLines={1}>{item.title}</Text>
                <View style={styles.portfolioAdminMeta}>
                  <View style={[styles.portfolioAdminCategoryBadge, { backgroundColor: item.color + '20' }]}>
                    <Text style={[styles.portfolioAdminCategoryText, { color: item.color }]}>{item.category}</Text>
                  </View>
                </View>
              </View>

              {/* Actions */}
              <View style={styles.portfolioAdminActions}>
                <TouchableOpacity style={styles.portfolioAdminActionBtn} onPress={() => toggleActive(item)}>
                  <Icon name={item.active ? 'eye-off' : 'eye'} size={16} color={item.active ? colors.gray : colors.green} />
                </TouchableOpacity>
                <TouchableOpacity style={styles.portfolioAdminActionBtn} onPress={() => openEditModal(item)}>
                  <Icon name="edit-2" size={16} color={colors.teal} />
                </TouchableOpacity>
                <TouchableOpacity style={styles.portfolioAdminActionBtn} onPress={() => handleDelete(item.id)}>
                  <Icon name="trash-2" size={16} color={colors.red} />
                </TouchableOpacity>
              </View>
            </View>
          ))}
        </View>
      )}

      {/* Add/Edit Modal */}
      <Modal visible={showAddModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { maxWidth: 550 }]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{editingItem ? 'Edit Portfolio Item' : 'Add Portfolio Item'}</Text>
              <TouchableOpacity onPress={() => { setShowAddModal(false); resetForm(); }}>
                <Icon name="x" size={24} color={colors.white} />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
              {/* Type Toggle */}
              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Type</Text>
                <View style={styles.portfolioTypeToggle}>
                  <TouchableOpacity 
                    style={[styles.portfolioTypeBtn, newItem.type === 'image' && styles.portfolioTypeBtnActive]}
                    onPress={() => setNewItem({...newItem, type: 'image'})}
                  >
                    <Icon name="image" size={18} color={newItem.type === 'image' ? colors.white : colors.gray} />
                    <Text style={[styles.portfolioTypeBtnText, newItem.type === 'image' && { color: colors.white }]}>Photo</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={[styles.portfolioTypeBtn, newItem.type === 'video' && styles.portfolioTypeBtnActive]}
                    onPress={() => setNewItem({...newItem, type: 'video'})}
                  >
                    <Icon name="video" size={18} color={newItem.type === 'video' ? colors.white : colors.gray} />
                    <Text style={[styles.portfolioTypeBtnText, newItem.type === 'video' && { color: colors.white }]}>Video</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {/* Title */}
              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Title *</Text>
                <TextInput
                  style={styles.modalInput}
                  value={newItem.title}
                  onChangeText={(val) => setNewItem({...newItem, title: val})}
                  placeholder="e.g., Kitchen Renovation"
                  placeholderTextColor={colors.gray}
                />
              </View>

              {/* Category */}
              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Category</Text>
                <View style={styles.portfolioCategoryGrid}>
                  {categories.map(cat => (
                    <TouchableOpacity 
                      key={cat}
                      style={[styles.portfolioCategoryBtn, newItem.category === cat && styles.portfolioCategoryBtnActive]}
                      onPress={() => setNewItem({...newItem, category: cat})}
                    >
                      <Text style={[styles.portfolioCategoryBtnText, newItem.category === cat && { color: colors.white }]}>{cat}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Image/Thumbnail URL */}
              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>{newItem.type === 'video' ? 'Thumbnail Image' : 'Image'}</Text>
                <Text style={styles.modalHint}>Upload an image or paste a URL</Text>
                
                {/* Upload Button */}
                {isWeb && (
                  <View style={styles.imageUploadRow}>
                    <TouchableOpacity 
                      style={[styles.imageUploadBtn, uploadingImage && styles.imageUploadBtnDisabled]}
                      onPress={() => imageInputRef.current?.click()}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? (
                        <ActivityIndicator size="small" color={colors.darkNavy} />
                      ) : (
                        <>
                          <Icon name="upload" size={16} color={colors.darkNavy} />
                          <Text style={styles.imageUploadBtnText}>Upload Image</Text>
                        </>
                      )}
                    </TouchableOpacity>
                    <Text style={styles.imageUploadOr}>or paste URL below</Text>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                  </View>
                )}
                
                <TextInput
                  style={styles.modalInput}
                  value={newItem.type === 'video' ? newItem.thumbnailUrl : newItem.imageUrl}
                  onChangeText={(val) => setNewItem({...newItem, [newItem.type === 'video' ? 'thumbnailUrl' : 'imageUrl']: val})}
                  placeholder="https://example.com/image.jpg"
                  placeholderTextColor={colors.gray}
                />
              </View>

              {/* Video URL (only for videos) */}
              {newItem.type === 'video' && (
                <View style={styles.modalField}>
                  <Text style={styles.modalLabel}>Video URL</Text>
                  <TextInput
                    style={styles.modalInput}
                    value={newItem.url}
                    onChangeText={(val) => setNewItem({...newItem, url: val})}
                    placeholder="YouTube, Vimeo, or direct video URL"
                    placeholderTextColor={colors.gray}
                  />
                  <Text style={styles.modalHint}>Supports YouTube, Vimeo, and direct video links</Text>
                </View>
              )}

              {/* Description */}
              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Description (optional)</Text>
                <TextInput
                  style={[styles.modalInput, { minHeight: 80, textAlignVertical: 'top' }]}
                  value={newItem.description}
                  onChangeText={(val) => setNewItem({...newItem, description: val})}
                  placeholder="Brief description of the project..."
                  placeholderTextColor={colors.gray}
                  multiline
                />
              </View>

              {/* Color */}
              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Accent Color</Text>
                <View style={styles.portfolioColorGrid}>
                  {colorOptions.map(clr => (
                    <TouchableOpacity
                      key={clr}
                      style={[
                        styles.portfolioColorBtn,
                        { backgroundColor: clr },
                        newItem.color === clr && styles.portfolioColorBtnActive
                      ]}
                      onPress={() => setNewItem({...newItem, color: clr})}
                    >
                      {newItem.color === clr && <Icon name="check" size={14} color={colors.white} />}
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Preview */}
              {(newItem.imageUrl || newItem.thumbnailUrl) && (
                <View style={styles.modalField}>
                  <Text style={styles.modalLabel}>Preview</Text>
                  <View style={styles.portfolioPreviewContainer}>
                  <Image 
                    source={{ uri: newItem.thumbnailUrl || newItem.imageUrl }} 
                    style={styles.portfolioPreviewImage}
                    resizeMode="cover"
                  />
                    <TouchableOpacity 
                      style={styles.portfolioPreviewRemove}
                      onPress={() => setNewItem({
                        ...newItem, 
                        imageUrl: newItem.type === 'video' ? newItem.imageUrl : '',
                        thumbnailUrl: newItem.type === 'video' ? '' : newItem.thumbnailUrl
                      })}
                    >
                      <Icon name="x" size={14} color={colors.white} />
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </ScrollView>

            <View style={styles.modalFooter}>
              <TouchableOpacity style={styles.modalCancelBtn} onPress={() => { setShowAddModal(false); resetForm(); }}>
                <Text style={styles.modalCancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.modalSaveBtn, saving && { opacity: 0.6 }]} 
                onPress={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color={colors.darkNavy} />
                ) : (
                  <Text style={styles.modalSaveBtnText}>{editingItem ? 'Update' : 'Add Item'}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// Testimonials Tab (Site Management > Testimonials)
const TestimonialsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [newTestimonial, setNewTestimonial] = useState({
    name: '',
    location: 'Las Vegas, NV',
    text: '',
    rating: 5,
    active: true,
    photoUrl: '',
  });

  const defaultTestimonials = [
    { id: '1', name: 'Dani Michales', location: 'Las Vegas, NV', text: 'Excellent Service! Work was perfect, completed in a timely manner. We had a room painted with an accent wall and it turned out wonderfully. Great price!', rating: 5, active: true },
    { id: '2', name: 'Mkkkgavnari Lane', location: 'Las Vegas, NV', text: 'Amazing work! Super affordable, Jesse was great! Super helpful & knowledgeable. Will be keeping his contact for any of my next handy needs!!!!', rating: 5, active: true },
    { id: '3', name: 'James Carter Jr', location: 'Las Vegas, NV', text: 'Jesse did an awesome job for us installing cabinets in our pantry. He was on time and gave an excellent quote. We will be calling him first if we have additional work.', rating: 5, active: true },
    { id: '4', name: 'Janet Ruiz', location: 'Las Vegas, NV', text: 'Jesse is very professional. Arrived on time. He did a great job installing our new garage door opener. He is very friendly and easy to talk to.', rating: 5, active: true },
    { id: '5', name: 'Grace B', location: 'Las Vegas, NV', text: 'I highly recommend this business. Jesse was very professional. He arrived on time, always keeps you updated when working on a project, and provided quality work.', rating: 5, active: true },
    { id: '6', name: 'Crystal Franklin-Barker', location: 'Henderson, NV', text: 'I had a plumbing issue on a Saturday. Jesse provided a work-around until he could get to me on Sunday. He was prompt, knowledgeable, and completed the work accurately.', rating: 5, active: true },
    { id: '7', name: 'Irina Rajala', location: 'Las Vegas, NV', text: 'When things totally fell apart to the point that I had no running water in the house I called Handy Hawk. Jesse came in at 9am next day and completed all work by noon.', rating: 5, active: true },
    { id: '8', name: 'AgentRood', location: 'Las Vegas, NV', text: "Jesse came out to take a look at my water heater. He not only made the time to get here today, but when he found the spark wasn't reaching the pilot, he fixed it on the spot!", rating: 5, active: true },
    { id: '9', name: 'Evian', location: 'Las Vegas, NV', text: "Jesse was very cool! Coolest handyman that I've met and he talked to my whole family and he did amazing work and was priced cheap. A+", rating: 5, active: true },
    { id: '10', name: 'Robert Carter', location: 'Las Vegas, NV', text: 'I had 2 (4x8) garage shelving units installed. Work was completed with 100% satisfaction. I plan to use this company in the future.', rating: 5, active: true },
    { id: '11', name: 'Anthony Powell', location: 'Henderson, NV', text: 'Mr. Jessie was very kind and considerate. I appreciated his professional demeanor. A job well done!', rating: 5, active: true },
    { id: '12', name: 'Joseph DiStephano', location: 'Las Vegas, NV', text: "Jessie and his co-worker were very professional. We appreciate you fixing our chandelier, you're the best. The price was reasonable as well.", rating: 5, active: true },
    { id: '13', name: 'Lil Butterfly', location: 'Las Vegas, NV', text: "Once again HandyHawk has saved the day. My garbage disposal stopped working & I couldn't fix it myself. I sent one text message and now I have a brand new one!", rating: 5, active: true },
    { id: '14', name: 'Venette Mahan', location: 'Las Vegas, NV', text: 'Great service!!!! Jesse arrived timely, communicated with me. Was respectful of the property. Found me a great handyman!!!', rating: 5, active: true },
    { id: '15', name: 'Darnell Buchanan', location: 'Henderson, NV', text: 'Very respectful, easy to work with on a job I needed done which was a new board under my sink in the kitchen. Thank you again for your help.', rating: 5, active: true },
  ];

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const testimonialsDoc = await getDoc(doc(db, 'settings', 'testimonials'));
      if (testimonialsDoc.exists()) {
        setTestimonials(testimonialsDoc.data().items || defaultTestimonials);
      } else {
        setTestimonials(defaultTestimonials);
      }
    } catch (error) {
      console.log('Error loading testimonials:', error);
      setTestimonials(defaultTestimonials);
    }
    setLoading(false);
  };

  const saveTestimonials = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'testimonials'), { items: testimonials });
      alert('Testimonials saved successfully!');
    } catch (error) {
      console.error('Error saving testimonials:', error);
      alert('Failed to save testimonials');
    }
    setSaving(false);
  };

  const toggleTestimonial = (id) => {
    setTestimonials(prev => prev.map(t => 
      t.id === id ? { ...t, active: !t.active } : t
    ));
  };

  const addTestimonial = () => {
    if (!newTestimonial.name || !newTestimonial.text) {
      alert('Please fill in name and review text');
      return;
    }
    const newItem = {
      ...newTestimonial,
      id: Date.now().toString(),
    };
    setTestimonials(prev => [newItem, ...prev]);
    setNewTestimonial({ name: '', location: 'Las Vegas, NV', text: '', rating: 5, active: true, photoUrl: '' });
    setShowAddModal(false);
  };

  const deleteTestimonial = (id) => {
    if (confirm('Are you sure you want to delete this testimonial?')) {
      setTestimonials(prev => prev.filter(t => t.id !== id));
    }
  };

  const activeCount = testimonials.filter(t => t.active).length;

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.teal} />
        <Text style={styles.loadingText}>Loading testimonials...</Text>
      </View>
    );
  }

  return (
  <View style={styles.tabContent}>
    <View style={styles.tabHeader}>
      <View>
        <Text style={styles.tabTitle}>Testimonials</Text>
          <Text style={styles.tabSubtitle}>Manage reviews shown on your website carousel</Text>
      </View>
        <View style={styles.tabHeaderActions}>
          <TouchableOpacity style={styles.secondaryBtn} onPress={() => setShowAddModal(true)}>
            <Text style={styles.secondaryBtnText}>+ Add New</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.primaryBtn, saving && styles.primaryBtnDisabled]} 
            onPress={saveTestimonials}
            disabled={saving}
          >
            <Icon name={saving ? 'loader' : 'save'} size={16} color="#0a0f1a" style={{marginRight: 8}} />
            <Text style={styles.primaryBtnText}>{saving ? 'Saving...' : 'Save All'}</Text>
      </TouchableOpacity>
        </View>
    </View>

      <View style={styles.testimonialStats}>
        <View style={styles.testimonialStat}>
          <Text style={styles.testimonialStatNum}>{testimonials.length}</Text>
          <Text style={styles.testimonialStatLabel}>Total Reviews</Text>
        </View>
        <View style={styles.testimonialStat}>
          <Text style={styles.testimonialStatNum}>{activeCount}</Text>
          <Text style={styles.testimonialStatLabel}>Active on Site</Text>
        </View>
        <View style={styles.testimonialStat}>
          <View style={styles.ratingIconWrap}>
            <Text style={styles.testimonialStatNum}>5.0</Text>
            <Icon name="star" size={18} color={colors.gold} style={{marginLeft: 4}} />
          </View>
          <Text style={styles.testimonialStatLabel}>Average Rating</Text>
        </View>
      </View>

      <ScrollView style={styles.testimonialScrollContainer} showsVerticalScrollIndicator={false}>
    <View style={styles.testimonialGrid}>
          {testimonials.map((testimonial) => (
            <View key={testimonial.id} style={[styles.testimonialGridCard, !testimonial.active && styles.testimonialCardInactive]}>
              {/* Active Badge */}
              {testimonial.active && (
                <View style={styles.testimonialActiveBadge}>
                  <Text style={styles.testimonialActiveBadgeText}>ACTIVE</Text>
                </View>
              )}
              
              {/* Toggle */}
              <TouchableOpacity 
                style={[styles.testimonialGridToggle, testimonial.active && styles.testimonialToggleOn]}
                onPress={() => toggleTestimonial(testimonial.id)}
              >
                <View style={[styles.testimonialToggleDot, testimonial.active && styles.testimonialToggleDotOn]} />
              </TouchableOpacity>
              
              {/* Avatar & Name */}
              <View style={styles.testimonialGridHeader}>
                <View style={styles.testimonialGridAvatar}>
                  {testimonial.photoUrl ? (
                    <Image source={{ uri: testimonial.photoUrl }} style={styles.testimonialAvatarImg} />
                  ) : (
              <Text style={styles.testimonialInitial}>{testimonial.name[0]}</Text>
                  )}
            </View>
                <Text style={styles.testimonialGridName}>{testimonial.name}</Text>
                <Text style={styles.testimonialGridLocation}>{testimonial.location}</Text>
              <View style={styles.testimonialStars}>
                {[...Array(testimonial.rating)].map((_, i) => (
                    <Text key={i} style={styles.starSmall}>★</Text>
                ))}
              </View>
            </View>
              
              {/* Review Text */}
              <Text style={styles.testimonialGridText} numberOfLines={4}>"{testimonial.text}"</Text>
              
              {/* Actions */}
              <View style={styles.testimonialGridActions}>
                <TouchableOpacity 
                  style={styles.testimonialDeleteBtn}
                  onPress={() => deleteTestimonial(testimonial.id)}
                >
                  <Icon name="trash-2" size={14} color={colors.red} />
                </TouchableOpacity>
            </View>
          </View>
          ))}
        </View>
      </ScrollView>

      {/* Add New Testimonial Modal */}
      <Modal visible={showAddModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add New Testimonial</Text>
              <TouchableOpacity onPress={() => setShowAddModal(false)}>
                <Icon name="x" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

            <ScrollView style={styles.modalBody}>
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Customer Name *</Text>
                <TextInput
                  style={styles.formInput}
                  value={newTestimonial.name}
                  onChangeText={(val) => setNewTestimonial({ ...newTestimonial, name: val })}
                  placeholder="John Smith"
                  placeholderTextColor={colors.gray}
                />
        </View>

              <View style={styles.formField}>
                <Text style={styles.formLabel}>Location</Text>
                <TextInput
                  style={styles.formInput}
                  value={newTestimonial.location}
                  onChangeText={(val) => setNewTestimonial({ ...newTestimonial, location: val })}
                  placeholder="Las Vegas, NV"
                  placeholderTextColor={colors.gray}
                />
              </View>

              <View style={styles.formField}>
                <Text style={styles.formLabel}>Review Text *</Text>
                <TextInput
                  style={[styles.formInput, { height: 100, textAlignVertical: 'top' }]}
                  value={newTestimonial.text}
                  onChangeText={(val) => setNewTestimonial({ ...newTestimonial, text: val })}
                  placeholder="Write the customer review..."
                  placeholderTextColor={colors.gray}
                  multiline
                />
              </View>

              <View style={styles.formField}>
                <Text style={styles.formLabel}>Rating</Text>
                <View style={styles.ratingSelector}>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <TouchableOpacity
                      key={rating}
                      onPress={() => setNewTestimonial({ ...newTestimonial, rating })}
                    >
                      <Text style={[
                        styles.ratingStar,
                        rating <= newTestimonial.rating && styles.ratingStarActive
                      ]}>★</Text>
            </TouchableOpacity>
      ))}
    </View>
              </View>

              <View style={styles.formField}>
                <Text style={styles.formLabel}>Profile Photo URL (optional)</Text>
                <TextInput
                  style={styles.formInput}
                  value={newTestimonial.photoUrl}
                  onChangeText={(val) => setNewTestimonial({ ...newTestimonial, photoUrl: val })}
                  placeholder="https://..."
                  placeholderTextColor={colors.gray}
                />
              </View>
            </ScrollView>

            <View style={styles.modalFooter}>
              <TouchableOpacity style={styles.secondaryBtn} onPress={() => setShowAddModal(false)}>
                <Text style={styles.secondaryBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.primaryBtn} onPress={addTestimonial}>
                <Text style={styles.primaryBtnText}>Add Testimonial</Text>
            </TouchableOpacity>
          </View>
        </View>
    </View>
      </Modal>
  </View>
);
};

// ============================================
// FIELD MANUALS LIBRARY
// ============================================

// Video URL Helper Functions
const getVideoType = (url) => {
  if (!url) return 'unknown';
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return 'youtube';
  if (lowerUrl.includes('vimeo.com')) return 'vimeo';
  if (lowerUrl.includes('loom.com')) return 'loom';
  if (lowerUrl.match(/\.(mp4|webm|ogg|mov)(\?|$)/i)) return 'direct';
  return 'unknown';
};

const getVideoEmbedUrl = (url, type) => {
  if (!url) return null;
  
  switch (type) {
    case 'youtube': {
      // Handle various YouTube URL formats
      let videoId = null;
      if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1]?.split(/[?&]/)[0];
      } else if (url.includes('youtube.com/watch')) {
        const urlParams = new URLSearchParams(url.split('?')[1]);
        videoId = urlParams.get('v');
      } else if (url.includes('youtube.com/embed/')) {
        videoId = url.split('youtube.com/embed/')[1]?.split(/[?&]/)[0];
      }
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }
    case 'vimeo': {
      // Handle Vimeo URLs
      const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
      const vimeoId = vimeoMatch ? vimeoMatch[1] : null;
      return vimeoId ? `https://player.vimeo.com/video/${vimeoId}` : url;
    }
    case 'loom': {
      // Handle Loom URLs
      const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
      const loomId = loomMatch ? loomMatch[1] : null;
      return loomId ? `https://www.loom.com/embed/${loomId}` : url;
    }
    case 'direct':
      return url;
    default:
      return url;
  }
};

const getVideoThumbnail = (url, type) => {
  if (type === 'youtube') {
    let videoId = null;
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split(/[?&]/)[0];
    } else if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('youtube.com/embed/')[1]?.split(/[?&]/)[0];
    }
    return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
  }
  return null;
};

// Video Player Component
const VideoPlayer = ({ video, style }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoType = video.type || getVideoType(video.url);
  const embedUrl = getVideoEmbedUrl(video.url, videoType);
  const thumbnail = getVideoThumbnail(video.url, videoType);
  
  if (videoType === 'direct') {
    // Direct video file - use HTML5 video
    return (
      <View style={[styles.videoPlayerContainer, style]}>
        {video.title && <Text style={styles.videoPlayerTitle}>{video.title}</Text>}
        <video 
          controls 
          style={{ width: '100%', maxWidth: 560, borderRadius: 12, backgroundColor: '#000' }}
          poster={thumbnail}
        >
          <source src={video.url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </View>
    );
  }
  
  if (videoType === 'youtube' || videoType === 'vimeo' || videoType === 'loom') {
    // Embeddable video - show thumbnail with play button, then embed
    if (!isPlaying) {
      return (
        <View style={[styles.videoPlayerContainer, style]}>
          {video.title && <Text style={styles.videoPlayerTitle}>{video.title}</Text>}
          <TouchableOpacity 
            style={styles.videoThumbnailContainer}
            onPress={() => setIsPlaying(true)}
          >
            {thumbnail ? (
              <img src={thumbnail} alt={video.title} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} />
            ) : (
              <View style={styles.videoThumbnailPlaceholder}>
                <Icon name="video" size={40} color={colors.gray} />
              </View>
            )}
            <View style={styles.videoPlayOverlay}>
              <View style={styles.videoPlayButton}>
                <Icon name="play" size={32} color={colors.white} />
              </View>
            </View>
            <View style={styles.videoTypeBadge}>
              <Text style={styles.videoTypeBadgeText}>{videoType.toUpperCase()}</Text>
            </View>
          </TouchableOpacity>
        </View>
      );
    }
    
    // Show embedded player
    return (
      <View style={[styles.videoPlayerContainer, style]}>
        {video.title && <Text style={styles.videoPlayerTitle}>{video.title}</Text>}
        <View style={styles.videoEmbedContainer}>
          <iframe
            src={embedUrl + '?autoplay=1'}
            style={{ width: '100%', height: '100%', border: 'none', borderRadius: 12 }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </View>
      </View>
    );
  }
  
  // Unknown video type - show as link
  return (
    <View style={[styles.videoPlayerContainer, style]}>
      {video.title && <Text style={styles.videoPlayerTitle}>{video.title}</Text>}
      <TouchableOpacity 
        style={styles.videoLinkCard}
        onPress={() => window.open(video.url, '_blank')}
      >
        <Icon name="external-link" size={20} color={colors.teal} />
        <Text style={styles.videoLinkText}>Open Video</Text>
      </TouchableOpacity>
    </View>
  );
};

// Field Manuals - Unified Tab (Browse, Manage, Categories all in one)
const FieldManualsTab = () => {
  const [categories, setCategories] = useState([]);
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedManual, setSelectedManual] = useState(null);
  const [viewMode, setViewMode] = useState('browse'); // 'browse', 'category', 'manual', 'reader'
  const [readerStep, setReaderStep] = useState(0);
  
  // Editor states
  const [showManualEditor, setShowManualEditor] = useState(false);
  const [showCategoryEditor, setShowCategoryEditor] = useState(false);
  const [showManagePanel, setShowManagePanel] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    categoryId: '',
    tags: [],
    steps: [{ title: '', description: '', warning: '', tip: '' }],
    videos: [],
    resources: [],
  });
  const [editCategory, setEditCategory] = useState({ name: '', icon: 'folder', color: colors.teal });
  const [newTag, setNewTag] = useState('');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const videoInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load categories
      const categoriesRef = collection(db, 'fieldManualCategories');
      const catSnapshot = await getDocs(categoriesRef);
      const catList = [];
      catSnapshot.forEach(doc => catList.push({ id: doc.id, ...doc.data() }));
      setCategories(catList.sort((a, b) => (a.order || 0) - (b.order || 0)));
      
      // Load manuals
      const manualsRef = collection(db, 'fieldManuals');
      const manSnapshot = await getDocs(manualsRef);
      const manList = [];
      manSnapshot.forEach(doc => manList.push({ id: doc.id, ...doc.data() }));
      setManuals(manList);
    } catch (error) {
      console.error('Error loading field manuals:', error);
    }
    setLoading(false);
  };

  const filteredManuals = selectedCategory
    ? manuals.filter(m => m.categoryId === selectedCategory.id)
    : manuals.filter(m => 
        m.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
      );

  const getCategoryIcon = (iconName) => {
    const iconMap = {
      'plumbing': 'droplet',
      'electrical': 'zap',
      'carpentry': 'tool',
      'painting': 'edit-2',
      'hvac': 'wind',
      'appliance': 'cpu',
      'general': 'briefcase',
      'safety': 'shield',
    };
    return iconMap[iconName] || 'folder';
  };

  // Manual Editor Functions
  const openManualEditor = (manual = null) => {
    if (manual) {
      setEditForm({
        title: manual.title || '',
        categoryId: manual.categoryId || '',
        tags: manual.tags || [],
        steps: manual.steps?.length > 0 ? manual.steps : [{ title: '', description: '', warning: '', tip: '' }],
        videos: manual.videos || [],
        resources: manual.resources || [],
      });
      setSelectedManual(manual);
    } else {
      setEditForm({
        title: '',
        categoryId: categories[0]?.id || '',
        tags: [],
        steps: [{ title: '', description: '', warning: '', tip: '' }],
        videos: [],
        resources: [],
      });
      setSelectedManual(null);
    }
    setShowManualEditor(true);
  };

  const saveManual = async () => {
    if (!editForm.title.trim()) {
      alert('Please enter a manual title');
      return;
    }
    
    setSaving(true);
    try {
      const manualData = {
        ...editForm,
        updatedAt: new Date().toISOString(),
      };
      
      if (selectedManual?.id) {
        await updateDoc(doc(db, 'fieldManuals', selectedManual.id), manualData);
      } else {
        manualData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'fieldManuals'), manualData);
      }
      
      setShowManualEditor(false);
      loadData();
    } catch (error) {
      console.error('Error saving manual:', error);
      alert('Failed to save manual');
    }
    setSaving(false);
  };

  const deleteManual = async (manual) => {
    if (!confirm(`Delete "${manual.title}"? This cannot be undone.`)) return;
    
    try {
      await deleteDoc(doc(db, 'fieldManuals', manual.id));
      loadData();
      setViewMode('browse');
      setSelectedManual(null);
    } catch (error) {
      console.error('Error deleting manual:', error);
    }
  };

  const addStep = () => {
    setEditForm(prevForm => ({
      ...prevForm,
      steps: [...prevForm.steps, { title: '', description: '', warning: '', tip: '' }]
    }));
  };

  const updateStep = (index, field, value) => {
    setEditForm(prevForm => {
      const newSteps = [...prevForm.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
      return { ...prevForm, steps: newSteps };
    });
  };

  const removeStep = (index) => {
    setEditForm(prevForm => {
      if (prevForm.steps.length <= 1) return prevForm;
      return {
        ...prevForm,
        steps: prevForm.steps.filter((_, i) => i !== index)
      };
    });
  };

  const addTag = () => {
    if (newTag.trim()) {
      const tagToAdd = newTag.trim().toLowerCase();
      setEditForm(prevForm => {
        if (prevForm.tags.includes(tagToAdd)) return prevForm;
        return { ...prevForm, tags: [...prevForm.tags, tagToAdd] };
      });
      setNewTag('');
    }
  };

  // Video functions
  const addVideo = () => {
    if (!newVideoUrl.trim()) return;
    
    const videoType = getVideoType(newVideoUrl.trim());
    const video = {
      url: newVideoUrl.trim(),
      title: newVideoTitle.trim() || 'Untitled Video',
      id: Date.now().toString(),
      type: videoType,
      addedAt: new Date().toISOString(),
    };
    
    // Use functional update to avoid stale closure issues
    setEditForm(prevForm => ({ 
      ...prevForm, 
      videos: [...(prevForm.videos || []), video] 
    }));
    setNewVideoUrl('');
    setNewVideoTitle('');
  };

  const removeVideo = (videoId) => {
    // Use functional update to avoid stale closure issues
    setEditForm(prevForm => ({
      ...prevForm,
      videos: (prevForm.videos || []).filter(v => v.id !== videoId)
    }));
  };

  // Handle video file upload
  const handleVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-m4v'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid video file (MP4, WebM, OGG, MOV, or M4V)');
      return;
    }
    
    // Validate file size (max 100MB)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      alert('Video file is too large. Maximum size is 100MB.');
      return;
    }
    
    setUploadingVideo(true);
    try {
      const fileName = `field-manuals/videos/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      
      const video = {
        url: downloadUrl,
        title: newVideoTitle.trim() || file.name.replace(/\.[^/.]+$/, ''),
        id: Date.now().toString(),
        type: 'direct',
        isUploaded: true,
        fileName: file.name,
        fileSize: file.size,
        addedAt: new Date().toISOString(),
      };
      
      setEditForm(prevForm => ({ 
        ...prevForm, 
        videos: [...(prevForm.videos || []), video] 
      }));
      setNewVideoTitle('');
      
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('Failed to upload video. Please try again.');
    } finally {
      setUploadingVideo(false);
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  const getVideoThumbnail = (url) => {
    // YouTube thumbnail
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (youtubeMatch) {
      return `https://img.youtube.com/vi/${youtubeMatch[1]}/mqdefault.jpg`;
    }
    // Vimeo - would need API call, return placeholder
    if (url.includes('vimeo')) {
      return null;
    }
    return null;
  };

  // Category Editor Functions
  const iconOptions = [
    { name: 'droplet', label: 'Plumbing' },
    { name: 'zap', label: 'Electrical' },
    { name: 'tool', label: 'Tools' },
    { name: 'edit-2', label: 'Painting' },
    { name: 'wind', label: 'HVAC' },
    { name: 'cpu', label: 'Appliance' },
    { name: 'briefcase', label: 'General' },
    { name: 'shield', label: 'Safety' },
    { name: 'home', label: 'Home' },
    { name: 'truck', label: 'Moving' },
    { name: 'trash-2', label: 'Cleanup' },
    { name: 'sun', label: 'Outdoor' },
  ];

  const colorOptions = [colors.teal, colors.gold, colors.green, colors.red, '#9b59b6', '#3498db', '#e67e22', '#1abc9c'];

  const openCategoryEditor = (category = null) => {
    if (category) {
      setEditCategory({ ...category });
    } else {
      setEditCategory({ name: '', icon: 'folder', color: colors.teal, order: categories.length });
    }
    setShowCategoryEditor(true);
  };

  const saveCategory = async () => {
    if (!editCategory.name.trim()) {
      alert('Please enter a category name');
      return;
    }
    
    setSaving(true);
    try {
      if (editCategory.id) {
        await updateDoc(doc(db, 'fieldManualCategories', editCategory.id), {
          name: editCategory.name,
          icon: editCategory.icon,
          color: editCategory.color,
          order: editCategory.order || 0,
        });
      } else {
        await addDoc(collection(db, 'fieldManualCategories'), {
          name: editCategory.name,
          icon: editCategory.icon,
          color: editCategory.color,
          order: categories.length,
          createdAt: new Date().toISOString(),
        });
      }
      setShowCategoryEditor(false);
      loadData();
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Failed to save category');
    }
    setSaving(false);
  };

  const deleteCategory = async (category) => {
    if (!confirm(`Delete "${category.name}"? Manuals will become uncategorized.`)) return;
    
    try {
      await deleteDoc(doc(db, 'fieldManualCategories', category.id));
      loadData();
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  // Reader Mode View (one step at a time, swipeable)
  const [readerShowVideos, setReaderShowVideos] = useState(false);
  
  const renderReaderMode = () => {
    const steps = selectedManual?.steps || [];
    const videos = selectedManual?.videos || [];
    const currentStep = steps[readerStep];
    const hasVideos = videos.length > 0;
    
    // If showing videos panel
    if (readerShowVideos && hasVideos) {
      return (
        <View style={styles.fmReaderContainer}>
          <View style={styles.fmReaderHeader}>
            <TouchableOpacity onPress={() => setReaderShowVideos(false)} style={styles.fmReaderBackBtn}>
              <Icon name="arrow-left" size={24} color={colors.white} />
            </TouchableOpacity>
            <Text style={styles.fmReaderTitle} numberOfLines={1}>📹 Videos</Text>
            <Text style={styles.fmReaderProgress}>{videos.length} video{videos.length !== 1 ? 's' : ''}</Text>
          </View>
          
          <ScrollView style={styles.fmReaderVideosContainer} showsVerticalScrollIndicator={false}>
            {videos.map((video, idx) => (
              <View key={video.id || idx} style={styles.fmReaderVideoItem}>
                <VideoPlayer video={video} style={styles.fmReaderVideoPlayer} />
              </View>
            ))}
          </ScrollView>
          
          <View style={styles.fmReaderNav}>
            <TouchableOpacity 
              style={styles.fmReaderNavBtn}
              onPress={() => setReaderShowVideos(false)}
            >
              <Icon name="list" size={20} color={colors.white} />
              <Text style={styles.fmReaderNavText}>Back to Steps</Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }
    
    return (
      <View style={styles.fmReaderContainer}>
        <View style={styles.fmReaderHeader}>
          <TouchableOpacity onPress={() => setViewMode('manual')} style={styles.fmReaderBackBtn}>
            <Icon name="x" size={24} color={colors.white} />
          </TouchableOpacity>
          <Text style={styles.fmReaderTitle} numberOfLines={1}>{selectedManual?.title}</Text>
          <View style={styles.fmReaderHeaderRight}>
            {hasVideos && (
              <TouchableOpacity 
                style={styles.fmReaderVideosBtn}
                onPress={() => setReaderShowVideos(true)}
              >
                <Icon name="play-circle" size={18} color={colors.teal} />
                <Text style={styles.fmReaderVideosBtnText}>{videos.length}</Text>
              </TouchableOpacity>
            )}
          <Text style={styles.fmReaderProgress}>{readerStep + 1} / {steps.length}</Text>
          </View>
        </View>
        
        {/* Progress bar */}
        <View style={styles.fmReaderProgressBar}>
          <View style={[styles.fmReaderProgressFill, { width: `${((readerStep + 1) / steps.length) * 100}%` }]} />
        </View>
        
        <ScrollView 
          style={styles.fmReaderScrollView} 
          contentContainerStyle={styles.fmReaderContent}
          showsVerticalScrollIndicator={false}
        >
          {currentStep && (
            <>
              <View style={styles.fmReaderStepNum}>
                <Text style={styles.fmReaderStepNumText}>{readerStep + 1}</Text>
              </View>
              <Text style={styles.fmReaderStepTitle}>{currentStep.title || `Step ${readerStep + 1}`}</Text>
              <Text style={styles.fmReaderStepDesc}>{currentStep.description}</Text>
              
              {currentStep.warning && (
                <View style={styles.fmReaderWarning}>
                  <Icon name="alert-triangle" size={16} color={colors.gold} />
                  <Text style={styles.fmReaderWarningText}>{currentStep.warning}</Text>
                </View>
              )}
              
              {currentStep.tip && (
                <View style={styles.fmReaderTip}>
                  <Icon name="info" size={16} color={colors.teal} />
                  <Text style={styles.fmReaderTipText}>{currentStep.tip}</Text>
                </View>
              )}
            </>
          )}
        </ScrollView>
        
        {/* Navigation */}
        <View style={styles.fmReaderNav}>
          <TouchableOpacity 
            style={[styles.fmReaderNavBtn, readerStep === 0 && styles.fmReaderNavBtnDisabled]}
            onPress={() => setReaderStep(Math.max(0, readerStep - 1))}
            disabled={readerStep === 0}
          >
            <Icon name="chevron-left" size={24} color={readerStep === 0 ? colors.gray : colors.white} />
            <Text style={[styles.fmReaderNavText, readerStep === 0 && { color: colors.gray }]}>Previous</Text>
          </TouchableOpacity>
          
          <View style={styles.fmReaderDots}>
            {steps.map((_, idx) => (
              <TouchableOpacity 
                key={idx} 
                style={[styles.fmReaderDot, idx === readerStep && styles.fmReaderDotActive]} 
                onPress={() => setReaderStep(idx)}
              />
            ))}
          </View>
          
          <TouchableOpacity 
            style={[styles.fmReaderNavBtn, styles.fmReaderNavBtnNext, readerStep === steps.length - 1 && styles.fmReaderNavBtnDisabled]}
            onPress={() => setReaderStep(Math.min(steps.length - 1, readerStep + 1))}
            disabled={readerStep === steps.length - 1}
          >
            <Text style={[styles.fmReaderNavText, readerStep === steps.length - 1 && { color: colors.gray }]}>Next</Text>
            <Icon name="chevron-right" size={24} color={readerStep === steps.length - 1 ? colors.gray : colors.white} />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  // Manual Detail View
  const renderManualView = () => (
    <View style={styles.fmManualView}>
      <View style={styles.fmManualHeader}>
        <TouchableOpacity onPress={() => { setViewMode('category'); setSelectedManual(null); }} style={styles.fmBackBtn}>
          <Icon name="arrow-left" size={20} color={colors.white} />
        </TouchableOpacity>
        <View style={styles.fmManualHeaderInfo}>
          <Text style={styles.fmManualTitle}>{selectedManual?.title}</Text>
          <Text style={styles.fmManualCategory}>{categories.find(c => c.id === selectedManual?.categoryId)?.name || 'Uncategorized'}</Text>
        </View>
        <View style={styles.fmManualActions}>
          <TouchableOpacity 
            style={styles.fmEditBtn}
            onPress={() => openManualEditor(selectedManual)}
          >
            <Icon name="edit-2" size={16} color={colors.teal} />
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.fmDeleteBtn}
            onPress={() => deleteManual(selectedManual)}
          >
            <Icon name="trash-2" size={16} color={colors.red} />
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.fmReaderModeBtn}
            onPress={() => { setViewMode('reader'); setReaderStep(0); }}
          >
            <Icon name="book-open" size={16} color={colors.gold} />
            <Text style={styles.fmReaderModeBtnText}>Reader</Text>
          </TouchableOpacity>
        </View>
      </View>
      
      <ScrollView style={styles.fmManualContent} showsVerticalScrollIndicator={false}>
        {/* Videos Section */}
        {selectedManual?.videos?.length > 0 && (
          <View style={styles.fmVideosSection}>
            <Text style={styles.fmSectionTitle}>
              <Icon name="play-circle" size={16} color={colors.teal} /> Videos ({selectedManual.videos.length})
            </Text>
            <View style={styles.fmVideosGrid}>
              {selectedManual.videos.map((video, idx) => (
                <VideoPlayer 
                  key={idx} 
                  video={video}
                  style={styles.fmVideoItem}
                />
              ))}
            </View>
          </View>
        )}
        
        {/* Steps Section */}
        <View style={styles.fmStepsSection}>
          <Text style={styles.fmSectionTitle}>
            <Icon name="list" size={16} color={colors.teal} /> Step-by-Step Instructions
          </Text>
          {selectedManual?.steps?.map((step, idx) => (
            <View key={idx} style={styles.fmStepCard}>
              <View style={styles.fmStepNumBadge}>
                <Text style={styles.fmStepNumBadgeText}>{idx + 1}</Text>
              </View>
              <View style={styles.fmStepContent}>
                {step.title && <Text style={styles.fmStepTitle}>{step.title}</Text>}
                <Text style={styles.fmStepDesc}>{step.description}</Text>
                {step.warning && (
                  <View style={styles.fmStepWarning}>
                    <Icon name="alert-triangle" size={14} color={colors.gold} />
                    <Text style={styles.fmStepWarningText}>{step.warning}</Text>
                  </View>
                )}
                {step.tip && (
                  <View style={styles.fmStepTip}>
                    <Icon name="info" size={14} color={colors.teal} />
                    <Text style={styles.fmStepTipText}>{step.tip}</Text>
                  </View>
                )}
              </View>
            </View>
          ))}
        </View>
        
        {/* URLs / Resources */}
        {selectedManual?.resources?.length > 0 && (
          <View style={styles.fmResourcesSection}>
            <Text style={styles.fmSectionTitle}>
              <Icon name="link" size={16} color={colors.teal} /> Resources & Links
            </Text>
            {selectedManual.resources.map((resource, idx) => {
              const resourceVideoType = getVideoType(resource.url);
              const isVideo = ['youtube', 'vimeo', 'loom', 'direct'].includes(resourceVideoType);
              
              if (isVideo) {
                // Render as video player if it's a video URL
                return (
                  <VideoPlayer 
                    key={idx} 
                    video={{ ...resource, type: resourceVideoType }}
                    style={styles.fmResourceVideoItem}
                  />
                );
              }
              
              // Regular link
              return (
                <TouchableOpacity 
                  key={idx}
                  style={styles.fmResourceItem}
                  onPress={() => window.open(resource.url, '_blank')}
                >
                  <Icon name="external-link" size={16} color={colors.teal} />
                  <Text style={styles.fmResourceTitle}>{resource.title || resource.url}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        )}
        
        {/* Tags */}
        {selectedManual?.tags?.length > 0 && (
          <View style={styles.fmTagsSection}>
            <View style={styles.fmTagsList}>
              {selectedManual.tags.map((tag, idx) => (
                <View key={idx} style={styles.fmTag}>
                  <Text style={styles.fmTagText}>#{tag}</Text>
                </View>
              ))}
            </View>
          </View>
        )}
      </ScrollView>
    </View>
  );

  // Category View (manuals in selected category)
  const renderCategoryView = () => (
    <View style={styles.fmCategoryView}>
      <View style={styles.fmCategoryHeader}>
        <TouchableOpacity onPress={() => { setViewMode('browse'); setSelectedCategory(null); }} style={styles.fmBackBtn}>
          <Icon name="arrow-left" size={20} color={colors.white} />
        </TouchableOpacity>
        <View style={styles.fmCategoryHeaderInfo}>
          <View style={styles.fmCategoryIconLg}>
            <Icon name={getCategoryIcon(selectedCategory?.icon)} size={24} color={colors.teal} />
          </View>
          <Text style={styles.fmCategoryTitle}>{selectedCategory?.name}</Text>
        </View>
      </View>
      
      <View style={styles.fmManualGrid}>
        {filteredManuals.length === 0 ? (
          <View style={styles.fmEmptyState}>
            <Icon name="file-text" size={40} color={colors.gray} />
            <Text style={styles.fmEmptyText}>No manuals in this category yet</Text>
          </View>
        ) : (
          filteredManuals.map(manual => (
            <TouchableOpacity 
              key={manual.id}
              style={styles.fmManualCard}
              onPress={() => { setSelectedManual(manual); setViewMode('manual'); }}
            >
              <View style={styles.fmManualCardIcon}>
                <Icon name="file-text" size={20} color={colors.teal} />
              </View>
              <Text style={styles.fmManualCardTitle} numberOfLines={2}>{manual.title}</Text>
              <Text style={styles.fmManualCardSteps}>
                {manual.steps?.length || 0} steps • {manual.videos?.length || 0} videos
              </Text>
            </TouchableOpacity>
          ))
        )}
      </View>
    </View>
  );

  // Browse View (categories grid + search)
  const renderBrowseView = () => (
    <>
      <View style={styles.tabHeader}>
        <View>
          <Text style={styles.tabTitle}>Field Manuals</Text>
          <Text style={styles.tabSubtitle}>SOPs & procedures for in-field reference</Text>
        </View>
        <View style={styles.tabHeaderButtons}>
          <TouchableOpacity 
            style={[styles.secondaryBtn, showManagePanel && { backgroundColor: colors.teal + '30', borderColor: colors.teal }]} 
            onPress={() => setShowManagePanel(!showManagePanel)}
          >
            <Icon name="settings" size={16} color={showManagePanel ? colors.teal : colors.grayLight} />
            {isWeb && width > 600 && <Text style={[styles.secondaryBtnText, showManagePanel && { color: colors.teal }]}>Manage</Text>}
          </TouchableOpacity>
          <TouchableOpacity style={styles.primaryBtn} onPress={() => openManualEditor()}>
            <Icon name="plus" size={16} color={colors.darkNavy} />
            {isWeb && width > 600 && <Text style={styles.primaryBtnText}>New Manual</Text>}
          </TouchableOpacity>
        </View>
      </View>

      {/* Manage Panel - Categories */}
      {showManagePanel && (
        <View style={styles.fmManagePanel}>
          <View style={styles.fmManagePanelHeader}>
            <Text style={styles.fmManagePanelTitle}>Manage Categories</Text>
            <TouchableOpacity style={styles.fmManagePanelAddBtn} onPress={() => openCategoryEditor()}>
              <Icon name="plus" size={14} color={colors.teal} />
              <Text style={styles.fmManagePanelAddText}>Add Category</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.fmManagePanelCategories}>
            {categories.map((cat) => (
              <View key={cat.id} style={styles.fmManageCatItem}>
                <View style={[styles.fmManageCatIcon, { backgroundColor: cat.color + '20' }]}>
                  <Icon name={cat.icon || 'folder'} size={14} color={cat.color} />
                </View>
                <Text style={styles.fmManageCatName}>{cat.name}</Text>
                <Text style={styles.fmManageCatCount}>{manuals.filter(m => m.categoryId === cat.id).length}</Text>
                <TouchableOpacity style={styles.fmManageCatEditBtn} onPress={() => openCategoryEditor(cat)}>
                  <Icon name="edit-2" size={12} color={colors.gray} />
                </TouchableOpacity>
                <TouchableOpacity style={styles.fmManageCatDeleteBtn} onPress={() => deleteCategory(cat)}>
                  <Icon name="trash-2" size={12} color={colors.red} />
                </TouchableOpacity>
              </View>
            ))}
            {categories.length === 0 && (
              <Text style={styles.fmManagePanelEmpty}>No categories yet. Create one to organize your manuals.</Text>
            )}
          </View>
        </View>
      )}
      
      {/* Search Bar */}
      <View style={styles.fmSearchBar}>
        <Icon name="search" size={18} color={colors.gray} />
        <TextInput
          style={styles.fmSearchInput}
          placeholder="Search manuals..."
          placeholderTextColor={colors.gray}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Icon name="x" size={18} color={colors.gray} />
          </TouchableOpacity>
        )}
      </View>
      
      {/* Show search results if searching */}
      {searchQuery.length > 0 ? (
        <View style={styles.fmSearchResults}>
          <Text style={styles.fmSearchResultsTitle}>
            {filteredManuals.length} result{filteredManuals.length !== 1 ? 's' : ''} for "{searchQuery}"
          </Text>
          <View style={styles.fmManualGrid}>
            {filteredManuals.map(manual => (
              <TouchableOpacity 
                key={manual.id}
                style={styles.fmManualCard}
                onPress={() => { 
                  setSelectedManual(manual); 
                  setSelectedCategory(categories.find(c => c.id === manual.categoryId));
                  setViewMode('manual'); 
                }}
              >
                <View style={styles.fmManualCardIcon}>
                  <Icon name="file-text" size={20} color={colors.teal} />
                </View>
                <Text style={styles.fmManualCardTitle} numberOfLines={2}>{manual.title}</Text>
                <Text style={styles.fmManualCardCategory}>
                  {categories.find(c => c.id === manual.categoryId)?.name || 'Uncategorized'}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      ) : (
        <>
          {/* Categories Grid */}
          <Text style={styles.fmCategoriesTitle}>Browse by Category</Text>
          <View style={styles.fmCategoriesGrid}>
            {categories.map(cat => {
              const manualCount = manuals.filter(m => m.categoryId === cat.id).length;
              return (
                <TouchableOpacity 
                  key={cat.id}
                  style={styles.fmCategoryCard}
                  onPress={() => { setSelectedCategory(cat); setViewMode('category'); }}
                >
                  <View style={[styles.fmCategoryIcon, { backgroundColor: (cat.color || colors.teal) + '20' }]}>
                    <Icon name={getCategoryIcon(cat.icon)} size={24} color={cat.color || colors.teal} />
                  </View>
                  <Text style={styles.fmCategoryName}>{cat.name}</Text>
                  <Text style={styles.fmCategoryCount}>{manualCount} manual{manualCount !== 1 ? 's' : ''}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
          
          {/* Recently Viewed or Popular */}
          <Text style={styles.fmSectionHeader}>All Manuals</Text>
          <View style={styles.fmManualGrid}>
            {manuals.slice(0, 8).map(manual => (
              <TouchableOpacity 
                key={manual.id}
                style={styles.fmManualCard}
                onPress={() => { 
                  setSelectedManual(manual); 
                  setSelectedCategory(categories.find(c => c.id === manual.categoryId));
                  setViewMode('manual'); 
                }}
              >
                <View style={styles.fmManualCardIcon}>
                  <Icon name="file-text" size={20} color={colors.teal} />
                </View>
                <Text style={styles.fmManualCardTitle} numberOfLines={2}>{manual.title}</Text>
                <Text style={styles.fmManualCardCategory}>
                  {categories.find(c => c.id === manual.categoryId)?.name || 'Uncategorized'}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </>
      )}
    </>
  );

  if (loading) {
    return (
      <View style={styles.tabContent}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.teal} />
          <Text style={styles.loadingText}>Loading field manuals...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.tabContent}>
      {viewMode === 'reader' && renderReaderMode()}
      {viewMode === 'manual' && renderManualView()}
      {viewMode === 'category' && renderCategoryView()}
      {viewMode === 'browse' && renderBrowseView()}

      {/* Manual Editor Modal */}
      <Modal visible={showManualEditor} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { maxWidth: 700, maxHeight: '90%' }]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{selectedManual?.id ? 'Edit Manual' : 'New Manual'}</Text>
              <TouchableOpacity onPress={() => setShowManualEditor(false)}>
                <Icon name="x" size={24} color={colors.white} />
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Title *</Text>
                <TextInput
                  style={styles.modalInput}
                  value={editForm.title}
                  onChangeText={(val) => setEditForm({...editForm, title: val})}
                  placeholder="Manual title"
                  placeholderTextColor={colors.gray}
                />
              </View>

              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Category</Text>
                <View style={styles.fmCategorySelect}>
                  {categories.map(cat => (
                    <TouchableOpacity
                      key={cat.id}
                      style={[styles.fmCategoryOption, editForm.categoryId === cat.id && styles.fmCategoryOptionActive]}
                      onPress={() => setEditForm({...editForm, categoryId: cat.id})}
                    >
                      <Icon name={cat.icon || 'folder'} size={14} color={editForm.categoryId === cat.id ? colors.white : cat.color} />
                      <Text style={[styles.fmCategoryOptionText, editForm.categoryId === cat.id && { color: colors.white }]}>{cat.name}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Tags</Text>
                <View style={styles.fmTagsWrap}>
                  {editForm.tags.map((tag, idx) => (
                    <View key={idx} style={styles.fmTag}>
                      <Text style={styles.fmTagText}>{tag}</Text>
                      <TouchableOpacity onPress={() => setEditForm({...editForm, tags: editForm.tags.filter((_, i) => i !== idx)})}>
                        <Icon name="x" size={12} color={colors.white} />
                      </TouchableOpacity>
                    </View>
                  ))}
                  <View style={styles.fmTagInput}>
                    <TextInput
                      style={styles.fmTagInputField}
                      value={newTag}
                      onChangeText={setNewTag}
                      placeholder="Add tag..."
                      placeholderTextColor={colors.gray}
                      onSubmitEditing={addTag}
                    />
                    <TouchableOpacity onPress={addTag}>
                      <Icon name="plus" size={16} color={colors.teal} />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              {/* Videos Section - positioned before Steps for better visibility */}
              <View style={styles.modalField}>
                <View style={styles.fmStepsHeader}>
                  <Text style={styles.modalLabel}>📹 Videos</Text>
                  <Text style={styles.fmVideoCount}>
                    {editForm.videos?.length || 0} video{(editForm.videos?.length || 0) !== 1 ? 's' : ''} added
                  </Text>
                </View>
                
                {/* Existing Videos */}
                {editForm.videos && editForm.videos.length > 0 && (
                  <View style={styles.fmVideosList}>
                    {editForm.videos.map((video, idx) => (
                      <View key={video.id || idx} style={styles.fmVideoItem}>
                        <View style={styles.fmVideoThumb}>
                          {getVideoThumbnail(video.url) ? (
                            <Image 
                              source={{ uri: getVideoThumbnail(video.url) }} 
                              style={styles.fmVideoThumbImg}
                              resizeMode="cover"
                            />
                          ) : (
                            <View style={styles.fmVideoThumbPlaceholder}>
                              <Icon name={video.isUploaded ? 'film' : 'play-circle'} size={24} color={video.isUploaded ? colors.gold : colors.gray} />
                            </View>
                          )}
                          {/* Type badge */}
                          <View style={[styles.fmVideoTypeBadge, { backgroundColor: video.isUploaded ? colors.gold : colors.teal }]}>
                            <Icon name={video.isUploaded ? 'upload-cloud' : 'link'} size={10} color={colors.white} />
                          </View>
                        </View>
                        <View style={styles.fmVideoInfo}>
                          <Text style={styles.fmVideoTitle} numberOfLines={1}>{video.title}</Text>
                          <Text style={styles.fmVideoUrl} numberOfLines={1}>
                            {video.isUploaded ? `📁 ${video.fileName || 'Uploaded video'}` : video.url}
                          </Text>
                        </View>
                        <TouchableOpacity 
                          style={styles.fmVideoRemoveBtn}
                          onPress={() => removeVideo(video.id)}
                        >
                          <Icon name="trash-2" size={16} color={colors.red} />
                        </TouchableOpacity>
                      </View>
                    ))}
                  </View>
                )}
                
                {/* Add Video Form */}
                <View style={styles.fmAddVideoForm}>
                  <TextInput
                    style={styles.fmStepEditInput}
                    value={newVideoTitle}
                    onChangeText={setNewVideoTitle}
                    placeholder="Video title (optional - will use filename or URL)"
                    placeholderTextColor={colors.gray}
                  />
                  
                  {/* Option 1: Add by URL */}
                  <View style={styles.fmVideoOptionSection}>
                    <Text style={styles.fmVideoOptionLabel}>
                      <Icon name="link" size={12} color={colors.teal} /> Add by URL
                    </Text>
                  <View style={styles.fmAddVideoRow}>
                    <TextInput
                      style={[styles.fmStepEditInput, { flex: 1, marginBottom: 0 }]}
                      value={newVideoUrl}
                      onChangeText={setNewVideoUrl}
                        placeholder="YouTube, Vimeo, or Loom URL"
                      placeholderTextColor={colors.gray}
                    />
                    <TouchableOpacity 
                      style={[styles.fmAddVideoBtn, !newVideoUrl.trim() && { opacity: 0.5 }]}
                      onPress={addVideo}
                      disabled={!newVideoUrl.trim()}
                    >
                      <Icon name="plus" size={18} color={colors.darkNavy} />
                      <Text style={styles.fmAddVideoBtnText}>Add</Text>
                    </TouchableOpacity>
                  </View>
                  </View>
                  
                  {/* Option 2: Upload Video File */}
                  <View style={styles.fmVideoOptionSection}>
                    <Text style={styles.fmVideoOptionLabel}>
                      <Icon name="upload" size={12} color={colors.gold} /> Upload Video File
                    </Text>
                    <View style={styles.fmAddVideoRow}>
                      {isWeb && (
                        <input
                          ref={videoInputRef}
                          type="file"
                          accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-m4v"
                          onChange={handleVideoUpload}
                          style={{ display: 'none' }}
                        />
                      )}
                      <TouchableOpacity 
                        style={[styles.fmUploadVideoBtn, uploadingVideo && { opacity: 0.6 }]}
                        onPress={() => videoInputRef.current?.click()}
                        disabled={uploadingVideo}
                      >
                        {uploadingVideo ? (
                          <>
                            <ActivityIndicator size="small" color={colors.darkNavy} />
                            <Text style={styles.fmUploadVideoBtnText}>Uploading...</Text>
                          </>
                        ) : (
                          <>
                            <Icon name="upload-cloud" size={18} color={colors.darkNavy} />
                            <Text style={styles.fmUploadVideoBtnText}>Choose Video File</Text>
                          </>
                        )}
                      </TouchableOpacity>
                    </View>
                    <Text style={styles.fmVideoHintSmall}>
                      Max 100MB • MP4, WebM, MOV supported
                    </Text>
                  </View>
                  
                  <Text style={styles.fmVideoHint}>
                    💡 Videos will be playable directly in the manual and reader mode.
                  </Text>
                </View>
              </View>

              <View style={styles.modalField}>
                <View style={styles.fmStepsHeader}>
                  <Text style={styles.modalLabel}>Steps</Text>
                  <TouchableOpacity style={styles.fmAddStepBtn} onPress={addStep}>
                    <Icon name="plus" size={14} color={colors.teal} />
                    <Text style={styles.fmAddStepText}>Add Step</Text>
                  </TouchableOpacity>
                </View>
                {editForm.steps.map((step, idx) => (
                  <View key={idx} style={styles.fmStepEditCard}>
                    <View style={styles.fmStepEditHeader}>
                      <Text style={styles.fmStepEditNum}>Step {idx + 1}</Text>
                      {editForm.steps.length > 1 && (
                        <TouchableOpacity onPress={() => removeStep(idx)}>
                          <Icon name="trash-2" size={16} color={colors.red} />
                        </TouchableOpacity>
                      )}
                    </View>
                    <TextInput
                      style={styles.fmStepEditInput}
                      value={step.title}
                      onChangeText={(val) => updateStep(idx, 'title', val)}
                      placeholder="Step title"
                      placeholderTextColor={colors.gray}
                    />
                    <TextInput
                      style={[styles.fmStepEditInput, styles.fmStepEditTextarea]}
                      value={step.description}
                      onChangeText={(val) => updateStep(idx, 'description', val)}
                      placeholder="Step description..."
                      placeholderTextColor={colors.gray}
                      multiline
                    />
                    <View style={styles.fmStepEditRow}>
                      <View style={styles.fmStepEditField}>
                        <Text style={styles.fmStepEditFieldLabel}>⚠️ Warning</Text>
                        <TextInput
                          style={styles.fmStepEditInput}
                          value={step.warning}
                          onChangeText={(val) => updateStep(idx, 'warning', val)}
                          placeholder="Optional warning..."
                          placeholderTextColor={colors.gray}
                        />
                      </View>
                      <View style={styles.fmStepEditField}>
                        <Text style={styles.fmStepEditFieldLabel}>💡 Tip</Text>
                        <TextInput
                          style={styles.fmStepEditInput}
                          value={step.tip}
                          onChangeText={(val) => updateStep(idx, 'tip', val)}
                          placeholder="Optional tip..."
                          placeholderTextColor={colors.gray}
                        />
                      </View>
                    </View>
                  </View>
                ))}
              </View>
            </ScrollView>

            <View style={styles.modalFooter}>
              <TouchableOpacity style={styles.modalCancelBtn} onPress={() => setShowManualEditor(false)}>
                <Text style={styles.modalCancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.modalSaveBtn, saving && { opacity: 0.6 }]} 
                onPress={saveManual} 
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color={colors.darkNavy} />
                ) : (
                  <>
                    <Icon name="save" size={18} color={colors.darkNavy} />
                    <Text style={styles.modalSaveBtnText}>Save Manual</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Category Editor Modal */}
      <Modal visible={showCategoryEditor} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { maxWidth: 450 }]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{editCategory.id ? 'Edit Category' : 'New Category'}</Text>
              <TouchableOpacity onPress={() => setShowCategoryEditor(false)}>
                <Icon name="x" size={24} color={colors.white} />
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.modalBody}>
              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Name *</Text>
                <TextInput
                  style={styles.modalInput}
                  value={editCategory.name}
                  onChangeText={(val) => setEditCategory({...editCategory, name: val})}
                  placeholder="Category name"
                  placeholderTextColor={colors.gray}
                />
              </View>

              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Icon</Text>
                <View style={styles.fmIconGrid}>
                  {iconOptions.map(opt => (
                    <TouchableOpacity
                      key={opt.name}
                      style={[styles.fmIconOption, editCategory.icon === opt.name && { backgroundColor: colors.teal + '30', borderColor: colors.teal }]}
                      onPress={() => setEditCategory({...editCategory, icon: opt.name})}
                    >
                      <Icon name={opt.name} size={18} color={editCategory.icon === opt.name ? colors.teal : colors.gray} />
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>Color</Text>
                <View style={styles.fmColorGrid}>
                  {colorOptions.map((clr, idx) => (
                    <TouchableOpacity
                      key={idx}
                      style={[styles.fmColorOption, { backgroundColor: clr }, editCategory.color === clr && styles.fmColorOptionActive]}
                      onPress={() => setEditCategory({...editCategory, color: clr})}
                    >
                      {editCategory.color === clr && <Icon name="check" size={14} color={colors.white} />}
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </ScrollView>

            <View style={styles.modalFooter}>
              <TouchableOpacity style={styles.modalCancelBtn} onPress={() => setShowCategoryEditor(false)}>
                <Text style={styles.modalCancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.modalSaveBtn, saving && { opacity: 0.6 }]} 
                onPress={saveCategory} 
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color={colors.darkNavy} />
                ) : (
                  <>
                    <Icon name="save" size={18} color={colors.darkNavy} />
                    <Text style={styles.modalSaveBtnText}>Save Category</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// Legacy component references removed - FieldManualsTab now handles all functionality
const FieldManualsBrowseTab = FieldManualsTab; // Alias for backwards compatibility

// Field Manuals - Manage Tab (Create/Edit Manuals) - DEPRECATED, functionality merged into FieldManualsTab
const FieldManualsManageTab = () => {
  const [categories, setCategories] = useState([]);
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedManual, setSelectedManual] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    categoryId: '',
    tags: [],
    steps: [{ title: '', description: '', warning: '', tip: '' }],
    videos: [],
    resources: [],
  });
  const [newTag, setNewTag] = useState('');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [newResourceUrl, setNewResourceUrl] = useState('');
  const [newResourceTitle, setNewResourceTitle] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const categoriesRef = collection(db, 'fieldManualCategories');
      const catSnapshot = await getDocs(categoriesRef);
      const catList = [];
      catSnapshot.forEach(doc => catList.push({ id: doc.id, ...doc.data() }));
      setCategories(catList);
      
      const manualsRef = collection(db, 'fieldManuals');
      const manSnapshot = await getDocs(manualsRef);
      const manList = [];
      manSnapshot.forEach(doc => manList.push({ id: doc.id, ...doc.data() }));
      setManuals(manList);
    } catch (error) {
      console.error('Error loading manuals:', error);
    }
    setLoading(false);
  };

  const openEditor = (manual = null) => {
    if (manual) {
      setEditForm({
        title: manual.title || '',
        categoryId: manual.categoryId || '',
        tags: manual.tags || [],
        steps: manual.steps?.length > 0 ? manual.steps : [{ title: '', description: '', warning: '', tip: '' }],
        videos: manual.videos || [],
        resources: manual.resources || [],
      });
      setSelectedManual(manual);
    } else {
      setEditForm({
        title: '',
        categoryId: categories[0]?.id || '',
        tags: [],
        steps: [{ title: '', description: '', warning: '', tip: '' }],
        videos: [],
        resources: [],
      });
      setSelectedManual(null);
    }
    setShowEditor(true);
  };

  const saveManual = async () => {
    if (!editForm.title.trim()) {
      alert('Please enter a manual title');
      return;
    }
    
    setSaving(true);
    try {
      const manualData = {
        ...editForm,
        updatedAt: new Date().toISOString(),
      };
      
      if (selectedManual) {
        await updateDoc(doc(db, 'fieldManuals', selectedManual.id), manualData);
      } else {
        manualData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'fieldManuals'), manualData);
      }
      
      setShowEditor(false);
      loadData();
    } catch (error) {
      console.error('Error saving manual:', error);
      alert('Failed to save manual');
    }
    setSaving(false);
  };

  const deleteManual = async (manual) => {
    if (!confirm(`Delete "${manual.title}"? This cannot be undone.`)) return;
    
    try {
      await deleteDoc(doc(db, 'fieldManuals', manual.id));
      loadData();
    } catch (error) {
      console.error('Error deleting manual:', error);
    }
  };

  const addStep = () => {
    setEditForm({
      ...editForm,
      steps: [...editForm.steps, { title: '', description: '', warning: '', tip: '' }]
    });
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...editForm.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setEditForm({ ...editForm, steps: newSteps });
  };

  const removeStep = (index) => {
    if (editForm.steps.length <= 1) return;
    setEditForm({
      ...editForm,
      steps: editForm.steps.filter((_, i) => i !== index)
    });
  };

  const addTag = () => {
    if (newTag.trim() && !editForm.tags.includes(newTag.trim().toLowerCase())) {
      setEditForm({ ...editForm, tags: [...editForm.tags, newTag.trim().toLowerCase()] });
      setNewTag('');
    }
  };

  const addVideo = () => {
    if (newVideoUrl.trim()) {
      const videoType = getVideoType(newVideoUrl.trim());
      setEditForm({ 
        ...editForm, 
        videos: [...editForm.videos, { 
          url: newVideoUrl.trim(), 
          title: newVideoTitle.trim(),
          type: videoType,
          addedAt: new Date().toISOString()
        }] 
      });
      setNewVideoUrl('');
      setNewVideoTitle('');
    }
  };

  const addResource = () => {
    if (newResourceUrl.trim()) {
      setEditForm({ 
        ...editForm, 
        resources: [...editForm.resources, { url: newResourceUrl.trim(), title: newResourceTitle.trim() }] 
      });
      setNewResourceUrl('');
      setNewResourceTitle('');
    }
  };

  if (loading) {
    return (
      <View style={styles.tabContent}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.teal} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.tabContent}>
      <View style={styles.tabHeader}>
        <View>
          <Text style={styles.tabTitle}>Manage Field Manuals</Text>
          <Text style={styles.tabSubtitle}>Create and edit SOPs</Text>
        </View>
        <TouchableOpacity style={styles.primaryBtn} onPress={() => openEditor()}>
          <Icon name="plus" size={16} color={colors.darkNavy} style={{ marginRight: 8 }} />
          <Text style={styles.primaryBtnText}>New Manual</Text>
        </TouchableOpacity>
      </View>

      {/* Manuals List */}
      <View style={styles.fmManageList}>
        {manuals.map(manual => (
          <View key={manual.id} style={styles.fmManageItem}>
            <View style={styles.fmManageItemLeft}>
              <View style={styles.fmManageItemIcon}>
                <Icon name="file-text" size={18} color={colors.teal} />
              </View>
              <View>
                <Text style={styles.fmManageItemTitle}>{manual.title}</Text>
                <Text style={styles.fmManageItemMeta}>
                  {categories.find(c => c.id === manual.categoryId)?.name || 'Uncategorized'} • {manual.steps?.length || 0} steps
                </Text>
              </View>
            </View>
            <View style={styles.fmManageItemActions}>
              <TouchableOpacity style={styles.fmManageEditBtn} onPress={() => openEditor(manual)}>
                <Icon name="edit-2" size={16} color={colors.teal} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.fmManageDeleteBtn} onPress={() => deleteManual(manual)}>
                <Icon name="trash-2" size={16} color={colors.red} />
              </TouchableOpacity>
            </View>
          </View>
        ))}
      </View>

      {/* Editor Modal */}
      <Modal visible={showEditor} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.fmEditorModal}>
            <View style={styles.fmEditorHeader}>
              <Text style={styles.fmEditorTitle}>
                {selectedManual ? 'Edit Manual' : 'New Field Manual'}
              </Text>
              <TouchableOpacity onPress={() => setShowEditor(false)}>
                <Icon name="x" size={24} color={colors.white} />
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.fmEditorContent} showsVerticalScrollIndicator={false}>
              {/* Basic Info */}
              <View style={styles.fmEditorSection}>
                <Text style={styles.fmEditorLabel}>Title</Text>
                <TextInput
                  style={styles.fmEditorInput}
                  value={editForm.title}
                  onChangeText={(text) => setEditForm({ ...editForm, title: text })}
                  placeholder="e.g., Water Heater Installation"
                  placeholderTextColor={colors.gray}
                />
                
                <Text style={styles.fmEditorLabel}>Category</Text>
                <View style={styles.fmEditorCategoryPicker}>
                  {categories.map(cat => (
                    <TouchableOpacity
                      key={cat.id}
                      style={[styles.fmEditorCategoryOption, editForm.categoryId === cat.id && styles.fmEditorCategoryOptionActive]}
                      onPress={() => setEditForm({ ...editForm, categoryId: cat.id })}
                    >
                      <Text style={[styles.fmEditorCategoryText, editForm.categoryId === cat.id && { color: colors.teal }]}>
                        {cat.name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
                
                {/* Tags */}
                <Text style={styles.fmEditorLabel}>Tags</Text>
                <View style={styles.fmEditorTagsRow}>
                  <TextInput
                    style={[styles.fmEditorInput, { flex: 1 }]}
                    value={newTag}
                    onChangeText={setNewTag}
                    placeholder="Add tag..."
                    placeholderTextColor={colors.gray}
                  />
                  <TouchableOpacity style={styles.fmEditorAddBtn} onPress={addTag}>
                    <Icon name="plus" size={18} color={colors.white} />
                  </TouchableOpacity>
                </View>
                <View style={styles.fmEditorTags}>
                  {editForm.tags.map((tag, idx) => (
                    <TouchableOpacity 
                      key={idx} 
                      style={styles.fmEditorTag}
                      onPress={() => setEditForm({ ...editForm, tags: editForm.tags.filter((_, i) => i !== idx) })}
                    >
                      <Text style={styles.fmEditorTagText}>#{tag}</Text>
                      <Icon name="x" size={12} color={colors.gray} />
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              
              {/* Videos */}
              <View style={styles.fmEditorSection}>
                <Text style={styles.fmEditorSectionTitle}>
                  <Icon name="play-circle" size={16} color={colors.teal} /> Videos
                </Text>
                {editForm.videos.map((video, idx) => (
                  <View key={idx} style={styles.fmEditorVideoItem}>
                    <Text style={styles.fmEditorVideoTitle}>{video.title || video.url}</Text>
                    <TouchableOpacity onPress={() => setEditForm({ ...editForm, videos: editForm.videos.filter((_, i) => i !== idx) })}>
                      <Icon name="x" size={16} color={colors.red} />
                    </TouchableOpacity>
                  </View>
                ))}
                <View style={styles.fmEditorAddVideoRow}>
                  <TextInput
                    style={[styles.fmEditorInput, { flex: 1 }]}
                    value={newVideoTitle}
                    onChangeText={setNewVideoTitle}
                    placeholder="Video title"
                    placeholderTextColor={colors.gray}
                  />
                </View>
                <View style={styles.fmEditorAddVideoRow}>
                  <TextInput
                    style={[styles.fmEditorInput, { flex: 1 }]}
                    value={newVideoUrl}
                    onChangeText={setNewVideoUrl}
                    placeholder="Video URL (YouTube, Vimeo, etc.)"
                    placeholderTextColor={colors.gray}
                  />
                  <TouchableOpacity style={styles.fmEditorAddBtn} onPress={addVideo}>
                    <Icon name="plus" size={18} color={colors.white} />
                  </TouchableOpacity>
                </View>
              </View>
              
              {/* Steps */}
              <View style={styles.fmEditorSection}>
                <Text style={styles.fmEditorSectionTitle}>
                  <Icon name="list" size={16} color={colors.teal} /> Steps
                </Text>
                {editForm.steps.map((step, idx) => (
                  <View key={idx} style={styles.fmEditorStepCard}>
                    <View style={styles.fmEditorStepHeader}>
                      <View style={styles.fmEditorStepNum}>
                        <Text style={styles.fmEditorStepNumText}>{idx + 1}</Text>
                      </View>
                      {editForm.steps.length > 1 && (
                        <TouchableOpacity onPress={() => removeStep(idx)}>
                          <Icon name="trash-2" size={16} color={colors.red} />
                        </TouchableOpacity>
                      )}
                    </View>
                    <TextInput
                      style={styles.fmEditorInput}
                      value={step.title}
                      onChangeText={(text) => updateStep(idx, 'title', text)}
                      placeholder="Step title (optional)"
                      placeholderTextColor={colors.gray}
                    />
                    <TextInput
                      style={[styles.fmEditorInput, styles.fmEditorTextarea]}
                      value={step.description}
                      onChangeText={(text) => updateStep(idx, 'description', text)}
                      placeholder="Step description..."
                      placeholderTextColor={colors.gray}
                      multiline
                      numberOfLines={3}
                    />
                    <TextInput
                      style={styles.fmEditorInput}
                      value={step.warning}
                      onChangeText={(text) => updateStep(idx, 'warning', text)}
                      placeholder="⚠️ Warning (optional)"
                      placeholderTextColor={colors.gray}
                    />
                    <TextInput
                      style={styles.fmEditorInput}
                      value={step.tip}
                      onChangeText={(text) => updateStep(idx, 'tip', text)}
                      placeholder="💡 Tip (optional)"
                      placeholderTextColor={colors.gray}
                    />
                  </View>
                ))}
                <TouchableOpacity style={styles.fmEditorAddStepBtn} onPress={addStep}>
                  <Icon name="plus" size={16} color={colors.teal} />
                  <Text style={styles.fmEditorAddStepText}>Add Step</Text>
                </TouchableOpacity>
              </View>
              
              {/* Resources */}
              <View style={styles.fmEditorSection}>
                <Text style={styles.fmEditorSectionTitle}>
                  <Icon name="link" size={16} color={colors.teal} /> Resources
                </Text>
                {editForm.resources.map((res, idx) => (
                  <View key={idx} style={styles.fmEditorVideoItem}>
                    <Text style={styles.fmEditorVideoTitle}>{res.title || res.url}</Text>
                    <TouchableOpacity onPress={() => setEditForm({ ...editForm, resources: editForm.resources.filter((_, i) => i !== idx) })}>
                      <Icon name="x" size={16} color={colors.red} />
                    </TouchableOpacity>
                  </View>
                ))}
                <View style={styles.fmEditorAddVideoRow}>
                  <TextInput
                    style={[styles.fmEditorInput, { flex: 1 }]}
                    value={newResourceTitle}
                    onChangeText={setNewResourceTitle}
                    placeholder="Resource title"
                    placeholderTextColor={colors.gray}
                  />
                </View>
                <View style={styles.fmEditorAddVideoRow}>
                  <TextInput
                    style={[styles.fmEditorInput, { flex: 1 }]}
                    value={newResourceUrl}
                    onChangeText={setNewResourceUrl}
                    placeholder="Resource URL"
                    placeholderTextColor={colors.gray}
                  />
                  <TouchableOpacity style={styles.fmEditorAddBtn} onPress={addResource}>
                    <Icon name="plus" size={18} color={colors.white} />
                  </TouchableOpacity>
                </View>
              </View>
            </ScrollView>
            
            {/* Actions */}
            <View style={styles.fmEditorActions}>
              <TouchableOpacity style={styles.fmEditorCancelBtn} onPress={() => setShowEditor(false)}>
                <Text style={styles.fmEditorCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.fmEditorSaveBtn} onPress={saveManual} disabled={saving}>
                {saving ? (
                  <ActivityIndicator size="small" color={colors.darkNavy} />
                ) : (
                  <>
                    <Icon name="save" size={16} color={colors.darkNavy} />
                    <Text style={styles.fmEditorSaveText}>Save Manual</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// Field Manuals - Categories Tab
const FieldManualsCategoriesTab = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editCategory, setEditCategory] = useState({ name: '', icon: 'folder', color: colors.teal });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const categoriesRef = collection(db, 'fieldManualCategories');
      const snapshot = await getDocs(categoriesRef);
      const catList = [];
      snapshot.forEach(doc => catList.push({ id: doc.id, ...doc.data() }));
      setCategories(catList.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (error) {
      console.error('Error loading categories:', error);
    }
    setLoading(false);
  };

  const iconOptions = [
    { name: 'droplet', label: 'Plumbing' },
    { name: 'zap', label: 'Electrical' },
    { name: 'tool', label: 'Tools' },
    { name: 'edit-2', label: 'Painting' },
    { name: 'wind', label: 'HVAC' },
    { name: 'cpu', label: 'Appliance' },
    { name: 'briefcase', label: 'General' },
    { name: 'shield', label: 'Safety' },
    { name: 'home', label: 'Home' },
    { name: 'truck', label: 'Moving' },
    { name: 'trash-2', label: 'Cleanup' },
    { name: 'sun', label: 'Outdoor' },
  ];

  const colorOptions = [
    colors.teal,
    colors.gold,
    colors.green,
    colors.red,
    '#9b59b6',
    '#3498db',
    '#e67e22',
    '#1abc9c',
  ];

  const openEditor = (category = null) => {
    if (category) {
      setEditCategory({ ...category });
    } else {
      setEditCategory({ name: '', icon: 'folder', color: colors.teal, order: categories.length });
    }
    setShowEditor(true);
  };

  const saveCategory = async () => {
    if (!editCategory.name.trim()) {
      alert('Please enter a category name');
      return;
    }
    
    setSaving(true);
    try {
      if (editCategory.id) {
        await updateDoc(doc(db, 'fieldManualCategories', editCategory.id), {
          name: editCategory.name,
          icon: editCategory.icon,
          color: editCategory.color,
          order: editCategory.order || 0,
        });
      } else {
        await addDoc(collection(db, 'fieldManualCategories'), {
          name: editCategory.name,
          icon: editCategory.icon,
          color: editCategory.color,
          order: categories.length,
          createdAt: new Date().toISOString(),
        });
      }
      setShowEditor(false);
      loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Failed to save category');
    }
    setSaving(false);
  };

  const deleteCategory = async (category) => {
    if (!confirm(`Delete "${category.name}"? Manuals in this category will become uncategorized.`)) return;
    
    try {
      await deleteDoc(doc(db, 'fieldManualCategories', category.id));
      loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const moveCategory = async (index, direction) => {
    const newCategories = [...categories];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newCategories.length) return;
    
    [newCategories[index], newCategories[targetIndex]] = [newCategories[targetIndex], newCategories[index]];
    
    // Update order in Firestore
    try {
      for (let i = 0; i < newCategories.length; i++) {
        await updateDoc(doc(db, 'fieldManualCategories', newCategories[i].id), { order: i });
      }
      setCategories(newCategories);
    } catch (error) {
      console.error('Error reordering categories:', error);
    }
  };

  if (loading) {
    return (
      <View style={styles.tabContent}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.teal} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.tabContent}>
      <View style={styles.tabHeader}>
        <View>
          <Text style={styles.tabTitle}>Categories</Text>
          <Text style={styles.tabSubtitle}>Organize your field manuals</Text>
        </View>
        <TouchableOpacity style={styles.primaryBtn} onPress={() => openEditor()}>
          <Icon name="plus" size={16} color={colors.darkNavy} style={{ marginRight: 8 }} />
          <Text style={styles.primaryBtnText}>New Category</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.fmCatList}>
        {categories.map((cat, index) => (
          <View key={cat.id} style={styles.fmCatItem}>
            <View style={styles.fmCatItemReorder}>
              <TouchableOpacity 
                style={styles.fmCatReorderBtn}
                onPress={() => moveCategory(index, -1)}
                disabled={index === 0}
              >
                <Icon name="chevron-up" size={16} color={index === 0 ? colors.navyMid : colors.gray} />
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.fmCatReorderBtn}
                onPress={() => moveCategory(index, 1)}
                disabled={index === categories.length - 1}
              >
                <Icon name="chevron-down" size={16} color={index === categories.length - 1 ? colors.navyMid : colors.gray} />
              </TouchableOpacity>
            </View>
            <View style={[styles.fmCatItemIcon, { backgroundColor: (cat.color || colors.teal) + '20' }]}>
              <Icon name={cat.icon || 'folder'} size={20} color={cat.color || colors.teal} />
            </View>
            <Text style={styles.fmCatItemName}>{cat.name}</Text>
            <View style={styles.fmCatItemActions}>
              <TouchableOpacity style={styles.fmCatEditBtn} onPress={() => openEditor(cat)}>
                <Icon name="edit-2" size={16} color={colors.teal} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.fmCatDeleteBtn} onPress={() => deleteCategory(cat)}>
                <Icon name="trash-2" size={16} color={colors.red} />
              </TouchableOpacity>
            </View>
          </View>
        ))}
      </View>

      {/* Category Editor Modal */}
      <Modal visible={showEditor} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.fmCatEditorModal}>
            <Text style={styles.fmCatEditorTitle}>
              {editCategory.id ? 'Edit Category' : 'New Category'}
            </Text>
            
            <Text style={styles.fmEditorLabel}>Category Name</Text>
            <TextInput
              style={styles.fmEditorInput}
              value={editCategory.name}
              onChangeText={(text) => setEditCategory({ ...editCategory, name: text })}
              placeholder="e.g., Plumbing"
              placeholderTextColor={colors.gray}
            />
            
            <Text style={styles.fmEditorLabel}>Icon</Text>
            <View style={styles.fmCatIconGrid}>
              {iconOptions.map(icon => (
                <TouchableOpacity
                  key={icon.name}
                  style={[styles.fmCatIconOption, editCategory.icon === icon.name && styles.fmCatIconOptionActive]}
                  onPress={() => setEditCategory({ ...editCategory, icon: icon.name })}
                >
                  <Icon name={icon.name} size={20} color={editCategory.icon === icon.name ? colors.teal : colors.gray} />
                </TouchableOpacity>
              ))}
            </View>
            
            <Text style={styles.fmEditorLabel}>Color</Text>
            <View style={styles.fmCatColorGrid}>
              {colorOptions.map(color => (
                <TouchableOpacity
                  key={color}
                  style={[styles.fmCatColorOption, { backgroundColor: color }, editCategory.color === color && styles.fmCatColorOptionActive]}
                  onPress={() => setEditCategory({ ...editCategory, color })}
                >
                  {editCategory.color === color && <Icon name="check" size={14} color={colors.white} />}
                </TouchableOpacity>
              ))}
            </View>
            
            {/* Preview */}
            <View style={styles.fmCatPreview}>
              <Text style={styles.fmCatPreviewLabel}>Preview</Text>
              <View style={[styles.fmCatPreviewCard, { borderColor: editCategory.color }]}>
                <View style={[styles.fmCategoryIcon, { backgroundColor: editCategory.color + '20' }]}>
                  <Icon name={editCategory.icon || 'folder'} size={24} color={editCategory.color} />
                </View>
                <Text style={styles.fmCategoryName}>{editCategory.name || 'Category Name'}</Text>
              </View>
            </View>
            
            <View style={styles.fmEditorActions}>
              <TouchableOpacity style={styles.fmEditorCancelBtn} onPress={() => setShowEditor(false)}>
                <Text style={styles.fmEditorCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.fmEditorSaveBtn} onPress={saveCategory} disabled={saving}>
                {saving ? (
                  <ActivityIndicator size="small" color={colors.darkNavy} />
                ) : (
                  <>
                    <Icon name="save" size={16} color={colors.darkNavy} />
                    <Text style={styles.fmEditorSaveText}>Save</Text>
                  </>
                )}
            </TouchableOpacity>
          </View>
        </View>
    </View>
      </Modal>
  </View>
);
};

// Reminders Tab
const RemindersTab = () => (
  <View style={styles.tabContent}>
    <View style={styles.tabHeader}>
      <View>
        <Text style={styles.tabTitle}>Reminders</Text>
        <Text style={styles.tabSubtitle}>SMS & Email reminder automation</Text>
      </View>
      <TouchableOpacity style={styles.primaryBtn}>
        <Text style={styles.primaryBtnText}>+ Create Reminder</Text>
      </TouchableOpacity>
    </View>

    <View style={styles.reminderConfig}>
      <Text style={styles.sectionTitle}>Automatic Reminder Settings</Text>
      
      <View style={styles.reminderOption}>
        <View style={styles.reminderOptionLeft}>
          <View style={styles.reminderOptionIcon}>
            <Icon name="mail" size={20} color={colors.gold} />
          </View>
          <View>
            <Text style={styles.reminderOptionTitle}>Email Reminders</Text>
            <Text style={styles.reminderOptionDesc}>Send via SendGrid</Text>
          </View>
        </View>
        <View style={[styles.toggle, styles.toggleOn]}>
          <View style={[styles.toggleDot, styles.toggleDotOn]} />
        </View>
      </View>

      <View style={styles.reminderOption}>
        <View style={styles.reminderOptionLeft}>
          <View style={styles.reminderOptionIcon}>
            <Icon name="smartphone" size={20} color={colors.gold} />
          </View>
          <View>
            <Text style={styles.reminderOptionTitle}>SMS Reminders</Text>
            <Text style={styles.reminderOptionDesc}>Send via Twilio</Text>
          </View>
        </View>
        <View style={[styles.toggle, styles.toggleOn]}>
          <View style={[styles.toggleDot, styles.toggleDotOn]} />
        </View>
      </View>

      <View style={styles.reminderTimings}>
        <Text style={styles.timingTitle}>Reminder Schedule</Text>
        <View style={styles.timingOption}>
          <Icon name="check" size={16} color={colors.teal} style={{marginRight: 8}} />
          <Text style={styles.timingText}>24 hours before appointment</Text>
        </View>
        <View style={styles.timingOption}>
          <Icon name="check" size={16} color={colors.teal} style={{marginRight: 8}} />
          <Text style={styles.timingText}>1 hour before appointment</Text>
        </View>
        <View style={styles.timingOption}>
          <Icon name="check" size={16} color={colors.teal} style={{marginRight: 8}} />
          <Text style={styles.timingText}>Follow-up after job completion</Text>
        </View>
      </View>
    </View>
  </View>
);

// Service areas with coordinates (MAPBOX_PUBLIC_TOKEN is defined at top of file)
const SERVICE_AREAS = [
  { name: 'Las Vegas', center: { lat: 36.1699, lng: -115.1398 }, color: '#58d68d' },
  { name: 'North Las Vegas', center: { lat: 36.2283, lng: -115.1391 }, color: '#58a6ff' },
  { name: 'Henderson', center: { lat: 36.0395, lng: -114.9817 }, color: '#a371f7' },
  { name: 'Summerlin', center: { lat: 36.1651, lng: -115.3300 }, color: '#f1c40f' },
  { name: 'Boulder City', center: { lat: 35.9786, lng: -114.8327 }, color: '#e74c3c' },
  { name: 'Blue Diamond', center: { lat: 36.0467, lng: -115.4089 }, color: '#3498db' },
  { name: 'Mesquite', center: { lat: 36.8055, lng: -114.0672 }, color: '#e67e22' },
];

// Estimates & Quotes Tab (Site Management > Estimates)
// Includes pricing, AI prompt config, service area map, and zipcode exclusions
const EstimatesQuotesTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('pricing'); // pricing, ai, service-area
  const [pricing, setPricing] = useState({
    lineItems: [],
    serviceFee: 50,
    mileageRate: 2.5,
    freeRadius: 15,
    minimumCharge: 99
  });
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({ name: '', price: '', category: 'general' });
  
  // AI Prompt Configuration
  const [aiConfig, setAiConfig] = useState({
    systemPromptAdditions: '',
    responseGuidelines: 'Be friendly and professional. Provide accurate estimates. If unsure, recommend an on-site assessment.',
    outOfScopeKeywords: ['roofing', 'foundation', 'hvac', 'gas line', 'structural'],
    complexityRules: {
      simple: { maxItems: 2, maxHours: 2, description: 'Single task or quick fix, typically under 2 hours' },
      moderate: { maxItems: 5, maxHours: 4, description: 'Multiple tasks or medium complexity, 2-4 hours' },
      complex: { minItems: 5, minHours: 4, description: 'Major work requiring half day or more' }
    }
  });
  const [newKeyword, setNewKeyword] = useState('');
  
  // Service Area Configuration
  const [serviceAreaConfig, setServiceAreaConfig] = useState({
    serviceRadius: 35,
    excludedZipcodes: [],
    customAreas: []
  });
  const [newExcludedZip, setNewExcludedZip] = useState('');

  const categories = [
    { id: 'plumbing', label: 'Plumbing', iconName: 'droplet' },
    { id: 'electrical', label: 'Electrical', iconName: 'zap' },
    { id: 'carpentry', label: 'Carpentry', iconName: 'hammer', iconType: 'material' },
    { id: 'drywall', label: 'Drywall', iconName: 'square' },
    { id: 'painting', label: 'Painting', iconName: 'edit-2' },
    { id: 'tile', label: 'Tile/Grout', iconName: 'grid' },
    { id: 'general', label: 'General', iconName: 'tool' },
  ];

  // Load all configurations from Firestore
  useEffect(() => {
    loadAllConfigs();
  }, []);

  const loadAllConfigs = async () => {
    try {
      // Load pricing
      const pricingDoc = await getDoc(doc(db, 'settings', 'pricing'));
      if (pricingDoc.exists()) {
        setPricing(pricingDoc.data());
      } else {
        const defaultPricing = getDefaultPricing();
        setPricing(defaultPricing);
      }
      
      // Load AI config
      const aiDoc = await getDoc(doc(db, 'settings', 'aiPrompt'));
      if (aiDoc.exists()) {
        setAiConfig(aiDoc.data());
      }
      
      // Load service area config
      const serviceAreaDoc = await getDoc(doc(db, 'settings', 'serviceArea'));
      if (serviceAreaDoc.exists()) {
        setServiceAreaConfig(serviceAreaDoc.data());
      }
    } catch (error) {
      console.error('Error loading configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPricing = () => ({
    lineItems: [
      { id: 'plumbing_minor', name: 'Minor Plumbing Repair', price: 85, category: 'plumbing' },
      { id: 'plumbing_major', name: 'Major Plumbing Work', price: 175, category: 'plumbing' },
      { id: 'faucet', name: 'Faucet Replacement', price: 115, category: 'plumbing' },
      { id: 'toilet', name: 'Toilet Repair/Install', price: 135, category: 'plumbing' },
      { id: 'garbage_disposal', name: 'Garbage Disposal Install', price: 125, category: 'plumbing' },
      { id: 'electrical_outlet', name: 'Outlet Installation/Repair', price: 75, category: 'electrical' },
      { id: 'electrical_fixture', name: 'Light Fixture Installation', price: 95, category: 'electrical' },
      { id: 'electrical_fan', name: 'Ceiling Fan Installation', price: 125, category: 'electrical' },
      { id: 'carpentry_minor', name: 'Minor Carpentry/Trim', price: 75, category: 'carpentry' },
      { id: 'carpentry_door', name: 'Door Installation/Repair', price: 150, category: 'carpentry' },
      { id: 'carpentry_cabinet', name: 'Cabinet Repair/Install', price: 125, category: 'carpentry' },
      { id: 'drywall_patch', name: 'Drywall Patch (Small)', price: 65, category: 'drywall' },
      { id: 'drywall_large', name: 'Drywall Repair (Large)', price: 145, category: 'drywall' },
      { id: 'painting_room', name: 'Room Painting', price: 250, category: 'painting' },
      { id: 'painting_touch', name: 'Touch-up Painting', price: 75, category: 'painting' },
      { id: 'general_repair', name: 'General Home Repair', price: 85, category: 'general' },
      { id: 'assembly', name: 'Furniture Assembly', price: 65, category: 'general' },
      { id: 'mounting', name: 'TV/Shelf Mounting', price: 75, category: 'general' },
      { id: 'tile_minor', name: 'Minor Tile Repair', price: 95, category: 'tile' },
      { id: 'tile_grout', name: 'Grout Repair/Caulking', price: 65, category: 'tile' },
    ],
    serviceFee: 50,
    mileageRate: 2.5,
    freeRadius: 15,
    minimumCharge: 99
  });

  const saveAllConfigs = async () => {
    setSaving(true);
    try {
      // Save pricing
      await setDoc(doc(db, 'settings', 'pricing'), {
        ...pricing,
        updatedAt: serverTimestamp()
      });
      
      // Save AI config
      await setDoc(doc(db, 'settings', 'aiPrompt'), {
        ...aiConfig,
        updatedAt: serverTimestamp()
      });
      
      // Save service area config
      await setDoc(doc(db, 'settings', 'serviceArea'), {
        ...serviceAreaConfig,
        updatedAt: serverTimestamp()
      });
      
      alert('All settings saved successfully!');
    } catch (error) {
      console.error('Error saving configs:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addLineItem = () => {
    if (!newItem.name || !newItem.price) return;
    
    const id = newItem.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    const updatedItems = [...pricing.lineItems, {
      id,
      name: newItem.name,
      price: parseFloat(newItem.price),
      category: newItem.category
    }];
    
    setPricing({ ...pricing, lineItems: updatedItems });
    setNewItem({ name: '', price: '', category: 'general' });
  };

  const updateLineItem = (id, field, value) => {
    const updatedItems = pricing.lineItems.map(item =>
      item.id === id ? { ...item, [field]: field === 'price' ? parseFloat(value) || 0 : value } : item
    );
    setPricing({ ...pricing, lineItems: updatedItems });
  };

  const deleteLineItem = (id) => {
    const updatedItems = pricing.lineItems.filter(item => item.id !== id);
    setPricing({ ...pricing, lineItems: updatedItems });
  };

  const addOutOfScopeKeyword = () => {
    if (!newKeyword.trim()) return;
    setAiConfig({
      ...aiConfig,
      outOfScopeKeywords: [...(aiConfig.outOfScopeKeywords || []), newKeyword.trim().toLowerCase()]
    });
    setNewKeyword('');
  };

  const removeOutOfScopeKeyword = (keyword) => {
    setAiConfig({
      ...aiConfig,
      outOfScopeKeywords: aiConfig.outOfScopeKeywords.filter(k => k !== keyword)
    });
  };

  const addExcludedZipcode = () => {
    if (!newExcludedZip.trim() || newExcludedZip.length !== 5) {
      alert('Please enter a valid 5-digit zipcode');
      return;
    }
    if (serviceAreaConfig.excludedZipcodes.includes(newExcludedZip)) {
      alert('This zipcode is already excluded');
      return;
    }
    setServiceAreaConfig({
      ...serviceAreaConfig,
      excludedZipcodes: [...serviceAreaConfig.excludedZipcodes, newExcludedZip.trim()]
    });
    setNewExcludedZip('');
  };

  const removeExcludedZipcode = (zip) => {
    setServiceAreaConfig({
      ...serviceAreaConfig,
      excludedZipcodes: serviceAreaConfig.excludedZipcodes.filter(z => z !== zip)
    });
  };

  if (loading) {
    return (
      <View style={styles.tabContent}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.teal} />
          <Text style={styles.loadingText}>Loading configuration...</Text>
        </View>
      </View>
    );
  }

  // Group items by category
  const groupedItems = pricing.lineItems.reduce((acc, item) => {
    const cat = item.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Calculate map center (North Las Vegas area)
  const mapCenter = { lat: 36.1699, lng: -115.1398 };
  
  // Build markers for all service areas
  const serviceAreaMarkers = SERVICE_AREAS.map((area, idx) => {
    // Convert hex color to mapbox marker format (remove #)
    const markerColor = area.color.replace('#', '');
    return `pin-s+${markerColor}(${area.center.lng},${area.center.lat})`;
  }).join(',');
  
  // Add the home base marker (North Las Vegas)
  const homeMarker = `pin-l-home+22c55e(-115.1177,36.2619)`;
  
  const mapboxStaticUrl = `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/${homeMarker},${serviceAreaMarkers}/${mapCenter.lng},${mapCenter.lat},8.5,0/800x400@2x?access_token=${MAPBOX_PUBLIC_TOKEN}`;

  return (
    <View style={styles.tabContent}>
      <View style={styles.tabHeader}>
        <View>
          <Text style={styles.tabTitle}>Estimates & Quotes Configuration</Text>
          <Text style={styles.tabSubtitle}>Manage pricing, AI behavior, and service area settings</Text>
        </View>
        <TouchableOpacity 
          style={[styles.primaryBtn, saving && styles.primaryBtnDisabled]} 
          onPress={saveAllConfigs}
          disabled={saving}
        >
          <Icon name={saving ? 'loader' : 'save'} size={16} color="#0a0f1a" style={{marginRight: 8}} />
          <Text style={styles.primaryBtnText}>{saving ? 'Saving...' : 'Save All Changes'}</Text>
        </TouchableOpacity>
      </View>

      {/* Section Tabs */}
      <View style={styles.sectionTabs}>
        <TouchableOpacity 
          style={[styles.sectionTab, activeSection === 'pricing' && styles.sectionTabActive]}
          onPress={() => setActiveSection('pricing')}
        >
          <Icon name="dollar-sign" size={16} color={activeSection === 'pricing' ? colors.gold : colors.textMuted} style={{marginRight: 10}} />
          <Text style={[styles.sectionTabText, activeSection === 'pricing' && styles.sectionTabTextActive]}>
            Pricing
          </Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.sectionTab, activeSection === 'ai' && styles.sectionTabActive]}
          onPress={() => setActiveSection('ai')}
        >
          <Icon name="cpu" size={16} color={activeSection === 'ai' ? colors.gold : colors.textMuted} style={{marginRight: 10}} />
          <Text style={[styles.sectionTabText, activeSection === 'ai' && styles.sectionTabTextActive]}>
            AI Prompt
          </Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.sectionTab, activeSection === 'service-area' && styles.sectionTabActive]}
          onPress={() => setActiveSection('service-area')}
        >
          <Icon name="map" size={16} color={activeSection === 'service-area' ? colors.gold : colors.textMuted} style={{marginRight: 10}} />
          <Text style={[styles.sectionTabText, activeSection === 'service-area' && styles.sectionTabTextActive]}>
            Service Area
          </Text>
        </TouchableOpacity>
      </View>

      {/* ==================== PRICING SECTION ==================== */}
      {activeSection === 'pricing' && (
        <>
          {/* General Settings */}
          <View style={styles.pricingSection}>
            <View style={styles.sectionTitleRow}>
              <Icon name="settings" size={18} color={colors.gold} style={{marginRight: 8}} />
              <Text style={styles.sectionTitle}>General Settings</Text>
            </View>
            <View style={styles.settingsGrid}>
              <View style={styles.settingCard}>
                <Text style={styles.settingCardLabel}>Service Fee</Text>
                <Text style={styles.settingCardDesc}>Base fee for every service call</Text>
                <View style={styles.settingInputWrap}>
                  <Text style={styles.currencyPrefix}>$</Text>
                  <TextInput
                    style={styles.settingInput}
                    value={String(pricing.serviceFee)}
                    onChangeText={(val) => setPricing({ ...pricing, serviceFee: parseFloat(val) || 0 })}
                    keyboardType="numeric"
                    placeholder="50"
                    placeholderTextColor={colors.gray}
                  />
                </View>
              </View>

              <View style={styles.settingCard}>
                <Text style={styles.settingCardLabel}>Minimum Charge</Text>
                <Text style={styles.settingCardDesc}>Minimum total for any job</Text>
                <View style={styles.settingInputWrap}>
                  <Text style={styles.currencyPrefix}>$</Text>
                  <TextInput
                    style={styles.settingInput}
                    value={String(pricing.minimumCharge)}
                    onChangeText={(val) => setPricing({ ...pricing, minimumCharge: parseFloat(val) || 0 })}
                    keyboardType="numeric"
                    placeholder="99"
                    placeholderTextColor={colors.gray}
                  />
                </View>
              </View>

              <View style={styles.settingCard}>
                <Text style={styles.settingCardLabel}>Free Radius</Text>
                <Text style={styles.settingCardDesc}>Miles from base (no travel fee)</Text>
                <View style={styles.settingInputWrap}>
                  <TextInput
                    style={styles.settingInput}
                    value={String(pricing.freeRadius)}
                    onChangeText={(val) => setPricing({ ...pricing, freeRadius: parseFloat(val) || 0 })}
                    keyboardType="numeric"
                    placeholder="15"
                    placeholderTextColor={colors.gray}
                  />
                  <Text style={styles.unitSuffix}>mi</Text>
                </View>
              </View>

              <View style={styles.settingCard}>
                <Text style={styles.settingCardLabel}>Mileage Rate</Text>
                <Text style={styles.settingCardDesc}>Per mile beyond free radius</Text>
                <View style={styles.settingInputWrap}>
                  <Text style={styles.currencyPrefix}>$</Text>
                  <TextInput
                    style={styles.settingInput}
                    value={String(pricing.mileageRate)}
                    onChangeText={(val) => setPricing({ ...pricing, mileageRate: parseFloat(val) || 0 })}
                    keyboardType="numeric"
                    placeholder="2.50"
                    placeholderTextColor={colors.gray}
                  />
                  <Text style={styles.unitSuffix}>/mi</Text>
                </View>
              </View>
            </View>
          </View>

          {/* Add New Line Item */}
          <View style={styles.pricingSection}>
            <View style={styles.sectionTitleRow}>
              <Icon name="plus-circle" size={18} color={colors.gold} style={{marginRight: 8}} />
              <Text style={styles.sectionTitle}>Add New Service</Text>
            </View>
            <View style={styles.addItemRow}>
              <View style={styles.addItemField}>
                <Text style={styles.addItemLabel}>Service Name</Text>
                <TextInput
                  style={styles.addItemInput}
                  value={newItem.name}
                  onChangeText={(val) => setNewItem({ ...newItem, name: val })}
                  placeholder="e.g., Water Heater Repair"
                  placeholderTextColor={colors.gray}
                />
              </View>
              <View style={styles.addItemFieldSmall}>
                <Text style={styles.addItemLabel}>Price</Text>
                <View style={styles.priceInputWrap}>
                  <Text style={styles.currencyPrefix}>$</Text>
                  <TextInput
                    style={styles.addItemInputPrice}
                    value={newItem.price}
                    onChangeText={(val) => setNewItem({ ...newItem, price: val })}
                    keyboardType="numeric"
                    placeholder="100"
                    placeholderTextColor={colors.gray}
                  />
                </View>
              </View>
              <View style={styles.addItemFieldSmall}>
                <Text style={styles.addItemLabel}>Category</Text>
                <View style={styles.categorySelect}>
                  {categories.map(cat => (
                    <TouchableOpacity
                      key={cat.id}
                      style={[
                        styles.categoryOption,
                        newItem.category === cat.id && styles.categoryOptionActive
                      ]}
                      onPress={() => setNewItem({ ...newItem, category: cat.id })}
                    >
                      <Icon name={cat.iconName} type={cat.iconType || 'feather'} size={16} color={newItem.category === cat.id ? colors.gold : colors.textMuted} />
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              <TouchableOpacity style={styles.addItemBtn} onPress={addLineItem}>
                <Text style={styles.addItemBtnText}>+ Add</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Line Items by Category */}
          <View style={styles.pricingSection}>
            <View style={styles.sectionTitleRow}>
              <Icon name="list" size={18} color={colors.gold} style={{marginRight: 8}} />
              <Text style={styles.sectionTitle}>Service Line Items</Text>
            </View>
            <Text style={styles.sectionSubtitle}>These prices are used by the AI to generate accurate quotes</Text>
            
            {categories.map(cat => {
              const items = groupedItems[cat.id] || [];
              if (items.length === 0) return null;
              
              return (
                <View key={cat.id} style={styles.categoryGroup}>
                  <View style={styles.categoryHeader}>
                    <Icon name={cat.iconName} type={cat.iconType || 'feather'} size={18} color={colors.gold} style={{marginRight: 8}} />
                    <Text style={styles.categoryLabel}>{cat.label}</Text>
                    <Text style={styles.categoryCount}>{items.length} items</Text>
                  </View>
                  
                  <View style={styles.lineItemsTable}>
                    {items.map(item => (
                      <View key={item.id} style={styles.lineItemRow}>
                        <View style={styles.lineItemMain}>
                          {editingItem === item.id ? (
                            <TextInput
                              style={styles.lineItemNameInput}
                              value={item.name}
                              onChangeText={(val) => updateLineItem(item.id, 'name', val)}
                              onBlur={() => setEditingItem(null)}
                              autoFocus
                            />
                          ) : (
                            <TouchableOpacity onPress={() => setEditingItem(item.id)}>
                              <Text style={styles.lineItemName}>{item.name}</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                        <View style={styles.lineItemPrice}>
                          <Text style={styles.currencySmall}>$</Text>
                          <TextInput
                            style={styles.lineItemPriceInput}
                            value={String(item.price)}
                            onChangeText={(val) => updateLineItem(item.id, 'price', val)}
                            keyboardType="numeric"
                          />
                        </View>
                        <TouchableOpacity 
                          style={styles.lineItemDelete} 
                          onPress={() => deleteLineItem(item.id)}
                        >
                          <Icon name="x" size={16} color={colors.red} />
                        </TouchableOpacity>
                      </View>
                    ))}
                  </View>
                </View>
              );
            })}
          </View>
        </>
      )}

      {/* ==================== AI PROMPT SECTION ==================== */}
      {activeSection === 'ai' && (
        <>
          <View style={styles.pricingSection}>
            <View style={styles.sectionTitleRow}>
              <Icon name="cpu" size={18} color={colors.gold} style={{marginRight: 8}} />
              <Text style={styles.sectionTitle}>AI Response Guidelines</Text>
            </View>
            <Text style={styles.sectionSubtitle}>Customize how the AI generates quotes and responds to customers</Text>
            
            <View style={styles.aiConfigField}>
              <Text style={styles.aiConfigLabel}>Response Guidelines</Text>
              <Text style={styles.aiConfigDesc}>General tone and behavior instructions for the AI</Text>
              <TextInput
                style={[styles.aiConfigInput, styles.aiConfigTextarea]}
                value={aiConfig.responseGuidelines}
                onChangeText={(val) => setAiConfig({ ...aiConfig, responseGuidelines: val })}
                placeholder="e.g., Be friendly and professional. Provide accurate estimates..."
                placeholderTextColor={colors.gray}
                multiline
                numberOfLines={4}
              />
            </View>

            <View style={styles.aiConfigField}>
              <Text style={styles.aiConfigLabel}>Additional Context / Notes</Text>
              <Text style={styles.aiConfigDesc}>Extra information to include in the AI system prompt</Text>
              <TextInput
                style={[styles.aiConfigInput, styles.aiConfigTextarea]}
                value={aiConfig.systemPromptAdditions}
                onChangeText={(val) => setAiConfig({ ...aiConfig, systemPromptAdditions: val })}
                placeholder="e.g., We specialize in older homes. We offer senior discounts..."
                placeholderTextColor={colors.gray}
                multiline
                numberOfLines={4}
              />
            </View>
          </View>

          <View style={styles.pricingSection}>
            <View style={styles.sectionTitleRow}>
              <Icon name="alert-octagon" size={18} color={colors.gold} style={{marginRight: 8}} />
              <Text style={styles.sectionTitle}>Out of Scope Keywords</Text>
            </View>
            <Text style={styles.sectionSubtitle}>Jobs containing these keywords will be flagged as outside our service scope</Text>
            
            <View style={styles.keywordsContainer}>
              {(aiConfig.outOfScopeKeywords || []).map((keyword, idx) => (
                <View key={idx} style={styles.keywordTag}>
                  <Text style={styles.keywordTagText}>{keyword}</Text>
                  <TouchableOpacity onPress={() => removeOutOfScopeKeyword(keyword)}>
                    <Text style={styles.keywordTagRemove}>✕</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </View>
            
            <View style={styles.addKeywordRow}>
              <TextInput
                style={styles.addKeywordInput}
                value={newKeyword}
                onChangeText={setNewKeyword}
                placeholder="Add keyword (e.g., roofing)"
                placeholderTextColor={colors.gray}
              />
              <TouchableOpacity style={styles.addKeywordBtn} onPress={addOutOfScopeKeyword}>
                <Text style={styles.addKeywordBtnText}>+ Add</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.pricingSection}>
            <View style={styles.sectionTitleRow}>
              <Icon name="sliders" size={18} color={colors.gold} style={{marginRight: 8}} />
              <Text style={styles.sectionTitle}>Complexity Rules</Text>
            </View>
            <Text style={styles.sectionSubtitle}>Define what constitutes simple, moderate, and complex jobs</Text>
            
            <View style={styles.complexityRulesGrid}>
              {['simple', 'moderate', 'complex'].map(level => (
                <View key={level} style={[styles.complexityCard, { borderColor: COMPLEXITY_TYPES.find(c => c.id === level)?.color || colors.gray }]}>
                  <View style={styles.complexityCardHeader}>
                    <View style={[styles.complexityDot, { backgroundColor: COMPLEXITY_TYPES.find(c => c.id === level)?.color }]} />
                    <Text style={styles.complexityCardTitle}>{level.charAt(0).toUpperCase() + level.slice(1)}</Text>
                  </View>
                  <TextInput
                    style={styles.complexityDescInput}
                    value={aiConfig.complexityRules?.[level]?.description || ''}
                    onChangeText={(val) => setAiConfig({
                      ...aiConfig,
                      complexityRules: {
                        ...aiConfig.complexityRules,
                        [level]: { ...aiConfig.complexityRules?.[level], description: val }
                      }
                    })}
                    placeholder="Description..."
                    placeholderTextColor={colors.gray}
                    multiline
                  />
                </View>
              ))}
            </View>
          </View>
        </>
      )}

      {/* ==================== SERVICE AREA SECTION ==================== */}
      {activeSection === 'service-area' && (
        <>
          <View style={styles.pricingSection}>
            <View style={styles.sectionTitleRow}>
              <Icon name="map" size={18} color={colors.gold} style={{marginRight: 8}} />
              <Text style={styles.sectionTitle}>Service Area Map</Text>
            </View>
            <Text style={styles.sectionSubtitle}>Visual representation of your service coverage in the Las Vegas metro area</Text>
            
            {/* Mapbox Static Map */}
            <View style={styles.mapContainer}>
              {isWeb ? (
                <img 
                  src={mapboxStaticUrl} 
                  style={{ width: '100%', height: 400, borderRadius: 12, objectFit: 'cover' }}
                  alt="Service Area Map"
                />
              ) : (
                <View style={styles.mapPlaceholder}>
                  <Icon name="map" size={32} color={colors.textMuted} />
                  <Text style={styles.mapPlaceholderText}>Map available on web</Text>
                </View>
              )}
              
              {/* Service Areas Legend */}
              <View style={styles.mapLegend}>
                <Text style={styles.mapLegendTitle}>Service Areas</Text>
                <View style={styles.mapLegendItems}>
                  {SERVICE_AREAS.map(area => (
                    <View key={area.name} style={styles.mapLegendItem}>
                      <View style={[styles.mapLegendDot, { backgroundColor: area.color }]} />
                      <Text style={styles.mapLegendText}>{area.name}</Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>
          </View>

          <View style={styles.pricingSection}>
            <View style={styles.sectionTitleRow}>
              <Icon name="map-pin" size={18} color={colors.gold} style={{marginRight: 8}} />
              <Text style={styles.sectionTitle}>Service Radius</Text>
            </View>
            <Text style={styles.sectionSubtitle}>Maximum distance from North Las Vegas we'll travel</Text>
            
            <View style={styles.radiusConfig}>
              <View style={styles.radiusInputWrap}>
                <TextInput
                  style={styles.radiusInput}
                  value={String(serviceAreaConfig.serviceRadius)}
                  onChangeText={(val) => setServiceAreaConfig({ ...serviceAreaConfig, serviceRadius: parseInt(val) || 0 })}
                  keyboardType="numeric"
                  placeholder="35"
                  placeholderTextColor={colors.gray}
                />
                <Text style={styles.radiusUnit}>miles</Text>
              </View>
              <View style={styles.radiusVisual}>
                <View style={[styles.radiusCircle, { width: Math.min(serviceAreaConfig.serviceRadius * 4, 200), height: Math.min(serviceAreaConfig.serviceRadius * 4, 200) }]}>
                  <Text style={styles.radiusCircleText}>{serviceAreaConfig.serviceRadius} mi</Text>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.pricingSection}>
            <View style={styles.sectionTitleRow}>
              <Icon name="slash" size={18} color={colors.gold} style={{marginRight: 8}} />
              <Text style={styles.sectionTitle}>Excluded Zipcodes</Text>
            </View>
            <Text style={styles.sectionSubtitle}>Zipcodes within the service area that we don't service</Text>
            
            <View style={styles.excludedZipsList}>
              {serviceAreaConfig.excludedZipcodes.length === 0 ? (
                <Text style={styles.noExcludedText}>No zipcodes excluded</Text>
              ) : (
                serviceAreaConfig.excludedZipcodes.map((zip, idx) => (
                  <View key={idx} style={styles.excludedZipTag}>
                    <Text style={styles.excludedZipText}>{zip}</Text>
                    <TouchableOpacity onPress={() => removeExcludedZipcode(zip)}>
                      <Icon name="x" size={14} color={colors.red} />
                    </TouchableOpacity>
                  </View>
                ))
              )}
            </View>
            
            <View style={styles.addExcludedRow}>
              <TextInput
                style={styles.addExcludedInput}
                value={newExcludedZip}
                onChangeText={setNewExcludedZip}
                placeholder="Enter 5-digit zipcode"
                placeholderTextColor={colors.gray}
                keyboardType="numeric"
                maxLength={5}
              />
              <TouchableOpacity style={styles.addExcludedBtn} onPress={addExcludedZipcode}>
                <Text style={styles.addExcludedBtnText}>+ Exclude</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.serviceAreaInfo}>
            <View style={styles.serviceAreaInfoIcon}>
              <Icon name="info" size={18} color={colors.teal} />
            </View>
            <View style={styles.serviceAreaInfoContent}>
              <Text style={styles.serviceAreaInfoTitle}>No Physical Location</Text>
              <Text style={styles.serviceAreaInfoText}>
                HandyHawk operates as a mobile service based out of North Las Vegas. We travel to your location within our service area.
              </Text>
            </View>
          </View>
        </>
      )}
    </View>
  );
};

// Payment Settings Tab (Site Management > Payment)
const PaymentSettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddMethodModal, setShowAddMethodModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [uploadingQr, setUploadingQr] = useState(false);
  const [uploadingQrFor, setUploadingQrFor] = useState(null); // tracks which standard method QR is uploading
  const qrInputRef = useRef(null);
  const venmoQrRef = useRef(null);
  const cashappQrRef = useRef(null);
  const zelleQrRef = useRef(null);
  const [newMethod, setNewMethod] = useState({
    id: '',
    name: '',
    handle: '',
    url: '',
    qrCodeUrl: '',
    icon: 'credit-card',
    color: '#41B69A',
    enabled: true,
  });

  // Handle QR code image upload
  const handleQrCodeUpload = async (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setUploadingQr(true);
    try {
      const fileName = `qr-codes/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      
      setNewMethod({ ...newMethod, qrCodeUrl: downloadUrl });
    } catch (error) {
      console.error('Error uploading QR code:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingQr(false);
      // Reset file input
      if (qrInputRef.current) {
        qrInputRef.current.value = '';
      }
    }
  };

  // Handle QR code image upload for standard payment methods
  const handleStandardQrUpload = async (event, methodKey, inputRef) => {
    const file = event.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setUploadingQrFor(methodKey);
    try {
      const fileName = `qr-codes/${methodKey}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      
      setSettings(prev => ({ ...prev, [`${methodKey}QrCode`]: downloadUrl }));
    } catch (error) {
      console.error('Error uploading QR code:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingQrFor(null);
      if (inputRef?.current) {
        inputRef.current.value = '';
      }
    }
  };

  const [settings, setSettings] = useState({
    testimonialDiscount: 10,
    customMessage: 'Thank you for choosing HandyHawk! We appreciate your business.',
    testimonialPrompt: 'We\'d love to hear about your experience! Leave a review and save {discount}% on your bill.',
    venmoHandle: '@HandyHawk',
    venmoQrCode: '',
    cashappHandle: '$HandyHawk',
    cashappQrCode: '',
    zelleEmail: 'payments@handyhawk.net',
    zelleQrCode: '',
    defaultLaborRate: 75,
    defaultServiceFee: 50,
    customPaymentMethods: [],
  });

  const PAYMENT_ICONS = [
    { name: 'credit-card', label: 'Card' },
    { name: 'dollar-sign', label: 'Dollar' },
    { name: 'home', label: 'Bank' },
    { name: 'smartphone', label: 'Mobile' },
    { name: 'link', label: 'Link' },
    { name: 'grid', label: 'QR' },
    { name: 'send', label: 'Send' },
    { name: 'zap', label: 'Zap' },
    { name: 'shield', label: 'Shield' },
    { name: 'box', label: 'Crypto' },
    { name: 'globe', label: 'Web' },
    { name: 'check-circle', label: 'Check' },
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'payment'));
      if (settingsDoc.exists()) {
        setSettings({ ...settings, ...settingsDoc.data() });
      }
    } catch (error) {
      console.log('Using default payment settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'payment'), {
        ...settings,
        updatedAt: serverTimestamp()
      });
      alert('Payment settings saved!');
    } catch (error) {
      console.error('Error saving payment settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addCustomPaymentMethod = () => {
    if (!newMethod.name.trim()) {
      alert('Please enter a payment method name');
      return;
    }
    
    const methodId = newMethod.id || newMethod.name.toLowerCase().replace(/\s+/g, '_') + '_' + Date.now();
    const methodToAdd = {
      ...newMethod,
      id: methodId,
    };
    
    if (editingMethod) {
      // Update existing method
      const updatedMethods = settings.customPaymentMethods.map(m => 
        m.id === editingMethod.id ? methodToAdd : m
      );
      setSettings({ ...settings, customPaymentMethods: updatedMethods });
    } else {
      // Add new method
      setSettings({
        ...settings,
        customPaymentMethods: [...(settings.customPaymentMethods || []), methodToAdd]
      });
    }
    
    setShowAddMethodModal(false);
    setEditingMethod(null);
    setNewMethod({
      id: '',
      name: '',
      handle: '',
      url: '',
      qrCodeUrl: '',
      icon: 'credit-card',
      color: '#41B69A',
      enabled: true,
    });
  };

  const editCustomPaymentMethod = (method) => {
    setNewMethod(method);
    setEditingMethod(method);
    setShowAddMethodModal(true);
  };

  const deleteCustomPaymentMethod = (methodId) => {
    if (confirm('Are you sure you want to delete this payment method?')) {
      setSettings({
        ...settings,
        customPaymentMethods: settings.customPaymentMethods.filter(m => m.id !== methodId)
      });
    }
  };

  const toggleCustomPaymentMethod = (methodId) => {
    const updatedMethods = settings.customPaymentMethods.map(m => 
      m.id === methodId ? { ...m, enabled: !m.enabled } : m
    );
    setSettings({ ...settings, customPaymentMethods: updatedMethods });
  };

  if (loading) {
    return (
      <View style={styles.tabContent}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.teal} />
          <Text style={styles.loadingText}>Loading settings...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.tabContent}>
      <View style={styles.tabHeader}>
        <View>
          <Text style={styles.tabTitle}>Payment Settings</Text>
          <Text style={styles.tabSubtitle}>Configure payment collection flow and testimonial rewards</Text>
        </View>
        <TouchableOpacity 
          style={[styles.primaryBtn, saving && styles.primaryBtnDisabled]} 
          onPress={saveSettings}
          disabled={saving}
        >
          <Icon name={saving ? 'loader' : 'save'} size={16} color="#0a0f1a" style={{marginRight: 8}} />
          <Text style={styles.primaryBtnText}>{saving ? 'Saving...' : 'Save Settings'}</Text>
        </TouchableOpacity>
      </View>

      {/* Testimonial Reward */}
      <View style={styles.pricingSection}>
        <View style={styles.sectionTitleRow}>
          <Icon name="star" size={18} color={colors.gold} style={{marginRight: 8}} />
          <Text style={styles.sectionTitle}>Testimonial Reward</Text>
        </View>
        <Text style={styles.sectionSubtitle}>Incentivize customers to leave reviews</Text>
        
        <View style={styles.settingsGrid}>
          <View style={styles.settingCard}>
            <Text style={styles.settingCardLabel}>Discount Percentage</Text>
            <Text style={styles.settingCardDesc}>% off for leaving a review</Text>
            <View style={styles.settingInputWrap}>
              <TextInput
                style={styles.settingInput}
                value={String(settings.testimonialDiscount)}
                onChangeText={(val) => setSettings({ ...settings, testimonialDiscount: parseInt(val) || 0 })}
                keyboardType="numeric"
                placeholder="10"
                placeholderTextColor={colors.gray}
              />
              <Text style={styles.unitSuffix}>%</Text>
            </View>
          </View>

          <View style={styles.settingCard}>
            <Text style={styles.settingCardLabel}>Default Labor Rate</Text>
            <Text style={styles.settingCardDesc}>Hourly rate for labor</Text>
            <View style={styles.settingInputWrap}>
              <Text style={styles.currencyPrefix}>$</Text>
              <TextInput
                style={styles.settingInput}
                value={String(settings.defaultLaborRate)}
                onChangeText={(val) => setSettings({ ...settings, defaultLaborRate: parseFloat(val) || 0 })}
                keyboardType="numeric"
                placeholder="75"
                placeholderTextColor={colors.gray}
              />
              <Text style={styles.unitSuffix}>/hr</Text>
            </View>
          </View>
        </View>
      </View>

      {/* Standard Payment Methods */}
      <View style={styles.pricingSection}>
        <View style={styles.sectionTitleRow}>
          <Icon name="credit-card" size={18} color={colors.gold} style={{marginRight: 8}} />
          <Text style={styles.sectionTitle}>Standard Payment Methods</Text>
        </View>
        <Text style={styles.sectionSubtitle}>Configure your payment handles for customers</Text>
        
        <View style={styles.paymentSettingsGrid}>
          {/* Venmo */}
          <View style={styles.paymentMethodCard}>
            <View style={styles.paymentMethodCardHeader}>
              <View style={[styles.paymentMethodIconBg, { backgroundColor: '#3D95CE20' }]}>
                <Text style={styles.paymentMethodIconText}>💜</Text>
              </View>
              <Text style={styles.paymentMethodCardTitle}>Venmo</Text>
            </View>
            <View style={styles.paymentMethodCardBody}>
              <View style={styles.paymentMethodField}>
                <Text style={styles.paymentMethodFieldLabel}>Handle</Text>
                <TextInput
                  style={styles.paymentMethodFieldInput}
                  value={settings.venmoHandle}
                  onChangeText={(val) => setSettings({ ...settings, venmoHandle: val })}
                  placeholder="@YourHandle"
                  placeholderTextColor={colors.gray}
                />
              </View>
              <View style={styles.paymentMethodField}>
                <Text style={styles.paymentMethodFieldLabel}>QR Code Image</Text>
                <View style={styles.paymentVideoUploadRow}>
                <TextInput
                    style={[styles.paymentMethodFieldInput, { flex: 1 }]}
                  value={settings.venmoQrCode || ''}
                  onChangeText={(val) => setSettings({ ...settings, venmoQrCode: val })}
                    placeholder="Image URL or upload"
                  placeholderTextColor={colors.gray}
                />
                  {isWeb && (
                    <input
                      ref={venmoQrRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleStandardQrUpload(e, 'venmo', venmoQrRef)}
                      style={{ display: 'none' }}
                    />
                  )}
                  <TouchableOpacity
                    style={styles.paymentVideoUploadBtn}
                    onPress={() => venmoQrRef.current?.click()}
                    disabled={uploadingQrFor === 'venmo'}
                  >
                    {uploadingQrFor === 'venmo' ? (
                      <ActivityIndicator size="small" color={colors.white} />
                    ) : (
                      <Icon name="upload-cloud" size={18} color={colors.white} />
                    )}
                  </TouchableOpacity>
                </View>
              </View>
              {settings.venmoQrCode && (
                <View style={styles.qrCodePreview}>
                  <Image source={{ uri: settings.venmoQrCode }} style={styles.qrCodePreviewImage} resizeMode="contain" />
                  <TouchableOpacity
                    style={styles.qrCodeRemoveBtn}
                    onPress={() => setSettings({ ...settings, venmoQrCode: '' })}
                  >
                    <Icon name="x" size={14} color={colors.white} />
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </View>

          {/* Cash App */}
          <View style={styles.paymentMethodCard}>
            <View style={styles.paymentMethodCardHeader}>
              <View style={[styles.paymentMethodIconBg, { backgroundColor: '#00D63220' }]}>
                <Text style={styles.paymentMethodIconText}>💚</Text>
              </View>
              <Text style={styles.paymentMethodCardTitle}>Cash App</Text>
            </View>
            <View style={styles.paymentMethodCardBody}>
              <View style={styles.paymentMethodField}>
                <Text style={styles.paymentMethodFieldLabel}>Handle</Text>
                <TextInput
                  style={styles.paymentMethodFieldInput}
                  value={settings.cashappHandle}
                  onChangeText={(val) => setSettings({ ...settings, cashappHandle: val })}
                  placeholder="$YourHandle"
                  placeholderTextColor={colors.gray}
                />
              </View>
              <View style={styles.paymentMethodField}>
                <Text style={styles.paymentMethodFieldLabel}>QR Code Image</Text>
                <View style={styles.paymentVideoUploadRow}>
                <TextInput
                    style={[styles.paymentMethodFieldInput, { flex: 1 }]}
                  value={settings.cashappQrCode || ''}
                  onChangeText={(val) => setSettings({ ...settings, cashappQrCode: val })}
                    placeholder="Image URL or upload"
                  placeholderTextColor={colors.gray}
                />
                  {isWeb && (
                    <input
                      ref={cashappQrRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleStandardQrUpload(e, 'cashapp', cashappQrRef)}
                      style={{ display: 'none' }}
                    />
                  )}
                  <TouchableOpacity
                    style={styles.paymentVideoUploadBtn}
                    onPress={() => cashappQrRef.current?.click()}
                    disabled={uploadingQrFor === 'cashapp'}
                  >
                    {uploadingQrFor === 'cashapp' ? (
                      <ActivityIndicator size="small" color={colors.white} />
                    ) : (
                      <Icon name="upload-cloud" size={18} color={colors.white} />
                    )}
                  </TouchableOpacity>
                </View>
              </View>
              {settings.cashappQrCode && (
                <View style={styles.qrCodePreview}>
                  <Image source={{ uri: settings.cashappQrCode }} style={styles.qrCodePreviewImage} resizeMode="contain" />
                  <TouchableOpacity
                    style={styles.qrCodeRemoveBtn}
                    onPress={() => setSettings({ ...settings, cashappQrCode: '' })}
                  >
                    <Icon name="x" size={14} color={colors.white} />
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </View>

          {/* Zelle */}
          <View style={styles.paymentMethodCard}>
            <View style={styles.paymentMethodCardHeader}>
              <View style={[styles.paymentMethodIconBg, { backgroundColor: '#6D1ED420' }]}>
                <Text style={styles.paymentMethodIconText}>💙</Text>
              </View>
              <Text style={styles.paymentMethodCardTitle}>Zelle</Text>
            </View>
            <View style={styles.paymentMethodCardBody}>
              <View style={styles.paymentMethodField}>
                <Text style={styles.paymentMethodFieldLabel}>Email / Phone</Text>
                <TextInput
                  style={styles.paymentMethodFieldInput}
                  value={settings.zelleEmail}
                  onChangeText={(val) => setSettings({ ...settings, zelleEmail: val })}
                  placeholder="email@example.com"
                  placeholderTextColor={colors.gray}
                />
              </View>
              <View style={styles.paymentMethodField}>
                <Text style={styles.paymentMethodFieldLabel}>QR Code Image</Text>
                <View style={styles.paymentVideoUploadRow}>
                <TextInput
                    style={[styles.paymentMethodFieldInput, { flex: 1 }]}
                  value={settings.zelleQrCode || ''}
                  onChangeText={(val) => setSettings({ ...settings, zelleQrCode: val })}
                    placeholder="Image URL or upload"
                  placeholderTextColor={colors.gray}
                />
                  {isWeb && (
                    <input
                      ref={zelleQrRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleStandardQrUpload(e, 'zelle', zelleQrRef)}
                      style={{ display: 'none' }}
                    />
                  )}
                  <TouchableOpacity
                    style={styles.paymentVideoUploadBtn}
                    onPress={() => zelleQrRef.current?.click()}
                    disabled={uploadingQrFor === 'zelle'}
                  >
                    {uploadingQrFor === 'zelle' ? (
                      <ActivityIndicator size="small" color={colors.white} />
                    ) : (
                      <Icon name="upload-cloud" size={18} color={colors.white} />
                    )}
                  </TouchableOpacity>
                </View>
              </View>
              {settings.zelleQrCode && (
                <View style={styles.qrCodePreview}>
                  <Image source={{ uri: settings.zelleQrCode }} style={styles.qrCodePreviewImage} resizeMode="contain" />
                  <TouchableOpacity
                    style={styles.qrCodeRemoveBtn}
                    onPress={() => setSettings({ ...settings, zelleQrCode: '' })}
                  >
                    <Icon name="x" size={14} color={colors.white} />
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </View>
        </View>
      </View>

      {/* Custom Payment Methods */}
      <View style={styles.pricingSection}>
        <View style={styles.sectionHeaderRow}>
          <View>
            <View style={styles.sectionTitleRow}>
              <Icon name="plus-circle" size={18} color={colors.gold} style={{marginRight: 8}} />
              <Text style={styles.sectionTitle}>Custom Payment Methods</Text>
            </View>
            <Text style={styles.sectionSubtitle}>Add additional payment options like Square, PayPal, etc.</Text>
          </View>
          <TouchableOpacity 
            style={styles.addMethodBtn}
            onPress={() => {
              setEditingMethod(null);
              setNewMethod({
                id: '',
                name: '',
                handle: '',
                url: '',
                qrCodeUrl: '',
                icon: 'credit-card',
                color: '#41B69A',
                enabled: true,
              });
              setShowAddMethodModal(true);
            }}
          >
            <Icon name="plus" size={16} color={colors.gold} />
            <Text style={styles.addMethodBtnText}>Add Method</Text>
          </TouchableOpacity>
        </View>
        
        {(!settings.customPaymentMethods || settings.customPaymentMethods.length === 0) ? (
          <View style={styles.emptyCustomMethods}>
            <Icon name="credit-card" size={32} color={colors.textMuted} />
            <Text style={styles.emptyCustomMethodsText}>No custom payment methods added yet</Text>
            <Text style={styles.emptyCustomMethodsSubtext}>Click "Add Method" to add Square, PayPal, or other payment options</Text>
          </View>
        ) : (
          <View style={styles.customMethodsGrid}>
            {settings.customPaymentMethods.map((method) => (
              <View key={method.id} style={[styles.customMethodCard, !method.enabled && styles.customMethodCardDisabled]}>
                <View style={styles.customMethodCardHeader}>
                  <View style={[styles.paymentMethodIconBg, { backgroundColor: method.color + '20' }]}>
                    <Icon name={method.icon || 'credit-card'} size={20} color={method.color} />
                  </View>
                  <View style={styles.customMethodInfo}>
                    <Text style={styles.customMethodName}>{method.name}</Text>
                    <Text style={styles.customMethodHandle}>{method.handle || method.url || 'No details'}</Text>
                  </View>
                  <TouchableOpacity 
                    style={[styles.methodToggle, method.enabled && styles.methodToggleActive]}
                    onPress={() => toggleCustomPaymentMethod(method.id)}
                  >
                    <View style={[styles.methodToggleKnob, method.enabled && styles.methodToggleKnobActive]} />
                  </TouchableOpacity>
                </View>
                
                {method.qrCodeUrl && (
                  <View style={styles.customMethodQr}>
                    <Image source={{ uri: method.qrCodeUrl }} style={styles.customMethodQrImage} resizeMode="contain" />
                  </View>
                )}
                
                <View style={styles.customMethodActions}>
                  <TouchableOpacity 
                    style={styles.customMethodEditBtn}
                    onPress={() => editCustomPaymentMethod(method)}
                  >
                    <Icon name="edit-2" size={14} color={colors.teal} />
                    <Text style={styles.customMethodEditBtnText}>Edit</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={styles.customMethodDeleteBtn}
                    onPress={() => deleteCustomPaymentMethod(method.id)}
                  >
                    <Icon name="trash-2" size={14} color={colors.red} />
                    <Text style={styles.customMethodDeleteBtnText}>Delete</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>

      {/* Custom Messages */}
      <View style={styles.pricingSection}>
        <View style={styles.sectionTitleRow}>
          <Icon name="message-square" size={18} color={colors.gold} style={{marginRight: 8}} />
          <Text style={styles.sectionTitle}>Custom Messages</Text>
        </View>
        <Text style={styles.sectionSubtitle}>Personalize the payment experience</Text>
        
        <View style={styles.aiConfigField}>
          <Text style={styles.aiConfigLabel}>Testimonial Prompt</Text>
          <Text style={styles.aiConfigDesc}>Message encouraging customers to leave a review. Use {'{discount}'} to show the discount %.</Text>
          <TextInput
            style={[styles.aiConfigInput, styles.aiConfigTextarea]}
            value={settings.testimonialPrompt}
            onChangeText={(val) => setSettings({ ...settings, testimonialPrompt: val })}
            placeholder="We'd love to hear about your experience..."
            placeholderTextColor={colors.gray}
            multiline
            numberOfLines={3}
          />
        </View>

        <View style={styles.aiConfigField}>
          <Text style={styles.aiConfigLabel}>Thank You Message</Text>
          <Text style={styles.aiConfigDesc}>Shown after payment is complete</Text>
          <TextInput
            style={[styles.aiConfigInput, styles.aiConfigTextarea]}
            value={settings.customMessage}
            onChangeText={(val) => setSettings({ ...settings, customMessage: val })}
            placeholder="Thank you for choosing HandyHawk!"
            placeholderTextColor={colors.gray}
            multiline
            numberOfLines={3}
          />
        </View>
      </View>

      {/* Preview */}
      <View style={styles.pricingSection}>
        <View style={styles.sectionTitleRow}>
          <Icon name="eye" size={18} color={colors.gold} style={{marginRight: 8}} />
          <Text style={styles.sectionTitle}>Preview</Text>
        </View>
        <View style={styles.paymentPreviewCard}>
          <View style={styles.paymentPreviewOffer}>
            <Icon name="star" size={24} color={colors.gold} />
            <Text style={styles.paymentPreviewOfferTitle}>Save {settings.testimonialDiscount}% with a Review!</Text>
            <Text style={styles.paymentPreviewOfferText}>
              {settings.testimonialPrompt.replace('{discount}', settings.testimonialDiscount)}
            </Text>
          </View>
        </View>
      </View>

      {/* Add/Edit Custom Payment Method Modal */}
      <Modal
        visible={showAddMethodModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowAddMethodModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{editingMethod ? 'Edit Payment Method' : 'Add Payment Method'}</Text>
              <TouchableOpacity style={styles.modalCloseBtn} onPress={() => setShowAddMethodModal(false)}>
                <Icon name="x" size={20} color={colors.white} />
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
              <View style={styles.modalField}>
                <Text style={styles.modalFieldLabel}>Method Name *</Text>
                <TextInput
                  style={styles.modalInput}
                  value={newMethod.name}
                  onChangeText={(val) => setNewMethod({ ...newMethod, name: val })}
                  placeholder="e.g., Square, PayPal, Bitcoin"
                  placeholderTextColor={colors.gray}
                />
              </View>
              
              <View style={styles.modalField}>
                <Text style={styles.modalFieldLabel}>Handle / Username</Text>
                <TextInput
                  style={styles.modalInput}
                  value={newMethod.handle}
                  onChangeText={(val) => setNewMethod({ ...newMethod, handle: val })}
                  placeholder="e.g., @YourHandle, $YourCashtag"
                  placeholderTextColor={colors.gray}
                />
              </View>
              
              <View style={styles.modalField}>
                <Text style={styles.modalFieldLabel}>Payment URL</Text>
                <Text style={styles.modalFieldHint}>Direct link for customers to pay</Text>
                <TextInput
                  style={styles.modalInput}
                  value={newMethod.url}
                  onChangeText={(val) => setNewMethod({ ...newMethod, url: val })}
                  placeholder="https://..."
                  placeholderTextColor={colors.gray}
                />
              </View>
              
              <View style={styles.modalField}>
                <Text style={styles.modalFieldLabel}>QR Code Image</Text>
                <Text style={styles.modalFieldHint}>Upload an image or paste a URL</Text>
                
                {/* Upload Button */}
                {isWeb && (
                  <View style={styles.qrUploadRow}>
                    <TouchableOpacity 
                      style={[styles.qrUploadBtn, uploadingQr && styles.qrUploadBtnDisabled]}
                      onPress={() => qrInputRef.current?.click()}
                      disabled={uploadingQr}
                    >
                      {uploadingQr ? (
                        <ActivityIndicator size="small" color={colors.white} />
                      ) : (
                        <>
                          <Icon name="upload" size={16} color={colors.white} />
                          <Text style={styles.qrUploadBtnText}>Upload Image</Text>
                        </>
                      )}
                    </TouchableOpacity>
                    <Text style={styles.qrUploadOr}>or</Text>
                    <input
                      ref={qrInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleQrCodeUpload}
                      style={{ display: 'none' }}
                    />
                  </View>
                )}
                
                <TextInput
                  style={styles.modalInput}
                  value={newMethod.qrCodeUrl}
                  onChangeText={(val) => setNewMethod({ ...newMethod, qrCodeUrl: val })}
                  placeholder="https://example.com/qr-code.png"
                  placeholderTextColor={colors.gray}
                />
                {newMethod.qrCodeUrl && (
                  <View style={styles.modalQrPreview}>
                    <Image source={{ uri: newMethod.qrCodeUrl }} style={styles.modalQrImage} resizeMode="contain" />
                    <TouchableOpacity 
                      style={styles.qrRemoveBtn}
                      onPress={() => setNewMethod({ ...newMethod, qrCodeUrl: '' })}
                    >
                      <Icon name="x" size={14} color={colors.white} />
                    </TouchableOpacity>
                  </View>
                )}
              </View>
              
              <View style={styles.modalField}>
                <Text style={styles.modalFieldLabel}>Icon</Text>
                <View style={styles.iconPickerGrid}>
                  {PAYMENT_ICONS.map((iconObj) => (
                    <TouchableOpacity
                      key={iconObj.name}
                      style={[styles.iconPickerItem, newMethod.icon === iconObj.name && styles.iconPickerItemActive]}
                      onPress={() => setNewMethod({ ...newMethod, icon: iconObj.name })}
                    >
                      <Icon name={iconObj.name} size={20} color={newMethod.icon === iconObj.name ? colors.gold : colors.gray} />
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              
              <View style={styles.modalField}>
                <Text style={styles.modalFieldLabel}>Brand Color</Text>
                <View style={styles.colorPickerGrid}>
                  {['#41B69A', '#3D95CE', '#00D632', '#6D1ED4', '#F59E0B', '#EF4444', '#EC4899', '#8B5CF6'].map((color) => (
                    <TouchableOpacity
                      key={color}
                      style={[styles.colorPickerItem, { backgroundColor: color }, newMethod.color === color && styles.colorPickerItemActive]}
                      onPress={() => setNewMethod({ ...newMethod, color })}
                    />
                  ))}
                </View>
              </View>
            </ScrollView>
            
            <View style={styles.modalFooter}>
              <TouchableOpacity style={styles.modalCancelBtn} onPress={() => setShowAddMethodModal(false)}>
                <Text style={styles.modalCancelBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalSaveBtn} onPress={addCustomPaymentMethod}>
                <Icon name="check" size={16} color={colors.darkNavy} style={{marginRight: 6}} />
                <Text style={styles.modalSaveBtnText}>{editingMethod ? 'Save Changes' : 'Add Method'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// Maintenance Mode Tab (Site Management > Maintenance)
const MaintenanceTab = () => {
  const [settings, setSettings] = useState({
    bannerEnabled: false,
    bannerMessage: 'We are currently performing scheduled maintenance. Some features may be unavailable.',
    fullLockEnabled: false,
    lockMessage: 'HandyHawk is currently undergoing maintenance. Please check back soon!',
    estimatedEndTime: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadMaintenanceSettings();
  }, []);

  const loadMaintenanceSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'maintenance'));
      if (settingsDoc.exists()) {
        setSettings(settingsDoc.data());
      }
    } catch (error) {
      console.error('Error loading maintenance settings:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'maintenance'), {
        ...settings,
        updatedAt: serverTimestamp()
      });
      alert('Maintenance settings saved!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={styles.tabContent}>
      <View style={styles.tabHeader}>
        <View>
          <Text style={styles.tabTitle}>Maintenance Mode</Text>
          <Text style={styles.tabSubtitle}>Control site availability and display maintenance messages</Text>
        </View>
        <TouchableOpacity 
          style={[styles.primaryBtn, saving && styles.primaryBtnDisabled]} 
          onPress={saveSettings}
          disabled={saving}
        >
          <Icon name={saving ? 'loader' : 'save'} size={16} color="#0a0f1a" style={{marginRight: 8}} />
          <Text style={styles.primaryBtnText}>{saving ? 'Saving...' : 'Save Settings'}</Text>
        </TouchableOpacity>
      </View>

      {/* Banner Mode */}
      <View style={styles.maintenanceSection}>
        <View style={styles.maintenanceHeader}>
          <View style={styles.maintenanceIcon}>
            <Icon name="alert-triangle" size={24} color={colors.orange} />
          </View>
          <View style={styles.maintenanceInfo}>
            <Text style={styles.maintenanceSectionTitle}>Maintenance Banner</Text>
            <Text style={styles.maintenanceSectionDesc}>
              Show a warning banner at the top of the site while it remains accessible
            </Text>
          </View>
          <TouchableOpacity 
            style={[styles.maintenanceToggle, settings.bannerEnabled && styles.maintenanceToggleOn]}
            onPress={() => setSettings({...settings, bannerEnabled: !settings.bannerEnabled})}
          >
            <View style={[styles.maintenanceToggleDot, settings.bannerEnabled && styles.maintenanceToggleDotOn]} />
          </TouchableOpacity>
        </View>

        {settings.bannerEnabled && (
          <View style={styles.maintenancePreview}>
            <Text style={styles.previewLabel}>Banner Preview</Text>
            <View style={styles.bannerPreview}>
              <Icon name="alert-triangle" size={16} color={colors.orange} style={{marginRight: 6}} />
              <Text style={styles.bannerPreviewText}>{settings.bannerMessage || 'Enter a message below'}</Text>
            </View>
            <View style={styles.maintenanceField}>
              <Text style={styles.maintenanceFieldLabel}>Banner Message</Text>
              <TextInput
                style={[styles.maintenanceInput, styles.maintenanceTextarea]}
                value={settings.bannerMessage}
                onChangeText={(val) => setSettings({...settings, bannerMessage: val})}
                placeholder="Enter maintenance banner message..."
                placeholderTextColor={colors.gray}
                multiline
                numberOfLines={3}
              />
            </View>
          </View>
        )}
      </View>

      {/* Full Site Lock */}
      <View style={styles.maintenanceSection}>
        <View style={styles.maintenanceHeader}>
          <View style={[styles.maintenanceIcon, { backgroundColor: colors.red + '20' }]}>
            <Icon name="lock" size={24} color={colors.red} />
          </View>
          <View style={styles.maintenanceInfo}>
            <Text style={styles.maintenanceSectionTitle}>Full Site Lockdown</Text>
            <Text style={styles.maintenanceSectionDesc}>
              Make the entire site inaccessible to non-admin users
            </Text>
          </View>
          <TouchableOpacity 
            style={[styles.maintenanceToggle, settings.fullLockEnabled && styles.maintenanceToggleOnRed]}
            onPress={() => setSettings({...settings, fullLockEnabled: !settings.fullLockEnabled})}
          >
            <View style={[styles.maintenanceToggleDot, settings.fullLockEnabled && styles.maintenanceToggleDotOn]} />
          </TouchableOpacity>
        </View>

        {settings.fullLockEnabled && (
          <View style={styles.maintenancePreview}>
            <View style={styles.warningBanner}>
              <Icon name="alert-circle" size={16} color={colors.red} style={{marginRight: 6}} />
              <Text style={styles.warningBannerText}>Site lockdown is ACTIVE - non-admin users cannot access the site</Text>
            </View>
            
            <View style={styles.maintenanceField}>
              <Text style={styles.maintenanceFieldLabel}>Lockdown Message</Text>
              <TextInput
                style={[styles.maintenanceInput, styles.maintenanceTextarea]}
                value={settings.lockMessage}
                onChangeText={(val) => setSettings({...settings, lockMessage: val})}
                placeholder="Message to show visitors..."
                placeholderTextColor={colors.gray}
                multiline
                numberOfLines={3}
              />
            </View>

            <View style={styles.maintenanceField}>
              <Text style={styles.maintenanceFieldLabel}>Estimated End Time (optional)</Text>
              <TextInput
                style={styles.maintenanceInput}
                value={settings.estimatedEndTime}
                onChangeText={(val) => setSettings({...settings, estimatedEndTime: val})}
                placeholder="e.g., December 24, 2024 at 6:00 PM PST"
                placeholderTextColor={colors.gray}
              />
            </View>

            {/* Lockdown Preview */}
            <Text style={[styles.previewLabel, { marginTop: 20 }]}>Lockdown Page Preview</Text>
            <View style={styles.lockdownPreview}>
              <View style={styles.lockdownPreviewIconWrap}>
                <Icon name="tool" size={32} color={colors.gold} />
              </View>
              <Text style={styles.lockdownPreviewTitle}>HandyHawk</Text>
              <Text style={styles.lockdownPreviewMessage}>{settings.lockMessage}</Text>
              {settings.estimatedEndTime && (
                <Text style={styles.lockdownPreviewTime}>Expected back: {settings.estimatedEndTime}</Text>
              )}
            </View>
          </View>
        )}
      </View>

      {/* Status Summary */}
      <View style={styles.statusSummary}>
        <Text style={styles.statusSummaryTitle}>Current Status</Text>
        <View style={styles.statusItems}>
          <View style={styles.statusItem}>
            <View style={[styles.statusDot, { backgroundColor: settings.fullLockEnabled ? colors.red : settings.bannerEnabled ? colors.gold : colors.teal }]} />
            <Text style={styles.statusItemText}>
              {settings.fullLockEnabled ? 'Site Locked' : settings.bannerEnabled ? 'Banner Active' : 'Site Fully Operational'}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
};

// Settings Tab
const SettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null);
  const [apiStatus, setApiStatus] = useState({
    sendgrid: { connected: true, lastTest: null, error: null },
    twilio: { connected: false, lastTest: null, error: null },
    stripe: { connected: true, lastTest: null, error: null },
  });

  useEffect(() => {
    loadApiStatus();
  }, []);

  const loadApiStatus = async () => {
    try {
      const statusDoc = await getDoc(doc(db, 'settings', 'apiStatus'));
      if (statusDoc.exists()) {
        setApiStatus(prev => ({ ...prev, ...statusDoc.data() }));
      }
    } catch (error) {
      console.log('Using default API status');
    }
    setLoading(false);
  };

  const testConnection = async (service) => {
    setTesting(service);
    try {
      if (service === 'sendgrid') {
        // Test by calling a function that uses SendGrid
        const sendTest = httpsCallable(functions, 'sendBookingConfirmation');
        await sendTest({
          clientEmail: 'test@test.com',
          clientName: 'Test',
          appointmentDate: 'Test',
          appointmentTime: 'Test',
          service: 'Test'
        });
        setApiStatus(prev => ({
          ...prev,
          sendgrid: { connected: true, lastTest: new Date().toISOString(), error: null }
        }));
      } else if (service === 'stripe') {
        // Stripe is connected if we have the key
        setApiStatus(prev => ({
          ...prev,
          stripe: { connected: true, lastTest: new Date().toISOString(), error: null }
        }));
      }
      // Save status
      await setDoc(doc(db, 'settings', 'apiStatus'), apiStatus, { merge: true });
    } catch (error) {
      const errorMsg = error.message || 'Connection failed';
      setApiStatus(prev => ({
        ...prev,
        [service]: { connected: false, lastTest: new Date().toISOString(), error: errorMsg }
      }));
    }
    setTesting(null);
  };

  const integrations = [
    { 
      id: 'sendgrid', 
      name: 'SendGrid', 
      desc: 'Email delivery service', 
      icon: 'mail',
      details: 'Sends booking confirmations, invoices, reminders'
    },
    { 
      id: 'twilio', 
      name: 'Twilio', 
      desc: 'SMS messaging service', 
      icon: 'smartphone',
      details: 'Coming soon - SMS appointment reminders'
    },
    { 
      id: 'stripe', 
      name: 'Stripe', 
      desc: 'Payment processing', 
      icon: 'credit-card',
      details: 'Accept card payments, send payment links'
    },
  ];

  if (loading) {
    return (
      <View style={styles.tabContent}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.teal} />
          <Text style={styles.loadingText}>Loading settings...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.tabContent}>
      <Text style={styles.tabTitle}>Settings</Text>
      <Text style={styles.tabSubtitle}>Configure your admin preferences</Text>
      
      <View style={styles.settingsSection}>
        <Text style={styles.sectionTitle}>API Integrations</Text>
        
        {integrations.map(integration => {
          const status = apiStatus[integration.id];
          const isConnected = status?.connected;
          const isTwilio = integration.id === 'twilio';
          
          return (
            <View key={integration.id} style={styles.settingItem}>
              <View style={styles.settingLeft}>
                <View style={[styles.settingIcon, isConnected && styles.settingIconConnected]}>
                  <Icon name={integration.icon} size={20} color={isConnected ? colors.teal : colors.gold} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.settingTitle}>{integration.name}</Text>
                  <Text style={styles.settingDesc}>{integration.desc}</Text>
                  <Text style={styles.settingDetails}>{integration.details}</Text>
                </View>
              </View>
              <View style={styles.settingRight}>
                <View style={[
                  styles.settingStatusBadge, 
                  isConnected ? styles.settingStatusConnected : (isTwilio ? styles.settingStatusPending : styles.settingStatusDisconnected)
                ]}>
                  <Icon 
                    name={isConnected ? "check-circle" : (isTwilio ? "clock" : "alert-circle")} 
                    size={14} 
                    color={isConnected ? colors.teal : (isTwilio ? colors.gold : colors.red)} 
                    style={{ marginRight: 4 }}
                  />
                  <Text style={[
                    styles.settingStatusText, 
                    isConnected ? styles.settingStatusTextConnected : (isTwilio ? styles.settingStatusTextPending : {})
                  ]}>
                    {isConnected ? 'Connected' : (isTwilio ? 'Coming Soon' : 'Not Connected')}
                  </Text>
                </View>
                {status?.error && (
                  <Text style={styles.settingError}>{status.error}</Text>
                )}
              </View>
            </View>
          );
        })}
      </View>

      <View style={styles.settingsSection}>
        <Text style={styles.sectionTitle}>Quick Info</Text>
        <View style={styles.infoCard}>
          <Icon name="info" size={18} color={colors.teal} style={{ marginRight: 10 }} />
          <Text style={styles.infoText}>
            API keys are securely stored in Firebase Functions configuration. 
            To update keys, modify the functions/index.js file or use Firebase CLI config.
          </Text>
        </View>
      </View>
    </View>
  );
};

// Login Screen
const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Check if user is admin in Firestore (query by email since doc ID may not match UID)
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', user.email)
      );
      const querySnapshot = await getDocs(usersQuery);
      
      let isAdmin = false;
      querySnapshot.forEach((doc) => {
        if (doc.data().userType === 'admin') {
          isAdmin = true;
        }
      });
      
      if (isAdmin) {
        // User is admin - allow access
        onLogin(user);
    } else {
        // User exists but is not admin
        await signOut(auth);
        setError('Access denied. Admin privileges required.');
      }
    } catch (error) {
      console.log('Login error:', error);
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (error.code === 'auth/invalid-credential') {
        setError('Invalid email or password');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.loginContainer}>
      <View style={styles.loginCard}>
        <View style={styles.loginHeader}>
          <Text style={styles.loginLogo}>HANDY</Text>
          <Text style={styles.loginLogoAccent}>HAWK</Text>
        </View>
        <Text style={styles.loginTitle}>Admin Login</Text>
        <Text style={styles.loginSubtitle}>Sign in with your admin account</Text>
        
        {error ? <Text style={styles.loginError}>{error}</Text> : null}
        
        <TextInput
          style={styles.loginInput}
          placeholder="Email"
          placeholderTextColor={colors.gray}
          keyboardType="email-address"
          autoCapitalize="none"
          value={email}
          onChangeText={setEmail}
        />
        
        <TextInput
          style={styles.loginInput}
          placeholder="Password"
          placeholderTextColor={colors.gray}
          secureTextEntry
          value={password}
          onChangeText={setPassword}
          onSubmitEditing={handleLogin}
        />
        
        <TouchableOpacity 
          style={[styles.loginBtn, loading && styles.loginBtnDisabled]} 
          onPress={handleLogin}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator color={colors.darkNavy} size="small" />
          ) : (
          <Text style={styles.loginBtnText}>Login</Text>
          )}
        </TouchableOpacity>
        
        <TouchableOpacity onPress={() => window.location.href = '/'}>
          <Text style={styles.backLink}>← Back to Website</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

// Main Admin Screen
export default function AdminScreen() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [navOptions, setNavOptions] = useState(null); // Options like { category: 'potential' }
  const [authLoading, setAuthLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Collapsed by default on mobile
  const [windowWidth, setWindowWidth] = useState(width);
  
  // Mobile detection
  const isMobile = windowWidth < 768;

  // Listen for window resize
  useEffect(() => {
    if (isWeb && typeof window !== 'undefined') {
      const handleResize = () => {
        const newWidth = window.innerWidth;
        const wasMobile = windowWidth < 768;
        const nowMobile = newWidth < 768;
        
        setWindowWidth(newWidth);
        
        // Only auto-change sidebar state when crossing the mobile/desktop threshold
        if (wasMobile !== nowMobile) {
          if (nowMobile) {
            setSidebarCollapsed(true); // Always collapse when switching to mobile
        } else {
            setSidebarCollapsed(false); // Expand when switching to desktop
          }
        }
      };
      
      // Set initial state based on screen size
      const initialWidth = window.innerWidth;
      setWindowWidth(initialWidth);
      setSidebarCollapsed(initialWidth < 768);
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Update page title based on active tab
  useEffect(() => {
    if (isWeb && typeof document !== 'undefined') {
      const tabTitles = {
        'dashboard': {
          'overview': 'Dashboard',
          'scheduler': 'Scheduler',
          'appointments': 'Appointments',
          'route-mapper': 'Route Mapper',
          'clients': 'Clients',
          'finances': 'Finances',
        },
        'site-management': {
          'hours': 'Business Hours',
          'services': 'Services',
          'portfolio': 'Portfolio',
          'testimonials': 'Testimonials',
          'estimates': 'Estimates & Quotes',
          'payment': 'Payment Settings',
          'maintenance': 'Maintenance',
        },
        'field-manuals': 'Field Manuals',
        'reminders': 'Reminders',
        'settings': 'Settings',
      };

      let pageTitle = 'Admin';
      if (typeof tabTitles[activeTab] === 'string') {
        pageTitle = tabTitles[activeTab];
      } else if (tabTitles[activeTab] && tabTitles[activeTab][activeSubTab]) {
        pageTitle = tabTitles[activeTab][activeSubTab];
      }
      
      document.title = `Handy Hawk | ${pageTitle}`;
    }
  }, [activeTab, activeSubTab]);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Animated margin for main content on desktop
  const contentMarginAnim = useRef(new Animated.Value(sidebarCollapsed ? 0 : 280)).current;
  
  useEffect(() => {
    Animated.spring(contentMarginAnim, {
      toValue: (!isMobile && !sidebarCollapsed) ? 280 : 0,
      friction: 20,
      tension: 100,
      useNativeDriver: false,
    }).start();
  }, [sidebarCollapsed, isMobile]);

  // Check Firebase Auth state on mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in - verify they're still an admin (query by email)
        try {
          const usersQuery = query(
            collection(db, 'users'),
            where('email', '==', user.email)
          );
          const querySnapshot = await getDocs(usersQuery);
          
          let isAdmin = false;
          querySnapshot.forEach((doc) => {
            if (doc.data().userType === 'admin') {
              isAdmin = true;
            }
          });
          
          if (isAdmin) {
            setCurrentUser(user);
        setIsLoggedIn(true);
          } else {
            // Not an admin, sign out
            await signOut(auth);
            setIsLoggedIn(false);
            setCurrentUser(null);
          }
        } catch (error) {
          console.log('Error verifying admin status:', error);
          setIsLoggedIn(false);
          setCurrentUser(null);
        }
      } else {
        // User is signed out
        setIsLoggedIn(false);
        setCurrentUser(null);
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = (user) => {
    setCurrentUser(user);
    setIsLoggedIn(true);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    setIsLoggedIn(false);
      setCurrentUser(null);
    if (isWeb && typeof window !== 'undefined') {
      window.location.href = '/';
      }
    } catch (error) {
      console.log('Logout error:', error);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.gold} />
        <Text style={styles.authLoadingText}>Loading...</Text>
      </View>
    );
  }

  if (!isLoggedIn) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  // Navigation handler for dashboard quick links
  const handleDashboardNavigate = (tab, subTab, options = null) => {
    setActiveTab(tab);
    if (subTab) {
      setActiveSubTab(subTab);
    }
    setNavOptions(options);
  };

  const renderTab = () => {
    // Dashboard section with sub-tabs
    if (activeTab === 'dashboard') {
      switch (activeSubTab) {
        case 'overview':
          return <OverviewTab onNavigate={handleDashboardNavigate} />;
        case 'leads':
          return <LeadsTab 
            onConvertToAppointment={(estimate) => {
              setActiveSubTab('scheduler');
              // Could pass estimate data to pre-fill
            }}
          />;
      case 'scheduler':
          return <SchedulerTab 
            onNavigateToRoute={() => { setActiveTab('dashboard'); setActiveSubTab('route-mapper'); }}
            onNavigateToAppointments={() => { setActiveTab('dashboard'); setActiveSubTab('appointments'); }}
          />;
        case 'appointments':
          return <AppointmentsTab 
            onNavigateToClient={(clientId) => { 
              setActiveTab('dashboard'); 
              setActiveSubTab('clients');
              // Could store clientId to highlight/filter in ClientsTab
            }}
          />;
        case 'route-mapper':
          return <RouteMapperTab 
            onNavigateToClient={(clientId) => { 
              setActiveTab('dashboard'); 
              setActiveSubTab('clients'); 
            }}
            onNavigateToAppointments={(aptId) => { 
              setActiveTab('dashboard'); 
              setActiveSubTab('appointments'); 
            }}
          />;
        case 'clients':
          return <ClientsTab 
            initialCategory={navOptions?.category || null}
            onViewClientJobs={(clientId) => {
              setActiveTab('dashboard');
              setActiveSubTab('appointments');
            }}
            onCategoryViewed={() => setNavOptions(null)}
          />;
        case 'finances':
          return <FinancesTab />;
        default:
          return <OverviewTab onNavigate={handleDashboardNavigate} />;
      }
    }
    
    // Site Management section with sub-tabs
    if (activeTab === 'site-management') {
      switch (activeSubTab) {
        case 'hours':
          return <HoursTab />;
        case 'services':
          return <ServicesTab />;
        case 'portfolio':
          return <PortfolioTab />;
      case 'testimonials':
        return <TestimonialsTab />;
        case 'estimates':
          return <EstimatesQuotesTab />;
        case 'payment':
          return <PaymentSettingsTab />;
        case 'maintenance':
          return <MaintenanceTab />;
        default:
          return <HoursTab />;
      }
    }
    
    // Field Manuals section with sub-tabs
    if (activeTab === 'field-manuals') {
      return <FieldManualsTab />;
    }
    
    // Other top-level tabs
    switch (activeTab) {
      case 'reminders':
        return <RemindersTab />;
      case 'settings':
        return <SettingsTab />;
      default:
        return <OverviewTab />;
    }
  };

  return (
    <View style={[styles.container, isMobile && styles.containerMobile]}>
      {/* Mobile Header with Hamburger */}
      {isMobile && (
        <View style={styles.mobileHeader}>
          <TouchableOpacity style={styles.hamburgerBtn} onPress={toggleSidebar}>
            <Icon name="menu" size={24} color={colors.white} />
          </TouchableOpacity>
          <View style={styles.mobileLogoWrap}>
            <Text style={styles.mobileLogoText}>HANDY</Text>
            <Text style={styles.mobileLogoAccent}>HAWK</Text>
          </View>
          <View style={styles.mobileHeaderRight}>
            <TouchableOpacity onPress={handleLogout}>
              <Icon name="log-out" size={20} color={colors.gray} />
            </TouchableOpacity>
          </View>
        </View>
      )}
      
      {/* Desktop Sidebar Toggle Button - shown when sidebar is collapsed */}
      {!isMobile && sidebarCollapsed && (
        <Animated.View style={[styles.desktopSidebarToggle, { opacity: contentMarginAnim.interpolate({
          inputRange: [0, 280],
          outputRange: [1, 0],
          extrapolate: 'clamp'
        }) }]}>
          <TouchableOpacity onPress={toggleSidebar}>
            <Icon name="menu" size={20} color={colors.white} />
          </TouchableOpacity>
        </Animated.View>
      )}
      
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        activeSubTab={activeSubTab}
        setActiveSubTab={setActiveSubTab}
        onLogout={handleLogout}
        isCollapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        isMobile={isMobile}
      />
      <Animated.ScrollView 
        style={[
          styles.mainContent, 
          isMobile && styles.mainContentMobile,
          !isMobile && { marginLeft: contentMarginAnim }
        ]} 
        showsVerticalScrollIndicator={false}
      >
        {renderTab()}
      </Animated.ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.darkNavy,
    minHeight: '100vh',
  },
  containerMobile: {
    flexDirection: 'column',
  },

  // Mobile Header
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.navy,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder,
    zIndex: 100,
  },
  hamburgerBtn: {
    padding: 8,
  },
  mobileLogoWrap: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  mobileLogoText: {
    fontSize: 18,
    fontWeight: '800',
    color: colors.white,
  },
  mobileLogoAccent: {
    fontSize: 18,
    fontWeight: '800',
    color: colors.gold,
  },
  mobileHeaderRight: {
    padding: 8,
  },

  // Sidebar Overlay (Mobile)
  sidebarOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 998,
  },

  // Sidebar
  sidebar: {
    width: 280,
    backgroundColor: colors.navy,
    borderRightWidth: 1,
    borderRightColor: colors.glassBorder,
    paddingVertical: 24,
    zIndex: 999,
    ...(isWeb ? { boxShadow: '4px 0 20px rgba(0,0,0,0.2)' } : {}),
  },
  sidebarMobile: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: 280,
    paddingTop: 60,
  },
  sidebarDesktop: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: 280,
  },
  sidebarHidden: {
    // Animation handles this via translateX
  },
  sidebarCloseBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    padding: 8,
  },
  sidebarCollapseBtnDesktop: {
    top: 24,
    right: 12,
    padding: 8,
    backgroundColor: colors.navyLight,
    borderRadius: 8,
  },
  desktopSidebarToggle: {
    position: 'absolute',
    top: 20,
    left: 20,
    zIndex: 100,
    backgroundColor: colors.navy,
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.navyLight,
    ...(isWeb ? { boxShadow: '0 4px 12px rgba(0,0,0,0.3)' } : {}),
  },
  sidebarHeader: {
    flexDirection: 'column',
    paddingHorizontal: 24,
    marginBottom: 32,
  },
  sidebarLogoRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  sidebarLogo: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.white,
  },
  sidebarLogoAccent: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.teal,
  },
  sidebarAdminBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: colors.teal + '15',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.teal + '30',
    alignSelf: 'flex-start',
  },
  sidebarAdminDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.teal,
    marginRight: 8,
  },
  sidebarAdminText: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.teal,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  sidebarMenu: {
    flex: 1,
  },
  sidebarItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 24,
    marginBottom: 4,
  },
  sidebarItemActive: {
    backgroundColor: colors.teal + '15',
    borderLeftWidth: 3,
    borderLeftColor: colors.teal,
  },
  sidebarItemIcon: {
    width: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  sidebarItemText: {
    fontSize: 14,
    color: colors.gray,
    fontWeight: '500',
    flex: 1,
  },
  sidebarItemTextActive: {
    color: colors.white,
  },
  sidebarItemExpanded: {
    backgroundColor: colors.navyLight + '50',
  },
  sidebarExpandIcon: {
    marginLeft: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sidebarSubMenu: {
    paddingLeft: 20,
    marginBottom: 8,
  },
  sidebarSubItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 24,
    paddingLeft: 44,
  },
  sidebarSubItemActive: {
    backgroundColor: colors.teal + '20',
    borderLeftWidth: 2,
    borderLeftColor: colors.teal,
  },
  sidebarSubItemIcon: {
    width: 22,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  sidebarSubItemText: {
    fontSize: 13,
    color: colors.gray,
    fontWeight: '400',
  },
  sidebarSubItemTextActive: {
    color: colors.teal,
    fontWeight: '500',
  },
  sidebarBadge: {
    marginLeft: 'auto',
    paddingRight: 4,
  },
  sidebarBadgeDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.gold,
  },
  sidebarFooter: {
    paddingHorizontal: 24,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.navyLight,
  },
  logoutBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
  },
  logoutIcon: {
    width: 22,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  logoutText: {
    fontSize: 14,
    color: colors.gray,
  },
  
  // Section Header Styles
  sidebarSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 10,
  },
  sidebarEventSource: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  sidebarEventSourceText: {
    fontSize: 11,
    color: colors.textMuted,
  },
  sidebarEventNotesWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    gap: 6,
  },
  dashboardCardTitleWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 10,
  },
  ratingIconWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  lockdownPreviewIconWrap: {
    marginBottom: 16,
  },

  // Main Content
  mainContent: {
    flex: 1,
    padding: 32,
  },
  mainContentMobile: {
    padding: 16,
  },
  tabContent: {
    flex: 1,
  },
  tabHeader: {
    flexDirection: isWeb && width > 768 ? 'row' : 'column',
    justifyContent: 'space-between',
    alignItems: isWeb && width > 768 ? 'flex-start' : 'stretch',
    gap: 16,
    marginBottom: 32,
    flexWrap: 'wrap',
  },
  tabTitle: {
    fontSize: 28,
    fontWeight: '800',
    color: colors.white,
    marginBottom: 10,
    letterSpacing: -0.5,
  },
  tabSubtitle: {
    fontSize: 15,
    color: colors.gray,
    lineHeight: 22,
    marginBottom: 28,
  },

  // Stats Grid
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: isWeb && width > 600 ? 20 : 12,
    marginBottom: isWeb && width > 600 ? 40 : 24,
  },
  statCard: {
    flex: 1,
    minWidth: isWeb && width > 600 ? 200 : 140,
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    borderLeftWidth: 4,
    position: 'relative',
    ...(isWeb ? { boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } : {}),
  },
  statCardClickable: {
    ...(isWeb ? { 
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    } : {}),
  },
  statCardArrow: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.navyMid,
    justifyContent: 'center',
    alignItems: 'center',
  },
  statCardArrowText: {
    color: colors.gray,
    fontSize: 14,
  },
  statNumber: {
    fontSize: 36,
    fontWeight: '800',
    color: colors.white,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 14,
    color: colors.gray,
    marginBottom: 8,
  },
  statChange: {
    fontSize: 12,
    color: colors.teal,
  },

  // Dashboard Grid
  dashboardGrid: {
    flexDirection: isWeb && width > 900 ? 'row' : 'column',
    gap: 24,
    marginBottom: 32,
  },
  dashboardCard: {
    flex: 1,
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    ...(isWeb ? { boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } : {}),
  },
  dashboardCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  dashboardCardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
  dashboardCardLink: {
    fontSize: 13,
    color: colors.teal,
  },
  miniEventCard: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
  },
  miniEventDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 12,
  },
  miniEventInfo: {
    flex: 1,
  },
  miniEventTitle: {
    fontSize: 14,
    color: colors.white,
    fontWeight: '500',
  },
  miniEventClient: {
    fontSize: 12,
    color: colors.gray,
  },
  miniEventTime: {
    alignItems: 'flex-end',
  },
  miniEventDate: {
    fontSize: 11,
    color: colors.gray,
  },
  miniEventHour: {
    fontSize: 12,
    color: colors.teal,
    fontWeight: '500',
  },
  quickActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  quickActionBtn: {
    flexBasis: isWeb && width > 600 ? 'calc(25% - 8px)' : 'calc(50% - 6px)',
    flexGrow: 0,
    flexShrink: 0,
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center',
  },
  quickActionIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.gold + '20',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  quickActionText: {
    fontSize: 12,
    color: colors.gray,
    fontWeight: '500',
    textAlign: 'center',
  },
  activitySection: {
    marginTop: 16,
  },
  activityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  activityViewAll: {
    color: colors.teal,
    fontSize: 13,
    fontWeight: '600',
  },
  activityFeed: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    ...(isWeb ? { cursor: 'pointer' } : {}),
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
  },
  activityDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginRight: 12,
    marginTop: 4,
  },
  activityContent: {
    flex: 1,
  },
  activityText: {
    fontSize: 14,
    color: colors.grayLight,
    lineHeight: 20,
  },
  activityBold: {
    fontWeight: '600',
    color: colors.white,
  },
  activityTime: {
    fontSize: 12,
    color: colors.gray,
    marginTop: 4,
  },

  // Scheduler / Calendar
  schedulerContainer: {
    flexDirection: isWeb && width > 1200 ? 'row' : 'column',
    gap: 24,
    flex: 1,
  },
  calendarMain: {
    flex: 1,
    minWidth: 0, // Prevent flex item from overflowing
  },
  calendarNav: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    gap: 12,
  },
  calendarNavBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  calendarNavBtnText: {
    fontSize: 14,
    color: colors.grayLight,
  },
  calendarMonthTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
    flex: 1,
  },
  todayBtn: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: colors.teal + '20',
    borderRadius: 8,
  },
  todayBtnText: {
    fontSize: 13,
    color: colors.teal,
    fontWeight: '600',
  },
  calendarGrid: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  calendarHeader: {
    flexDirection: 'row',
    backgroundColor: colors.navyLight,
  },
  calendarHeaderCell: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
  },
  calendarHeaderText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.gray,
    textTransform: 'uppercase',
  },
  calendarBody: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  calendarCell: {
    width: '14.28%',
    minHeight: isWeb && width > 768 ? 100 : 80,
    padding: 6,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: colors.glassBorder,
    alignItems: 'stretch',
    transition: 'all 0.15s ease',
  },
  calendarCellToday: {
    backgroundColor: colors.teal + '10',
  },
  calendarCellSelected: {
    backgroundColor: colors.gold + '20',
    borderColor: colors.gold,
    borderWidth: 2,
  },
  calendarCellHasEvents: {
    backgroundColor: colors.navyMid + '50',
  },
  calendarDayNum: {
    fontSize: 14,
    color: colors.grayLight,
    fontWeight: '500',
    marginBottom: 4,
  },
  calendarDayNumToday: {
    color: colors.teal,
    fontWeight: '700',
  },
  calendarDayNumSelected: {
    color: colors.gold,
    fontWeight: '700',
  },
  calendarEventsList: {
    flex: 1,
    gap: 3,
    marginTop: 4,
  },
  calendarEventItem: {
    backgroundColor: colors.navyLight,
    borderRadius: 4,
    paddingVertical: 3,
    paddingHorizontal: 5,
    borderLeftWidth: 3,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  calendarEventTime: {
    fontSize: 9,
    color: colors.gray,
    fontWeight: '600',
  },
  calendarEventTitle: {
    fontSize: 10,
    color: colors.white,
    fontWeight: '500',
  },
  calendarEventDots: {
    flexDirection: 'row',
    gap: 3,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  calendarEventDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  calendarMoreEvents: {
    fontSize: 9,
    color: colors.gray,
    textAlign: 'center',
    marginTop: 2,
  },
  calendarLegend: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 24,
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.navyLight,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  legendText: {
    fontSize: 12,
    color: colors.gray,
  },

  // Events Sidebar
  eventsSidebar: {
    width: isWeb && width > 1200 ? 350 : '100%',
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: colors.navyLight,
    maxHeight: isWeb && width > 1200 ? 700 : 'auto',
    marginTop: isWeb && width <= 1200 ? 16 : 0,
  },
  sidebarSection: {
    marginBottom: 24,
  },
  sidebarSectionSelected: {
    backgroundColor: colors.teal + '10',
    borderRadius: 12,
    padding: 12,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: colors.teal + '30',
  },
  sidebarSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 0,
  },
  sidebarEventAddressWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
  },
  sidebarEventAddress: {
    fontSize: 12,
    color: colors.textMuted,
    flex: 1,
  },
  sidebarEventStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  sidebarEventStatusText: {
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  noEventsText: {
    fontSize: 13,
    color: colors.gray,
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 20,
  },
  sidebarEventCardWrapper: {
    flexDirection: 'row',
    alignItems: 'stretch',
    marginBottom: 12,
    gap: 8,
  },
  sidebarEventCard: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    overflow: 'hidden',
  },
  sidebarEventArchiveBtn: {
    backgroundColor: colors.red + '15',
    borderRadius: 8,
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.red + '30',
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  sidebarEventBar: {
    width: 4,
  },
  sidebarEventContent: {
    flex: 1,
    padding: 12,
  },
  sidebarEventTime: {
    fontSize: 12,
    color: colors.teal,
    fontWeight: '600',
    marginBottom: 4,
  },
  sidebarEventTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 2,
  },
  sidebarEventClient: {
    fontSize: 13,
    color: colors.grayLight,
    marginBottom: 8,
  },
  sidebarEventMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  sidebarEventSource: {
    fontSize: 11,
    color: colors.gray,
  },
  sidebarEventNotes: {
    fontSize: 11,
    color: colors.gray,
    marginTop: 8,
    fontStyle: 'italic',
  },
  sidebarEventMiniWrapper: {
    flexDirection: 'row',
    alignItems: 'stretch',
    marginBottom: 10,
    gap: 6,
  },
  sidebarEventMini: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.navyLight + '50',
    borderRadius: 8,
    overflow: 'hidden',
  },
  sidebarEventMiniArchiveBtn: {
    backgroundColor: colors.red + '10',
    borderRadius: 6,
    padding: 6,
    justifyContent: 'center',
    alignItems: 'center',
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  sidebarEventActions: {
    position: 'relative',
  },
  sidebarEventMenuBtn: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: colors.navyLight,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  sidebarEventMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    padding: 6,
    minWidth: 140,
    zIndex: 100,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  sidebarEventMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 6,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  sidebarEventMenuText: {
    fontSize: 13,
    fontWeight: '500',
  },
  sidebarEventMenuDivider: {
    height: 1,
    backgroundColor: colors.glassBorder,
    marginVertical: 6,
  },
  sidebarEventMiniBar: {
    width: 3,
  },
  sidebarEventMiniContent: {
    padding: 10,
    flex: 1,
  },
  sidebarEventMiniDate: {
    fontSize: 10,
    color: colors.gray,
  },
  sidebarEventMiniTitle: {
    fontSize: 12,
    color: colors.grayLight,
    fontWeight: '500',
  },
  sidebarEventMiniClient: {
    fontSize: 11,
    color: colors.gray,
  },

  // Client Type Badge
  clientTypeBadge: {
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  clientTypeBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
  },

  // Route Mapper Styles
  routeSettingsPanel: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  routeSettingsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 12,
  },
  routeSettingsInput: {
    backgroundColor: colors.navy,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  routeSettingsHint: {
    fontSize: 12,
    color: colors.gray,
    marginTop: 8,
  },
  routeDateNav: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.navyLight,
    borderRadius: 16,
    padding: 16,
    marginBottom: 24,
  },
  routeDateArrow: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.navy,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  routeDateCenter: {
    alignItems: 'center',
    flex: 1,
  },
  routeDateText: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
    textAlign: 'center',
  },
  routeTodayBtn: {
    marginTop: 8,
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: colors.teal + '20',
    borderRadius: 20,
  },
  routeTodayBtnText: {
    fontSize: 12,
    color: colors.teal,
    fontWeight: '600',
  },
  routeTodayBadge: {
    marginTop: 8,
    paddingVertical: 4,
    paddingHorizontal: 12,
    backgroundColor: colors.green,
    borderRadius: 20,
  },
  routeTodayBadgeText: {
    fontSize: 11,
    color: colors.white,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  routeSummaryCard: {
    backgroundColor: colors.navyLight,
    borderRadius: 16,
    padding: 24,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  routeSummaryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  routeSummaryIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    backgroundColor: colors.teal + '15',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  routeSummaryInfo: {
    flex: 1,
  },
  routeSummaryTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 4,
  },
  routeSummarySubtitle: {
    fontSize: 14,
    color: colors.gray,
  },
  openRouteBtn: {
    flex: isWeb && width > 600 ? 0 : 1,
    minWidth: isWeb && width > 600 ? 'auto' : 80,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.teal,
    paddingVertical: 12,
    paddingHorizontal: isWeb && width > 600 ? 20 : 12,
    borderRadius: 10,
    gap: 6,
  },
  openRouteBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  routeStopsList: {
    backgroundColor: colors.navyLight,
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  routeStop: {
    flexDirection: 'row',
    marginBottom: 8,
    position: 'relative',
  },
  routeStopMarker: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    zIndex: 2,
  },
  routeStopNumber: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  routeStopLine: {
    position: 'absolute',
    left: 17,
    top: 36,
    bottom: -8,
    width: 2,
    backgroundColor: colors.navyMid,
    zIndex: 1,
  },
  routeStopContent: {
    flex: 1,
    paddingBottom: 24,
  },
  routeStopLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: colors.green,
    letterSpacing: 1,
    marginBottom: 4,
  },
  routeStopTime: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.gold,
    marginBottom: 4,
  },
  routeStopTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 4,
  },
  routeStopService: {
    fontSize: 13,
    color: colors.gray,
    marginBottom: 8,
  },
  routeStopAddressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
  },
  routeStopAddress: {
    fontSize: 13,
    color: colors.gray,
  },
  stopDurationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: colors.navyMid,
  },
  stopDurationLabel: {
    fontSize: 13,
    color: colors.gold,
    fontWeight: '500',
  },
  stopDurationInput: {
    backgroundColor: colors.navy,
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    width: 60,
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    textAlign: 'center',
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  stopDurationUnit: {
    fontSize: 13,
    color: colors.gray,
  },
  routeStopPhone: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: colors.teal + '15',
    borderRadius: 20,
    alignSelf: 'flex-start',
  },
  routeStopPhoneText: {
    fontSize: 12,
    color: colors.teal,
    fontWeight: '600',
  },
  routeWarningBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.gold + '15',
    borderWidth: 1,
    borderColor: colors.gold + '40',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    gap: 10,
  },
  routeWarningText: {
    flex: 1,
    fontSize: 13,
    color: colors.gold,
    lineHeight: 18,
  },
  routeEmptyState: {
    alignItems: 'center',
    paddingVertical: 48,
  },
  routeEmptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.white,
    marginTop: 16,
    marginBottom: 8,
  },
  routeEmptyText: {
    fontSize: 14,
    color: colors.gray,
    textAlign: 'center',
    lineHeight: 22,
  },
  routeMapContainer: {
    marginVertical: 16,
    borderRadius: 12,
    overflow: 'hidden',
    cursor: 'pointer',
    height: 350,
    backgroundColor: colors.navyMid,
  },
  mapClickOverlay: {
    position: 'absolute',
    bottom: 16,
    right: 16,
    zIndex: 10,
  },
  mapClickBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 25,
    backdropFilter: 'blur(8px)',
  },
  mapClickText: {
    color: colors.white,
    fontSize: 13,
    fontWeight: '600',
  },
  mapZoomControls: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 30,
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 8,
    padding: 6,
    backdropFilter: 'blur(8px)',
  },
  mapZoomBtn: {
    width: 36,
    height: 36,
    borderRadius: 6,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  mapZoomLevel: {
    color: colors.white,
    fontSize: 12,
    fontWeight: '700',
    marginVertical: 4,
  },
  routeButtonsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  mapToggleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: colors.navy,
    borderRadius: 8,
    gap: 6,
  },
  mapToggleBtnText: {
    fontSize: 12,
    color: colors.white,
    fontWeight: '500',
  },
  routeStopHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  routeStopHeaderInfo: {
    flex: 1,
  },
  routeStopActions: {
    flexDirection: 'row',
    gap: 8,
  },
  routeStopNavBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: colors.teal + '20',
    borderRadius: 6,
    gap: 4,
  },
  routeStopNavBtnText: {
    fontSize: 11,
    color: colors.teal,
    fontWeight: '600',
  },
  routeStopFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 8,
  },
  routeStopViewBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  routeStopViewBtnText: {
    fontSize: 11,
    color: colors.gray,
  },
  
  // Enhanced Route Mapper Styles
  routeStatsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 16,
    marginVertical: 16,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: colors.navyMid,
  },
  routeStatItem: {
    alignItems: 'center',
    gap: 4,
  },
  routeStatValue: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
  },
  routeStatLabel: {
    fontSize: 11,
    color: colors.gray,
  },
  routeActionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 16,
  },
  routeStopsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
  },
  routeStopsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
  routeStopsStatusCounts: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 6,
  },
  routeStopsCountBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  routeStopsCountDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  routeStopsCountText: {
    fontSize: 12,
    fontWeight: '600',
  },
  routeStopsSubtitle: {
    fontSize: 12,
    color: colors.gray,
  },
  routeLegInfo: {
    paddingLeft: 18,
    marginVertical: 4,
    position: 'relative',
  },
  routeLegLine: {
    position: 'absolute',
    left: 17,
    top: 0,
    bottom: 0,
    width: 2,
    backgroundColor: colors.navyMid,
  },
  routeLegBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.navy,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 20,
    marginLeft: 20,
    alignSelf: 'flex-start',
  },
  routeLegText: {
    fontSize: 11,
    color: colors.teal,
    fontWeight: '500',
  },
  routeStopETA: {
    backgroundColor: colors.teal + '20',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
  },
  routeStopETAText: {
    fontSize: 11,
    color: colors.teal,
    fontWeight: '600',
  },
  routeStopPending: {
    borderLeftWidth: 3,
    borderLeftColor: colors.gold,
    backgroundColor: colors.gold + '08',
  },
  routeStopStatusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
    alignSelf: 'flex-start',
    marginBottom: 10,
    gap: 6,
  },
  routeStopStatusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  routeStopStatusText: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  routePendingActions: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 12,
  },
  routeApproveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: colors.green,
    borderRadius: 8,
  },
  routeApproveBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  routeRejectBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: colors.red,
    borderRadius: 8,
  },
  routeRejectBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.white,
  },
  routeAddStoreBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: colors.orange + '20',
    borderRadius: 6,
    alignSelf: 'flex-start',
    marginTop: 8,
    borderWidth: 1,
    borderColor: colors.orange + '40',
  },
  routeAddStoreBtnText: {
    fontSize: 12,
    color: colors.orange,
    fontWeight: '500',
  },
  routeStopActionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 10,
  },
  routeStopActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: colors.navy,
    borderRadius: 6,
  },
  routeStopActionBtnDisabled: {
    opacity: 0.5,
  },
  routeStopActionBtnText: {
    fontSize: 11,
    color: colors.white,
    fontWeight: '500',
  },
  routeStopRemoveBtn: {
    padding: 6,
    backgroundColor: colors.red + '20',
    borderRadius: 6,
  },
  routeMapLoading: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.navy + '90',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
    zIndex: 1,
  },
  routeMapLoadingText: {
    fontSize: 12,
    color: colors.teal,
    marginTop: 8,
  },
  routeSettingsLocation: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.navyMid,
  },
  routeSettingsLocationText: {
    fontSize: 12,
    color: colors.teal,
  },
  storeOptionsDropdown: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 12,
    marginTop: 10,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  storeOptionsTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.gray,
    marginBottom: 10,
  },
  storeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
  },
  storeOptionInfo: {
    flex: 1,
  },
  storeOptionName: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  storeOptionAddress: {
    fontSize: 11,
    color: colors.gray,
    marginTop: 2,
  },
  storeOptionsClose: {
    alignItems: 'center',
    paddingVertical: 10,
    marginTop: 8,
  },
  storeOptionsCloseText: {
    fontSize: 12,
    color: colors.teal,
    fontWeight: '500',
  },
  addStopModal: {
    backgroundColor: colors.navyLight,
    borderRadius: 16,
    width: isWeb ? 450 : '95%',
    maxWidth: 450,
    maxHeight: '90%',
  },
  addStopModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
  },
  addStopModalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
  },
  addStopModalBody: {
    padding: 20,
  },
  addStopLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.gray,
    marginBottom: 8,
    marginTop: 16,
  },
  addStopInput: {
    backgroundColor: colors.navy,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 16,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  addStopDurationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  addStopDurationUnit: {
    fontSize: 14,
    color: colors.gray,
  },
  addStopSelectRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  addStopSelectBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.navy,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: colors.navyMid,
  },
  addStopSelectBtnActive: {
    borderColor: colors.teal,
    backgroundColor: colors.teal + '20',
  },
  addStopSelectBtnText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.gray,
  },
  addStopSelectBtnTextActive: {
    color: colors.teal,
  },
  quickStoreButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  quickStoreBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: colors.navy,
    borderRadius: 20,
    borderWidth: 1,
  },
  quickStoreBtnIcon: {
    fontSize: 14,
  },
  quickStoreBtnText: {
    fontSize: 12,
    color: colors.white,
    fontWeight: '500',
  },
  addStopModalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    padding: 20,
    paddingTop: 0,
  },
  addStopCancelBtn: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    backgroundColor: colors.navy,
  },
  addStopCancelBtnText: {
    fontSize: 14,
    color: colors.gray,
    fontWeight: '500',
  },
  addStopSaveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    backgroundColor: colors.teal,
  },
  addStopSaveBtnText: {
    fontSize: 14,
    color: colors.darkNavy,
    fontWeight: '600',
  },
  
  // Appointments Tab Styles
  appointmentTabs: {
    flexDirection: 'row',
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 4,
    marginBottom: 24,
    gap: 4,
  },
  appointmentTab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    gap: 8,
  },
  appointmentTabActive: {
    backgroundColor: colors.navyLight,
  },
  appointmentTabBadge: {
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
  },
  appointmentTabBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  appointmentTabText: {
    fontSize: 13,
    color: colors.gray,
    fontWeight: '500',
  },
  appointmentTabTextActive: {
    color: colors.white,
  },
  appointmentsList: {
    gap: 16,
  },
  appointmentsEmpty: {
    alignItems: 'center',
    paddingVertical: 60,
    backgroundColor: colors.navyLight,
    borderRadius: 16,
  },
  appointmentsEmptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.white,
    marginTop: 16,
    marginBottom: 8,
  },
  appointmentsEmptyText: {
    fontSize: 14,
    color: colors.gray,
    textAlign: 'center',
  },
  appointmentCard: {
    backgroundColor: colors.navyLight,
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  appointmentConflict: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.gold + '15',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  appointmentConflictText: {
    fontSize: 12,
    color: colors.gold,
    fontWeight: '500',
  },
  appointmentCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  appointmentDateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.navy,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 20,
  },
  appointmentDateText: {
    fontSize: 13,
    color: colors.white,
    fontWeight: '600',
  },
  appointmentTimeText: {
    fontSize: 13,
    color: colors.gold,
    fontWeight: '600',
  },
  appointmentStatus: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
  },
  appointmentStatusText: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  appointmentClientRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  appointmentClientName: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
    flex: 1,
  },
  appointmentService: {
    fontSize: 15,
    color: colors.teal,
    fontWeight: '500',
    marginBottom: 8,
  },
  appointmentDescription: {
    fontSize: 13,
    color: colors.gray,
    lineHeight: 20,
    marginBottom: 12,
  },
  appointmentEstimate: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.green + '15',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginBottom: 12,
  },
  appointmentEstimateText: {
    fontSize: 13,
    color: colors.green,
    fontWeight: '600',
  },
  appointmentContact: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 12,
  },
  appointmentContactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  appointmentContactText: {
    fontSize: 13,
    color: colors.teal,
  },
  appointmentSuggestions: {
    backgroundColor: colors.navy,
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  appointmentSuggestionsTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.teal,
    marginBottom: 8,
  },
  appointmentSuggestionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 4,
  },
  appointmentSuggestionText: {
    fontSize: 12,
    color: colors.gray,
  },
  appointmentActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: colors.navyMid,
    paddingTop: 16,
  },
  appointmentActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    gap: 6,
  },
  appointmentApproveBtn: {
    backgroundColor: colors.teal,
  },
  appointmentRejectBtn: {
    backgroundColor: colors.red,
  },
  appointmentCompleteBtn: {
    backgroundColor: colors.green,
  },
  appointmentActionBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.white,
  },
  appointmentViewBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: colors.navy,
    gap: 6,
    marginLeft: 'auto',
  },
  appointmentViewBtnText: {
    fontSize: 13,
    color: colors.teal,
    fontWeight: '500',
  },
  confirmModal: {
    backgroundColor: colors.navyLight,
    borderRadius: 16,
    padding: 24,
    width: isWeb ? 400 : '90%',
    maxWidth: 400,
  },
  confirmModalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 12,
  },
  confirmModalText: {
    fontSize: 14,
    color: colors.gray,
    lineHeight: 22,
    marginBottom: 24,
  },
  confirmModalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  confirmModalCancel: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: colors.navy,
  },
  confirmModalCancelText: {
    fontSize: 14,
    color: colors.gray,
    fontWeight: '500',
  },
  confirmModalConfirm: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  confirmModalConfirmText: {
    fontSize: 14,
    color: colors.white,
    fontWeight: '600',
  },
  detailModal: {
    backgroundColor: colors.navyLight,
    borderRadius: 16,
    width: isWeb ? 500 : '95%',
    maxWidth: 500,
    maxHeight: '80%',
  },
  detailModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
  },
  detailModalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
  },
  detailModalContent: {
    padding: 20,
  },
  detailSection: {
    marginBottom: 16,
  },
  detailLabel: {
    fontSize: 12,
    color: colors.gray,
    fontWeight: '500',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  detailValue: {
    fontSize: 15,
    color: colors.white,
    lineHeight: 22,
  },
  
  // NEW Compact Appointment Card Styles
  aptStatusTabs: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  aptStatusTab: {
    flex: isWeb ? 'none' : 1,
    minWidth: isWeb ? 100 : undefined,
    backgroundColor: colors.navy,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  aptStatusTabActive: {
    backgroundColor: colors.navyLight,
    borderWidth: 1,
    borderColor: colors.teal + '40',
  },
  aptStatusTabCount: {
    fontSize: 22,
    fontWeight: '800',
  },
  aptStatusTabLabel: {
    fontSize: 12,
    color: colors.gray,
    fontWeight: '500',
    marginTop: 2,
  },
  aptStatusTabLabelActive: {
    color: colors.white,
  },
  aptEmptyState: {
    alignItems: 'center',
    paddingVertical: 50,
  },
  aptEmptyIcon: {
    width: 70,
    height: 70,
    borderRadius: 35,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  aptEmptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 6,
  },
  aptEmptyText: {
    fontSize: 13,
    color: colors.gray,
  },
  aptGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  aptCard: {
    width: isWeb ? 'calc(25% - 9px)' : '100%',
    minWidth: isWeb ? 220 : undefined,
    backgroundColor: colors.navyLight,
    borderRadius: 14,
    padding: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
    ...(isWeb ? { transition: 'all 0.2s ease' } : {}),
  },
  aptCardTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  aptCardDate: {
    alignItems: 'center',
    backgroundColor: colors.navy,
    borderRadius: 10,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  aptCardDateDay: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.white,
    lineHeight: 22,
  },
  aptCardDateMonth: {
    fontSize: 10,
    fontWeight: '600',
    color: colors.teal,
    textTransform: 'uppercase',
  },
  aptCardTopRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  aptRevisedBadge: {
    backgroundColor: colors.gold + '20',
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  aptRevisedBadgeText: {
    fontSize: 9,
    fontWeight: '700',
    color: colors.gold,
    textTransform: 'uppercase',
  },
  aptConflictBadge: {
    backgroundColor: colors.gold + '20',
    padding: 5,
    borderRadius: 6,
  },
  aptStatusBadge: {
    padding: 6,
    borderRadius: 6,
  },
  aptStatusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  aptCardTime: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.gold,
    marginBottom: 6,
  },
  aptCardClient: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 3,
  },
  aptCardService: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.teal,
    marginBottom: 6,
  },
  aptCardDesc: {
    fontSize: 11,
    color: colors.gray,
    lineHeight: 16,
    marginBottom: 8,
  },
  aptCardEstimate: {
    backgroundColor: colors.green + '15',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    alignSelf: 'flex-start',
    marginBottom: 10,
  },
  aptCardEstimateText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.green,
  },
  aptCardQuickActions: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 10,
  },
  aptCardQuickBtn: {
    backgroundColor: colors.navy,
    padding: 8,
    borderRadius: 8,
  },
  aptCardActions: {
    flexDirection: 'row',
    gap: 6,
    borderTopWidth: 1,
    borderTopColor: colors.navyMid,
    paddingTop: 10,
    marginTop: 'auto',
  },
  aptApproveBtn: {
    flex: 1,
    backgroundColor: colors.green,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  aptRejectBtn: {
    flex: 1,
    backgroundColor: colors.red,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  aptCompleteBtn: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.teal,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  aptCompleteBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.white,
  },
  aptStartBtn: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.blue,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  aptStartBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.white,
  },
  
  // Confirmation Modal Enhanced Styles
  aptConfirmModal: {
    backgroundColor: colors.navy,
    borderRadius: 20,
    width: isWeb ? 450 : '95%',
    maxWidth: 450,
    overflow: 'hidden',
  },
  aptConfirmHeader: {
    alignItems: 'center',
    paddingTop: 28,
    paddingBottom: 16,
  },
  aptConfirmIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  aptConfirmTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
  },
  aptConfirmSummary: {
    alignItems: 'center',
    paddingVertical: 16,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: colors.navyLight,
    marginHorizontal: 20,
  },
  aptConfirmClient: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 2,
  },
  aptConfirmService: {
    fontSize: 14,
    color: colors.teal,
    marginBottom: 4,
  },
  aptConfirmDate: {
    fontSize: 12,
    color: colors.gray,
  },
  aptManualSection: {
    padding: 20,
  },
  aptManualTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  aptSuggestBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.gold + '20',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    alignSelf: 'flex-start',
  },
  aptSuggestBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.gold,
  },
  aptSelectedManuals: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  aptSelectedManualTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.teal + '20',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.teal + '40',
  },
  aptSelectedManualText: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.teal,
    maxWidth: 150,
  },
  aptManualActions: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  aptSuggestedSection: {
    marginBottom: 12,
  },
  aptSuggestedLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  aptSuggestedList: {
    gap: 6,
  },
  aptAllManualsSection: {
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
  },
  aptAllManualsList: {
    maxHeight: 150,
  },
  aptNoManualsText: {
    fontSize: 13,
    color: colors.gray,
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 20,
  },
  aptManualBrowseItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
  },
  aptManualBrowseItemSelected: {
    backgroundColor: colors.teal + '10',
    marginHorizontal: -12,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderBottomWidth: 0,
  },
  aptManualBrowseCheck: {
    marginRight: 12,
  },
  aptManualBrowseInfo: {
    flex: 1,
  },
  aptManualBrowseTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.white,
  },
  aptManualBrowseMeta: {
    fontSize: 11,
    color: colors.gray,
    marginTop: 2,
  },
  aptSuggestedItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  aptSuggestedItemSelected: {
    borderColor: colors.teal,
    backgroundColor: colors.teal + '10',
  },
  aptSuggestedItemText: {
    fontSize: 13,
    color: colors.white,
  },
  aptManualNote: {
    fontSize: 11,
    color: colors.gray,
    fontStyle: 'italic',
    marginTop: 12,
  },
  aptRejectSection: {
    padding: 20,
  },
  aptRejectModeTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 12,
  },
  aptRejectModes: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 16,
  },
  aptRejectMode: {
    flex: 1,
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    padding: 14,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  aptRejectModeActive: {
    borderColor: colors.teal,
  },
  aptRejectModeText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.white,
    marginTop: 8,
    marginBottom: 4,
  },
  aptRejectModeDesc: {
    fontSize: 10,
    color: colors.gray,
    textAlign: 'center',
  },
  aptRejectMsgLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.gray,
    marginBottom: 8,
  },
  aptRejectMsgInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    padding: 12,
    fontSize: 14,
    color: colors.white,
    minHeight: 80,
    textAlignVertical: 'top',
  },
  aptConfirmActions: {
    flexDirection: 'row',
    gap: 10,
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: colors.navyLight,
  },
  aptConfirmCancel: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: colors.navyLight,
    alignItems: 'center',
  },
  aptConfirmCancelText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray,
  },
  aptConfirmSubmit: {
    flex: 2,
    flexDirection: 'row',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  aptConfirmSubmitText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  
  // Detail Modal Enhanced Styles
  aptDetailModal: {
    backgroundColor: colors.navy,
    borderRadius: 20,
    width: isWeb ? 520 : '95%',
    maxWidth: 520,
    maxHeight: '90%',
  },
  aptDetailHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
  },
  aptDetailHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  aptDetailTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
  },
  aptDetailRevisedTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.gold + '20',
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  aptDetailRevisedText: {
    fontSize: 10,
    fontWeight: '600',
    color: colors.gold,
  },
  aptDetailClose: {
    padding: 4,
  },
  aptDetailContent: {
    padding: 20,
  },
  aptDetailStatusBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    marginBottom: 20,
    borderLeftWidth: 3,
  },
  aptDetailStatusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  aptDetailStatusText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.white,
  },
  aptDetailCard: {
    flexDirection: 'row',
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
    gap: 14,
  },
  aptDetailCardIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: colors.navy,
    justifyContent: 'center',
    alignItems: 'center',
  },
  aptDetailCardContent: {
    flex: 1,
  },
  aptDetailCardLabel: {
    fontSize: 11,
    color: colors.gray,
    fontWeight: '500',
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  aptDetailCardValue: {
    fontSize: 15,
    color: colors.white,
    fontWeight: '600',
  },
  aptDetailCardSub: {
    fontSize: 13,
    color: colors.gold,
    fontWeight: '500',
    marginTop: 2,
  },
  aptDetailContactRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 10,
  },
  aptDetailContactBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.teal,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  aptDetailContactBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.white,
  },
  aptDetailSection: {
    marginTop: 16,
    marginBottom: 16,
  },
  aptDetailSectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.gray,
    marginBottom: 10,
  },
  aptDetailDescription: {
    fontSize: 14,
    color: colors.white,
    lineHeight: 22,
    backgroundColor: colors.navyLight,
    padding: 14,
    borderRadius: 10,
  },
  aptDetailEstimateCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: colors.green + '15',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    marginVertical: 12,
  },
  aptDetailEstimateLabel: {
    fontSize: 13,
    color: colors.green,
    fontWeight: '500',
  },
  aptDetailEstimateValue: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.green,
    marginLeft: 'auto',
  },
  aptDetailMapBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 10,
  },
  aptDetailMapBtnText: {
    fontSize: 12,
    color: colors.teal,
    fontWeight: '500',
  },
  aptDetailManualsList: {
    gap: 8,
  },
  aptDetailManualItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: colors.navyLight,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
  },
  aptDetailManualText: {
    fontSize: 13,
    color: colors.white,
  },
  aptDetailRevisionBox: {
    backgroundColor: colors.gold + '15',
    borderLeftWidth: 3,
    borderLeftColor: colors.gold,
    borderRadius: 8,
    padding: 14,
    marginVertical: 12,
  },
  aptDetailRevisionTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.gold,
    marginBottom: 8,
  },
  aptDetailRevisionText: {
    fontSize: 13,
    color: colors.white,
    lineHeight: 20,
  },
  aptDetailMeta: {
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.navyLight,
    gap: 4,
  },
  aptDetailMetaText: {
    fontSize: 11,
    color: colors.gray,
  },
  aptDetailActions: {
    gap: 10,
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.navyLight,
  },
  aptDetailActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingVertical: 14,
    borderRadius: 12,
  },
  aptDetailApproveBtn: {
    backgroundColor: colors.green,
  },
  aptDetailRejectBtn: {
    backgroundColor: colors.navyLight,
    borderWidth: 1,
    borderColor: colors.red,
  },
  aptDetailCompleteBtn: {
    backgroundColor: colors.teal,
  },
  aptDetailActionText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  aptDetailClientBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: colors.navyLight,
  },
  aptDetailClientBtnText: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.teal,
  },
  aptDetailArchiveBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: colors.gold + '10',
    borderWidth: 1,
    borderColor: colors.gold + '30',
  },
  aptDetailArchiveBtnText: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.gold,
  },
  aptDetailDeleteBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: colors.red + '10',
    borderWidth: 1,
    borderColor: colors.red + '30',
  },
  aptDetailDeleteBtnText: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.red,
  },
  aptDetailStatusSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.glassBorder,
  },
  aptDetailStatusSectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.gray,
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  aptDetailStatusBtns: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  aptDetailStatusBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: colors.navyLight,
    borderWidth: 1,
  },
  aptDetailStatusBtnText: {
    fontSize: 12,
    fontWeight: '600',
  },
  aptDetailDangerSection: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.glassBorder,
  },
  statusChangeModal: {
    backgroundColor: colors.navyLight,
    borderRadius: 16,
    padding: 24,
    width: isWeb && width > 500 ? 420 : '90%',
    maxWidth: 420,
  },
  statusChangeHeader: {
    alignItems: 'center',
    marginBottom: 20,
  },
  statusChangeIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  statusChangeTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
  },
  statusChangeSummary: {
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder,
  },
  statusChangeClient: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 4,
  },
  statusChangeService: {
    fontSize: 14,
    color: colors.teal,
    marginBottom: 4,
  },
  statusChangeDate: {
    fontSize: 13,
    color: colors.gray,
  },
  statusChangeWarning: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    backgroundColor: colors.red + '15',
    padding: 12,
    borderRadius: 10,
    marginBottom: 20,
  },
  statusChangeWarningText: {
    flex: 1,
    fontSize: 13,
    color: colors.red,
    lineHeight: 18,
  },
  statusChangeEmailOption: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    backgroundColor: colors.navy,
    padding: 14,
    borderRadius: 10,
    marginBottom: 20,
  },
  statusChangeEmailOptionText: {
    flex: 1,
  },
  statusChangeEmailLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 4,
  },
  statusChangeEmailDesc: {
    fontSize: 12,
    color: colors.gray,
  },
  statusChangeActions: {
    flexDirection: 'row',
    gap: 12,
  },
  statusChangeCancel: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    backgroundColor: colors.navy,
  },
  statusChangeCancelText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray,
  },
  statusChangeConfirm: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 10,
  },
  statusChangeConfirmText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  aptDetailEditBtn: {
    backgroundColor: colors.blue || '#3B82F6',
  },
  aptDetailRevertBtn: {
    backgroundColor: colors.navyLight,
    borderWidth: 1,
    borderColor: colors.gold,
  },
  aptDetailHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  aptDetailCancelBtn: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: colors.navyLight,
  },
  aptDetailCancelBtnText: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.gray,
  },
  aptDetailSaveBtn: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: colors.teal,
    minWidth: 70,
    alignItems: 'center',
  },
  aptDetailSaveBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.darkNavy,
  },
  // Edit Form Styles
  aptEditForm: {
    gap: 16,
  },
  aptEditRow: {
    flexDirection: 'row',
    gap: 12,
  },
  aptEditField: {
    marginBottom: 0,
  },
  aptEditLabel: {
    fontSize: 12,
    color: colors.gray,
    marginBottom: 8,
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  aptEditInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  },
  aptEditTextarea: {
    minHeight: 80,
    textAlignVertical: 'top',
    paddingTop: 12,
  },
  
  // ============================================
  // FIELD MANUALS STYLES
  // ============================================
  fmManagePanel: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: colors.teal + '30',
  },
  fmManagePanelHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  fmManagePanelTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  fmManagePanelAddBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  fmManagePanelAddText: {
    fontSize: 13,
    color: colors.teal,
    fontWeight: '500',
  },
  fmManagePanelCategories: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  fmManageCatItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyLight,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    gap: 8,
  },
  fmManageCatIcon: {
    width: 28,
    height: 28,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmManageCatName: {
    fontSize: 13,
    color: colors.white,
    fontWeight: '500',
  },
  fmManageCatCount: {
    fontSize: 11,
    color: colors.gray,
    backgroundColor: colors.navy,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  fmManageCatEditBtn: {
    padding: 4,
  },
  fmManageCatDeleteBtn: {
    padding: 4,
  },
  fmManagePanelEmpty: {
    fontSize: 13,
    color: colors.gray,
    fontStyle: 'italic',
  },
  fmCategorySelect: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  fmCategoryOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  fmCategoryOptionActive: {
    backgroundColor: colors.teal,
    borderColor: colors.teal,
  },
  fmCategoryOptionText: {
    fontSize: 13,
    color: colors.white,
  },
  fmTagsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  fmTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.teal + '30',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 20,
  },
  fmTagText: {
    fontSize: 12,
    color: colors.teal,
  },
  fmTagInput: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.navyLight,
    paddingVertical: 4,
    paddingLeft: 10,
    paddingRight: 6,
    borderRadius: 20,
  },
  fmTagInputField: {
    fontSize: 12,
    color: colors.white,
    minWidth: 80,
  },
  fmStepsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  fmAddStepBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  fmAddStepText: {
    fontSize: 13,
    color: colors.teal,
    fontWeight: '500',
  },
  // Step edit card styles (for editing manuals)
  fmStepEditCard: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  fmStepEditHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
  },
  fmStepEditNum: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.gold,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  fmStepEditInput: {
    backgroundColor: colors.navy,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
    marginBottom: 10,
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  },
  fmStepEditTextarea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  fmStepEditRow: {
    flexDirection: isWeb && width > 600 ? 'row' : 'column',
    gap: 12,
    marginTop: 4,
  },
  fmStepEditField: {
    flex: 1,
  },
  fmStepEditFieldLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.gray,
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  // Video section styles
  fmVideoCount: {
    fontSize: 12,
    color: colors.gray,
    fontStyle: 'italic',
  },
  fmVideosList: {
    marginBottom: 16,
  },
  fmVideoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navy,
    borderRadius: 10,
    padding: 10,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  fmVideoThumb: {
    width: 80,
    height: 45,
    borderRadius: 6,
    overflow: 'hidden',
    marginRight: 12,
  },
  fmVideoThumbImg: {
    width: '100%',
    height: '100%',
  },
  fmVideoThumbPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmVideoTypeBadge: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    width: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmVideoInfo: {
    flex: 1,
  },
  fmVideoTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 2,
  },
  fmVideoUrl: {
    fontSize: 11,
    color: colors.gray,
  },
  fmVideoRemoveBtn: {
    padding: 8,
    marginLeft: 8,
  },
  fmAddVideoForm: {
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    padding: 12,
    borderWidth: 1,
    borderColor: colors.navyMid,
    borderStyle: 'dashed',
  },
  fmAddVideoRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  fmAddVideoBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.teal,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    gap: 6,
  },
  fmAddVideoBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.darkNavy,
  },
  fmVideoOptionSection: {
    marginBottom: 12,
  },
  fmVideoOptionLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.grayLight,
    marginBottom: 8,
  },
  fmUploadVideoBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.gold,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    gap: 8,
  },
  fmUploadVideoBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.darkNavy,
  },
  fmVideoHint: {
    fontSize: 12,
    color: colors.gray,
    marginTop: 12,
    textAlign: 'center',
  },
  fmVideoHintSmall: {
    fontSize: 10,
    color: colors.gray,
    marginTop: 6,
    fontStyle: 'italic',
  },
  // Step card styles (for reading/viewing manuals)
  fmStepCard: {
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    padding: 14,
    marginBottom: 12,
  },
  fmStepHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  fmStepNum: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.gold,
    textTransform: 'uppercase',
  },
  fmIconGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  fmIconOption: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  fmColorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  fmColorOption: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmColorOptionActive: {
    borderWidth: 3,
    borderColor: colors.white,
  },
  fmSearchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navy,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 24,
    gap: 10,
  },
  fmSearchInput: {
    flex: 1,
    fontSize: 14,
    color: colors.white,
  },
  fmCategoriesTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray,
    marginBottom: 16,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  fmCategoriesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 32,
  },
  fmCategoryCard: {
    width: isWeb ? 'calc(25% - 9px)' : '48%',
    backgroundColor: colors.navyLight,
    borderRadius: 16,
    padding: 20,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  fmCategoryIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  fmCategoryName: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 4,
    textAlign: 'center',
  },
  fmCategoryCount: {
    fontSize: 12,
    color: colors.gray,
  },
  fmSectionHeader: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray,
    marginBottom: 16,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  fmManualGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  fmManualCard: {
    width: isWeb ? 'calc(25% - 9px)' : '48%',
    backgroundColor: colors.navyLight,
    borderRadius: 14,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  fmManualCardIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: colors.navy,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  fmManualCardTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 6,
    lineHeight: 20,
  },
  fmManualCardSteps: {
    fontSize: 11,
    color: colors.gray,
  },
  fmManualCardCategory: {
    fontSize: 11,
    color: colors.teal,
    fontWeight: '500',
  },
  fmSearchResults: {
    marginBottom: 24,
  },
  fmSearchResultsTitle: {
    fontSize: 13,
    color: colors.gray,
    marginBottom: 16,
  },
  fmEmptyState: {
    alignItems: 'center',
    paddingVertical: 40,
    flex: 1,
  },
  fmEmptyText: {
    fontSize: 14,
    color: colors.gray,
    marginTop: 12,
  },
  
  // Category View
  fmCategoryView: {
    flex: 1,
  },
  fmCategoryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
  },
  fmBackBtn: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: colors.navy,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmCategoryHeaderInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  fmCategoryIconLg: {
    width: 48,
    height: 48,
    borderRadius: 14,
    backgroundColor: colors.navy,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmCategoryTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.white,
  },
  
  // Manual View
  fmManualView: {
    flex: 1,
  },
  fmManualHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginBottom: 20,
  },
  fmManualHeaderInfo: {
    flex: 1,
  },
  fmManualTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
  },
  fmManualCategory: {
    fontSize: 13,
    color: colors.teal,
    fontWeight: '500',
    marginTop: 2,
  },
  fmManualActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  fmEditBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: colors.teal + '20',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmDeleteBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: colors.red + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmReaderModeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.gold + '20',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
  },
  fmReaderModeBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.gold,
  },
  fmManualContent: {
    flex: 1,
  },
  fmVideosSection: {
    marginBottom: 24,
  },
  fmSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 14,
  },
  fmVideosScroll: {
    marginHorizontal: -10,
    paddingHorizontal: 10,
  },
  fmVideoCard: {
    width: 180,
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    overflow: 'hidden',
    marginRight: 12,
  },
  fmVideoThumb: {
    height: 100,
    backgroundColor: colors.navy,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmVideoTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.white,
    padding: 10,
    paddingBottom: 4,
  },
  fmVideoDuration: {
    fontSize: 11,
    color: colors.gray,
    paddingHorizontal: 10,
    paddingBottom: 10,
  },
  // Video Player Styles
  fmVideosGrid: {
    flexDirection: isWeb && width > 768 ? 'row' : 'column',
    flexWrap: 'wrap',
    gap: 16,
  },
  fmVideoItem: {
    width: isWeb && width > 768 ? '48%' : '100%',
    marginBottom: 12,
  },
  videoPlayerContainer: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 8,
  },
  videoPlayerTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    padding: 12,
    paddingBottom: 8,
  },
  videoThumbnailContainer: {
    position: 'relative',
    width: '100%',
    aspectRatio: 16/9,
    backgroundColor: colors.navy,
    borderRadius: 12,
    overflow: 'hidden',
  },
  videoThumbnailPlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.navyMid,
  },
  videoPlayOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
  videoPlayButton: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.teal,
    justifyContent: 'center',
    alignItems: 'center',
    ...(isWeb ? { boxShadow: '0 4px 12px rgba(0,0,0,0.3)' } : {}),
  },
  videoTypeBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(0,0,0,0.7)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  videoTypeBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: colors.white,
    letterSpacing: 0.5,
  },
  videoEmbedContainer: {
    width: '100%',
    aspectRatio: 16/9,
    backgroundColor: colors.navy,
    borderRadius: 12,
    overflow: 'hidden',
  },
  videoLinkCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 16,
    backgroundColor: colors.navyMid,
    borderRadius: 8,
    margin: 12,
  },
  videoLinkText: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.teal,
  },
  fmResourceVideoItem: {
    marginBottom: 16,
  },
  fmStepsSection: {
    marginBottom: 24,
  },
  fmStepCard: {
    flexDirection: 'row',
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 16,
    marginBottom: 10,
    gap: 14,
  },
  fmStepNumBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.teal,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmStepNumBadgeText: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  fmStepContent: {
    flex: 1,
  },
  fmStepTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 6,
  },
  fmStepDesc: {
    fontSize: 14,
    color: colors.grayLight,
    lineHeight: 22,
  },
  fmStepWarning: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: colors.gold + '15',
    padding: 10,
    borderRadius: 8,
    marginTop: 10,
  },
  fmStepWarningText: {
    flex: 1,
    fontSize: 12,
    color: colors.gold,
    lineHeight: 18,
  },
  fmStepTip: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: colors.teal + '15',
    padding: 10,
    borderRadius: 8,
    marginTop: 10,
  },
  fmStepTipText: {
    flex: 1,
    fontSize: 12,
    color: colors.teal,
    lineHeight: 18,
  },
  
  // ========== RESPONSIVE MANUAL EDITOR MODAL ==========
  fmEditorModalResponsive: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    width: '95%',
    maxWidth: 600,
    maxHeight: '90%',
    ...(isWeb ? { boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)' } : {}),
    overflow: 'hidden',
  },
  fmEditorModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
  },
  fmEditorModalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
  },
  fmEditorModalClose: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmEditorModalBody: {
    flex: 1,
    maxHeight: isWeb ? 500 : 400,
  },
  fmEditorModalBodyContent: {
    padding: 20,
    paddingTop: 16,
  },
  fmEditorField: {
    marginBottom: 20,
  },
  fmEditorLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.gold,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 10,
  },
  fmEditorInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    padding: 14,
    fontSize: 15,
    color: colors.white,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  fmEditorTextarea: {
    minHeight: 80,
    textAlignVertical: 'top',
    marginTop: 10,
  },
  fmEditorCategoryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  fmEditorCategoryOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  fmEditorCategoryOptionActive: {
    backgroundColor: colors.teal,
    borderColor: colors.teal,
  },
  fmEditorCategoryText: {
    fontSize: 13,
    color: colors.gray,
    fontWeight: '500',
  },
  fmEditorTagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  fmEditorTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.teal + '30',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 20,
  },
  fmEditorTagText: {
    fontSize: 12,
    color: colors.teal,
    fontWeight: '500',
  },
  fmEditorTagInputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyLight,
    borderRadius: 20,
    paddingLeft: 12,
    paddingRight: 4,
    borderWidth: 1,
    borderColor: colors.navyMid,
    minWidth: 120,
  },
  fmEditorTagInput: {
    flex: 1,
    fontSize: 13,
    color: colors.white,
    paddingVertical: 6,
  },
  fmEditorTagAddBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.teal + '20',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmEditorStepsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  fmEditorAddStepBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  fmEditorAddStepText: {
    fontSize: 13,
    color: colors.gold,
    fontWeight: '600',
  },
  fmEditorStepCard: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  fmEditorStepHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  fmEditorStepBadge: {
    backgroundColor: colors.gold + '20',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 6,
  },
  fmEditorStepBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.gold,
    letterSpacing: 0.5,
  },
  fmEditorStepDelete: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: colors.red + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmEditorStepFields: {
    gap: 10,
  },
  fmEditorModalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
    padding: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.navyMid,
  },
  fmEditorCancelBtn: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    backgroundColor: colors.navyLight,
  },
  fmEditorCancelBtnText: {
    fontSize: 14,
    color: colors.gray,
    fontWeight: '500',
  },
  fmEditorSaveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    backgroundColor: colors.teal,
  },
  fmEditorSaveBtnText: {
    fontSize: 14,
    color: colors.darkNavy,
    fontWeight: '600',
  },
  
  fmResourcesSection: {
    marginBottom: 24,
  },
  fmResourceItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: colors.navyLight,
    padding: 14,
    borderRadius: 10,
    marginBottom: 8,
  },
  fmResourceTitle: {
    fontSize: 13,
    color: colors.teal,
    fontWeight: '500',
  },
  fmTagsSection: {
    marginBottom: 24,
  },
  fmTagsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  fmTag: {
    backgroundColor: colors.navy,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 20,
  },
  fmTagText: {
    fontSize: 12,
    color: colors.gray,
  },
  
  // Reader Mode
  fmReaderContainer: {
    flex: 1,
    backgroundColor: colors.darkNavy,
  },
  fmReaderHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
  },
  fmReaderBackBtn: {
    padding: 4,
  },
  fmReaderTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  fmReaderProgress: {
    fontSize: 13,
    color: colors.teal,
    fontWeight: '600',
  },
  fmReaderProgressBar: {
    height: 3,
    backgroundColor: colors.navyLight,
  },
  fmReaderProgressFill: {
    height: '100%',
    backgroundColor: colors.teal,
  },
  fmReaderHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  fmReaderVideosBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.teal + '20',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 16,
  },
  fmReaderVideosBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.teal,
  },
  fmReaderVideosContainer: {
    flex: 1,
    padding: 20,
  },
  fmReaderVideoItem: {
    marginBottom: 20,
  },
  fmReaderVideoPlayer: {
    width: '100%',
  },
  fmReaderScrollView: {
    flex: 1,
  },
  fmReaderContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  fmReaderStepNum: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: colors.teal,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  fmReaderStepNumText: {
    fontSize: 28,
    fontWeight: '800',
    color: colors.darkNavy,
  },
  fmReaderStepTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.white,
    textAlign: 'center',
    marginBottom: 16,
  },
  fmReaderStepDesc: {
    fontSize: 16,
    color: colors.grayLight,
    textAlign: 'center',
    lineHeight: 26,
    maxWidth: 500,
  },
  fmReaderWarning: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    backgroundColor: colors.gold + '15',
    padding: 14,
    borderRadius: 10,
    marginTop: 20,
    maxWidth: 500,
  },
  fmReaderWarningText: {
    flex: 1,
    fontSize: 14,
    color: colors.gold,
    lineHeight: 22,
  },
  fmReaderTip: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    backgroundColor: colors.teal + '15',
    padding: 14,
    borderRadius: 10,
    marginTop: 12,
    maxWidth: 500,
  },
  fmReaderTipText: {
    flex: 1,
    fontSize: 14,
    color: colors.teal,
    lineHeight: 22,
  },
  fmReaderNav: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: colors.navyLight,
  },
  fmReaderNavBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    backgroundColor: colors.navy,
  },
  fmReaderNavBtnNext: {
    backgroundColor: colors.teal,
  },
  fmReaderNavBtnDisabled: {
    backgroundColor: colors.navyLight,
  },
  fmReaderNavText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  fmReaderDots: {
    flexDirection: 'row',
    gap: 6,
  },
  fmReaderDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.navyMid,
  },
  fmReaderDotActive: {
    backgroundColor: colors.teal,
    width: 20,
  },
  
  // Manage Tab
  fmManageList: {
    gap: 10,
  },
  fmManageItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 14,
  },
  fmManageItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  fmManageItemIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: colors.navy,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmManageItemTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  fmManageItemMeta: {
    fontSize: 12,
    color: colors.gray,
    marginTop: 2,
  },
  fmManageItemActions: {
    flexDirection: 'row',
    gap: 8,
  },
  fmManageEditBtn: {
    padding: 8,
    backgroundColor: colors.navy,
    borderRadius: 8,
  },
  fmManageDeleteBtn: {
    padding: 8,
    backgroundColor: colors.red + '20',
    borderRadius: 8,
  },
  
  // Editor Modal
  fmEditorModal: {
    backgroundColor: colors.navy,
    borderRadius: 20,
    width: isWeb ? 600 : '95%',
    maxWidth: 600,
    maxHeight: '90%',
  },
  fmEditorHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
  },
  fmEditorTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
  },
  fmEditorContent: {
    padding: 20,
    maxHeight: isWeb ? 500 : 400,
  },
  fmEditorSection: {
    marginBottom: 24,
  },
  fmEditorSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 14,
  },
  fmEditorLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.gray,
    marginBottom: 8,
  },
  fmEditorInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    padding: 12,
    fontSize: 14,
    color: colors.white,
    marginBottom: 12,
  },
  fmEditorTextarea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  fmEditorCategoryPicker: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  fmEditorCategoryOption: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: colors.navyLight,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  fmEditorCategoryOptionActive: {
    borderColor: colors.teal,
    backgroundColor: colors.teal + '15',
  },
  fmEditorCategoryText: {
    fontSize: 13,
    color: colors.gray,
  },
  fmEditorTagsRow: {
    flexDirection: 'row',
    gap: 8,
  },
  fmEditorAddBtn: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: colors.teal,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmEditorTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  fmEditorTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.navyLight,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
  },
  fmEditorTagText: {
    fontSize: 12,
    color: colors.teal,
  },
  fmEditorVideoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.navyLight,
    padding: 10,
    borderRadius: 8,
    marginBottom: 8,
  },
  fmEditorVideoItemEnhanced: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    marginBottom: 10,
    overflow: 'hidden',
  },
  fmEditorVideoThumb: {
    width: 80,
    height: 56,
    backgroundColor: colors.navyMid,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmEditorVideoInfo: {
    flex: 1,
    padding: 10,
    gap: 4,
  },
  fmEditorVideoMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  fmEditorVideoTypeBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  fmEditorVideoTypeText: {
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  fmEditorVideoUrl: {
    flex: 1,
    fontSize: 11,
    color: colors.gray,
  },
  fmEditorVideoDelete: {
    padding: 12,
  },
  fmEditorAddVideoForm: {
    backgroundColor: colors.navyMid,
    borderRadius: 10,
    padding: 12,
    marginTop: 8,
  },
  fmEditorVideoTypePreview: {
    fontSize: 11,
    color: colors.teal,
    marginTop: 8,
    fontWeight: '600',
  },
  fmEditorHelpText: {
    fontSize: 12,
    color: colors.gray,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  fmEditorVideoTitle: {
    fontSize: 13,
    color: colors.white,
    flex: 1,
  },
  fmEditorAddVideoRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  fmEditorStepCard: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
  },
  fmEditorStepHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  fmEditorStepNum: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.teal,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmEditorStepNumText: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  fmEditorAddStepBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.teal,
    marginTop: 8,
  },
  fmEditorAddStepText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.teal,
  },
  fmEditorActions: {
    flexDirection: 'row',
    gap: 12,
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: colors.navyLight,
  },
  fmEditorCancelBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: colors.navyLight,
    alignItems: 'center',
  },
  fmEditorCancelText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray,
  },
  fmEditorSaveBtn: {
    flex: 2,
    flexDirection: 'row',
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: colors.teal,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  fmEditorSaveText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.darkNavy,
  },
  
  // Categories Tab
  fmCatList: {
    gap: 10,
  },
  fmCatItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 12,
    gap: 12,
  },
  fmCatItemReorder: {
    gap: 2,
  },
  fmCatReorderBtn: {
    padding: 4,
  },
  fmCatItemIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fmCatItemName: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
  },
  fmCatItemActions: {
    flexDirection: 'row',
    gap: 8,
  },
  fmCatEditBtn: {
    padding: 8,
    backgroundColor: colors.navy,
    borderRadius: 8,
  },
  fmCatDeleteBtn: {
    padding: 8,
    backgroundColor: colors.red + '20',
    borderRadius: 8,
  },
  fmCatEditorModal: {
    backgroundColor: colors.navy,
    borderRadius: 20,
    width: isWeb ? 420 : '95%',
    maxWidth: 420,
    padding: 24,
  },
  fmCatEditorTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 24,
  },
  fmCatIconGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  fmCatIconOption: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  fmCatIconOptionActive: {
    borderColor: colors.teal,
    backgroundColor: colors.teal + '15',
  },
  fmCatColorGrid: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 20,
  },
  fmCatColorOption: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  fmCatColorOptionActive: {
    borderColor: colors.white,
  },
  fmCatPreview: {
    marginBottom: 20,
  },
  fmCatPreviewLabel: {
    fontSize: 12,
    color: colors.gray,
    marginBottom: 10,
  },
  fmCatPreviewCard: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    borderWidth: 2,
  },
  
  // Hours Calendar View Styles
  hoursViewToggle: {
    flexDirection: 'row',
    backgroundColor: colors.navy,
    borderRadius: 10,
    padding: 4,
    marginBottom: 24,
  },
  hoursViewBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 8,
    gap: 8,
  },
  hoursViewBtnActive: {
    backgroundColor: colors.teal,
  },
  hoursViewBtnText: {
    fontSize: 14,
    color: colors.gray,
    fontWeight: '500',
  },
  hoursViewBtnTextActive: {
    color: colors.darkNavy,
  },
  hoursCalendarContainer: {
    backgroundColor: colors.navyLight,
    borderRadius: 16,
    padding: 20,
    marginBottom: 24,
  },
  hoursCalendarNav: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  hoursCalendarNavBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: colors.navy,
    justifyContent: 'center',
    alignItems: 'center',
  },
  hoursCalendarTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
  },
  hoursCalendarHeader: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  hoursCalendarHeaderCell: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 8,
  },
  hoursCalendarHeaderText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.gray,
  },
  hoursCalendarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  hoursCalendarCell: {
    width: '14.28%',
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    position: 'relative',
  },
  hoursCalendarCellPast: {
    opacity: 0.4,
  },
  hoursCalendarCellClosed: {
    backgroundColor: colors.red + '10',
  },
  hoursCalendarCellOverride: {
    backgroundColor: colors.gold + '15',
  },
  hoursCalendarDayNum: {
    fontSize: 14,
    color: colors.white,
    fontWeight: '500',
  },
  hoursCalendarDayNumClosed: {
    color: colors.red,
  },
  hoursCalendarDayNumToday: {
    color: colors.teal,
    fontWeight: '700',
  },
  hoursCalendarBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 14,
    height: 14,
    borderRadius: 7,
    justifyContent: 'center',
    alignItems: 'center',
  },
  hoursCalendarCellText: {
    fontSize: 9,
    color: colors.red,
    marginTop: 2,
  },
  hoursCalendarLegend: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 20,
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.navyMid,
  },
  hoursCalendarLegendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  hoursCalendarLegendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  hoursCalendarLegendText: {
    fontSize: 11,
    color: colors.gray,
  },
  hoursCalendarHint: {
    fontSize: 12,
    color: colors.gray,
    textAlign: 'center',
    marginTop: 12,
  },
  hoursOverridesList: {
    backgroundColor: colors.navyLight,
    borderRadius: 16,
    padding: 20,
    marginBottom: 24,
  },
  hoursOverridesTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 16,
  },
  hoursOverrideItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
  },
  hoursOverrideInfo: {
    flex: 1,
  },
  hoursOverrideDate: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 2,
  },
  hoursOverrideStatus: {
    fontSize: 13,
    color: colors.gray,
  },
  hoursOverrideRemove: {
    padding: 8,
  },
  hoursOverrideModal: {
    backgroundColor: colors.navyLight,
    borderRadius: 16,
    width: isWeb ? 420 : '90%',
    maxWidth: 420,
  },
  hoursOverrideModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
  },
  hoursOverrideModalTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
  hoursOverrideModalBody: {
    padding: 20,
  },
  hoursOverrideToggleRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 20,
  },
  hoursOverrideToggleBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: colors.navy,
    gap: 8,
  },
  hoursOverrideToggleBtnActive: {
    backgroundColor: colors.teal,
  },
  hoursOverrideToggleBtnText: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.gray,
  },
  hoursOverrideToggleBtnTextActive: {
    color: colors.darkNavy,
  },
  hoursOverrideField: {
    flex: 1,
  },
  hoursOverrideFieldLabel: {
    fontSize: 12,
    color: colors.gray,
    fontWeight: '500',
    marginBottom: 8,
  },
  hoursOverrideInput: {
    backgroundColor: colors.navy,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  hoursOverrideTimeRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 12,
  },
  hoursOverrideTimeSep: {
    fontSize: 14,
    color: colors.gray,
    paddingBottom: 14,
  },
  hoursOverrideModalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    padding: 20,
    paddingTop: 0,
  },
  hoursOverrideRemoveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: colors.red + '20',
    gap: 8,
  },
  hoursOverrideRemoveBtnText: {
    fontSize: 13,
    color: colors.red,
    fontWeight: '500',
  },
  hoursOverrideSaveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: colors.teal,
    gap: 8,
  },
  hoursOverrideSaveBtnText: {
    fontSize: 13,
    color: colors.darkNavy,
    fontWeight: '600',
  },
  
  settingsBtn: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.navyMid,
  },

  // Clients Grid
  // Client category tabs
  clientCategoryTabs: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  clientCategoryTab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  clientCategoryTabActive: {
    backgroundColor: colors.teal + '15',
    borderColor: colors.teal,
  },
  clientCategoryTabText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray,
  },
  clientCategoryTabTextActive: {
    color: colors.teal,
  },
  clientCategoryBadge: {
    backgroundColor: colors.navyMid,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    minWidth: 24,
    alignItems: 'center',
  },
  clientCategoryBadgeActive: {
    backgroundColor: colors.teal + '30',
  },
  clientCategoryBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.white,
  },
  // Sort dropdown
  sortDropdown: {
    flexDirection: isWeb && width > 600 ? 'row' : 'column',
    alignItems: isWeb && width > 600 ? 'center' : 'flex-start',
    gap: 8,
  },
  sortLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.gray,
  },
  sortOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  sortOption: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: colors.navyLight,
    borderRadius: 6,
  },
  sortOptionActive: {
    backgroundColor: colors.teal + '20',
  },
  sortOptionText: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.gray,
  },
  sortOptionTextActive: {
    color: colors.teal,
  },
  // Client upcoming badge
  clientUpcomingBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: colors.green + '15',
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  clientUpcomingText: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.green,
  },
  // Client card actions
  clientCardActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.navyLight,
  },
  clientEditBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: colors.teal + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  clientArchiveBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  clientRestoreBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: colors.green + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  clientsToolbar: {
    flexDirection: isWeb && width > 768 ? 'row' : 'column',
    gap: 16,
    marginBottom: 24,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    paddingHorizontal: 16,
    flex: 1,
    maxWidth: 400,
  },
  searchIcon: {
    fontSize: 16,
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 12,
    color: colors.white,
    fontSize: 14,
  },
  clientsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  },
  clientCard: {
    width: isWeb && width > 1000 ? 'calc(33.333% - 14px)' : isWeb && width > 600 ? 'calc(50% - 10px)' : '100%',
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  clientCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  clientAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.teal,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  clientAvatarText: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  clientCardInfo: {
    flex: 1,
  },
  clientName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 4,
  },
  clientCardBody: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
  },
  clientContactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  clientContactIcon: {
    marginRight: 10,
    width: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clientContactText: {
    fontSize: 13,
    color: colors.gray,
    flex: 1,
  },
  clientCardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  clientStat: {
    alignItems: 'center',
    flex: 1,
  },
  clientStatValue: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.white,
  },
  clientStatLabel: {
    fontSize: 10,
    color: colors.gray,
    textTransform: 'uppercase',
  },

  // Payment Alert Banner
  paymentAlertBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    backgroundColor: colors.green + '20',
    borderBottomWidth: 1,
    borderBottomColor: colors.green + '40',
  },
  paymentAlertIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.green + '30',
    justifyContent: 'center',
    alignItems: 'center',
  },
  paymentAlertContent: {
    flex: 1,
  },
  paymentAlertTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.green,
  },
  paymentAlertText: {
    fontSize: 14,
    color: colors.white,
  },

  // Client Detail Modal Styles
  clientDetailOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: isWeb && width > 600 ? 24 : 0,
  },
  clientDetailModal: {
    backgroundColor: colors.darkNavy,
    borderRadius: isWeb && width > 600 ? 24 : 0,
    width: '100%',
    maxWidth: 560,
    height: isWeb && width > 600 ? 'auto' : '100%',
    maxHeight: isWeb && width > 600 ? '92vh' : '100%',
    borderWidth: isWeb && width > 600 ? 1 : 0,
    borderColor: colors.navyMid,
    overflow: 'hidden',
    ...(isWeb ? { boxShadow: '0 25px 60px rgba(0,0,0,0.5)' } : {}),
  },
  clientDetailHeader: {
    alignItems: 'center',
    paddingTop: isWeb && width > 500 ? 56 : 60, // More top padding on mobile for buttons
    paddingBottom: 20,
    paddingHorizontal: 24,
    backgroundColor: colors.navy,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
    position: 'relative',
  },
  clientDetailHeaderLeft: {
    alignItems: 'center',
    gap: 8,
    width: '100%',
    maxWidth: 400,
  },
  clientDetailAvatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: colors.teal,
    justifyContent: 'center',
    alignItems: 'center',
    ...(isWeb ? { boxShadow: '0 4px 20px rgba(0, 184, 148, 0.35)' } : {}),
  },
  clientDetailAvatarText: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  clientDetailName: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.white,
    marginTop: 4,
    textAlign: 'center',
  },
  clientDetailCloseBtn: {
    position: 'absolute',
    top: isWeb && width > 500 ? 12 : 16,
    right: isWeb && width > 500 ? 12 : 16,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  clientDetailHeaderRight: {
    position: 'absolute',
    top: isWeb && width > 500 ? 12 : 16,
    left: isWeb && width > 500 ? 12 : 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    zIndex: 10,
  },
  clientDetailHeaderBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 8,
    paddingHorizontal: isWeb && width > 500 ? 14 : 10,
    borderRadius: 10,
  },
  clientDetailHeaderBtnEdit: {
    backgroundColor: colors.navyLight,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  clientDetailHeaderBtnEditText: {
    color: colors.teal,
    fontSize: 13,
    fontWeight: '600',
  },
  clientDetailHeaderBtnSave: {
    backgroundColor: colors.teal,
  },
  clientDetailHeaderBtnSaveText: {
    color: colors.darkNavy,
    fontSize: 13,
    fontWeight: '600',
  },
  clientDetailHeaderBtnCancel: {
    backgroundColor: colors.navyLight,
  },
  clientDetailHeaderBtnCancelText: {
    color: colors.gray,
    fontSize: 13,
    fontWeight: '600',
  },
  editableNameInput: {
    backgroundColor: colors.navyLight,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.teal + '40',
    width: '100%',
    maxWidth: 320,
    textAlign: 'center',
    fontSize: 22,
    fontWeight: '700',
    color: colors.white,
    marginTop: 4,
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  },
  clientTypeSelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
    justifyContent: 'center',
  },
  clientTypeSelectorBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.navyMid,
    backgroundColor: colors.navyLight,
  },
  clientTypeSelectorText: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.gray,
  },
  clientDetailEditGrid: {
    gap: 16,
  },
  clientDetailEditField: {
    gap: 6,
  },
  clientDetailEditLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.gold,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginLeft: 4,
  },
  clientDetailEditInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  clientDetailEditInput: {
    flex: 1,
    color: colors.white,
    fontSize: 15,
    paddingVertical: 12,
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  },
  clientDetailStatValueInput: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.gold,
    backgroundColor: colors.navyLight,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    textAlign: 'center',
    minWidth: 70,
    borderWidth: 1,
    borderColor: colors.gold + '30',
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  },
  clientDetailStatValueInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.green + '30',
    paddingLeft: 10,
  },
  clientDetailStatDollar: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.green,
  },
  clientDetailStatMoneyInput: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.green,
    paddingHorizontal: 6,
    paddingVertical: 8,
    textAlign: 'center',
    minWidth: 60,
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  },
  clientDetailNotesInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 16,
    color: colors.white,
    fontSize: 14,
    lineHeight: 22,
    minHeight: 80,
    textAlignVertical: 'top',
    borderWidth: 1,
    borderColor: colors.navyMid,
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  },
  clientDetailNotesDisplay: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  clientDetailNotesText: {
    color: colors.grayLight,
    fontSize: 14,
    lineHeight: 22,
    fontStyle: 'italic',
  },
  clientDetailBody: {
    flex: 1,
    padding: 20,
  },
  clientDetailSection: {
    marginBottom: 20,
  },
  clientDetailSectionTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    marginBottom: 12,
  },
  clientDetailInfoGrid: {
    backgroundColor: colors.navy,
    borderRadius: 14,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  clientDetailInfoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
  },
  clientDetailInfoText: {
    fontSize: 15,
    color: colors.white,
    flex: 1,
  },
  clientDetailStatsRow: {
    flexDirection: 'row',
    backgroundColor: colors.navy,
    borderRadius: 14,
    padding: 0,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: colors.navyMid,
    overflow: 'hidden',
  },
  clientDetailStat: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 20,
    borderRightWidth: 1,
    borderRightColor: colors.navyMid,
  },
  clientDetailStatValue: {
    fontSize: 26,
    fontWeight: '800',
    color: colors.gold,
    marginBottom: 6,
  },
  clientDetailStatLabel: {
    fontSize: 10,
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    fontWeight: '600',
  },
  clientDetailJobTabs: {
    flexDirection: 'row',
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 4,
    marginBottom: 16,
    gap: 4,
  },
  clientDetailJobTab: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    gap: 6,
  },
  clientDetailJobTabActive: {
    backgroundColor: colors.navy,
  },
  clientDetailJobTabBadge: {
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
  },
  clientDetailJobTabBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  clientDetailJobTabText: {
    fontSize: 13,
    color: colors.gray,
    fontWeight: '600',
  },
  clientDetailJobTabTextActive: {
    color: colors.teal,
  },
  clientDetailJobsLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 12,
  },
  clientDetailJobsLoadingText: {
    color: colors.gray,
    fontSize: 14,
  },
  clientDetailJobsList: {
    gap: 12,
  },
  clientDetailNoJobs: {
    textAlign: 'center',
    color: colors.gray,
    fontSize: 14,
    padding: 24,
  },
  clientDetailJobCard: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 16,
    borderLeftWidth: 3,
    borderLeftColor: colors.teal,
  },
  clientDetailJobHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  clientDetailJobDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginTop: 5,
  },
  clientDetailJobInfo: {
    flex: 1,
  },
  clientDetailJobTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 4,
  },
  clientDetailJobDate: {
    fontSize: 13,
    color: colors.gray,
  },
  clientDetailJobStatus: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  clientDetailJobStatusText: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  clientDetailJobNotes: {
    fontSize: 13,
    color: colors.textMuted,
    marginTop: 12,
    paddingLeft: 22,
  },
  clientDetailJobDescription: {
    marginTop: 12,
    paddingLeft: 22,
    paddingRight: 12,
    paddingVertical: 10,
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: colors.teal,
  },
  clientDetailJobDescLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.teal,
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  clientDetailJobDescText: {
    fontSize: 13,
    color: colors.grayLight,
    lineHeight: 20,
  },
  clientDetailJobEstimate: {
    marginTop: 8,
    paddingLeft: 22,
  },
  clientDetailJobEstLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.gold,
  },
  clientDetailJobEstBreakdown: {
    fontSize: 12,
    color: colors.gray,
    marginTop: 4,
  },
  clientDetailJobActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 16,
    paddingLeft: 22,
    flexWrap: 'wrap',
  },
  clientDetailJobActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 14,
    backgroundColor: colors.teal + '15',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.teal + '30',
  },
  clientDetailJobActionBtnPrimary: {
    backgroundColor: colors.gold,
    borderColor: colors.gold,
  },
  clientDetailJobActionText: {
    fontSize: 13,
    color: colors.teal,
    fontWeight: '500',
  },
  clientDetailJobActionTextPrimary: {
    fontSize: 13,
    color: colors.darkNavy,
    fontWeight: '600',
  },
  clientDetailNotes: {
    fontSize: 14,
    color: colors.grayLight,
    backgroundColor: colors.navy,
    borderRadius: 8,
    padding: 16,
    lineHeight: 22,
  },
  clientDetailQuoteBox: {
    backgroundColor: colors.navyLight,
    borderRadius: 14,
    padding: 18,
    borderWidth: 1,
    borderColor: colors.gold + '30',
    ...(isWeb ? { boxShadow: 'inset 0 0 20px rgba(212, 168, 75, 0.05)' } : {}),
  },
  clientDetailQuoteText: {
    fontSize: 14,
    color: colors.white,
    lineHeight: 22,
  },
  clientDetailQuoteEstimate: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 14,
    paddingTop: 14,
    borderTopWidth: 1,
    borderTopColor: colors.navyMid,
    gap: 10,
  },
  clientDetailQuoteEstLabel: {
    fontSize: 13,
    color: colors.gray,
  },
  clientDetailQuoteEstValue: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.gold,
  },
  clientDetailFooterContainer: {
    borderTopWidth: 1,
    borderTopColor: colors.navyMid,
    backgroundColor: colors.navy,
    padding: isWeb && width > 500 ? 16 : 12,
    paddingBottom: isWeb && width > 500 ? 16 : 24, // Extra padding at bottom for mobile safe area
    gap: 12,
  },
  clientDetailFooterRow: {
    flexDirection: 'row',
    gap: 10,
    justifyContent: 'center',
  },
  clientDetailFooter: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: isWeb && width > 500 ? 20 : 12,
    paddingBottom: isWeb && width > 500 ? 20 : 24,
    gap: isWeb && width > 500 ? 10 : 8,
    borderTopWidth: 1,
    borderTopColor: colors.navyMid,
    backgroundColor: colors.navy,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clientDetailFooterBtnSecondary: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 14,
    paddingHorizontal: 12,
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  clientDetailFooterBtnSecondaryText: {
    fontSize: 13,
    color: colors.teal,
    fontWeight: '600',
  },
  clientDetailFooterBtnPrimary: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    paddingHorizontal: 24,
    backgroundColor: colors.gold,
    borderRadius: 12,
    ...(isWeb ? { boxShadow: '0 4px 15px rgba(212, 168, 75, 0.3)' } : {}),
  },
  clientDetailFooterBtnPrimaryText: {
    fontSize: 15,
    color: colors.darkNavy,
    fontWeight: '700',
  },
  clientDetailFooterBtnCheckout: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingVertical: 16,
    paddingHorizontal: 24,
    backgroundColor: colors.gold,
    borderRadius: 12,
    ...(isWeb ? { boxShadow: '0 4px 15px rgba(212, 168, 75, 0.3)' } : {}),
  },
  clientDetailFooterBtnCheckoutText: {
    fontSize: 16,
    color: colors.darkNavy,
    fontWeight: '700',
  },

  // Invoice Modal Styles
  invoiceModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  invoiceModalContent: {
    backgroundColor: colors.navy,
    borderRadius: 20,
    width: '100%',
    maxWidth: 480,
    maxHeight: '90%',
    borderWidth: 1,
    borderColor: 'rgba(212, 168, 75, 0.3)',
    overflow: 'hidden',
  },
  invoiceModalHeader: {
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
  },
  invoiceModalTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.gold,
    marginBottom: 8,
  },
  invoiceModalSubtitle: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.6)',
    textAlign: 'center',
  },
  invoiceModalBody: {
    padding: 24,
  },
  invoiceFormGroup: {
    marginBottom: 20,
  },
  invoiceLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gold,
    marginBottom: 8,
  },
  invoiceRecipientInfo: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  invoiceRecipientIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(212, 168, 75, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  invoiceRecipientDetails: {
    flex: 1,
  },
  invoiceRecipientName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 2,
  },
  invoiceRecipientEmail: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.6)',
  },
  invoiceSummary: {
    backgroundColor: 'rgba(212, 168, 75, 0.1)',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: 'rgba(212, 168, 75, 0.2)',
  },
  invoiceSummaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.05)',
  },
  invoiceSummaryLabel: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.7)',
  },
  invoiceSummaryValue: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '500',
  },
  invoiceTotalRow: {
    marginTop: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(212, 168, 75, 0.3)',
  },
  invoiceTotalLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.gold,
  },
  invoiceTotalValue: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.gold,
  },
  invoiceInput: {
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: 14,
    color: '#fff',
    fontSize: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  invoiceTextArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  invoiceModalFooter: {
    flexDirection: 'row',
    gap: 12,
    padding: 24,
    paddingTop: 0,
  },
  invoiceCancelBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  invoiceCancelBtnText: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.7)',
    fontWeight: '600',
  },
  invoiceSendBtn: {
    flex: 2,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: colors.gold,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    ...(isWeb ? { boxShadow: '0 4px 20px rgba(212, 168, 75, 0.4)' } : {}),
  },
  invoiceSendBtnDisabled: {
    opacity: 0.5,
  },
  invoiceSendBtnText: {
    fontSize: 15,
    color: colors.darkNavy,
    fontWeight: '700',
  },
  invoiceSuccessContent: {
    alignItems: 'center',
    padding: 32,
  },
  invoiceSuccessIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  invoiceSuccessTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#4CAF50',
    marginBottom: 12,
  },
  invoiceSuccessMessage: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.7)',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
  },
  invoiceLinkBox: {
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderRadius: 10,
    padding: 14,
    width: '100%',
    marginBottom: 20,
  },
  invoiceLinkLabel: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.5)',
    marginBottom: 6,
  },
  invoiceLinkUrl: {
    fontSize: 14,
    color: colors.gold,
    fontFamily: isWeb ? 'monospace' : 'Courier',
  },
  invoiceDoneBtn: {
    paddingVertical: 14,
    paddingHorizontal: 48,
    borderRadius: 12,
    backgroundColor: colors.gold,
  },
  invoiceDoneBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.darkNavy,
  },

  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    width: '100%',
    maxWidth: 500,
    maxHeight: '85vh',
    borderWidth: 1,
    borderColor: colors.navyLight,
    overflow: 'hidden',
    ...(isWeb ? { display: 'flex', flexDirection: 'column' } : {}),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
  },
  modalClose: {
    fontSize: 20,
    color: colors.gray,
  },
  modalBody: {
    padding: 20,
    paddingBottom: 0,
    flex: 1,
  },
  modalField: {
    marginBottom: 16,
  },
  modalRow: {
    flexDirection: 'row',
    gap: 12,
  },
  modalLabel: {
    fontSize: 12,
    color: colors.gray,
    marginBottom: 8,
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  modalHint: {
    fontSize: 12,
    color: colors.textMuted,
    marginBottom: 6,
  },
  modalInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  },
  modalTextarea: {
    minHeight: 80,
    textAlignVertical: 'top',
    paddingTop: 12,
  },
  modalOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: colors.navyLight,
    borderWidth: 1,
    borderColor: colors.navyMid,
    gap: 6,
  },
  modalOptionIcon: {
    fontSize: 14,
  },
  modalOptionText: {
    fontSize: 12,
    color: colors.grayLight,
    fontWeight: '500',
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: colors.navyLight,
  },
  modalCancelBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 10,
    backgroundColor: colors.navyLight,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  modalCancelBtnText: {
    color: colors.grayLight,
    fontSize: 15,
    fontWeight: '600',
  },
  modalSubmitBtn: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: colors.teal,
  },
  modalSubmitBtnText: {
    color: colors.darkNavy,
    fontSize: 14,
    fontWeight: '700',
  },
  modalSaveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 28,
    borderRadius: 10,
    backgroundColor: colors.teal,
    gap: 8,
    minWidth: 140,
    ...(isWeb ? { 
      boxShadow: '0 4px 14px rgba(45, 212, 191, 0.3)',
      transition: 'all 0.2s ease',
    } : {}),
  },
  modalSaveBtnText: {
    color: colors.darkNavy,
    fontSize: 15,
    fontWeight: '700',
    letterSpacing: 0.3,
  },

  // ========== PAYMENT COLLECTION FLOW STYLES (Mobile-First) ==========
  paymentModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'flex-end',
  },
  paymentModalContent: {
    backgroundColor: colors.darkNavy,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '95%',
    paddingBottom: 30,
  },
  paymentModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
  },
  paymentModalHeaderLeft: {
    flex: 1,
  },
  paymentModalHeaderCenter: {
    flex: 2,
    alignItems: 'center',
  },
  paymentModalTitle: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  paymentBackBtn: {
    padding: 8,
  },
  paymentBackBtnText: {
    color: colors.teal,
    fontSize: 14,
  },
  paymentCloseBtn: {
    flex: 1,
    alignItems: 'flex-end',
    padding: 8,
  },
  paymentCloseBtnText: {
    color: colors.gray,
    fontSize: 20,
  },
  paymentProgress: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
    paddingHorizontal: 40,
    position: 'relative',
  },
  paymentProgressStep: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.navyMid,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    marginHorizontal: 24,
  },
  paymentProgressStepActive: {
    backgroundColor: colors.teal,
  },
  paymentProgressStepComplete: {
    backgroundColor: colors.green,
  },
  paymentProgressStepText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '700',
  },
  paymentProgressLine: {
    position: 'absolute',
    left: 72,
    right: 72,
    height: 3,
    backgroundColor: colors.navyMid,
    top: '50%',
    marginTop: -1.5,
    zIndex: 0,
  },
  paymentProgressLineFill: {
    height: '100%',
    backgroundColor: colors.teal,
  },
  paymentModalBody: {
    flex: 1,
    paddingHorizontal: 20,
  },
  paymentStep: {
    paddingTop: 10,
    paddingBottom: 30,
  },
  paymentStepTitle: {
    color: colors.white,
    fontSize: 22,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 6,
  },
  paymentStepSubtitle: {
    color: colors.gray,
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 24,
  },
  paymentClientCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
  },
  paymentClientAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.teal,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  paymentClientAvatarText: {
    color: colors.darkNavy,
    fontSize: 20,
    fontWeight: '700',
  },
  paymentClientInfo: {},
  paymentClientName: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  paymentClientContact: {
    color: colors.gray,
    fontSize: 13,
  },
  paymentSection: {
    marginBottom: 24,
  },
  paymentSectionLabel: {
    color: colors.teal,
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 10,
  },
  paymentLineItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    gap: 8,
  },
  paymentInput: {
    backgroundColor: colors.navy,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  paymentInputSmall: {
    width: 50,
    textAlign: 'center',
  },
  paymentInputPrice: {
    flex: 1,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  paymentInputWithPrefix: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  paymentInputPrefix: {
    backgroundColor: colors.navyMid,
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    color: colors.gray,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  paymentRemoveItem: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 100, 100, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paymentRemoveItemText: {
    color: '#ff6464',
    fontSize: 14,
  },
  paymentAddItem: {
    paddingVertical: 12,
    alignItems: 'center',
    backgroundColor: colors.navy,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.navyMid,
    borderStyle: 'dashed',
  },
  paymentAddItemText: {
    color: colors.teal,
    fontSize: 14,
    fontWeight: '600',
  },
  paymentRow: {
    flexDirection: 'row',
    gap: 12,
  },
  paymentFieldHalf: {
    flex: 1,
  },
  paymentFieldLabel: {
    color: colors.gray,
    fontSize: 12,
    marginBottom: 6,
  },
  paymentTextarea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  paymentTotalCard: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: colors.teal,
    marginTop: 8,
  },
  paymentTotalLabel: {
    color: colors.gray,
    fontSize: 12,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  paymentTotalAmount: {
    color: colors.teal,
    fontSize: 36,
    fontWeight: '700',
  },

  // Step 2: Invoice
  paymentInvoiceCard: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
  },
  paymentInvoiceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  paymentInvoiceLogo: {
    color: colors.white,
    fontSize: 18,
    fontWeight: '800',
    letterSpacing: -0.5,
  },
  paymentInvoiceDate: {
    color: colors.gray,
    fontSize: 12,
  },
  paymentInvoiceClientSection: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
  },
  paymentInvoiceLabel: {
    color: colors.gray,
    fontSize: 10,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  paymentInvoiceClientName: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  paymentInvoiceClientDetail: {
    color: colors.gray,
    fontSize: 13,
    marginBottom: 1,
  },
  paymentInvoiceDivider: {
    height: 1,
    backgroundColor: colors.navyMid,
    marginVertical: 12,
  },
  paymentInvoiceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  paymentInvoiceItemName: {
    color: colors.textMuted,
    fontSize: 14,
    flex: 1,
  },
  paymentInvoiceItemPrice: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '600',
  },
  paymentInvoiceTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 12,
  },
  paymentInvoiceTotalLabel: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '700',
  },
  paymentInvoiceTotalAmount: {
    color: colors.teal,
    fontSize: 22,
    fontWeight: '700',
  },

  // Testimonial Offer
  paymentTestimonialOffer: {
    backgroundColor: 'rgba(74, 222, 128, 0.1)',
    borderRadius: 12,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 24,
    borderWidth: 1,
    borderColor: 'rgba(74, 222, 128, 0.3)',
  },
  paymentTestimonialOfferIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(74, 222, 128, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  paymentTestimonialOfferIconText: {
    fontSize: 22,
  },
  paymentTestimonialOfferContent: {
    flex: 1,
  },
  paymentTestimonialOfferTitle: {
    color: colors.green,
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
  },
  paymentTestimonialOfferText: {
    color: colors.textMuted,
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 8,
  },
  paymentTestimonialOfferSavings: {
    color: colors.green,
    fontSize: 12,
    fontWeight: '600',
  },

  // Choice Buttons
  paymentChoiceButtons: {
    gap: 12,
  },
  paymentChoicePrimary: {
    backgroundColor: colors.teal,
    borderRadius: 12,
    padding: 18,
    alignItems: 'center',
  },
  paymentChoicePrimaryIcon: {
    fontSize: 28,
    marginBottom: 6,
  },
  paymentChoicePrimaryText: {
    color: colors.darkNavy,
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 4,
  },
  paymentChoicePrimarySave: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: 13,
    fontWeight: '600',
  },
  paymentChoiceSecondary: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  paymentChoiceSecondaryText: {
    color: colors.gray,
    fontSize: 15,
  },

  // Step 3: Testimonial Form
  paymentTestimonialForm: {
    marginBottom: 24,
  },
  paymentRatingStars: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 20,
    gap: 8,
  },
  paymentRatingStar: {
    fontSize: 40,
    color: colors.navyMid,
  },
  paymentRatingStarActive: {
    color: '#FFD700',
  },
  paymentFinalAmount: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  paymentFinalDiscount: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 12,
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
  },
  paymentFinalDiscountText: {
    color: colors.green,
    fontSize: 14,
  },
  paymentFinalDiscountAmount: {
    color: colors.green,
    fontSize: 14,
    fontWeight: '600',
  },
  paymentFinalTotal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  paymentFinalTotalLabel: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  paymentFinalTotalAmount: {
    color: colors.teal,
    fontSize: 28,
    fontWeight: '700',
  },

  // Payment Methods
  paymentMethods: {
    marginBottom: 20,
  },
  paymentMethodGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  paymentMethodCard: {
    width: '31%',
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 14,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  paymentMethodCardActive: {
    borderColor: colors.teal,
    backgroundColor: 'rgba(74, 222, 128, 0.1)',
  },
  paymentMethodCardDisabled: {
    opacity: 0.5,
  },
  paymentMethodIcon: {
    fontSize: 28,
    marginBottom: 6,
  },
  paymentMethodName: {
    color: colors.white,
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 2,
  },
  paymentMethodHandle: {
    color: colors.gray,
    fontSize: 10,
    textAlign: 'center',
  },
  paymentInstructions: {
    backgroundColor: 'rgba(74, 222, 128, 0.1)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    borderLeftWidth: 3,
    borderLeftColor: colors.teal,
  },
  paymentInstructionsTitle: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  paymentInstructionsText: {
    color: colors.gray,
    fontSize: 13,
  },
  paymentCompleteBtn: {
    backgroundColor: colors.teal,
    borderRadius: 12,
    padding: 18,
    alignItems: 'center',
  },
  paymentCompleteBtnDisabled: {
    backgroundColor: colors.navyMid,
  },
  paymentCompleteBtnText: {
    color: colors.darkNavy,
    fontSize: 16,
    fontWeight: '700',
  },
  paymentCompleteBtnAmount: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: 14,
    fontWeight: '600',
    marginTop: 2,
  },

  // Success Screen
  paymentSuccess: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  paymentSuccessIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(74, 222, 128, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  paymentSuccessIconText: {
    fontSize: 40,
  },
  paymentSuccessTitle: {
    color: colors.white,
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 8,
  },
  paymentSuccessAmount: {
    color: colors.teal,
    fontSize: 42,
    fontWeight: '800',
    marginBottom: 16,
  },
  paymentSuccessBadge: {
    backgroundColor: 'rgba(74, 222, 128, 0.2)',
    borderRadius: 20,
    paddingVertical: 8,
    paddingHorizontal: 16,
    marginBottom: 20,
  },
  paymentSuccessBadgeText: {
    color: colors.green,
    fontSize: 13,
    fontWeight: '600',
  },
  paymentSuccessMessage: {
    color: colors.gray,
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 30,
    paddingHorizontal: 20,
  },
  paymentSuccessBtn: {
    backgroundColor: colors.teal,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 48,
  },
  paymentSuccessBtnText: {
    color: colors.darkNavy,
    fontSize: 16,
    fontWeight: '700',
  },
  paymentModalFooter: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: colors.navyMid,
  },
  paymentNextBtn: {
    backgroundColor: colors.teal,
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
  },
  paymentNextBtnText: {
    color: colors.darkNavy,
    fontSize: 16,
    fontWeight: '700',
  },

  // Client Card Payment Button
  clientPaymentBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.teal,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginTop: 12,
    gap: 6,
  },
  clientPaymentBtnIcon: {
    fontSize: 16,
  },
  clientPaymentBtnText: {
    color: colors.darkNavy,
    fontSize: 13,
    fontWeight: '700',
  },

  // Payment Settings Tab
  paymentSettingsGrid: {
    gap: 12,
  },
  paymentSettingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 16,
  },
  paymentSettingIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.navyMid,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  paymentSettingIconText: {
    fontSize: 22,
  },
  paymentSettingContent: {
    flex: 1,
  },
  paymentSettingLabel: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 6,
  },
  paymentSettingInput: {
    backgroundColor: colors.darkNavy,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  paymentSettingNote: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(74, 222, 128, 0.1)',
    borderRadius: 8,
    padding: 12,
    marginTop: 16,
    gap: 10,
  },
  paymentSettingNoteIcon: {
    fontSize: 18,
  },
  paymentSettingNoteText: {
    color: colors.gray,
    fontSize: 13,
    flex: 1,
  },
  paymentPreviewCard: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
  },
  paymentPreviewOffer: {
    alignItems: 'center',
  },
  paymentPreviewOfferIcon: {
    fontSize: 40,
    marginBottom: 12,
  },
  paymentPreviewOfferTitle: {
    color: colors.green,
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
  },
  paymentPreviewOfferText: {
    color: colors.gray,
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },

  // Payment Method Cards
  paymentMethodCard: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  paymentMethodCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder,
    gap: 12,
  },
  paymentMethodIconBg: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  paymentMethodCardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
  paymentMethodCardBody: {
    padding: 16,
    gap: 12,
  },
  paymentMethodField: {
    gap: 6,
  },
  paymentMethodFieldLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  paymentMethodFieldInput: {
    backgroundColor: colors.navy,
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 14,
    color: colors.white,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  qrCodePreview: {
    position: 'relative',
    alignItems: 'center',
    marginTop: 8,
    padding: 12,
    backgroundColor: colors.navy,
    borderRadius: 8,
  },
  qrCodePreviewImage: {
    width: 120,
    height: 120,
    borderRadius: 8,
    backgroundColor: colors.white,
  },
  qrCodeRemoveBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: colors.red,
    borderRadius: 12,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  paymentVideoUploadRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  paymentVideoUploadBtn: {
    backgroundColor: colors.teal,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  paymentVideoPreview: {
    marginTop: 12,
    position: 'relative',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: colors.navy,
  },
  paymentVideoRemoveBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: colors.red,
    borderRadius: 12,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Custom Payment Methods
  sectionHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  addMethodBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.gold + '20',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.gold + '40',
  },
  addMethodBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.gold,
  },
  emptyCustomMethods: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 20,
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.glassBorder,
  },
  emptyCustomMethodsText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
    marginTop: 16,
    marginBottom: 4,
  },
  emptyCustomMethodsSubtext: {
    fontSize: 13,
    color: colors.gray,
    textAlign: 'center',
  },
  customMethodsGrid: {
    gap: 16,
  },
  customMethodCard: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    overflow: 'hidden',
  },
  customMethodCardDisabled: {
    opacity: 0.5,
  },
  customMethodCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    gap: 12,
  },
  customMethodInfo: {
    flex: 1,
  },
  customMethodName: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
  },
  customMethodHandle: {
    fontSize: 13,
    color: colors.gray,
    marginTop: 2,
  },
  methodToggle: {
    width: 48,
    height: 26,
    borderRadius: 13,
    backgroundColor: colors.navyMid,
    padding: 2,
    justifyContent: 'center',
  },
  methodToggleActive: {
    backgroundColor: colors.teal + '40',
  },
  methodToggleKnob: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: colors.gray,
  },
  methodToggleKnobActive: {
    backgroundColor: colors.teal,
    alignSelf: 'flex-end',
  },
  customMethodQr: {
    alignItems: 'center',
    padding: 16,
    backgroundColor: colors.navy,
  },
  customMethodQrImage: {
    width: 100,
    height: 100,
    borderRadius: 8,
    backgroundColor: colors.white,
  },
  customMethodActions: {
    flexDirection: 'row',
    gap: 8,
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: colors.glassBorder,
  },
  customMethodEditBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    backgroundColor: colors.teal + '15',
    borderRadius: 8,
  },
  customMethodEditBtnText: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.teal,
  },
  customMethodDeleteBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    backgroundColor: colors.red + '15',
    borderRadius: 8,
  },
  customMethodDeleteBtnText: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.red,
  },

  // Icon & Color Pickers (for custom payment methods)
  iconPickerGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  iconPickerItem: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  iconPickerItemActive: {
    borderColor: colors.gold,
    backgroundColor: colors.gold + '20',
  },
  iconPickerEmoji: {
    fontSize: 22,
  },
  colorPickerGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  colorPickerItem: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 3,
    borderColor: 'transparent',
  },
  colorPickerItemActive: {
    borderColor: colors.white,
  },
  modalQrPreview: {
    alignItems: 'center',
    marginTop: 12,
    padding: 16,
    backgroundColor: colors.navy,
    borderRadius: 8,
    position: 'relative',
  },
  modalQrImage: {
    width: 150,
    height: 150,
    borderRadius: 8,
    backgroundColor: colors.white,
  },
  qrRemoveBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.red,
    justifyContent: 'center',
    alignItems: 'center',
  },
  qrUploadRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
  },
  qrUploadBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.teal,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  qrUploadBtnDisabled: {
    opacity: 0.6,
  },
  qrUploadBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  qrUploadOr: {
    fontSize: 13,
    color: colors.gray,
  },
  modalFieldLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gold,
    marginBottom: 8,
  },
  modalFieldHint: {
    fontSize: 12,
    color: colors.textMuted,
    marginTop: 4,
    marginBottom: 8,
  },

  // Maintenance Mode
  maintenanceSection: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 24,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  maintenanceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  maintenanceIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: colors.gold + '20',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  maintenanceIconText: {
    // Icon component used instead, no emoji needed
  },
  maintenanceInfo: {
    flex: 1,
  },
  maintenanceSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 4,
  },
  maintenanceSectionDesc: {
    fontSize: 13,
    color: colors.gray,
  },
  maintenanceToggle: {
    width: 56,
    height: 30,
    borderRadius: 15,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    padding: 3,
  },
  maintenanceToggleOn: {
    backgroundColor: colors.teal,
  },
  maintenanceToggleOnRed: {
    backgroundColor: colors.red,
  },
  maintenanceToggleDot: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.gray,
  },
  maintenanceToggleDotOn: {
    backgroundColor: colors.white,
    alignSelf: 'flex-end',
  },
  maintenancePreview: {
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: colors.navyLight,
  },
  previewLabel: {
    fontSize: 11,
    color: colors.gray,
    textTransform: 'uppercase',
    fontWeight: '600',
    marginBottom: 12,
  },
  bannerPreview: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.gold + '20',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: colors.gold + '30',
  },
  bannerPreviewIcon: {
    fontSize: 16,
    marginRight: 10,
  },
  bannerPreviewText: {
    flex: 1,
    fontSize: 13,
    color: colors.gold,
  },
  maintenanceField: {
    marginBottom: 16,
  },
  maintenanceFieldLabel: {
    fontSize: 12,
    color: colors.gray,
    marginBottom: 8,
  },
  maintenanceInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  maintenanceTextarea: {
    height: 80,
    textAlignVertical: 'top',
  },
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.red + '20',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: colors.red + '30',
  },
  warningBannerIcon: {
    fontSize: 16,
    marginRight: 10,
  },
  warningBannerText: {
    flex: 1,
    fontSize: 13,
    color: colors.red,
    fontWeight: '500',
  },
  lockdownPreview: {
    backgroundColor: colors.darkNavy,
    borderRadius: 12,
    padding: 40,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  lockdownPreviewIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  lockdownPreviewTitle: {
    fontSize: 24,
    fontWeight: '800',
    color: colors.white,
    marginBottom: 12,
  },
  lockdownPreviewMessage: {
    fontSize: 14,
    color: colors.gray,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 12,
  },
  lockdownPreviewTime: {
    fontSize: 12,
    color: colors.teal,
  },
  statusSummary: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  statusSummaryTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 12,
  },
  statusItems: {},
  statusItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  statusDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  statusItemText: {
    fontSize: 14,
    color: colors.grayLight,
  },

  // Tables
  tableContainer: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.navyLight,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.navyLight,
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
    alignItems: 'center',
  },
  tableCell: {
    fontSize: 14,
    color: colors.gray,
  },
  tableCellHeader: {
    fontWeight: '600',
    color: colors.grayLight,
    textTransform: 'uppercase',
    fontSize: 11,
    letterSpacing: 0.5,
  },
  cellSubtext: {
    fontSize: 13,
    color: colors.gray,
    marginBottom: 2,
  },

  // Status Badges
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 100,
    alignSelf: 'flex-start',
  },
  status_new: {
    backgroundColor: colors.teal + '20',
  },
  status_contacted: {
    backgroundColor: colors.blue + '20',
  },
  status_scheduled: {
    backgroundColor: colors.gold + '20',
  },
  status_pending: {
    backgroundColor: colors.orange + '20',
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.grayLight,
    textTransform: 'capitalize',
  },

  // Buttons
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: colors.teal,
    paddingVertical: 10,
    paddingHorizontal: isWeb && width > 900 ? 20 : 12,
    borderRadius: 10,
    flexShrink: 1,
    minWidth: 0,
    ...(isWeb ? { boxShadow: '0 4px 15px rgba(212, 168, 75, 0.25)' } : {}),
  },
  primaryBtnText: {
    color: colors.darkNavy,
    fontSize: isWeb && width > 600 ? 14 : 13,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyLight,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  actionBtnText: {
    fontSize: 14,
  },

  // Filter Bar
  filterBar: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 24,
  },
  filterBtn: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 100,
    backgroundColor: colors.navyLight,
  },
  filterBtnActive: {
    backgroundColor: colors.teal,
  },
  filterBtnText: {
    fontSize: 13,
    color: colors.gray,
    fontWeight: '500',
  },
  filterBtnTextActive: {
    color: colors.darkNavy,
  },

  // Finances Tab Styles
  financesHeaderBtns: {
    flexDirection: 'row',
    gap: 12,
  },
  exportBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: colors.teal + '20',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.teal + '30',
  },
  exportBtnText: {
    color: colors.teal,
    fontSize: 14,
    fontWeight: '600',
  },
  financesSummaryRow: {
    flexDirection: isWeb && width > 768 ? 'row' : 'column',
    gap: 16,
    marginBottom: 24,
  },
  financesSummaryCard: {
    flex: 1,
    minWidth: isWeb && width > 600 ? 200 : 140,
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    gap: 12,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  financesSummaryIncome: {
    borderColor: colors.gold + '30',
    backgroundColor: colors.gold + '08',
  },
  financesSummaryExpense: {
    borderColor: colors.red + '30',
    backgroundColor: colors.red + '08',
  },
  financesSummaryProfit: {
    borderColor: colors.green + '30',
    backgroundColor: colors.green + '08',
  },
  financesSummaryIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  financesSummaryLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  financesSummaryValue: {
    fontSize: 28,
    fontWeight: '700',
  },
  financesFilters: {
    flexDirection: isWeb && width > 768 ? 'row' : 'column',
    gap: 16,
    marginBottom: 24,
  },
  filterGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  filterGroupLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.gray,
  },
  financesTableContainer: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  financesTableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.navyLight,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.navy,
  },
  financesTableHeaderCell: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  financesTableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
  },
  financesTableRowAlt: {
    backgroundColor: colors.navyLight + '30',
  },
  financesTableRowImported: {
    backgroundColor: colors.teal + '08',
  },
  financesTableCell: {
    fontSize: 14,
    color: colors.white,
  },
  financesTableCellText: {
    fontSize: 14,
    color: colors.white,
  },
  financesTypeBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  financesTypeBadgeIncome: {
    backgroundColor: colors.green + '20',
  },
  financesTypeBadgeExpense: {
    backgroundColor: colors.red + '20',
  },
  financesImportedBadge: {
    fontSize: 10,
    color: colors.teal,
    backgroundColor: colors.teal + '20',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginTop: 4,
    alignSelf: 'flex-start',
  },
  financesEmpty: {
    alignItems: 'center',
    paddingVertical: 60,
    gap: 12,
  },
  financesEmptyText: {
    fontSize: 16,
    color: colors.gray,
  },
  financesEmptyBtn: {
    marginTop: 8,
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: colors.teal + '20',
    borderRadius: 8,
  },
  financesEmptyBtnText: {
    color: colors.teal,
    fontSize: 14,
    fontWeight: '600',
  },
  financesModal: {
    backgroundColor: colors.darkNavy,
    borderRadius: 24,
    width: '94%',
    maxWidth: 520,
    maxHeight: '90%',
    borderWidth: 1,
    borderColor: colors.navyLight,
    overflow: 'hidden',
  },
  financesModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 24,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
    backgroundColor: colors.navy,
    gap: 16,
  },
  financesModalHeaderIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  financesModalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
  },
  financesModalSubtitle: {
    fontSize: 13,
    color: colors.gray,
    marginTop: 2,
  },
  financesModalCloseBtn: {
    position: 'absolute',
    top: 20,
    right: 20,
    padding: 4,
  },
  financesModalBody: {
    padding: 24,
    maxHeight: isWeb && width > 600 ? 500 : 400,
  },
  financesModalSection: {
    marginBottom: 28,
  },
  financesModalSectionTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 12,
  },
  financesModalField: {
    marginBottom: 20,
  },
  financesModalLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  financesModalInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 16,
    color: colors.white,
    fontSize: 15,
    borderWidth: 1,
    borderColor: colors.navyLight,
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  },
  financesModalTextarea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  financesTypeToggle: {
    flexDirection: 'row',
    gap: 12,
  },
  financesTypeIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  financesTypeToggleBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: colors.navy,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: colors.navyLight,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  financesTypeToggleBtnIncomeActive: {
    backgroundColor: colors.gold + '12',
    borderColor: colors.gold + '50',
  },
  financesTypeToggleBtnExpenseActive: {
    backgroundColor: colors.red + '12',
    borderColor: colors.red + '50',
  },
  financesTypeToggleText: {
    fontSize: 15,
    fontWeight: '500',
    color: colors.gray,
  },
  financesCategoryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  financesCategoryBtn: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: colors.navy,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  financesCategoryBtnActive: {
    backgroundColor: colors.teal + '20',
    borderColor: colors.teal + '40',
  },
  financesCategoryBtnIncomeActive: {
    backgroundColor: colors.gold + '15',
    borderColor: colors.gold + '40',
  },
  financesCategoryBtnExpenseActive: {
    backgroundColor: colors.red + '15',
    borderColor: colors.red + '40',
  },
  financesCategoryBtnText: {
    fontSize: 13,
    color: colors.gray,
    fontWeight: '500',
  },
  financesCategoryBtnTextActive: {
    color: colors.teal,
  },
  financesAmountBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navy,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: colors.navyLight,
    paddingHorizontal: 20,
    paddingVertical: 8,
  },
  financesAmountInput: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.navyLight,
    paddingLeft: 16,
  },
  financesAmountDollar: {
    fontSize: 36,
    fontWeight: '700',
  },
  financesAmountField: {
    flex: 1,
    fontSize: 36,
    fontWeight: '700',
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  financesInputGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navy,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.navyLight,
    marginBottom: 12,
  },
  financesInputIcon: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: colors.navyLight,
  },
  financesInputField: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 14,
    fontSize: 15,
    color: colors.white,
  },
  financesInputFieldMulti: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  financesModalFooter: {
    flexDirection: 'row',
    padding: 24,
    paddingTop: 20,
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: colors.navyLight,
    backgroundColor: colors.navy,
  },
  financesModalCancelBtn: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    backgroundColor: colors.navyLight,
  },
  financesModalCancelBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.gray,
  },
  financesModalSaveBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: colors.teal,
  },
  financesModalSaveBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  financesEditInfoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    padding: 14,
    backgroundColor: colors.blue + '15',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.blue + '30',
    marginTop: 8,
  },
  financesEditInfoText: {
    flex: 1,
    fontSize: 13,
    color: colors.blue,
    lineHeight: 18,
  },
  financesOldAmountField: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 8,
    color: colors.white,
    fontSize: 20,
    fontWeight: '600',
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  },

  // ==================== LEADS TAB STYLES ====================
  leadsStatsRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  leadsStatCard: {
    flex: 1,
    minWidth: 120,
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 16,
    borderLeftWidth: 3,
  },
  leadsStatNumber: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.white,
  },
  leadsStatLabel: {
    fontSize: 12,
    color: colors.gray,
    marginTop: 4,
  },
  leadsFilters: {
    marginBottom: 16,
  },
  leadsFilterTabs: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  leadsFilterTab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 14,
    backgroundColor: colors.navy,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  leadsFilterTabActive: {
    backgroundColor: colors.gold + '20',
    borderColor: colors.gold,
  },
  leadsFilterTabText: {
    fontSize: 13,
    color: colors.gray,
    fontWeight: '500',
  },
  leadsFilterTabTextActive: {
    color: colors.gold,
  },
  leadsFilterBadge: {
    backgroundColor: colors.gold,
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
  },
  leadsFilterBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  leadsSearchSort: {
    flexDirection: 'row',
    gap: 12,
  },
  leadsSearchBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: colors.navy,
    borderRadius: 10,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  leadsSearchInput: {
    flex: 1,
    paddingVertical: 10,
    fontSize: 14,
    color: colors.white,
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  },
  leadsList: {
    flex: 1,
  },
  leadsEmptyState: {
    alignItems: 'center',
    paddingVertical: 60,
    gap: 12,
  },
  leadsEmptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.white,
  },
  leadsEmptyText: {
    fontSize: 14,
    color: colors.gray,
    textAlign: 'center',
  },
  leadsCard: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  leadsCardNew: {
    borderColor: colors.gold + '50',
    backgroundColor: colors.gold + '08',
  },
  leadsCardSelected: {
    borderColor: colors.teal,
  },
  leadsCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  leadsCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  leadsStatusDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  leadsCardName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
  leadsCardEmail: {
    fontSize: 13,
    color: colors.gray,
    marginTop: 2,
  },
  leadsCardRight: {
    alignItems: 'flex-end',
  },
  leadsCardAmount: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.gold,
  },
  leadsCardTime: {
    fontSize: 12,
    color: colors.gray,
    marginTop: 2,
  },
  leadsCardDescription: {
    fontSize: 14,
    color: colors.grayLight,
    lineHeight: 20,
    marginBottom: 12,
  },
  leadsCardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  leadsCardStatus: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
  },
  leadsCardStatusText: {
    fontSize: 12,
    fontWeight: '600',
  },
  leadsCardZip: {
    fontSize: 12,
    color: colors.gray,
  },
  leadsCardPhone: {
    fontSize: 12,
    color: colors.gray,
  },
  leadsDetailSection: {
    marginBottom: 24,
  },
  leadsDetailSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
  },
  leadsDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
  },
  leadsDetailText: {
    fontSize: 15,
    color: colors.white,
  },
  leadsDetailDescription: {
    fontSize: 15,
    color: colors.grayLight,
    lineHeight: 22,
    backgroundColor: colors.navy,
    padding: 14,
    borderRadius: 10,
  },
  leadsDetailBreakdownRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
  },
  leadsDetailBreakdownItem: {
    fontSize: 14,
    color: colors.grayLight,
  },
  leadsDetailBreakdownPrice: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  leadsDetailBreakdownTotal: {
    borderBottomWidth: 0,
    marginTop: 8,
    paddingTop: 12,
    borderTopWidth: 2,
    borderTopColor: colors.gold + '30',
  },
  leadsDetailBreakdownTotalLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
  },
  leadsDetailBreakdownTotalPrice: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.gold,
  },
  leadsStatusButtons: {
    flexDirection: 'row',
    gap: 10,
  },
  leadsStatusBtn: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.navyLight,
    alignItems: 'center',
  },
  leadsStatusBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.gray,
  },
  leadsArchiveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: colors.navyLight,
  },
  leadsArchiveBtnText: {
    fontSize: 14,
    color: colors.gray,
  },
  leadsActionBtns: {
    flexDirection: 'row',
    gap: 10,
    marginLeft: 'auto',
  },
  leadsEmailBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: colors.teal,
  },
  leadsEmailBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  leadsCallBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: colors.green,
  },
  leadsCallBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  leadsEmailRecipient: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    backgroundColor: colors.navy,
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
  },
  leadsEmailRecipientIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.teal + '20',
    justifyContent: 'center',
    alignItems: 'center',
  },
  leadsEmailRecipientName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
  leadsEmailRecipientEmail: {
    fontSize: 14,
    color: colors.teal,
    marginTop: 2,
  },
  leadsEmailField: {
    marginBottom: 16,
  },
  leadsEmailFieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.gray,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  leadsEmailInput: {
    backgroundColor: colors.navy,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.navyLight,
    padding: 14,
    fontSize: 15,
    color: colors.white,
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  },
  leadsEmailTextarea: {
    minHeight: 200,
    textAlignVertical: 'top',
    lineHeight: 22,
  },
  leadsEmailTemplates: {
    marginTop: 8,
  },
  leadsEmailTemplatesLabel: {
    fontSize: 13,
    color: colors.gray,
    marginBottom: 10,
  },
  leadsEmailTemplatesBtns: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  leadsEmailTemplateBtn: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    backgroundColor: colors.navy,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  leadsEmailTemplateBtnText: {
    fontSize: 13,
    color: colors.grayLight,
  },
  leadsEmailSendBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.teal,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 10,
  },
  leadsEmailSendBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
  },

  // Section Titles
  recentSection: {
    marginTop: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 16,
  },

  // Calendar Placeholder
  calendarPlaceholder: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 60,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.navyLight,
    borderStyle: 'dashed',
    marginBottom: 32,
  },
  placeholderIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  placeholderText: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.grayLight,
    marginBottom: 8,
  },
  placeholderSubtext: {
    fontSize: 14,
    color: colors.gray,
  },

  // Appointments
  upcomingSection: {
    marginTop: 20,
  },
  appointmentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  appointmentDate: {
    backgroundColor: colors.teal,
    borderRadius: 10,
    padding: 12,
    alignItems: 'center',
    marginRight: 16,
    minWidth: 60,
  },
  appointmentDay: {
    fontSize: 24,
    fontWeight: '800',
    color: colors.darkNavy,
  },
  appointmentMonth: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.darkNavy,
  },
  appointmentDetails: {
    flex: 1,
  },
  appointmentCustomer: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 4,
  },
  appointmentService: {
    fontSize: 14,
    color: colors.gray,
    marginBottom: 4,
  },
  appointmentTime: {
    fontSize: 13,
    color: colors.teal,
  },

  // Testimonials
  testimonialGrid: {
    gap: 20,
  },
  testimonialCard: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  testimonialHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  testimonialAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.teal,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  testimonialInitial: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  testimonialName: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
  },
  testimonialStars: {
    flexDirection: 'row',
  },
  star: {
    fontSize: 14,
    color: colors.gold,
  },
  testimonialText: {
    fontSize: 15,
    color: colors.grayLight,
    lineHeight: 24,
    marginBottom: 16,
    fontStyle: 'italic',
  },
  testimonialActions: {
    flexDirection: 'row',
    gap: 12,
  },

  // Reminders
  reminderConfig: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  reminderOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
  },
  reminderOptionLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  reminderOptionIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: colors.gold + '15',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  reminderOptionTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
  },
  reminderOptionDesc: {
    fontSize: 13,
    color: colors.gray,
  },
  toggle: {
    width: 50,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    padding: 3,
  },
  toggleOn: {
    backgroundColor: colors.teal,
  },
  toggleDot: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: colors.gray,
  },
  toggleDotOn: {
    backgroundColor: colors.white,
    alignSelf: 'flex-end',
  },
  reminderTimings: {
    marginTop: 24,
  },
  timingTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.grayLight,
    marginBottom: 16,
  },
  timingOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  timingCheck: {
    fontSize: 14,
    color: colors.teal,
    marginRight: 12,
    fontWeight: '700',
  },
  timingText: {
    fontSize: 14,
    color: colors.gray,
  },

  // Settings
  settingsSection: {
    marginTop: 24,
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  settingLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 1,
  },
  settingRight: {
    alignItems: 'flex-end',
  },
  settingIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: colors.gold + '15',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  settingIconConnected: {
    backgroundColor: colors.teal + '20',
  },
  settingTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
  },
  settingDesc: {
    fontSize: 13,
    color: colors.gray,
  },
  settingDetails: {
    fontSize: 11,
    color: colors.gray,
    marginTop: 4,
    opacity: 0.7,
  },
  settingStatusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  settingStatusConnected: {
    backgroundColor: colors.teal + '20',
  },
  settingStatusDisconnected: {
    backgroundColor: colors.red + '20',
  },
  settingStatusPending: {
    backgroundColor: colors.gold + '20',
  },
  settingStatus: {
    backgroundColor: colors.orange + '20',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  settingStatusText: {
    fontSize: 12,
    color: colors.red,
    fontWeight: '600',
  },
  settingStatusTextConnected: {
    color: colors.teal,
  },
  settingStatusTextPending: {
    color: colors.gold,
  },
  settingError: {
    fontSize: 11,
    color: colors.red,
    marginTop: 4,
    maxWidth: 150,
    textAlign: 'right',
  },
  infoCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: colors.teal + '10',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.teal + '30',
  },
  infoText: {
    flex: 1,
    fontSize: 13,
    color: colors.grayLight,
    lineHeight: 20,
  },

  // Loading
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 80,
  },
  loadingText: {
    color: colors.gray,
    fontSize: 14,
    marginTop: 16,
  },
  emptyStateText: {
    color: colors.gray,
    fontSize: 14,
    textAlign: 'center',
    paddingVertical: 20,
    fontStyle: 'italic',
  },

  // Pricing Tab
  pricingSection: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 24,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    ...(isWeb ? { boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } : {}),
  },
  sectionSubtitle: {
    fontSize: 14,
    color: colors.gray,
    marginBottom: 20,
  },
  settingsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  settingCard: {
    flex: 1,
    minWidth: 180,
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    padding: 16,
  },
  settingCardLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 4,
  },
  settingCardDesc: {
    fontSize: 12,
    color: colors.gray,
    marginBottom: 12,
  },
  settingInputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyMid,
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  currencyPrefix: {
    color: colors.teal,
    fontSize: 16,
    fontWeight: '600',
    marginRight: 4,
  },
  settingInput: {
    flex: 1,
    color: colors.white,
    fontSize: 18,
    fontWeight: '600',
    paddingVertical: 12,
  },
  unitSuffix: {
    color: colors.gray,
    fontSize: 14,
    marginLeft: 4,
  },
  addItemRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: 12,
  },
  addItemField: {
    flex: 2,
    minWidth: 200,
  },
  addItemFieldSmall: {
    flex: 1,
    minWidth: 100,
  },
  addItemLabel: {
    fontSize: 12,
    color: colors.gray,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  addItemInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  priceInputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  addItemInputPrice: {
    flex: 1,
    color: colors.white,
    fontSize: 14,
    paddingVertical: 12,
  },
  categorySelect: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  categoryOption: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: colors.navyLight,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  categoryOptionActive: {
    borderColor: colors.teal,
    backgroundColor: colors.teal + '20',
  },
  categoryOptionIcon: {
    // Icon component is used directly, no emoji style needed
  },
  addItemBtn: {
    backgroundColor: colors.teal,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignSelf: 'flex-end',
  },
  addItemBtnText: {
    color: colors.darkNavy,
    fontSize: 14,
    fontWeight: '700',
  },
  categoryGroup: {
    marginBottom: 24,
  },
  categoryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
  },
  categoryIcon: {
    // Icon component styling handled inline
    marginRight: 10,
  },
  categoryLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    flex: 1,
  },
  categoryCount: {
    fontSize: 12,
    color: colors.gray,
    backgroundColor: colors.navyLight,
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 100,
  },
  lineItemsTable: {
    gap: 8,
  },
  lineItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    padding: 12,
  },
  lineItemMain: {
    flex: 1,
  },
  lineItemName: {
    color: colors.grayLight,
    fontSize: 14,
  },
  lineItemNameInput: {
    color: colors.white,
    fontSize: 14,
    backgroundColor: colors.navyMid,
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginRight: 10,
  },
  lineItemPrice: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.navyMid,
    borderRadius: 6,
    paddingHorizontal: 10,
    marginRight: 10,
  },
  currencySmall: {
    color: colors.teal,
    fontSize: 14,
    fontWeight: '600',
  },
  lineItemPriceInput: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '600',
    paddingVertical: 8,
    width: 60,
    textAlign: 'right',
  },
  lineItemDelete: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: colors.red + '20',
    justifyContent: 'center',
    alignItems: 'center',
  },
  lineItemDeleteText: {
    color: colors.red,
    fontSize: 14,
    fontWeight: '600',
  },
  primaryBtnDisabled: {
    opacity: 0.6,
  },

  // Login - Gold & Blue Theme
  loginContainer: {
    flex: 1,
    backgroundColor: colors.darkNavy,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    position: 'relative',
    overflow: 'hidden',
  },
  loginCard: {
    backgroundColor: colors.navy,
    borderRadius: 20,
    padding: 40,
    width: '90%',
    maxWidth: 420,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    ...(isWeb ? { boxShadow: '0 25px 50px rgba(0,0,0,0.4)' } : {}),
    position: 'relative',
    overflow: 'hidden',
  },
  loginHeader: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 32,
  },
  loginLogo: {
    fontSize: 32,
    fontWeight: '800',
    color: colors.white,
    letterSpacing: 1,
  },
  loginLogoAccent: {
    fontSize: 32,
    fontWeight: '800',
    color: colors.teal,
    letterSpacing: 1,
  },
  loginTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.white,
    textAlign: 'center',
    marginBottom: 8,
  },
  loginSubtitle: {
    fontSize: 14,
    color: colors.gray,
    textAlign: 'center',
    marginBottom: 32,
  },
  loginError: {
    color: colors.red,
    fontSize: 13,
    textAlign: 'center',
    marginBottom: 16,
    backgroundColor: colors.red + '15',
    padding: 12,
    borderRadius: 10,
  },
  loginInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 20,
    fontSize: 16,
    color: colors.white,
    borderWidth: 1,
    borderColor: colors.navyMid,
    marginBottom: 16,
  },
  loginBtn: {
    backgroundColor: colors.teal,
    paddingVertical: 16,
    borderRadius: 12,
    marginBottom: 24,
    position: 'relative',
    overflow: 'hidden',
    ...(isWeb ? { boxShadow: '0 4px 20px rgba(212, 168, 75, 0.35)' } : {}),
  },
  loginBtnText: {
    color: colors.darkNavy,
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
    letterSpacing: 0.5,
  },
  backLink: {
    color: colors.teal,
    fontSize: 14,
    textAlign: 'center',
  },
  loginBtnDisabled: {
    opacity: 0.7,
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: colors.darkNavy,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
  },
  authLoadingText: {
    color: colors.grayLight,
    fontSize: 14,
    marginTop: 16,
    fontWeight: '500',
  },

  // Hours Tab Styles
  hoursPreview: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  hoursPreviewLabel: {
    color: colors.gray,
    fontSize: 14,
  },
  hoursPreviewBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.teal + '15',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    gap: 8,
  },
  hoursPreviewIcon: {
    marginRight: 8,
  },
  hoursPreviewText: {
    color: colors.teal,
    fontSize: 14,
    fontWeight: '600',
  },
  hoursGrid: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: colors.navyLight,
    marginBottom: 24,
  },
  hoursRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyLight,
  },
  hoursDaySection: {
    flexDirection: 'row',
    alignItems: 'center',
    width: 180,
    gap: 12,
  },
  hoursToggle: {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.navyMid,
    padding: 2,
  },
  hoursToggleOn: {
    backgroundColor: colors.teal,
  },
  hoursToggleDot: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.gray,
  },
  hoursToggleDotOn: {
    backgroundColor: colors.white,
    marginLeft: 'auto',
  },
  hoursDayName: {
    color: colors.white,
    fontSize: 15,
    fontWeight: '500',
  },
  hoursDayNameClosed: {
    color: colors.gray,
  },
  hoursTimeSection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  hoursTimeInput: {
    flex: 1,
  },
  hoursTimeLabel: {
    color: colors.gray,
    fontSize: 11,
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  hoursInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  hoursTimeSep: {
    color: colors.gray,
    fontSize: 14,
  },
  hoursClosedText: {
    flex: 1,
    color: colors.gray,
    fontSize: 14,
    fontStyle: 'italic',
  },
  hoursNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: colors.blue + '10',
    padding: 16,
    borderRadius: 10,
    gap: 12,
  },
  hoursNoteIcon: {
    marginRight: 12,
    marginTop: 2,
  },
  hoursNoteText: {
    flex: 1,
    color: colors.grayLight,
    fontSize: 13,
    lineHeight: 20,
  },

  // Services Tab Styles
  servicesStats: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 24,
  },
  servicesStat: {
    flex: 1,
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  servicesStatNum: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.teal,
    marginBottom: 4,
  },
  servicesStatLabel: {
    fontSize: 13,
    color: colors.gray,
  },
  servicesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  viewAllLink: {
    color: colors.teal,
    fontSize: 14,
    fontWeight: '500',
  },
  servicesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  serviceCard: {
    width: isWeb && width > 768 ? 'calc(25% - 12px)' : isWeb && width > 500 ? 'calc(50% - 8px)' : '100%',
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    paddingTop: 55,
    borderWidth: 1,
    borderColor: colors.navyLight,
    position: 'relative',
    minHeight: 220,
  },
  serviceCardInactive: {
    opacity: 0.5,
  },
  serviceToggleWrap: {
    position: 'absolute',
    top: 42,
    right: 14,
    zIndex: 5,
  },
  serviceIconBox: {
    width: 48,
    height: 48,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14,
  },
  serviceIcon: {
    fontSize: 24,
  },
  serviceToggle: {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.navyMid,
    padding: 2,
    justifyContent: 'center',
  },
  serviceToggleOn: {
    backgroundColor: colors.teal,
  },
  serviceToggleDot: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.gray,
  },
  serviceToggleDotOn: {
    backgroundColor: colors.white,
    marginLeft: 'auto',
  },
  serviceTitle: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 6,
  },
  serviceDesc: {
    color: colors.gray,
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 12,
  },
  serviceEditBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: colors.gold + '15',
    borderRadius: 8,
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: colors.gold + '30',
  },
  serviceEditBtnText: {
    color: colors.gold,
    fontSize: 12,
    fontWeight: '600',
  },
  serviceEditForm: {
    gap: 4,
  },
  serviceEditInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  },
  serviceEditInputSmall: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  serviceEditTextarea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  serviceEditLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.gray,
    marginTop: 12,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  serviceEditHint: {
    fontSize: 11,
    color: colors.gray,
    fontStyle: 'italic',
    marginTop: 4,
    marginBottom: 8,
    lineHeight: 16,
  },
  serviceEditRow: {
    flexDirection: 'row',
    gap: 12,
  },
  serviceColorPicker: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
    marginTop: 4,
  },
  serviceColorOption: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  serviceColorOptionActive: {
    borderColor: colors.white,
    ...(isWeb ? { boxShadow: '0 0 10px rgba(255,255,255,0.3)' } : {}),
  },
  serviceEditActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  serviceEditDone: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    backgroundColor: colors.teal,
    borderRadius: 10,
    marginTop: 12,
  },
  serviceEditDoneText: {
    color: colors.darkNavy,
    fontSize: 14,
    fontWeight: '700',
  },
  serviceDisplayBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    backgroundColor: colors.teal + '30',
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 6,
    zIndex: 10,
  },
  serviceDisplayBadgeText: {
    color: colors.teal,
    fontSize: 9,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // Testimonials Tab Styles
  tabHeaderActions: {
    flexDirection: 'row',
    gap: 12,
  },
  secondaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: isWeb && width > 600 ? 16 : 12,
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.navyMid,
    flexShrink: 0,
  },
  tabHeaderButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
  },
  secondaryBtnText: {
    color: colors.grayLight,
    fontSize: 14,
    fontWeight: '500',
  },
  nextJobBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: isWeb && width > 900 ? 16 : 12,
    backgroundColor: colors.green,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.green,
    shadowColor: colors.green,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 3,
    flexShrink: 1,
    minWidth: 0,
  },
  nextJobBtnText: {
    color: colors.darkNavy,
    fontSize: 14,
    fontWeight: '700',
  },
  testimonialStats: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 24,
  },
  testimonialStat: {
    flex: 1,
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  testimonialStatNum: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.gold,
    marginBottom: 4,
  },
  testimonialStatLabel: {
    fontSize: 12,
    color: colors.gray,
  },
  testimonialScrollContainer: {
    flex: 1,
  },
  testimonialGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    paddingBottom: 20,
  },
  testimonialGridCard: {
    width: isWeb && width > 1024 ? 'calc(33.333% - 11px)' : isWeb && width > 600 ? 'calc(50% - 8px)' : '100%',
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    paddingTop: 50,
    borderWidth: 1,
    borderColor: colors.navyLight,
    position: 'relative',
    minHeight: 220,
  },
  testimonialCardInactive: {
    opacity: 0.5,
  },
  testimonialActiveBadge: {
    position: 'absolute',
    top: 12,
    left: 12,
    backgroundColor: colors.teal + '30',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  testimonialActiveBadgeText: {
    color: colors.teal,
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  testimonialGridToggle: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 40,
    height: 22,
    borderRadius: 11,
    backgroundColor: colors.navyMid,
    padding: 2,
    justifyContent: 'center',
  },
  testimonialGridHeader: {
    alignItems: 'center',
    marginBottom: 12,
  },
  testimonialGridAvatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colors.teal,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
  },
  testimonialGridName: {
    color: colors.white,
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 2,
  },
  testimonialGridLocation: {
    color: colors.gray,
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 6,
  },
  testimonialGridText: {
    color: colors.grayLight,
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
    fontStyle: 'italic',
    flex: 1,
  },
  testimonialGridActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.navyMid,
  },
  testimonialDeleteBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.red + '20',
    justifyContent: 'center',
    alignItems: 'center',
  },
  starSmall: {
    color: colors.gold,
    fontSize: 12,
  },
  testimonialHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  testimonialAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.teal + '20',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  testimonialAvatarImg: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  testimonialInitial: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.teal,
  },
  testimonialInfo: {
    flex: 1,
  },
  testimonialName: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  testimonialLocation: {
    color: colors.gray,
    fontSize: 12,
    marginBottom: 4,
  },
  testimonialStars: {
    flexDirection: 'row',
  },
  star: {
    color: colors.gold,
    fontSize: 14,
  },
  testimonialToggle: {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.navyMid,
    padding: 2,
  },
  testimonialToggleOn: {
    backgroundColor: colors.teal,
  },
  testimonialToggleDot: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.gray,
  },
  testimonialToggleDotOn: {
    backgroundColor: colors.white,
    marginLeft: 'auto',
  },
  testimonialText: {
    color: colors.grayLight,
    fontSize: 14,
    lineHeight: 22,
    fontStyle: 'italic',
    marginBottom: 16,
  },
  testimonialActions: {
    flexDirection: 'row',
    gap: 10,
  },
  ratingSelector: {
    flexDirection: 'row',
    gap: 8,
  },
  ratingStar: {
    fontSize: 28,
    color: colors.navyMid,
  },
  ratingStarActive: {
    color: colors.gold,
  },
  primaryBtnDisabled: {
    opacity: 0.6,
  },

  // Section Tabs (for Estimates & Quotes)
  sectionTabs: {
    flexDirection: 'row',
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 4,
    marginBottom: 24,
    gap: 4,
  },
  sectionTab: {
    flex: 1,
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionTabActive: {
    backgroundColor: colors.teal,
  },
  sectionTabText: {
    color: colors.gray,
    fontSize: 14,
    fontWeight: '600',
  },
  sectionTabTextActive: {
    color: colors.darkNavy,
  },

  // AI Config Styles
  aiConfigField: {
    marginBottom: 24,
  },
  aiConfigLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 4,
  },
  aiConfigDesc: {
    fontSize: 12,
    color: colors.gray,
    marginBottom: 12,
  },
  aiConfigInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  aiConfigTextarea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },

  // Keywords Styles
  keywordsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  keywordTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.red + '20',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    gap: 8,
  },
  keywordTagText: {
    color: colors.red,
    fontSize: 12,
    fontWeight: '500',
  },
  keywordTagRemove: {
    color: colors.red,
    fontSize: 14,
    fontWeight: '700',
  },
  addKeywordRow: {
    flexDirection: 'row',
    gap: 12,
  },
  addKeywordInput: {
    flex: 1,
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  addKeywordBtn: {
    backgroundColor: colors.teal,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    justifyContent: 'center',
  },
  addKeywordBtnText: {
    color: colors.darkNavy,
    fontSize: 14,
    fontWeight: '600',
  },

  // Complexity Rules Styles
  complexityRulesGrid: {
    flexDirection: isWeb && width > 800 ? 'row' : 'column',
    gap: 16,
  },
  complexityCard: {
    flex: 1,
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 16,
    borderWidth: 2,
    borderColor: colors.navyMid,
  },
  complexityCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  complexityDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  complexityCardTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  complexityDescInput: {
    backgroundColor: colors.navy,
    borderRadius: 8,
    padding: 12,
    color: colors.grayLight,
    fontSize: 13,
    minHeight: 60,
    textAlignVertical: 'top',
  },

  // Map Container Styles
  mapContainer: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 16,
  },
  mapPlaceholder: {
    height: 300,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.navy,
  },
  mapPlaceholderText: {
    color: colors.gray,
    fontSize: 16,
  },
  mapLegend: {
    padding: 16,
    backgroundColor: colors.navy,
  },
  mapLegendTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 12,
  },
  mapLegendItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  mapLegendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  mapLegendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  mapLegendText: {
    color: colors.grayLight,
    fontSize: 12,
  },

  // Radius Config Styles
  radiusConfig: {
    flexDirection: isWeb && width > 600 ? 'row' : 'column',
    gap: 24,
    alignItems: 'center',
  },
  radiusInputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  radiusInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    color: colors.white,
    fontSize: 24,
    fontWeight: '700',
    width: 100,
    textAlign: 'center',
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  radiusUnit: {
    color: colors.gray,
    fontSize: 16,
  },
  radiusVisual: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  radiusCircle: {
    borderRadius: 999,
    borderWidth: 3,
    borderColor: colors.teal,
    backgroundColor: colors.teal + '10',
    justifyContent: 'center',
    alignItems: 'center',
  },
  radiusCircleText: {
    color: colors.teal,
    fontSize: 16,
    fontWeight: '600',
  },

  // Excluded Zipcodes Styles
  excludedZipsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  noExcludedText: {
    color: colors.gray,
    fontSize: 14,
    fontStyle: 'italic',
  },
  excludedZipTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.orange + '20',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    gap: 8,
  },
  excludedZipText: {
    color: colors.orange,
    fontSize: 13,
    fontWeight: '600',
  },
  excludedZipRemove: {
    color: colors.orange,
    fontSize: 14,
    fontWeight: '700',
  },
  addExcludedRow: {
    flexDirection: 'row',
    gap: 12,
  },
  addExcludedInput: {
    flex: 1,
    maxWidth: 200,
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  addExcludedBtn: {
    backgroundColor: colors.orange,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    justifyContent: 'center',
  },
  addExcludedBtnText: {
    color: colors.darkNavy,
    fontSize: 14,
    fontWeight: '600',
  },

  // Service Area Info Box
  serviceAreaInfo: {
    flexDirection: 'row',
    backgroundColor: colors.blue + '15',
    borderRadius: 12,
    padding: 16,
    gap: 12,
    marginTop: 16,
    borderWidth: 1,
    borderColor: colors.blue + '30',
  },
  serviceAreaInfoIcon: {
    fontSize: 24,
  },
  serviceAreaInfoContent: {
    flex: 1,
  },
  serviceAreaInfoTitle: {
    color: colors.blue,
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  serviceAreaInfoText: {
    color: colors.grayLight,
    fontSize: 13,
    lineHeight: 20,
  },
  servicesStatLabel: {
    fontSize: 13,
    color: colors.gray,
  },
  servicesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  viewAllLink: {
    color: colors.teal,
    fontSize: 14,
    fontWeight: '500',
  },
  servicesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  serviceCard: {
    width: isWeb && width > 768 ? 'calc(25% - 12px)' : isWeb && width > 500 ? 'calc(50% - 8px)' : '100%',
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    paddingTop: 55,
    borderWidth: 1,
    borderColor: colors.navyLight,
    position: 'relative',
    minHeight: 220,
  },
  serviceCardInactive: {
    opacity: 0.5,
  },
  serviceToggleWrap: {
    position: 'absolute',
    top: 42,
    right: 14,
    zIndex: 5,
  },
  serviceIconBox: {
    width: 48,
    height: 48,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14,
  },
  serviceIcon: {
    fontSize: 24,
  },
  serviceToggle: {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.navyMid,
    padding: 2,
    justifyContent: 'center',
  },
  serviceToggleOn: {
    backgroundColor: colors.teal,
  },
  serviceToggleDot: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.gray,
  },
  serviceToggleDotOn: {
    backgroundColor: colors.white,
    marginLeft: 'auto',
  },
  serviceTitle: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 6,
  },
  serviceDesc: {
    color: colors.gray,
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 12,
  },
  serviceEditBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: colors.gold + '15',
    borderRadius: 8,
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: colors.gold + '30',
  },
  serviceEditBtnText: {
    color: colors.gold,
    fontSize: 12,
    fontWeight: '600',
  },
  serviceEditForm: {
    gap: 4,
  },
  serviceEditInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
    ...(isWeb ? { outlineStyle: 'none' } : {}),
  },
  serviceEditInputSmall: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  serviceEditTextarea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  serviceEditLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.gray,
    marginTop: 12,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  serviceEditHint: {
    fontSize: 11,
    color: colors.gray,
    fontStyle: 'italic',
    marginTop: 4,
    marginBottom: 8,
    lineHeight: 16,
  },
  serviceEditRow: {
    flexDirection: 'row',
    gap: 12,
  },
  serviceColorPicker: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
    marginTop: 4,
  },
  serviceColorOption: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  serviceColorOptionActive: {
    borderColor: colors.white,
    ...(isWeb ? { boxShadow: '0 0 10px rgba(255,255,255,0.3)' } : {}),
  },
  serviceEditActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  serviceEditDone: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    backgroundColor: colors.teal,
    borderRadius: 10,
    marginTop: 12,
  },
  serviceEditDoneText: {
    color: colors.darkNavy,
    fontSize: 14,
    fontWeight: '700',
  },
  serviceDisplayBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    backgroundColor: colors.teal + '30',
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 6,
    zIndex: 10,
  },
  serviceDisplayBadgeText: {
    color: colors.teal,
    fontSize: 9,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // Testimonials Tab Styles
  tabHeaderActions: {
    flexDirection: 'row',
    gap: 12,
  },
  secondaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: isWeb && width > 900 ? 16 : 10,
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.navyMid,
    flexShrink: 1,
    minWidth: 0,
  },
  tabHeaderButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    maxWidth: '100%',
  },
  secondaryBtnText: {
    color: colors.grayLight,
    fontSize: 14,
    fontWeight: '500',
  },
  nextJobBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: isWeb && width > 900 ? 16 : 12,
    backgroundColor: colors.green,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.green,
    shadowColor: colors.green,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 3,
    flexShrink: 1,
    minWidth: 0,
  },
  nextJobBtnText: {
    color: colors.darkNavy,
    fontSize: 14,
    fontWeight: '700',
  },
  testimonialStats: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 24,
  },
  testimonialStat: {
    flex: 1,
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.navyLight,
  },
  testimonialStatNum: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.gold,
    marginBottom: 4,
  },
  testimonialStatLabel: {
    fontSize: 12,
    color: colors.gray,
  },
  testimonialScrollContainer: {
    flex: 1,
  },
  testimonialGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    paddingBottom: 20,
  },
  testimonialGridCard: {
    width: isWeb && width > 1024 ? 'calc(33.333% - 11px)' : isWeb && width > 600 ? 'calc(50% - 8px)' : '100%',
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 20,
    paddingTop: 50,
    borderWidth: 1,
    borderColor: colors.navyLight,
    position: 'relative',
    minHeight: 220,
  },
  testimonialCardInactive: {
    opacity: 0.5,
  },
  testimonialActiveBadge: {
    position: 'absolute',
    top: 12,
    left: 12,
    backgroundColor: colors.teal + '30',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  testimonialActiveBadgeText: {
    color: colors.teal,
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  testimonialGridToggle: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 40,
    height: 22,
    borderRadius: 11,
    backgroundColor: colors.navyMid,
    padding: 2,
    justifyContent: 'center',
  },
  testimonialGridHeader: {
    alignItems: 'center',
    marginBottom: 12,
  },
  testimonialGridAvatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colors.teal,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
  },
  testimonialGridName: {
    color: colors.white,
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 2,
  },
  testimonialGridLocation: {
    color: colors.gray,
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 6,
  },
  testimonialGridText: {
    color: colors.grayLight,
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
    fontStyle: 'italic',
    flex: 1,
  },
  testimonialGridActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.navyMid,
  },
  testimonialDeleteBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.red + '20',
    justifyContent: 'center',
    alignItems: 'center',
  },
  starSmall: {
    color: colors.gold,
    fontSize: 12,
  },
  testimonialHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  testimonialAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.teal + '20',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  testimonialAvatarImg: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  testimonialInitial: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.teal,
  },
  testimonialInfo: {
    flex: 1,
  },
  testimonialName: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  testimonialLocation: {
    color: colors.gray,
    fontSize: 12,
    marginBottom: 4,
  },
  testimonialStars: {
    flexDirection: 'row',
  },
  star: {
    color: colors.gold,
    fontSize: 14,
  },
  testimonialToggle: {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.navyMid,
    padding: 2,
  },
  testimonialToggleOn: {
    backgroundColor: colors.teal,
  },
  testimonialToggleDot: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.gray,
  },
  testimonialToggleDotOn: {
    backgroundColor: colors.white,
    marginLeft: 'auto',
  },
  testimonialText: {
    color: colors.grayLight,
    fontSize: 14,
    lineHeight: 22,
    fontStyle: 'italic',
    marginBottom: 16,
  },
  testimonialActions: {
    flexDirection: 'row',
    gap: 10,
  },
  ratingSelector: {
    flexDirection: 'row',
    gap: 8,
  },
  ratingStar: {
    fontSize: 28,
    color: colors.navyMid,
  },
  ratingStarActive: {
    color: colors.gold,
  },
  primaryBtnDisabled: {
    opacity: 0.6,
  },

  // Section Tabs (for Estimates & Quotes)
  sectionTabs: {
    flexDirection: 'row',
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 4,
    marginBottom: 24,
    gap: 4,
  },
  sectionTab: {
    flex: 1,
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionTabActive: {
    backgroundColor: colors.teal,
  },
  sectionTabText: {
    color: colors.gray,
    fontSize: 14,
    fontWeight: '600',
  },
  sectionTabTextActive: {
    color: colors.darkNavy,
  },

  // AI Config Styles
  aiConfigField: {
    marginBottom: 24,
  },
  aiConfigLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 4,
  },
  aiConfigDesc: {
    fontSize: 12,
    color: colors.gray,
    marginBottom: 12,
  },
  aiConfigInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  aiConfigTextarea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },

  // Keywords Styles
  keywordsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  keywordTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.red + '20',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    gap: 8,
  },
  keywordTagText: {
    color: colors.red,
    fontSize: 12,
    fontWeight: '500',
  },
  keywordTagRemove: {
    color: colors.red,
    fontSize: 14,
    fontWeight: '700',
  },
  addKeywordRow: {
    flexDirection: 'row',
    gap: 12,
  },
  addKeywordInput: {
    flex: 1,
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  addKeywordBtn: {
    backgroundColor: colors.teal,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    justifyContent: 'center',
  },
  addKeywordBtnText: {
    color: colors.darkNavy,
    fontSize: 14,
    fontWeight: '600',
  },

  // Complexity Rules Styles
  complexityRulesGrid: {
    flexDirection: isWeb && width > 800 ? 'row' : 'column',
    gap: 16,
  },
  complexityCard: {
    flex: 1,
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 16,
    borderWidth: 2,
    borderColor: colors.navyMid,
  },
  complexityCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  complexityDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  complexityCardTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  complexityDescInput: {
    backgroundColor: colors.navy,
    borderRadius: 8,
    padding: 12,
    color: colors.grayLight,
    fontSize: 13,
    minHeight: 60,
    textAlignVertical: 'top',
  },

  // Map Container Styles
  mapContainer: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 16,
  },
  mapPlaceholder: {
    height: 300,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.navy,
  },
  mapPlaceholderText: {
    color: colors.gray,
    fontSize: 16,
  },
  mapLegend: {
    padding: 16,
    backgroundColor: colors.navy,
  },
  mapLegendTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 12,
  },
  mapLegendItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  mapLegendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  mapLegendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  mapLegendText: {
    color: colors.grayLight,
    fontSize: 12,
  },

  // Radius Config Styles
  radiusConfig: {
    flexDirection: isWeb && width > 600 ? 'row' : 'column',
    gap: 24,
    alignItems: 'center',
  },
  radiusInputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  radiusInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    color: colors.white,
    fontSize: 24,
    fontWeight: '700',
    width: 100,
    textAlign: 'center',
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  radiusUnit: {
    color: colors.gray,
    fontSize: 16,
  },
  radiusVisual: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  radiusCircle: {
    borderRadius: 999,
    borderWidth: 3,
    borderColor: colors.teal,
    backgroundColor: colors.teal + '10',
    justifyContent: 'center',
    alignItems: 'center',
  },
  radiusCircleText: {
    color: colors.teal,
    fontSize: 16,
    fontWeight: '600',
  },

  // Excluded Zipcodes Styles
  excludedZipsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  noExcludedText: {
    color: colors.gray,
    fontSize: 14,
    fontStyle: 'italic',
  },
  excludedZipTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.orange + '20',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    gap: 8,
  },
  excludedZipText: {
    color: colors.orange,
    fontSize: 13,
    fontWeight: '600',
  },
  excludedZipRemove: {
    color: colors.orange,
    fontSize: 14,
    fontWeight: '700',
  },
  addExcludedRow: {
    flexDirection: 'row',
    gap: 12,
  },
  addExcludedInput: {
    flex: 1,
    maxWidth: 200,
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    color: colors.white,
    fontSize: 14,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  addExcludedBtn: {
    backgroundColor: colors.orange,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    justifyContent: 'center',
  },
  addExcludedBtnText: {
    color: colors.darkNavy,
    fontSize: 14,
    fontWeight: '600',
  },

  // Service Area Info Box
  serviceAreaInfo: {
    flexDirection: 'row',
    backgroundColor: colors.blue + '15',
    borderRadius: 12,
    padding: 16,
    gap: 12,
    marginTop: 16,
    borderWidth: 1,
    borderColor: colors.blue + '30',
  },
  serviceAreaInfoIcon: {
    fontSize: 24,
  },
  serviceAreaInfoContent: {
    flex: 1,
  },
  serviceAreaInfoTitle: {
    color: colors.blue,
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  serviceAreaInfoText: {
    color: colors.grayLight,
    fontSize: 13,
    lineHeight: 20,
  },

  // Portfolio Admin Styles
  portfolioStatsRow: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  portfolioStatCard: {
    flex: 1,
    minWidth: 100,
    backgroundColor: colors.navy,
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  portfolioStatNumber: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.white,
  },
  portfolioStatLabel: {
    fontSize: 12,
    color: colors.gray,
    textTransform: 'uppercase',
  },
  portfolioAdminGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  portfolioAdminCard: {
    width: isWeb && width > 900 ? 'calc(33.333% - 12px)' : isWeb && width > 600 ? 'calc(50% - 8px)' : '100%',
    backgroundColor: colors.navy,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  portfolioAdminCardInactive: {
    opacity: 0.6,
  },
  portfolioAdminPreview: {
    aspectRatio: 16/9,
    backgroundColor: colors.navyLight,
    position: 'relative',
  },
  portfolioAdminImage: {
    width: '100%',
    height: '100%',
  },
  portfolioAdminPlaceholder: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  portfolioAdminVideoBadge: {
    position: 'absolute',
    top: 10,
    left: 10,
    backgroundColor: colors.red,
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  portfolioAdminHiddenBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: colors.gray + '90',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  portfolioAdminHiddenText: {
    fontSize: 10,
    fontWeight: '700',
    color: colors.white,
  },
  portfolioAdminInfo: {
    padding: 14,
    gap: 8,
  },
  portfolioAdminTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
  },
  portfolioAdminMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  portfolioAdminCategoryBadge: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
  },
  portfolioAdminCategoryText: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    color: colors.teal, // Fallback color
  },
  portfolioAdminActions: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: colors.navyMid,
  },
  portfolioAdminActionBtn: {
    flex: 1,
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  portfolioTypeToggle: {
    flexDirection: 'row',
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    padding: 4,
    gap: 4,
  },
  portfolioTypeBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  portfolioTypeBtnActive: {
    backgroundColor: colors.teal,
  },
  portfolioTypeBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray,
  },
  portfolioCategoryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  portfolioCategoryBtn: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: colors.navyLight,
    borderWidth: 1,
    borderColor: colors.navyMid,
  },
  portfolioCategoryBtnActive: {
    backgroundColor: colors.teal,
    borderColor: colors.teal,
  },
  portfolioCategoryBtnText: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.gray,
  },
  portfolioColorGrid: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
  },
  portfolioColorBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  portfolioColorBtnActive: {
    borderColor: colors.white,
  },
  portfolioPreviewImage: {
    width: '100%',
    height: 200,
    borderRadius: 10,
  },
  portfolioPreviewContainer: {
    position: 'relative',
  },
  portfolioPreviewRemove: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.red,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageUploadRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
    marginTop: 8,
  },
  imageUploadBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.teal,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  imageUploadBtnDisabled: {
    opacity: 0.6,
  },
  imageUploadBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.darkNavy,
  },
  imageUploadOr: {
    fontSize: 13,
    color: colors.gray,
  },

  // Template Section Styles
  templatesSection: {
    backgroundColor: colors.navy,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.navyMid,
    marginBottom: 24,
    overflow: 'hidden',
  },
  templatesSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.navyMid,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  templatesSectionTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
  },
  templatesBadge: {
    backgroundColor: colors.teal + '30',
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  templatesBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.teal,
  },
  templatesHint: {
    fontSize: 12,
    color: colors.gray,
  },
  templatesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 12,
    gap: 12,
  },
  templateCard: {
    width: isWeb && width > 900 ? 'calc(33.333% - 8px)' : isWeb && width > 600 ? 'calc(50% - 6px)' : '100%',
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.navyMid,
    ...(isWeb ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {}),
  },
  templateCardAdded: {
    opacity: 0.6,
    ...(isWeb ? { cursor: 'default' } : {}),
  },
  templatePreview: {
    aspectRatio: 2.5,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  templateAddedOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  templateInfo: {
    padding: 12,
    gap: 6,
  },
  templateTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  templateMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  templateCategoryBadge: {
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  templateCategoryText: {
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  templateTypeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  templateTypeText: {
    fontSize: 10,
    color: colors.gray,
    textTransform: 'capitalize',
  },
  templateAction: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.navy,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.teal,
  },
});

